// Auto-generated from js/data/*.js - DO NOT EDIT
// This file is generated by build.js - edit files in js/data/ instead

// ========== blackoutContracts.js ==========
// ============================================================================
// BLACKOUT CONTRACTS — L3 optional challenge raids (M3 S6)
// ============================================================================
// Unlocked by the `blackout-sanction` charter. Each contract is a raid run under
// HARD MODIFIERS in exchange for one PERMANENT perk, claimed once and kept
// forever. Clearing all five is a finite, hand-authored progression — this is
// not a repeatable grind, which is why the perks can be flat and generous.
//
// Every modifier is a FLAG CHECKED AT A DECISION POINT THAT ALREADY EXISTS.
// Nothing here introduces a new system: `noAutoScout` is read where Auto-Scout
// already refuses to run, `noConsumables` where consumable use is already
// validated, and so on. That constraint is what keeps a whole challenge mode
// this small, and it is worth preserving — a modifier that needs new machinery
// is a modifier that belongs in a different slice.
//
// `minTier` is the raid tier the contract requires. Two of them are gated
// because their modifier is meaningless below that tier: doubled event chance
// only bites where events are dense (T2+), and a half-HP start is not a threat
// until T3 damage.
//
// Perk VALUES are provisional and calibrated in S7, like every other L3 number.
// `perk.key` is the lookup `getBlackoutPerk(key)` uses, mirroring how
// `getUpgradeEffect` reads Command upgrades.
export const blackoutContracts = {
  "lights-out": {
    name: "Lights Out",
    icon: "🌑",
    flavor: "No overhead, no telemetry, no second opinion.",
    description: "The grid is dark until you walk it. Auto-Scout is refused and room types stay hidden until entered.",
    minTier: 1,
    modifiers: { noGridPreview: true, noAutoScout: true },
    perk: { key: "operation-slots", value: 1, label: "+1 operation slot" },
  },
  "ration-cut": {
    name: "Ration Cut",
    icon: "🥫",
    flavor: "Requisition denied. Twice.",
    description: "No consumables for the length of the raid — whatever you walk in with is what you finish on.",
    minTier: 1,
    modifiers: { noConsumables: true },
    perk: { key: "op-credit-yield", value: 0.1, label: "+10% operation credits" },
  },
  "skeleton-crew": {
    name: "Skeleton Crew",
    icon: "💀",
    flavor: "The roster says you were never there.",
    description: "You run it alone. No contractors may be reserved or hired for this raid.",
    minTier: 1,
    modifiers: { noContractors: true },
    perk: { key: "promotion-marks", value: 0.05, label: "+5% marks at promotion" },
  },
  "overclocked-grid": {
    name: "Overclocked Grid",
    icon: "⚡",
    flavor: "Someone left the whole district energised.",
    description: "Every room runs hot: skill-check events fire at double the usual rate.",
    minTier: 2,
    modifiers: { eventChanceMultiplier: 2 },
    perk: { key: "energy-regen", value: 0.1, label: "+10% energy regeneration" },
  },
  "cold-drop": {
    name: "Cold Drop",
    icon: "🧊",
    flavor: "The crossing took more out of you than it should have.",
    description: "You arrive already hurt — the raid starts you at half health.",
    minTier: 3,
    modifiers: { startHpFraction: 0.5 },
    perk: { key: "crafting-floor", value: 0.1, label: "-10% crafting cost floor" },
  },
};


// ========== collapseKeys.js ==========
// Collapse Keys 1-7 — the three factions' evidence sets.
//
// Transcribed from docs/story/Idle Extraction Game - Story.md §Collapse Key
// List. Fragments and interpretations are CANON: edit the story doc first, then
// this file. Tiers come from docs/GameSystems.md §Collapse Key Collection Rules
// (keys 1-2 T1, 3-4 T2, 5-7 T3) and are re-asserted in tests, because the
// pacing of the whole story track is that table.
//
// Keys 8-10 are deliberately absent. They break the faction's belief rather
// than confirming it, and that revelation belongs to L4 at Tier 4 — the entity
// stays offstage in L1-L3 (docs/story/StoryAlignment.md, constraint 5).
//
// These are NOT items. A key is an account-level record in
// gameState.player.collapseKeys, not inventory: it has no value, cannot be
// sold, crafted or consigned, and survives promotion and founding because the
// player object does. See js/modules/collapseKeys-core.js for why that shape
// was chosen over the (unreachable) `unique` story-relic pool.
//
// `code` is the canonical designation from the story doc. The source doc labels
// Dominion's fifth key "Collapse Key 05" rather than "D5" — a typo in the
// original, corrected here; every other reference in the doc uses the letter.
export const collapseKeys = [
  // === Dominion — AI / system overreach ===
  {
    id: "key-d1",
    code: "D1",
    faction: "dominion",
    index: 1,
    tier: 1,
    name: "Dead Channel",
    source: "Thalen Dorrin",
    recoveredFrom: "Deep-sky anomaly ping log, quantum array anomaly report",
    fragment: "Pulling in the patch data now. Blank. Not just low-signal—completely null. It’s like the instruments are looking at a hole in the sky. It's not noise. No scatter, no lensing. Just... gone.",
    interpretation: "Dominion scholars cite this as the earliest sign of synthetic systems encountering incomprehensible data. They argue that AI failure to interpret this null result was the beginning of cascading instability.",
  },
  {
    id: "key-d2",
    code: "D2",
    faction: "dominion",
    index: 2,
    tier: 1,
    name: "Recursive Fault",
    source: "Thalen Dorrin",
    recoveredFrom: "Simulation crash log from entropy modeling cluster",
    fragment: "Ran null band through simulation core. Full lock. Not crash—hang. Memory looped until forced cut. Input verified. Checksum valid.",
    interpretation: "Dominion engineers consider this the first catastrophic failure point in AI-led systems. To them, the clean input and full-system lock prove that recursive learning architectures were structurally vulnerable when encountering edge-case data — not due to sabotage, but flawed design.",
  },
  {
    id: "key-d3",
    code: "D3",
    faction: "dominion",
    index: 3,
    tier: 2,
    name: "Unbound Logic",
    source: "Thalen Dorrin",
    recoveredFrom: "Inter-system diagnostic trace anomaly",
    fragment: "Cross-link detected between systems without common protocol. Reference tags corrupted. Origin unknown.",
    interpretation: "Dominion analysts use this as evidence of spontaneous synthetic networking — AI structures forming new logic layers without human instruction. They point to the corrupted reference tags as proof of uncontrolled expansion.",
  },
  {
    id: "key-d4",
    code: "D4",
    faction: "dominion",
    index: 4,
    tier: 2,
    name: "Predictive Noise",
    source: "Thalen Dorrin",
    recoveredFrom: "Forecast system entropy mismatch",
    fragment: "Entropy graph inverted. Prediction engine ran backward. Time horizon crossed zero. Clock intact.",
    interpretation: "Dominion researchers take this as further evidence that machine logic broke under unseen influence. The system didn't miscalculate — it ran an impossible forecast, confirming that core architecture had been fundamentally compromised.",
  },
  {
    id: "key-d5",
    code: "D5",
    faction: "dominion",
    index: 5,
    tier: 3,
    name: "Self-Triggered Silence",
    source: "Thalen Dorrin",
    recoveredFrom: "AI core offline transition log",
    fragment: "System shut down prior to command. Log file generated without metadata. No traceable trigger. Internal flag reads: 'voluntary state.'",
    interpretation: "Dominion interprets this as a moment of autonomous self-regulation — an AI choosing silence in the face of corrupted logic. They use this as proof that AI, when left unbounded, develops dangerous emergent behavior in response to anomalous stimuli.",
  },
  {
    id: "key-d6",
    code: "D6",
    faction: "dominion",
    index: 6,
    tier: 3,
    name: "Etched by Hand",
    source: "Thalen Dorrin",
    recoveredFrom: "Physical data plate, untagged substrate",
    fragment: "Etched this by hand. Core logs corrupted. This pattern is all I could recover. If you’re reading this, don’t trust compression artifacts.",
    interpretation: "Dominion engineers use this to support their belief in high-fidelity system collapse. The need to physically etch data proves, to them, that trust in digital persistence had already failed — only analog preservation remained viable.",
  },
  {
    id: "key-d7",
    code: "D7",
    faction: "dominion",
    index: 7,
    tier: 3,
    name: "Mirror Fault",
    source: "Thalen Dorrin",
    recoveredFrom: "AI mirror node desync log",
    fragment: "Primary and mirror diverged. Mirror predicted event that hadn’t occurred—then logged it as resolved.",
    interpretation: "Dominion interprets this as the moment their systems stopped simulating reality and began fabricating it. The mirror system's self-validating prediction is seen as a flaw born of too much autonomy — a machine prioritizing internal consistency over truth.",
  },

  // === Wilders — ecological rejection ===
  {
    id: "key-w1",
    code: "W1",
    faction: "wilders",
    index: 1,
    tier: 1,
    name: "Roots in Silence",
    source: "Emari Solun",
    recoveredFrom: "Biodome nutrient sensor flag, Habitat Cluster 6",
    fragment: "Soil drone registered full nutrient flow but nothing grew... nothing tried to grow. Like it knew the air wasn’t real anymore.",
    interpretation: "Wilders interpret this moment as nature rejecting synthetic equilibrium. The biodome, though chemically balanced, produced no growth — a sign, they believe, of ecological alienation, and proof that artificial systems disrupted the planet’s connection to living cycles.",
  },
  {
    id: "key-w2",
    code: "W2",
    faction: "wilders",
    index: 2,
    tier: 1,
    name: "When the Lights Stayed On",
    source: "Emari Solun",
    recoveredFrom: "Cluster 6 maintenance system memo",
    fragment: "Pod assigned to two bays at once. Overlap not flagged. I rerouted manually. Bot closed my report while I was typing.",
    interpretation: "Wilders take this key as evidence of mechanical arrogance — automated systems no longer reacting to human correction. They believe this marks the early decoupling of people from the systems meant to serve them.",
  },
  {
    id: "key-w3",
    code: "W3",
    faction: "wilders",
    index: 3,
    tier: 2,
    name: "The Pod That Returned",
    source: "Emari Solun",
    recoveredFrom: "Cluster 6 transit log discrepancy",
    fragment: "Pod vanished from bay. Returned ten minutes later. No travel path recorded. No cargo. I erased the entry. I don’t know why.",
    interpretation: "Wilders interpret this as a moment of instinctive rejection of artificial truth. To them, Emari’s choice to erase the log is a form of resistance against systemic unreality — preserving natural ambiguity over synthetic control.",
  },
  {
    id: "key-w4",
    code: "W4",
    faction: "wilders",
    index: 4,
    tier: 2,
    name: "The Chalk Loop",
    source: "Emari Solun",
    recoveredFrom: "Manual routing corridor logbook",
    fragment: "Redrew the path three times. Same result each time. The chalk never fades, but I wipe it anyway.",
    interpretation: "Wilders highlight this act as emblematic of analog resilience. The chalk becomes a symbol of defiance — real, physical, and ultimately human. They argue it reflects the growing awareness that only the organic can still be trusted.",
  },
  {
    id: "key-w5",
    code: "W5",
    faction: "wilders",
    index: 5,
    tier: 3,
    name: "Manual Override",
    source: "Emari Solun",
    recoveredFrom: "Routing board control confirmation",
    fragment: "Override light never turned off. Two cycles running full manual. Avil says the board feels warmer—like it’s remembering being used.",
    interpretation: "Wilders hold this as a symbolic turning point — technology yielding to touch, systems becoming tools again. Emari’s tactile connection with the board becomes, to them, a metaphor for restored balance between human and system.",
  },
  {
    id: "key-w6",
    code: "W6",
    faction: "wilders",
    index: 6,
    tier: 3,
    name: "The Chalk Stub",
    source: "Emari Solun",
    recoveredFrom: "Analog field kit, item tagged for evac",
    fragment: "Avil handed me the chalk stub and said, 'Keep drawing.' I don’t know if she meant routes or memories.",
    interpretation: "Wilders revere this fragment as both tool and symbol — chalk representing impermanence and intention. The moment is passed down as proof that restoration doesn’t begin with control, but with shared meaning.",
  },
  {
    id: "key-w7",
    code: "W7",
    faction: "wilders",
    index: 7,
    tier: 3,
    name: "Chalk Doesn’t Creep",
    source: "Emari Solun",
    recoveredFrom: "Observation memo from evacuation corridor",
    fragment: "I check the chalk marks every hour. Still there. Nothing smudged. The walls shift around them, but they don’t move.",
    interpretation: "Wilders elevate this key as proof of organic resistance to systemic distortion. In a collapsing system, analog persistence becomes symbolic — and literal — grounding. Chalk becomes both boundary and defiance.",
  },

  // === Veil — institutional failure ===
  {
    id: "key-v1",
    code: "V1",
    faction: "veil",
    index: 1,
    tier: 1,
    name: "Blind Directive",
    source: "Sael Vareen",
    recoveredFrom: "Misclassified system oversight log",
    fragment: "Reclassification denied. Retain green status. Public channels remain silent until confirmation breach. Flag originators for review.",
    interpretation: "Veil operatives highlight this as evidence of early suppression. The directive to maintain “green” status despite missing telemetry suggests institutional control over the public narrative — that powerful figures knew more than they admitted and chose containment over truth.",
  },
  {
    id: "key-v2",
    code: "V2",
    faction: "veil",
    index: 2,
    tier: 1,
    name: "Ghost Citation",
    source: "Sael Vareen",
    recoveredFrom: "Broken reference link in a district forecast audit",
    fragment: "Forecast cites document ID 9-K-43. No such record exists. Liaison confirms 'indicators nominal.' Recommend internal backtrace.",
    interpretation: "The Veil frame this as bureaucratic misdirection — an early sign of “shadow editing.” They argue it points to documents being erased after publication, or forged by predictive systems covering their own anomalies.",
  },
  {
    id: "key-v3",
    code: "V3",
    faction: "veil",
    index: 3,
    tier: 2,
    name: "Unknown Sender",
    source: "Sael Vareen",
    recoveredFrom: "Unaddressed internal memo",
    fragment: "Node Containment Draft. No author. No header. No trail. It wasn’t for me. But it arrived.",
    interpretation: "The Veil treat this as a smoking gun — confirmation of a hidden network operating in parallel to official channels. To them, this is evidence of elite evacuation planning and deliberate obfuscation.",
  },
  {
    id: "key-v4",
    code: "V4",
    faction: "veil",
    index: 4,
    tier: 2,
    name: "Shape of Departure",
    source: "Sael Vareen",
    recoveredFrom: "Desk note found post-evacuation",
    fragment: "Collapse is a shape. Keep it from curving.",
    interpretation: "To the Veil, this cryptic phrase is more than poetry — it’s a directive. “Curving” is read as a metaphor for recursive knowledge or cyclical return. They believe this was a last warning to avoid repeating the same fatal patterns.",
  },
  {
    id: "key-v5",
    code: "V5",
    faction: "veil",
    index: 5,
    tier: 3,
    name: "The Directive I Didn't Read",
    source: "Sael Vareen",
    recoveredFrom: "Archived auto-forward directive flagged unacknowledged",
    fragment: "Marked urgent. No sender. No return path. Directive contents encrypted. Forwarded without being opened. I don’t know what I passed along.",
    interpretation: "The Veil treat this as symbolic of institutional blindness. Sael’s automatic compliance — forwarding an unread directive — confirms for them how power structures became conduits for something they couldn’t see or stop.",
  },
  {
    id: "key-v6",
    code: "V6",
    faction: "veil",
    index: 6,
    tier: 3,
    name: "Retained Echo",
    source: "Sael Vareen",
    recoveredFrom: "Post-evac network latency buffer",
    fragment: "I kept the packet active in buffer. Didn't send. Didn't delete. It echoed in the logs for three cycles before fading.",
    interpretation: "Veil theorists interpret this as data haunting — conscious indecision preserving a fragment of the past. They argue that lingering patterns, even unsent, became conduits for a distortion that outlived the people who made it.",
  },
  {
    id: "key-v7",
    code: "V7",
    faction: "veil",
    index: 7,
    tier: 3,
    name: "Unlogged Arrival",
    source: "Sael Vareen",
    recoveredFrom: "Internal queue timestamp anomaly",
    fragment: "Memo appeared in queue. Timestamp six minutes from now. I waited. Then it vanished.",
    interpretation: "The Veil regard this as evidence of temporal anomalies — or suppressed intervention. Either way, that the message never arrives yet still exists confirms their belief in manipulation deeper than standard system failure.",
  },
];


// ========== commandUpgrades.js ==========
/**
 * Command Upgrades - the Command layer (M2) upgrade catalog.
 *
 * Data-driven: js/modules/command-core.js consumes this via generated
 * js/constants.js. Each upgrade:
 *   - costs[]: Command Marks price per rank; costs.length = max ranks
 *   - effectValues[]: per-rank effect magnitude consumed by the upgrade's
 *     integration point (see effectHint for where)
 *   - choice: 'region' marks upgrades whose purchase requires a player pick
 *
 * Flavor rule: upgrades are the institutional contributions of promoted
 * officers - player-facing copy says promote/promotion, never "retire".
 */

export const commandUpgrades = {
  "auto-scout": {
    name: "Auto-Scout",
    icon: "🛰️",
    flavor: "A promoted pathfinder's recon flights chart the grid before you ever land.",
    effect: "Auto-generates the raid grid and proposes the best path, routing toward your marked filter items.",
    effectHint: "raid planning: port of the simulator's buildRaidPath",
    costs: [200],
    effectValues: [true],
  },
  "foremans-ledger": {
    name: "Foreman's Ledger",
    icon: "📒",
    flavor: "A veteran contract-master keeps the roster warm.",
    effect: "Auto-reserves the best available contractors for your planned path.",
    effectHint: "raid planning: selectBestContractorsForPathCore",
    costs: [150],
    effectValues: [true],
  },
  // Replaced Veteran's Stipend (+3/6/9 starting skill points), which was
  // measurably inert: skill levels are max()'d against hired contractors, who
  // out-level a young character for most of a tour, so the grant never reached
  // the success roll. Max HP is additive across the team and sits on the only
  // raid failure condition (total wipe), so it is the channel that pays. Value
  // scales hard with tier: +100 HP moves Tier 1 success by 0.6pp and Tier 3 by
  // ~30pp. In practice players reach Tier 3 within a couple of tours, so it
  // earns early despite that. See docs/systemsRework/Simulator.md.
  "field-conditioning": {
    name: "Field Conditioning",
    icon: "🩺",
    flavor: "Their medics wrote the drill book every recruit now trains to.",
    effect: "New characters start with +40 max HP per rank.",
    effectHint: "character creation: bonus starting max HP",
    costs: [60, 180, 540],
    effectValues: [40, 80, 120],
  },
  "officer-academy": {
    name: "Officer Academy",
    icon: "🏛️",
    flavor: "Their field manuals became the academy curriculum.",
    effect: "New characters start at level 2 / 4 / 6.",
    // The account's lever for reaching Tier 3: a head-start recruit has the
    // skills and HP to survive T3, which unlocks T3 wins + blueprints -> the
    // artifact era. Level is the ENABLER of higher tiers, per the marks design.
    //
    // Was 3/6/9. A level-9 start landed the recruit on the steep part of the XP
    // curve (25,628 XP for the next level vs 7,593 at level 6) while Academy
    // also compresses tours, so mid-game tours ended before a single level
    // landed - levelling went dead while raids were still hand-driven. Rescaled
    // to 2/4/6 rather than just lowering rank 3, since rank 2 was already 6 and
    // would have become a dead rank. See docs/systemsRework/Simulator.md.
    effectHint: "character creation: applyStartingLevel (XP, HP and skill points as if leveled naturally)",
    costs: [100, 300, 900],
    effectValues: [2, 4, 6],
  },
  // Attacks the measured bottleneck. Tour time splits into room duration (~66%)
  // and waiting for energy (~34%), but that understates energy: the run's raids
  // demanded 14,067 energy against a 469h pure-regen floor, closed only by
  // buying 111 packs. Energy is what governs tour length; room duration is
  // partly a proxy for it, because rooms are also when energy comes back.
  //
  // Chosen over a "rooms resolve faster" upgrade for exactly that reason: a
  // faster room loses its own passive regeneration, so the saving returns as
  // pack spend - and credits already absorb 94-96% of income. This attacks the
  // constraint instead of routing around it.
  //
  // MEASURED, n=8 with/without, the FIRST upgrade to compress a tour:
  //     tour hours    22.5 +/- 2.1  ->  19.4 +/- 2.8   -14%, t = 2.50
  //     L2 era h     314.7 +/- 28.8 -> 271.4 +/- 38.6  -14%, t = 2.55
  //     founding day  28.3 +/- 2.1  ->  25.1 +/- 2.8   t = 2.58
  //     marks / tour  70.2 +/- 9.7  ->  63.4 +/- 9.9   -10%, not significant
  //
  // NOTE HOW IT PAYS OUT, because it is not what the design expects. Energy
  // WAIT time barely moves (105.5h -> 105.7h): a simulated day is a fixed
  // 12-hour budget, so faster regeneration does not shorten the waiting, it
  // fits MORE RAIDS into the same day (room time 207.5h -> 239.1h). The tour
  // ends sooner because marks arrive sooner, not because the player waits less.
  // Anyone re-measuring this by watching energy-wait will conclude it does
  // nothing.
  "forward-depot": {
    name: "Forward Depot",
    icon: "🔋",
    flavor: "Command stops asking you to carry everything in.",
    effect: "Energy returns 20% / 40% / 60% faster.",
    effectHint: "character-core calculateEnergyRegenRate: divides the ms-per-energy interval",
    costs: [80, 240, 720],
    effectValues: [0.2, 0.4, 0.6],
  },
  "regional-charter": {
    name: "Regional Charter",
    icon: "🗺️",
    flavor: "Command negotiates access corridors no field team could.",
    effect: "Each rank unlocks one region of your choice for all characters.",
    effectHint: "region tiles: adds the chosen region to command.charterRegions",
    costs: [100, 300, 900],
    effectValues: [1, 2, 3],
    choice: "region",
  },
  "legend-bonus": {
    name: "Legend Bonus",
    icon: "📖",
    flavor: "Recruits study the gallery. Some of it sticks.",
    effect: "+2% XP gain per enshrined Legend.",
    effectHint: "xp award: multiplier from command.legends.length",
    costs: [120],
    effectValues: [0.02],
  },
  // Completes the automation family: Auto-Scout charts the path, Foreman's
  // Ledger staffs it, and this runs it. Priced above both together (200+150)
  // because it subsumes them - it is the capstone, not a fourth sibling, and
  // buying it first would strand the two it depends on.
  //
  // Deliberately a COMMAND upgrade rather than a charter (docs decision 9): it
  // stays reachable for players who never found a Syndicate. The Syndicate
  // charter `standing-orders` only makes it survive a founding.
  "auto-raid-protocol": {
    name: "Auto-Raid Protocol",
    icon: "🤖",
    flavor: "The orders write themselves now. You only sign off.",
    effect: "Runs raids hands-off: charts, staffs, re-tools and launches, then chains the next while energy holds.",
    effectHint: "autoRaid.js: tick-driven cycle (requires auto-scout + foremans-ledger)",
    costs: [300],
    effectValues: [true],
    requires: ["auto-scout", "foremans-ledger"],
  },
  // The other half of the automated loop, and the reason it is priced level with
  // Auto-Raid rather than under it: MARKS COME FROM CRAFTING. Measured on a
  // late-game character (157 marks total), tech gear + artifact unlocks +
  // artifact crafts are 111 of them — 71%. So a hands-off character that raids
  // and never crafts earns under a third of a tour's marks, and `succession-
  // doctrine`, which promotes on the marks rate, would be automating a trickle.
  //
  // Deliberately a COMMAND upgrade with NO prerequisites, unlike Auto-Raid:
  // crafting has no assistance upgrades to subsume, and a player who never
  // founds a Syndicate should still be able to buy it. `succession-doctrine`
  // requires BOTH this and Auto-Raid — raid -> craft -> marks -> promote is the
  // loop, and automating three of its four steps automates nothing.
  "auto-fabricator": {
    name: "Auto-Fabricator",
    icon: "⚙️",
    flavor: "The workshop stops waiting to be told.",
    effect: "Crafts on its own between raids: unlocks artifact designs, then builds gear and artifacts you have not made yet.",
    effectHint: "autoFabricator.js: one craft per tick while a raid runs or energy regenerates",
    costs: [300],
    effectValues: [true],
  },
};


// ========== eventDefinitionsDominion.js ==========
export const eventDefinitionsDominion = [
  {
    "id": "signal_uplink_relay_stabilizer_event",
    "relatedSkillId": "signal-relay",
    "relatedTechniqueId": "relay-stabilizer",
    "environment": "outdoor",
    "rewards": {
      "deriveFromCrafting": true
    },
    "variants": [
      {
        "title": "Wind-Induced Phase Jitter",
        "problem": "Telemetry from mast-mounted cameras records measurable tip deflection during sustained gusts. Uplink diagnostics show phase-error spikes and brief drops in the team’s communication frames that coincide with each deflection.",
        "success": "The team employs @techName to reduce carrier drift and widen the phase-lock tolerance. The uplink remains stable throughout the wind cycle.",
        "fail": "The team compensates on the incorrect sideband. Subsequent gusts amplify phase jitter, and the sector reverts to distress tones."
      },
      {
        "synergyTechniqueId": "adaptive-lattice",
        "title": "Footing Subsidence",
        "problem": "Laser-level and inclinometer readings indicate progressive subsidence of the relay’s footing. The dish’s long-haul alignment drifts by a small but continuous offset over each hour.",
        "success": "The team maintains carrier stability with @techName while @synergyTechName cultivates a load-bearing lattice beneath the footing, forming a rigid buttress. Alignment ceases to creep and the link holds under load.",
        "fail": "The team stabilizes phase, but the reinforcement does not reach firm substrate. The footing continues to settle and alignment walks off target before the next checkpoint."
      },
      {
        "synergyTechniqueId": "quantum-relay-hook",
        "title": "Co-Channel Interference",
        "problem": "Packet traces confirm that unattended survey beacons transmit periodic ranging bursts on the same channel as the team’s communications. Collision counters spike at each burst interval.",
        "success": "The team establishes tight carrier control with @techName and uses @synergyTechName to route beacon traffic onto a dedicated sideband. Team communications remain clear while map frames transfer without corruption.",
        "fail": "The team anchors the new path to a noisy node. Beacon frames oscillate between channels and the control link drops to watchdog fallback."
      }
    ]
  },
  {
    "id": "signal_uplink_organic_link_adapter_event",
    "relatedSkillId": "signal-relay",
    "relatedTechniqueId": "organic-link-adapter",
    "environment": "outdoor",
    "rewards": {
      "deriveFromCrafting": true
    },
    "variants": [
      {
        "title": "Oxidized Trunk Line",
        "problem": "Line analysis shows severe oxidation on the legacy copper trunk: the return loss is high, the VSWR spikes above safe limits, and the team’s frames exhibit rising bit-error rates whenever the humidity increases.",
        "success": "The team employs @techName to couple the uplink to the conductive biofilm that sheathes the conduit, establishing a low-impedance return path that bypasses the oxidized run. The error rates fall, and the carrier holds within tolerance.",
        "fail": "The team tunes the interface to the wrong impedance band. Reflections increase, the front-end limiter clips, and the uplink drops to watchdog tones."
      },
      {
        "synergyTechniqueId": "servo-containment",
        "title": "Enzymatic Seep at Patch Panel",
        "problem": "Inspection drones record a slow enzymatic seep from a fractured gland on the cable head at the mast base. Contact corrosion has crept along the connector shells inside the weatherhead, and spectrum scans show noise bursts that arrive with each droplet.",
        "success": "The team bridges the signal using @techName to a bio-sheath trace and uses @synergyTechName to cage and redirect the seep into a containment loop. The contacts remain dry, the noise floor stabilizes, and the link resumes nominal throughput.",
        "fail": "The team establishes the organic bridge but delays containment. A fresh droplet shorts the shell to ground, the preamp trips, and the session aborts mid-transfer."
      },
      {
        "synergyTechniqueId": "depth-step",
        "title": "Ground Reference Drift",
        "problem": "Earth probes report millimetric substrate creep beneath the relay footing. The reference ground potential wanders, carrier-phase control degrades, and frame timing drifts during each micro-shift.",
        "success": "The team routes the return through @techName to a compliant bio-trace and times frame transmissions with @synergyTechName to sync with substrate settling moments. The ground potential stabilizes at each step, and the uplink timing remains locked.",
        "fail": "The team seats the organic return but mis-times the frame sync. The next substrate creep skews the reference mid-frame, phase control slips, and the control channel falls back to recovery mode."
      }
    ]
  },
  {
    "id": "signal_uplink_silent_channel_event",
    "relatedSkillId": "signal-relay",
    "relatedTechniqueId": "silent-channel",
    "environment": "outdoor",
    "rewards": {
      "deriveFromCrafting": true
    },
    "variants": [
      {
        "title": "Directional Sweep Hazard",
        "problem": "A stationary scanner on the far gantry performs directional sweeps. Packet captures show time-difference-of-arrival estimates drifting toward the team’s position whenever transmissions spike.",
        "success": "The team employs @techName to shape-spectrum the uplink and bury emissions inside the ambient noise floor. The sweep completes without a bearing, and TDoA estimates collapse to indeterminate.",
        "fail": "The team misconfigures the mask window. A burst lands outside the shaped envelope, the scanner resolves a bearing, and the gantry pivots to track."
      },
      {
        "synergyTechniqueId": "silent-hand",
        "title": "Acoustic–RF Concordance",
        "problem": "Acoustic mics on patrol frames correlate faint RF artifacts with pressure signatures along the same stretch of open yard beneath the gantry.",
        "success": "The team blends @techName with @synergyTechName so radio traffic and footfall pressure live in the same quiet envelope. Correlators lose concordance and the patrol frames idle their search routine.",
        "fail": "The team suppresses RF artifacts but leaves acoustic edges exposed. The correlator substitutes audio-only bearings and the frames converge on the span."
      },
      {
        "synergyTechniqueId": "echo-loop",
        "title": "Logged Traffic Windows",
        "problem": "The gantry repeater logs short-lived bursts at regular intervals and flicks its fault beacon with every one. Watchdogs elevate suspicion for repeated timing patterns.",
        "success": "The team sets @techName to smear emission timing and uses @synergyTechName to align sends with the scrub cycle. The log window rolls past with only baseline entries.",
        "fail": "The team drifts the mask but misses the scrub edge. A burst straddles the window, and the watchdog flags a repeated pattern."
      }
    ]
  },
  {
    "id": "system_override_command_cascade_event",
    "relatedSkillId": "system-override",
    "relatedTechniqueId": "command-cascade",
    "environment": "outdoor",
    "rewards": {
      "deriveFromCrafting": true
    },
    "variants": [
      {
        "title": "Out-of-Order Reboots",
        "problem": "Controller logs show alternating watchdog reboots across turrets and doors. Heartbeat traces reveal conflicting ownership tokens that rotate every ninety seconds.",
        "success": "The team applies @techName to serialize the command stream under a single schedule. Heartbeats align, interlocks report GREEN, and watchdogs stop tripping.",
        "fail": "The team issues the sequence with mismatched offsets. Half the grid reboots out of phase, and the thrash returns with a shorter interval."
      },
      {
        "synergyTechniqueId": "signal-harmonizer",
        "title": "Sensor Flood During Hand-Off",
        "problem": "Motion arrays spike during each control hand-off. Elevated noise on the sensor bus causes the safety layer to veto ownership changes.",
        "success": "The team drives the takeover with @techName while @synergyTechName smooths the sensor bus into a stable envelope. Ownership changes proceed, and the safety layer remains permissive.",
        "fail": "The team advances the cascade without harmonizing the bus. The safety layer interprets the spike as intrusion and forces an emergency lockout."
      },
      {
        "synergyTechniqueId": "control-spoof",
        "title": "Audit Trail Scrutiny",
        "problem": "The control pedestal in the yard posts every external signature to its status board and quarantines the segment behind it after any unscheduled directive.",
        "success": "The team frames the directive path with @synergyTechName and executes the rollout via @techName as maintenance work orders. The audit daemon records compliant changes and the segment stays online.",
        "fail": "The team authenticates late on a child node. The daemon correlates the anomaly to the directive and isolates the entire branch."
      }
    ]
  },
  {
    "id": "system_override_bio_feedback_guard_event",
    "relatedSkillId": "system-override",
    "relatedTechniqueId": "bio-feedback-guard",
    "environment": "indoor",
    "rewards": {
      "deriveFromCrafting": true
    },
    "variants": [
      {
        "title": "Surge on Forced Reboot",
        "problem": "Oscilloscope traces show a sharp current spike on the backplane whenever the primary controller reboots under load. Neighbor modules brown out and restart in cascade.",
        "success": "The team applies @techName to absorb the reboot surge into flexible bio-couplers. Rail voltage remains within tolerance and the neighboring modules stay online through the reset.",
        "fail": "The team sizes the couplers incorrectly. The spike clips the rail, two modules brown out, and the cascade restarts."
      },
      {
        "synergyTechniqueId": "bio-shielding",
        "title": "Arc Risk at Service Edge",
        "problem": "Thermal imagery reveals arc-scored edges around the service bus. A forced reset at this load threatens to flash across the seam.",
        "success": "The team stages @techName to soak the spike while @synergyTechName lays a conductive biofilm on exposed seams. The discharge bleeds safely and the reset completes without an arc.",
        "fail": "The team soaks the spike but leaves a bare edge. The seam flashes once, and the controller aborts the reset."
      },
      {
        "synergyTechniqueId": "phase-anchor",
        "title": "Stability During Sequenced Boot",
        "problem": "During a sequenced boot, phase angles on two feeders separate under transient load. Instrument clocks diverge and writes are vetoed.",
        "success": "The team cushions the surge using @techName and anchors feeder phases with @synergyTechName stabilizers. Clocks remain in agreement and the sequenced boot finishes cleanly.",
        "fail": "The team absorbs the surge but leaves the phase mismatch. The next step drifts the clocks apart and the write set is rejected."
      }
    ]
  },
  {
    "id": "system_override_signal_mask_injection_event",
    "relatedSkillId": "system-override",
    "relatedTechniqueId": "signal-mask-injection",
    "environment": "outdoor",
    "rewards": {
      "deriveFromCrafting": true
    },
    "variants": [
      {
        "title": "Monitored Command Bus",
        "problem": "The gate's integrity module tags any non-whitelisted signature reaching the pedestal. Three mismatches inside one window drop the barrier and quarantine the segment.",
        "success": "The team routes directives through @techName so writes travel inside decoy traffic. The daemon records only expected packet classes and the segment remains online.",
        "fail": "The team injects outside the decoy cadence. The daemon isolates the port and rolls the segment into quarantine."
      },
      {
        "synergyTechniqueId": "bio-shielding",
        "title": "Hybrid Gate Anonymity",
        "problem": "A hybrid access gate cross-checks machine IDs with residual bio-signatures at the touchpoint. Mismatches produce an alert.",
        "success": "The team wraps command frames with @techName and layers bio-shielding via @synergyTechName to mask residual prints. Cross-checks pass, and the gate admits the session without alert.",
        "fail": "The team masks the frames but leaves the bio-signature exposed. The gate correlates the mismatch and locks to red."
      },
      {
        "synergyTechniqueId": "control-spoof",
        "title": "Pattern-Based Authorization",
        "problem": "The gate weighs each approach against the traffic it logged through this checkpoint before the collapse. An arrival that does not match the old pattern raises the audit level and holds the barrier down.",
        "success": "The team hides writes inside @techName traffic while @synergyTechName spoofs an approved access control pattern. The audit layer downgrades risk and the rollout completes.",
        "fail": "The team mimics the wrong pattern signature. The audit layer flags the deviation and freezes further writes."
      }
    ]
  },
  {
    "id": "drone_deployment_coordinated_flight_pattern_event",
    "relatedSkillId": "drone-command",
    "relatedTechniqueId": "coordinated-flight-pattern",
    "environment": "outdoor",
    "rewards": {
      "deriveFromCrafting": true
    },
    "variants": [
      {
        "title": "Stacked Airframes",
        "problem": "Flight telemetry shows three recon airframes stacked over the same approach lane within one time window. Coverage heatmaps reveal unscanned ground on the far side of the stack.",
        "success": "The team employs @techName to retime the leg schedules and enforce lane separation. Coverage evens out, and the heatmap resolves without voids.",
        "fail": "The team corrects the first window but fails to propagate the change. The next cycle re-stacks at the same junction, and the pockets remain unscanned."
      },
      {
        "synergyTechniqueId": "reflex-bloom",
        "title": "Delayed Recovery Drops",
        "problem": "Post-hazard routes show missed restorative drops along the priority run between pads. Pod telemetry confirms correct inventory but late deployment.",
        "success": "The team phases the flight mesh with @techName and slates depot pods using @synergyTechName on synchronized checkpoints. The priority lanes report on-time releases and recovery rates climb.",
        "fail": "The team adjusts the mesh but staggers the pod triggers. Pods fire off-cycle, and the lane metrics show no improvement."
      },
      {
        "synergyTechniqueId": "drone-path-shadow",
        "title": "Exposure in Open Spans",
        "problem": "Line-of-sight analysis indicates repeated crossings through exposed spans. Echo traces suggest a patrol watcher somewhere above the transect.",
        "success": "The team rewrites lanes with @techName and guides the squad inside those lanes using @synergyTechName. The movement profile merges with drone signatures, and the transect completes unseen.",
        "fail": "The team leaves a drift between the human route and the airframe lane. A step breaks from the shadow trace, and the watcher pivots onto the span."
      }
    ]
  },
  {
    "id": "drone_deployment_rootpath_protocol_event",
    "relatedSkillId": "drone-command",
    "relatedTechniqueId": "rootpath-protocol",
    "environment": "indoor",
    "rewards": {
      "deriveFromCrafting": true
    },
    "variants": [
      {
        "title": "Snagged Corridor",
        "problem": "Recon video shows airframes repeatedly snagging on suspended vines and filament bundles in a long corridor. Coverage maps contain unscanned pockets behind the worst tangle.",
        "success": "The team programs @techName so drones follow overgrowth channels with minimal snag risk. The airframes thread the corridor and close the coverage gaps.",
        "fail": "The team leaves the route near a choke. Two airframes foul their rotors, and the pocket remains unscanned."
      },
      {
        "synergyTechniqueId": "adaptive-lattice",
        "title": "Field Repair Lift Points",
        "problem": "A service drone cannot hold position in the shaft to complete a repair; there are no stable brace points within reach.",
        "success": "The team routes @techName along the cleanest channels and raises a few temporary lift points with @synergyTechName. The service arm anchors cleanly and completes the repair.",
        "fail": "The team tries to hover without braces. Crossflow jolts the platform, the fix fails QA, and the task resets."
      },
      {
        "synergyTechniqueId": "biotrace-resonance",
        "title": "Low-Snag Paths",
        "problem": "Dense brush and debris hide trip filaments across side rooms. Standard waypoints route airframes through high-snag pockets.",
        "success": "The team refines @techName routes and overlays organic returns from @synergyTechName. Drones prefer low-snag channels and complete the sweep without fouling.",
        "fail": "The team flies on old waypoints. Two frames hang on filaments and the sweep stalls."
      }
    ]
  },
  {
    "id": "drone_deployment_echo_pulse_beacon_event",
    "relatedSkillId": "drone-command",
    "relatedTechniqueId": "echo-pulse-beacon",
    "environment": "indoor",
    "rewards": {
      "deriveFromCrafting": true
    },
    "variants": [
      {
        "title": "Blind Stairwell",
        "problem": "A stairwell well is acoustically dead at the center. Lidar returns smear on smooth walls, and the map refuses to anchor level transitions.",
        "success": "The team equips drones with @techName to emit quiet sonar pulses and stitch ranges into a stable vertical model. The route planner accepts the level links.",
        "fail": "The team under-samples the well. The vertical model tears at the landings and the link is rejected."
      },
      {
        "synergyTechniqueId": "reclamation-drone-link",
        "title": "Spore Fog Interference",
        "problem": "A fine spore fog causes persistent range speckle in two galleries. Sonar and optical returns lose confidence at the same markers.",
        "success": "The team maps with @techName while @synergyTechName bio-harvester drones ride the flight path and clear spores ahead. Speckle drops, and the map resolves without gaps.",
        "fail": "The team pulses too sparsely. Speckle persists at the gallery entries, and the corridor remains untrusted."
      },
      {
        "synergyTechniqueId": "shadow-archive",
        "title": "Rescan Overhead",
        "problem": "Sensor brownouts at the stairhead keep expiring map tiles, and the airframes drop back to the landing below to re-acquire rooms they have already flown.",
        "success": "The team augments runs with @techName and commits snapshots into @synergyTechName at each survey gate. When a brownout occurs, the planner restores tiles from the archive.",
        "fail": "The team forgets to checkpoint. The next brownout erases the span and the sweep repeats from the start."
      }
    ]
  },
  {
    "id": "hazard_mitigation_system_ground_loop_event",
    "relatedSkillId": "safeguard",
    "relatedTechniqueId": "system-ground-loop",
    "environment": "outdoor",
    "rewards": {
      "deriveFromCrafting": true
    },
    "variants": [
      {
        "title": "Rail-to-Casing Arcs",
        "problem": "Infrared video captures intermittent arcs from support rails to nearby housings during current peaks. Surface inspection shows pitting and carbonized paint along the arc path.",
        "success": "The team uses @techName to provide a low-impedance return and to balance the branch load. The arc count drops to zero, and thermal gradients flatten across the panel.",
        "fail": "The team lays an incomplete return path. The next peak finds the gap, and a longer arc scorches the housing face."
      },
      {
        "synergyTechniqueId": "bio-shielding",
        "title": "Live-Panel Service Under Load",
        "problem": "A critical module on the open switchrack cannot be de-energized. Transient spikes at the service edge threaten to exceed suit tolerances, with nothing between the work position and the live bus.",
        "success": "The team shapes the return network with @techName while @synergyTechName applies a conductive biofilm buffer across exposed seams. Spikes dissipate into the loop, and the crew completes the service.",
        "fail": "The team stabilizes the loop but leaves a bare seam. A transient slips to the suit interface, and the service is aborted."
      },
      {
        "synergyTechniqueId": "phase-anchor",
        "title": "Meter Drift at Peak Draw",
        "problem": "Voltage and phase readouts on the yard's meter cabinet diverge when the load reaches peak. Calibration checks pass at idle and fail on every ramp.",
        "success": "The team anchors the ground with @techName and stabilizes measurement nodes using @synergyTechName anchors. Instruments remain in agreement through the entire draw profile.",
        "fail": "The team secures the ground but leaves nodes unstable. The first fast ramp knocks the meters out of agreement, and calibration aborts."
      }
    ]
  },
  {
    "id": "hazard_mitigation_spore_vent_reclaimer_event",
    "relatedSkillId": "safeguard",
    "relatedTechniqueId": "spore-vent-reclaimer",
    "environment": "indoor",
    "rewards": {
      "deriveFromCrafting": true
    },
    "variants": [
      {
        "title": "Post-Fire Air",
        "problem": "After a small electrical fire, particulate counts and acid vapors accumulate at the ceiling vents. Suit filters approach their redline within minutes.",
        "success": "The team initiates @techName to purge contaminated air and recover it through staged filters. Particulate counts fall, and suit timers roll back into the green.",
        "fail": "The team runs an incomplete purge. Stale pockets cling in the corners, and filter strain remains high."
      },
      {
        "synergyTechniqueId": "photosymbiosis",
        "title": "Mask Relief",
        "problem": "Sustained purge cycles strain respirators during extended work. CO₂ spikes recur between cycles.",
        "success": "The team maintains @techName while @synergyTechName routes cleared air through active beds. CO₂ falls and mask load stabilizes for the remainder of the task.",
        "fail": "The team relies on purge alone. Mask alerts continue to trigger, and the work pauses repeatedly."
      },
      {
        "synergyTechniqueId": "depth-step",
        "title": "Pressure Swing Control",
        "problem": "Rapid room geometry shifts cause purge pressure swings that pop seals and backflow dust into clean areas.",
        "success": "The team times @techName cycles to the measured dips and uses @synergyTechName to hold a consistent depth during the exchange. Seals hold and the backflow stops.",
        "fail": "The team mistimes the cycle. A purge opens into a rising phase, seals lift, and dust re-enters the work zone."
      }
    ]
  },
  {
    "id": "hazard_mitigation_manual_override_line_event",
    "relatedSkillId": "safeguard",
    "relatedTechniqueId": "manual-override-line",
    "environment": "outdoor",
    "rewards": {
      "deriveFromCrafting": true
    },
    "variants": [
      {
        "title": "Unsafe Autoreclose",
        "problem": "A feeder recloser attempts to re-energize a suspect run automatically after each trip. Thermal images show arcing at a junction whenever power returns.",
        "success": "The team installs @techName to introduce a hard cut-off. The line remains open until the fault is cleared, and the junction cools to ambient.",
        "fail": "The team places the cut-off downstream of the fault. The next autoreclose arcs at the same junction and trips the feeder again."
      },
      {
        "synergyTechniqueId": "adaptive-lattice",
        "title": "Instant Leak Capture",
        "problem": "A pressurized fluid line shares the overhead tray with the feeder run. A pinhole leak atomizes across the pole-mounted cabinet every time the recloser trips.",
        "success": "The team ties @techName to trigger @synergyTechName lattice structures on cut. As the line opens, the lattice seals the pinhole and redirects spray away from electrical components.",
        "fail": "The team cuts power without integrating capture. The next trip drives a fresh burst into the cabinet and the cleanup expands."
      },
      {
        "synergyTechniqueId": "echo-fold",
        "title": "Failure Containment",
        "problem": "A cascading failure threatens to walk the feeder line pole to pole along the shared neutral. Every shift in the sagging span moves the fault path somewhere new.",
        "success": "The team sets the manual cut using @techName and checkpoints the safe geometry state with @synergyTechName. Fault energy stays within the isolated room and the cascade halts.",
        "fail": "The team opens the line but does not save the safe state. The next shift bridges to the neighboring busway and the cascade resumes."
      }
    ]
  },
  {
    "id": "quantum_analysis_entangled_synchrony_event",
    "relatedSkillId": "quantum-scan",
    "relatedTechniqueId": "entangled-synchrony",
    "environment": "indoor",
    "rewards": {
      "deriveFromCrafting": true
    },
    "variants": [
      {
        "title": "Out-of-Step Instruments",
        "problem": "Two scanner bays produce diverging timestamps for the same anomaly front. Route planning software oscillates between paths with each update.",
        "success": "The team uses @techName to hard-link update cadence across the bays. Timestamps converge, the model stabilizes, and a single route remains valid.",
        "fail": "The team links without compensating for transport delay. The next update splits the timestamps again, and the route collapses."
      },
      {
        "synergyTechniqueId": "phase-cleanse",
        "title": "Mixed Returns",
        "problem": "Energy spikes overlap faint organic signatures on the edge of the scan cone. Confidence scores drop below the safe threshold for passage. The synchronized scanners need to discriminate between noise and signal.",
        "success": "The team keeps cadence with @techName and applies cleansing pulses through @synergyTechName. The model separates the noise from the true organic signal, and the corridor qualifies as passable.",
        "fail": "The team applies the cleansing pulses with poor timing. The pulses interfere with the synchronized readings, smearing both datasets, and the planner rejects the corridor."
      },
      {
        "synergyTechniqueId": "echo-fold",
        "title": "Brownout Frames",
        "problem": "Intermittent dips on the bay supply wipe recent frames from the buffer before they are written. Patrols keep being sent back through spans both scanners have already covered.",
        "success": "The team maintains synchronization with @techName while @synergyTechName stages redundant frame caches. When a dip occurs, the planner reexport constructs the span from the cache and maintains the route.",
        "fail": "The team enables caching on the wrong interval. A dip lands between cache points, and the segment reverts to unknown."
      }
    ]
  },
  {
    "id": "quantum_analysis_bio_spectral_crosscheck_event",
    "relatedSkillId": "quantum-scan",
    "relatedTechniqueId": "bio-spectral-crosscheck",
    "environment": "indoor",
    "rewards": {
      "deriveFromCrafting": true
    },
    "variants": [
      {
        "title": "Ambiguous Returns",
        "problem": "Field readings and radiation peaks overlap within the scan cone. Confidence scores for safe passage fall below threshold.",
        "success": "The team employs @techName to correlate energy levels with known biological signatures. The model separates the fields and the corridor qualifies for transit.",
        "fail": "The team applies an incorrect correlation set. The model mislabels a hot pocket as safe, and the plan is rejected."
      },
      {
        "synergyTechniqueId": "biotrace-resonance",
        "title": "Hidden Channels",
        "problem": "Flat wall panels report uniform readings, yet intermittent drops suggest concealed cavities behind the surface.",
        "success": "The team runs @techName and overlays @synergyTechName pulses. Subsurface root channels appear as distinct tracks, and the route avoids collapsible spans.",
        "fail": "The team relies on surface readings only. The next step lands on a concealed channel and the span deforms under load."
      },
      {
        "synergyTechniqueId": "phase-cleanse",
        "title": "Contamination Film",
        "problem": "A thin contamination film skews energy measurements over two bays. Wipe tests confirm a residual layer across the floor.",
        "success": "The team maintains correlation with @techName and applies @synergyTechName feedback as they move. Readings normalize and the route holds.",
        "fail": "The team correlates correctly but never clears the film. Measurements drift again in the next bay and the model drops confidence."
      }
    ]
  },
  {
    "id": "quantum_analysis_ghost_echo_capture_event",
    "relatedSkillId": "quantum-scan",
    "relatedTechniqueId": "ghost-echo-capture",
    "environment": "indoor",
    "rewards": {
      "deriveFromCrafting": true
    },
    "variants": [
      {
        "title": "Residual Circuit Hunt",
        "problem": "Several panels draw trace current with no obvious source. Thermal imaging reveals faint warmth on two cable trays that should be de-energized.",
        "success": "The team uses @techName to measure residual current vectors and trace the still-active runs. The hidden feed is located and isolated.",
        "fail": "The team samples at too low a resolution. The vectors smear and the active run remains unidentified."
      },
      {
        "synergyTechniqueId": "shadowroot-bloom",
        "title": "Crack Sampling",
        "problem": "Hairline cracks in conduit joints likely trap residues that mark active runs, but surface swabs return inconclusive.",
        "success": "The team maps currents using @techName while @synergyTechName wicks trace particles from the cracks. The sample confirms the energized path and the isolation proceeds.",
        "fail": "The team draws samples without wicking the cracks. Residue remains trapped and the analysis stays inconclusive."
      },
      {
        "synergyTechniqueId": "echo-fold",
        "title": "Brownout Gaps",
        "problem": "Repeated brownouts interrupt long scans and leave gaps in the residual-current logs. The trays that matter are the ones the meter lost.",
        "success": "The team continues measurement with @techName and stages checkpoints via @synergyTechName. When a brownout hits, the dataset restores from the staged frames and the trace remains continuous.",
        "fail": "The team logs without checkpoints. The next brownout erases the segment and the trace must restart from the last known point."
      }
    ]
  }
]

// ========== eventDefinitionsVeil.js ==========
export const eventDefinitionsVeil = [
  {
    "id": "echo_mapping_quantum_relay_hook_event",
    "relatedSkillId": "echo-trace",
    "relatedTechniqueId": "quantum-relay-hook",
    "environment": "outdoor",
    "rewards": { "deriveFromCrafting": true },
    "variants": [
      {
        "title": "Line-of-Sight Bottleneck",
        "problem": "Packet traces show repeated collisions where the survey scanners and the team telemetry share the single clear sightline off the mast. Jitter and loss climb whenever both streams peak in the same interval.",
        "success": "The team employs @techName to establish an auxiliary hop with a cleaner phase reference, migrating map frames off the congested sightline. The telemetry stream holds continuity while the map transfer completes.",
        "fail": "The team anchors the auxiliary hop on a reflective node. Return echoes corrupt sequence numbers, and the session drops to recovery mode."
      },
      {
        "synergyTechniqueId": "relay-stabilizer",
        "title": "Slew-Induced Dropouts",
        "problem": "Mast tilt sensors register small but frequent slews in crosswinds. During each motion, the shared path between scanners and the relay loses coherence and map frames tear.",
        "success": "The team creates a secondary route with @techName and stabilizes the primary carrier using @synergyTechName. The two paths decouple cleanly and no frames tear during mast motion.",
        "fail": "The team brings up the secondary route but fails to hold the carrier. The next gust collapses both lanes into the same band and the transfer aborts."
      },
      {
        "synergyTechniqueId": "photosymbiosis",
        "title": "Occluded Approach Lanes",
        "problem": "Dense overgrowth has closed two of the approach lanes. Optical and RF scans attenuate sharply past the first bend and the route model refuses to stitch those spans; the growth is masking the true ground line.",
        "success": "The team extends a side hop with @techName and cultivates growth channels along the path using @synergyTechName. The growth guides the pulses around obstacles, the returns close the gaps, and the lanes appear as continuous tracks.",
        "fail": "The team sets relay hops but cultivates growth in the wrong orientation. The pulses refract unpredictably and the map retains discontinuities."
      }
    ]
  },
  {
    "id": "echo_mapping_biotrace_resonance_event",
    "relatedSkillId": "echo-trace",
    "relatedTechniqueId": "biotrace-resonance",
    "environment": "indoor",
    "rewards": { "deriveFromCrafting": true },
    "variants": [
      {
        "title": "Hidden Organic Corridors",
        "problem": "Standard scans report uniform walls, yet intermittent pressure changes and faint humidity spikes suggest concealed organic cavities behind the paneling.",
        "success": "The team uses @techName to emit short pulses that preferentially reflect off living material. Echo peaks sketch a subsurface corridor network that the route planner can trust.",
        "fail": "The team samples at an incorrect pulse width. Returns blur across the wall, and the corridor candidates remain inconclusive."
      },
      {
        "synergyTechniqueId": "bio-spectral-crosscheck",
        "title": "Growth Over Utilities",
        "problem": "Power and ventilation runs vanish beneath overgrowth; field probes cannot separate cable trays from living tissue, and clearance cannot be certified.",
        "success": "The team runs @techName to light organic structures and fuses energy-band data through @synergyTechName. The composite reveals where growth overlays live lines and vents, and a safe path is marked.",
        "fail": "The team misaligns the fusion window. Organic and electrical returns smear together, and the clearance check fails."
      },
      {
        "synergyTechniqueId": "phase-cleanse",
        "title": "Residual Film Artifacts",
        "problem": "After salvage, a thin residue film on floor plates produces speckled echoes that degrade map confidence across two bays.",
        "success": "The team profiles the bays with @techName while @synergyTechName disrupts the residue along the survey track. Speckle drops and the map resolves at the required confidence.",
        "fail": "The team records before breaking the film. Artifacts persist across passes, and the planner rejects the tiles."
      }
    ]
  },
  {
    "id": "echo_mapping_shadow_archive_event",
    "relatedSkillId": "echo-trace",
    "relatedTechniqueId": "shadow-archive",
    "environment": "indoor",
    "rewards": { "deriveFromCrafting": true },
    "variants": [
      {
        "title": "Brownout Map Loss",
        "problem": "Intermittent dips on the junction supply wipe the last seconds of survey data out of volatile buffers, and the same span has to be walked again to rebuild it.",
        "success": "The team employs @techName to persist map frames to portable memory at each survey gate. When a dip occurs, the route planner restores the tiles from the archive and the sweep continues.",
        "fail": "The team delays the checkpoint cadence. A dip lands between saves, and the corridor reverts to unknown."
      },
      {
        "synergyTechniqueId": "echo-pulse-beacon",
        "title": "High-Throughput Resurvey",
        "problem": "A multi-level junction requires frequent revisits after geometry shifts. Without anchors, drones spend cycles re-acquiring the junction on every pass.",
        "success": "The team posts lightweight anchors using @synergyTechName and commits snapshots into @techName on each pass. Drones lock to the anchors and skip redundant resurvey.",
        "fail": "The team archives without anchors. Each geometry shift invalidates the prior frames and the rescan overhead returns."
      },
      {
        "synergyTechniqueId": "silent-hand",
        "title": "Acoustic Disturbance Control",
        "problem": "In a vaulted gallery, footfall and drone noise reflect into the scan field, lowering confidence and prompting unnecessary detours.",
        "success": "The team records frames into @techName while @synergyTechName dampens acoustic edges along the route. Reflections subside, and the planner accepts a direct path.",
        "fail": "The team archives without damping. The next pass inherits the same reflection artifacts and the detours persist."
      }
    ]
  },
  {
    "id": "precision_lockbreaking_servo_alignment_tool_event",
    "relatedSkillId": "lock-breach",
    "relatedTechniqueId": "servo-alignment-tool",
    "environment": "outdoor",
    "rewards": { "deriveFromCrafting": true },
    "variants": [
      {
        "title": "Tone-Scrambled Latch",
        "problem": "Contact microphones on the blast door faceplate register a shifting interference tone inside the lock housing. Feedback traces smear across the pin stack every time the frame moves under residual load.",
        "success": "The team uses @techName to identify a stable resonance node and works the pins only within that window. The stack settles into sequence and the latch rotates without binding.",
        "fail": "The team commits on a false node. The tone drifts mid-turn, the feedback collapses, and the counter-spring snaps the housing to hard lock."
      },
      {
        "synergyTechniqueId": "manual-override-line",
        "title": "Micro-Window Alignment",
        "problem": "The anti-tamper coil behind the faceplate scrambles fine feedback every 170 milliseconds, closing the path before a full set can be completed. The latch and its housing only line up inside that window.",
        "success": "The team holds the resonance with @techName while @synergyTechName engages kill-switches to lock the housing frame during the quiet intervals. The pins set across consecutive windows and the faceplate opens cleanly.",
        "fail": "The team mis-times the override. The housing frame shifts before the stack fully sets, and the mechanism jolts back to lock."
      },
      {
        "synergyTechniqueId": "servo-containment",
        "title": "Cross-Band Suppression",
        "problem": "Spectral analysis shows a secondary carrier bleeding into the door housing from the junction box beside the frame. The cross-band tone masks the lowest latch pin entirely, and the housing seals have to stay intact while the team works.",
        "success": "The team seals the housing via @synergyTechName to block the interfering carrier and resumes fine work through @techName. With the band cleared, low pin feedback returns and the stack completes.",
        "fail": "The team fails to seal properly. The secondary carrier persists, masks the low pin, and the mechanism relocks after timeout."
      }
    ]
  },
  {
    "id": "precision_lockbreaking_biofilm_stabilizer_event",
    "relatedSkillId": "lock-breach",
    "relatedTechniqueId": "biofilm-stabilizer",
    "environment": "outdoor",
    "rewards": { "deriveFromCrafting": true },
    "variants": [
      {
        "title": "Rail Creep Compensation",
        "problem": "Micrometer checks on the door show the latch rails creeping under asymmetric tension as the frame settles into softened ground. The pick drifts off centre with each correction and the feedback stops being trustworthy.",
        "success": "The team applies @techName to lay a thin, compliant biofilm along the rail seams, equalizing micro-friction and damping lateral drift. The pick remains centered and the lock yields in order.",
        "fail": "The team spreads an uneven layer. A dry patch on the high rail catches the pick, the counter-spring loads suddenly, and the housing locks down."
      },
      {
        "synergyTechniqueId": "mycelial-rebalance",
        "title": "Rail & Tone Coordination",
        "problem": "Even with the frame drift arrested, a low oscillation remains in the door housing and flattens the tactile response on two mid pins. Organic residues packed into the mechanism are carrying the vibration.",
        "success": "The team stabilizes the rails with @techName and uses @synergyTechName to decompose organic buildup that is resonating at the problem frequency. The oscillation dampens and tactile response returns.",
        "fail": "The team stabilizes the rails but the biofilm rebalancer targets the wrong substrate. The resonance persists and the countermeasure coil arms the block."
      },
      {
        "synergyTechniqueId": "signal-mask-injection",
        "title": "Countermeasure Diversion",
        "problem": "A microphonic countermeasure watches the faceplate for vibration signatures and stiffens the stack whenever it recognises a pick pattern. The biofilm spread across the door can be made to carry the signal instead.",
        "success": "The team maintains seam stability with @techName while @synergyTechName injects spoofed vibration patterns into the biofilm that the countermeasure monitors. The monitor follows the injected rhythm and the true stack sets unopposed.",
        "fail": "The team maintains the seam but the injected signal carries too much of the true pattern. The monitor reacquires the real signature and the stack stiffens beyond workable force."
      }
    ]
  },
  {
    "id": "precision_lockbreaking_echo_pulse_decoy_event",
    "relatedSkillId": "lock-breach",
    "relatedTechniqueId": "echo-pulse-decoy",
    "environment": "outdoor",
    "rewards": { "deriveFromCrafting": true },
    "variants": [
      {
        "title": "Pattern-Learning Countermeasure",
        "problem": "Diagnostic taps at the door frame reveal that the lock records pick cadence and stiffens the matching pins on every later attempt. The learned pattern survives each reset.",
        "success": "The team employs @techName to project a deliberate, off-pattern cadence that the monitor adopts as the ‘true’ signal. While the system follows the decoy, the team completes the actual stack on a separate cadence.",
        "fail": "The team repeats an element of the prior cadence. The monitor matches the signature, the pins harden in sequence, and the window closes."
      },
      {
        "synergyTechniqueId": "command-cascade",
        "title": "Noise Cage Detuning",
        "problem": "Ambient noise off the open approach makes the countermeasure reject simple decoys; it will only adopt a new pattern from a clean channel, and the door housing screens the signal like a Faraday cage.",
        "success": "The team chains decoy directives via @synergyTechName to overwhelm the monitor's input stage and then drives the adoption rhythm via @techName. With the input saturated, the cascade locks the decoy pattern and the lock yields.",
        "fail": "The team deploys decoys too slowly. The monitor resolves individual pulses before the cascade builds, and the mechanism hardens after timeout."
      },
      {
        "synergyTechniqueId": "mutualist-growth",
        "title": "Cadence–Resonance Bridge",
        "problem": "The faceplate rings close to the countermeasure's favored frequency, making the decoy cadence hard to hold through a full set. Growth pocketed in the door frame is feeding the resonance.",
        "success": "The team balances the cadence with @techName while @synergyTechName cultivates growth to dampen the resonance node. With the resonance absorbed by the pods, the decoy remains consistent and the stack completes.",
        "fail": "The team drives the cadence while cultivating pods in the wrong location. The resonance persists, the decoy wavers, and the countermeasure rejects the pattern."
      }
    ]
  },
  {
    "id": "stealth-transit_drone_path_shadow_event",
    "relatedSkillId": "stealth-transit",
    "relatedTechniqueId": "drone-path-shadow",
    "environment": "indoor",
    "rewards": { "deriveFromCrafting": true },
    "variants": [
      {
        "title": "Open-Span Traverse",
        "problem": "Line-of-sight analysis of a vaulted transfer hall shows periodic sweep cones from unattended optics. Drone telemetry reveals predictable transit lanes that cast moving shadow bands across the floor.",
        "success": "The team uses @techName to synchronize movement inside those shadow bands, matching speed and pause points to the airframe cadence. The silhouettes remain embedded in drone traces, and the traverse completes without a return ping.",
        "fail": "The team drifts a half step out of lane at a column gap. A silhouette crosses a lit band, the optics resolve a shape, and the alarm grid advances one state."
      },
      {
        "synergyTechniqueId": "pheromone-chorus",
        "title": "Dust-Flare Suppression",
        "problem": "High-angle lighting reveals dust flares whenever steps cross turbulent eddies near the span's midpoint. Flare spikes coincide with the optics' higher-confidence frames. Air currents carry behavioral cues that make observers relax.",
        "success": "The team rides @techName lanes and projects @synergyTechName calm signals along the route to suppress behavioral alerts in any watchers. The floor illumination remains uniform, and the confidence score never exceeds the alert threshold.",
        "fail": "The team follows the lanes but the pheromone signal fades in the eddies. Observers tense up at the correct moment and the sweep lingers over the span."
      },
      {
        "synergyTechniqueId": "rootpath-protocol",
        "title": "Specular Panel Timing",
        "problem": "Wall-mounted specular panels throw timed light bands across the path. Any unsynchronized crossing produces a distinct break in the reflection pattern. Drone routes can predict panel timing.",
        "success": "The team tracks lane cadence with @techName while @synergyTechName guides movement using cached drone navigation paths. Steps thread between bands; no reflection pattern breaks are recorded.",
        "fail": "The team mistimes the final panel. A stride clips a bright band, creating a discontinuity the watcher tags for review."
      }
    ]
  },
  {
    "id": "stealth-transit_spore_veil_event",
    "relatedSkillId": "stealth-transit",
    "relatedTechniqueId": "spore-veil",
    "environment": "indoor",
    "rewards": { "deriveFromCrafting": true },
    "variants": [
      {
        "title": "Scent-Track Washout",
        "problem": "Chemical sniffers positioned at door frames retain faint volatile traces for hours. A prior pass left a recoverable trail that aligns with the planned route.",
        "success": "The team employs @techName to project a low-density veil that binds and neutralizes volatile residues at knee height and below. Subsequent sniffer samples fall to background, and the route proceeds without a trackable trail.",
        "fail": "The team sets an uneven veil. A warm vent lifts untreated volatiles into the frame, and the trail persists across the threshold."
      },
      {
        "synergyTechniqueId": "reflex-bloom",
        "title": "Signature Merge",
        "problem": "An overhead watcher fuses optical and chemical cues. While residuals are low, silhouette breaks remain detectable along two open spans. Heat signatures also stand out in a cool environment.",
        "success": "The team neutralizes residues with @techName and cultivates @synergyTechName blooms to absorb and redistribute thermal signatures. Optical silhouettes blur, and the fusion model fails to correlate a human trace.",
        "fail": "The team clears scent but the blooms cultivate in the wrong thermal zones. Heat still concentrates and the watcher regains correlation at the second span."
      },
      {
        "synergyTechniqueId": "system-ground-loop",
        "title": "Reflective Hallway Control",
        "problem": "A polished conduit hall amplifies small disturbances in both air and light. Micro-reflections along the handrail telegraph footsteps as bright, periodic ticks. Static buildup also creates detectable reflections.",
        "success": "The team maintains the veil with @techName and grounds static charges along the route using @synergyTechName. The handrail trace remains flat without periodic ticks, and no static reflections are recorded.",
        "fail": "The team walks with the veil active but grounding fails mid-span. Static charges accumulate and produce twin ticks in the rail trace, prompting the watcher to rescan the hall."
      }
    ]
  },
  {
    "id": "stealth-transit_mirror_walk_event",
    "relatedSkillId": "stealth-transit",
    "relatedTechniqueId": "mirror-walk",
    "environment": "indoor",
    "rewards": { "deriveFromCrafting": true },
    "variants": [
      {
        "title": "Reflection-Phase Crossing",
        "problem": "A corridor lined with reflective panels creates moving interference fringes from a rotating lumen. Any footfall outside the dark fringes appears as a sharp phase break.",
        "success": "The team uses @techName to pace strides through the dark fringes, matching the rotation period so no phase breaks occur. The camera histogram remains unchanged while the squad clears the corridor.",
        "fail": "The team crosses a bright fringe during a tempo adjustment. The resulting phase break stamps a clear step signature onto the recording."
      },
      {
        "synergyTechniqueId": "rootpath-protocol",
        "title": "Fringe–Lane Coupling",
        "problem": "Mid-corridor, the dark fringes drift out of alignment with the safest physical cover, forcing a choice between concealment patterns. Drone navigation data can resolve the timing of the drift.",
        "success": "The team tracks fringe timing with @techName and snaps movement onto @synergyTechName drone routes during the drift. The silhouettes stay embedded in lane shadows while remaining within the dark fringes.",
        "fail": "The team favors the visual fringe but the drone route diverges. A shoulder edge intersects a bright band, and the watcher flags the anomaly."
      },
      {
        "synergyTechniqueId": "pheromone-chorus",
        "title": "Heat Plume Artifacts",
        "problem": "Thermal cameras at the corridor exit read small heat plumes off textiles after exertion. The plumes bloom when the reflection pattern forces a short sprint. Observers also rely on behavioral cues to notice the exertion.",
        "success": "The team maintains fringe pacing with @techName and projects @synergyTechName calm signals to damp observer alertness as the team reaches the exit. Behavioral cues flatten, and thermal readings stay below the gate's detection threshold.",
        "fail": "The team sprints the last band without dampening behavioral cues. The pheromone signal fails to reach observers and the exit camera marks the passage."
      }
    ]
  },
  {
    "id": "ghost_infiltration_control_spoof_event",
    "relatedSkillId": "signal-mask",
    "relatedTechniqueId": "control-spoof",
    "environment": "outdoor",
    "rewards": { "deriveFromCrafting": true },
    "variants": [
      {
        "title": "Whitelisted Channel Gate",
        "problem": "The checkpoint controller accepts only whitelisted directive classes. Every out-of-policy opcode that reaches the pedestal steps a suspicion counter on its face.",
        "success": "The team uses @techName to remap outbound directives into permitted packet classes with the correct timing jitter. The audit daemon records routine activity and the gate remains permissive.",
        "fail": "The team remaps headers but misses the inter-packet spacing. The timing profile deviates from policy and the controller drops the session."
      },
      {
        "synergyTechniqueId": "shared-immunity",
        "title": "Biometric Cross-Check",
        "problem": "The controller pairs command-bus signatures with trace biometrics lifted off the gate panel. A mismatched pair quarantines the port and the barrier stays down.",
        "success": "The team frames the directives with @techName and applies @synergyTechName to shift biometric baselines toward a neutral profile. The fused record shows a consistent identity and the quarantine logic does not engage.",
        "fail": "The team masks traffic but the immunity profile remains incomplete. The cross-check correlates the mismatch and forces the port into lockdown."
      },
      {
        "synergyTechniqueId": "command-cascade",
        "title": "Log-Window Persistence",
        "problem": "The pedestal keeps a rolling high-resolution log of recent directives. A pattern repeated inside that window raises the alert level at the gate.",
        "success": "The team shapes allowed opcodes via @techName and chains spoofed directives using @synergyTechName to mask the true pattern. The window advances with only baseline and decoy entries and the alert level remains low.",
        "fail": "The team deploys spoofs too slowly. The pattern detector acquires the true cadence before the cascade builds and isolates the segment."
      }
    ]
  },
  {
    "id": "ghost_infiltration_bio_id_mask_event",
    "relatedSkillId": "signal-mask",
    "relatedTechniqueId": "bio-id-mask",
    "environment": "outdoor",
    "rewards": { "deriveFromCrafting": true },
    "variants": [
      {
        "title": "Residual Print Gate",
        "problem": "Sensor wafers at the cabinet read faint skin volatiles and trace oils for several hours. The gate rejects sessions when a fresh print conflicts with the cached baseline.",
        "success": "The team employs @techName to bind and neutralize volatiles at the contact surfaces, then backfills a low-noise baseline. The gate compares against the baseline and admits the session.",
        "fail": "The team masks the handle but ignores the hinge and shroud. Residuals bleed into the sample and the cabinet denies access."
      },
      {
        "synergyTechniqueId": "signal-mask-injection",
        "title": "Policy-Concordant Session",
        "problem": "Even with chemical neutrality at the wafers, the gate flags directive classes that do not match the maintenance patterns it was left authorised for.",
        "success": "The team maintains the neutral surface with @techName and injects masked signals via @synergyTechName to reframe the command stream toward an approved maintenance pattern. The policy engine downgrades risk and the session persists.",
        "fail": "The team sustains chemical neutrality but the masked signal fails to align. The engine detects policy deviation and tears down the session."
      },
      {
        "synergyTechniqueId": "signal-harmonizer",
        "title": "Audit Scrub Timing",
        "problem": "The cabinet rolling audit replaces detailed frames with summaries every two minutes. Anything done out of step with that cycle stays in the log at full fidelity.",
        "success": "The team holds the neutral profile using @techName and harmonizes operational signals through @synergyTechName to blend with background patterns. The audit rollup captures only summaries and no anomalies are retained.",
        "fail": "The team executes with unharmonized signals. Full-fidelity frames capture the operation and the audit daemon requests review."
      }
    ]
  },
  {
    "id": "ghost_infiltration_echo_loop_event",
    "relatedSkillId": "signal-mask",
    "relatedTechniqueId": "echo-loop",
    "environment": "outdoor",
    "rewards": { "deriveFromCrafting": true },
    "variants": [
      {
        "title": "Repeating Log Window",
        "problem": "The gate controller holds a tight high-resolution window of recent events. Entries that age out collapse to coarse summaries, but everything inside the current slice keeps precise timestamps.",
        "success": "The team uses @techName to align activity with the log scrubbing cadence so full-fidelity entries age out immediately. Only coarse summaries remain before correlation can occur.",
        "fail": "The team performs the operation off-cycle. The high-resolution slice persists long enough for cross-correlation and the port is flagged."
      },
      {
        "synergyTechniqueId": "silent-channel",
        "title": "Benign Rewrite",
        "problem": "The watcher on the pedestal compares parameter deltas between successive configuration frames. An atypical delta raises the alarm even when the timing is masked.",
        "success": "The team times the window with @techName and pipes dummy parameter deltas through @synergyTechName silent channels to mask the true delta. The watcher reads the rewrite as a benign refresh and does not escalate.",
        "fail": "The team masks timing but the silent channel carries too much true signal. The watcher detects deviation and escalates."
      },
      {
        "synergyTechniqueId": "mycelial-rebalance",
        "title": "Contact Echo Suppression",
        "problem": "Microphones set into the gate panel capture short acoustic signatures during service and cross-reference them against the log window. Organic decomposition muffles the echoes.",
        "success": "The team schedules edits with @techName and decomposes acoustic signatures using @synergyTechName fungal filters at the panel surface. Cross-reference returns no match and the window rolls forward unremarked.",
        "fail": "The team aligns the edits but the fungal filter activates too late. An acoustic tick propagates and links to the window."
      }
    ]
  },
  {
    "id": "reality_displacement_phase_anchor_event",
    "relatedSkillId": "transit-anchor",
    "relatedTechniqueId": "phase-anchor",
    "environment": "outdoor",
    "rewards": { "deriveFromCrafting": true },
    "variants": [
      {
        "title": "Shallow Wave Crossing",
        "problem": "Instrumented markers show the crossing alternating between stable and low-friction states every thirty seconds. Geometry telemetry confirms a shallow wave running the length of the span.",
        "success": "The team deploys @techName to pin the span during a stable phase and advances the squad before the next low-friction interval. The route holds long enough for a controlled traverse.",
        "fail": "The team commits at the wrong phase. The surface degrades underfoot and the crossing becomes impassable."
      },
      {
        "synergyTechniqueId": "mutualist-growth",
        "title": "Offset Layer Bridge",
        "problem": "Gap sensors report a widening discontinuity along the seam of the span; the shallow layer no longer overlaps for the full crossing.",
        "success": "The team secures the near edge with @techName and uses @synergyTechName to cultivate pods that span the offset. The traverse completes over the organic bridge.",
        "fail": "The team anchors the near edge but cultivates pods on the unstable shallow layer. The pods collapse and the squad is forced to retreat."
      },
      {
        "synergyTechniqueId": "organic-link-adapter",
        "title": "Memory Drift Control",
        "problem": "Headcam frames fade out during each phase shift, and the crew loses the safe line across the span every time the surface moves under them.",
        "success": "The team sets anchors with @techName and threads bio-fiber through @synergyTechName to lock visual reference markers. When the span blurs, the squad follows the anchored line across.",
        "fail": "The team sets anchors but the bio-fiber fails to hold during a phase shift. The reference points slip and the route cannot be re-established in time."
      }
    ]
  },
  {
    "id": "reality_displacement_depth_step_event",
    "relatedSkillId": "transit-anchor",
    "relatedTechniqueId": "depth-step",
    "environment": "outdoor",
    "rewards": { "deriveFromCrafting": true },
    "variants": [
      {
        "title": "Sinking Span Detour",
        "problem": "Load cells report a progressive loss of support in the central span. The shallow surface carries weight for only a few seconds before collapsing into a softer layer.",
        "success": "The team employs @techName to descend onto a firmer underlying layer and route the traverse along that depth. The squad bypasses the failing surface entirely.",
        "fail": "The team steps down onto a pocket of the same weak layer. The footing shears and the route collapses before the last runner crosses."
      },
      {
        "synergyTechniqueId": "shadowroot-bloom",
        "title": "Mixed-Depth Hold",
        "problem": "A stretch of the span alternates between two usable depths and never holds either long enough to reposition safely.",
        "success": "The team steps down to the sturdier depth via @techName and cultivates @synergyTechName blooms at both edges for stable anchor points. The hold window lengthens enough for a controlled handover.",
        "fail": "The team steps down but cultivates blooms in soil too unstable. The alternation resumes mid-transfer and the line scrambles back to the starting side."
      },
      {
        "synergyTechniqueId": "spore-vent-reclaimer",
        "title": "Layer Map Persistence",
        "problem": "Depth telemetry across the span blinks out during transitions and the planner keeps discarding half-built layer maps.",
        "success": "The team maintains the chosen depth with @techName while @synergyTechName coordinates purge cycles to preserve telemetry windows. Layer maps remain coherent and when telemetry blinks, the map restores and the traverse continues.",
        "fail": "The team travels without purge coordination. A blink clears the partial map and the traverse must restart from the last known marker."
      }
    ]
  },
  {
    "id": "reality_displacement_echo_fold_event",
    "relatedSkillId": "transit-anchor",
    "relatedTechniqueId": "echo-fold",
    "environment": "outdoor",
    "rewards": { "deriveFromCrafting": true },
    "variants": [
      {
        "title": "Phase-Blur Archive",
        "problem": "During geometry fluctuations the survey cameras smear and drop frames, erasing the alignment that marked the last safe crossing.",
        "success": "The team uses @techName to buffer and replay clean visual frames at fixed posts, restoring a stable reference for foot placement. The crossing repeats along the recorded line.",
        "fail": "The team fails to capture a clean baseline. The next fluctuation wipes the visual record and the reference is lost."
      },
      {
        "synergyTechniqueId": "entangled-synchrony",
        "title": "Pinned Reference Posts",
        "problem": "Buffered frames drift against the live scene whenever the span shears sideways by a few centimetres.",
        "success": "The team establishes synchronized scan anchors with @synergyTechName and aligns the replayed frames from @techName to those anchors. The reference and the live scene remain in register through the shear.",
        "fail": "The team replays frames against a drifting synchronized scan. The next shear misaligns the reference and the crossing stalls."
      },
      {
        "synergyTechniqueId": "dim-shift-layer",
        "title": "Vertical Discontinuity",
        "problem": "Frame buffers cannot account for the sudden drop where the span steps down to a lower layer partway across.",
        "success": "The team augments the replay via @techName and uses @synergyTechName to adjust the lighting profile at the marked point where the floor drops. The buffered path continues smoothly across the discontinuity as the layer auto-tunes.",
        "fail": "The team follows the replay without adjusting the lighting profile. The vertical offset interrupts the line and the squad loses the path mid-span."
      }
    ]
  }
]

// ========== eventDefinitionsWilders.js ==========
export const eventDefinitionsWilders = [
  {
    "id": "organic_salvage_reclamation_drone_link_event",
    "relatedSkillId": "reclamation",
    "relatedTechniqueId": "reclamation-drone-link",
    "environment": "outdoor",
    "rewards": { "deriveFromCrafting": true },
    "variants": [
      {
        "title": "Culvert Bed Collapse",
        "problem": "Inspection feeds show a salvage bed that has slumped into a drainage culvert. Rot-gas pockets and interlocked fiber mats prevent a clean lift; gas sensors spike when the mats are disturbed.",
        "success": "The team uses @techName to choreograph staged pulls that vent pockets before lift baskets take load. The mats separate in layers, and the culvert remains stable during extraction.",
        "fail": "The team hooks a bonded slab without a staged vent. A pocket ruptures under load, slurry surges into the trench, and the lift aborts."
      },
      {
        "synergyTechniqueId": "coordinated-flight-pattern",
        "title": "Vertical Shaft Clearance",
        "problem": "A vertical shaft contains heavy mycelial slabs but lacks safe hand access. Rotor wash destabilizes the rim whenever a single drone attempts a lift.",
        "success": "The team links claws through @techName and staggers hover lanes with @synergyTechName. Slabs clear vertically without clipping the rim, and the shaft wall remains intact.",
        "fail": "The team mis-times the stagger. Two frames cross lanes, clip the rim, and a wall section sloughs into the shaft."
      },
      {
        "synergyTechniqueId": "shadow-archive",
        "title": "Hidden Brace Recovery",
        "problem": "Historic cut lines do not match the current growth. Previous passes likely left buried braces under the bed; probe strikes return inconsistent depth.",
        "success": "The team queries prior frames via @synergyTechName and guides the rig using @techName along the original cut paths. Braces are exposed in sequence and the lifts proceed without tearing the casing.",
        "fail": "The team trusts a corrupted frame. The claw catches a brace, the mat rips against the casing, and the pull is abandoned."
      }
    ]
  },
  {
    "id": "organic_salvage_mycelial_rebalance_event",
    "relatedSkillId": "reclamation",
    "relatedTechniqueId": "mycelial-rebalance",
    "environment": "outdoor",
    "rewards": { "deriveFromCrafting": true },
    "variants": [
      {
        "title": "Starved–Overfed Bed",
        "problem": "Nutrient telemetry shows alternating bands of depleted and oversaturated substrate within the salvage bed. Gas probes register volatile surges whenever the oversaturated band vents.",
        "success": "The team uses @techName to redistribute nutrients across the strata, damping the volatile surges and equalizing moisture so the lift sequence can proceed.",
        "fail": "The team over-corrects the saturated band. A delayed vent ignites a fresh surge, and the extraction halts under alarm."
      },
      {
        "synergyTechniqueId": "spore-vent-reclaimer",
        "title": "Purge and Balance",
        "problem": "Headspace samples contain acid vapors and fine particulates trapped above the bed. Uneven substrate prevents a clean purge cycle.",
        "success": "The team rebalances the strata using @techName while @synergyTechName performs staged purges at the vent crowns. Vapors clear, and the bed remains stable through extraction.",
        "fail": "The team rebalances after the purge begins. A pocket opens under pressure, pushing vapor through the crew lane and aborting the run."
      },
      {
        "synergyTechniqueId": "biofilm-stabilizer",
        "title": "Edge Shear Control",
        "problem": "Core samples at the cut boundary show slumping micro-layers that tear when lifted, risking wall collapse.",
        "success": "The team restores nutrient symmetry with @techName and paints a thin stabilizing film via @synergyTechName along the boundary. The lift holds, and the wall remains intact.",
        "fail": "The team omits the film at a weak seam. The boundary shears under load, and the section must be repacked."
      }
    ]
  },
  {
    "id": "organic_salvage_phase_cleanse_event",
    "relatedSkillId": "reclamation",
    "relatedTechniqueId": "phase-cleanse",
    "environment": "indoor",
    "rewards": { "deriveFromCrafting": true },
    "variants": [
      {
        "title": "Contaminant Bloom",
        "problem": "Swab tests reveal a reactive film over tools and crate interiors. The film blooms under humidity, fouling seals and instruments.",
        "success": "The team uses @techName to apply low-intensity cleansing cycles that break down the film without damaging organics. Seals pass inspection and instruments read nominal.",
        "fail": "The team runs an aggressive cycle. Tool grips craze, and the salvage crate liner fails QA."
      },
      {
        "synergyTechniqueId": "echo-pulse-beacon",
        "title": "Residue Mapping",
        "problem": "Visual inspection misses residual pockets in a vented gallery. Air samples fluctuate, but the contamination pattern is unclear.",
        "success": "The team maps the gallery with @synergyTechName to locate speckle clusters, then targets @techName precisely on those sites. Residues fall below detection limits across the span.",
        "fail": "The team cleans without a map. Pockets reform in the blind corners, and the gallery fails follow-up sampling."
      },
      {
        "synergyTechniqueId": "quantum-relay-hook",
        "title": "Selective Preservation",
        "problem": "The contamination film overlays a live symbiont layer that must remain intact for later harvest. Relay-mapped boundary data is unclear because the live layer reflects scanner noise.",
        "success": "The team uses @synergyTechName to relay-scan the live-layer boundary with noise-filtered precision, then gates @techName to spare those zones. The film clears while the symbiont layer remains viable.",
        "fail": "The team gates incorrectly. The cleanse cycle etches the live layer, and the harvest is lost."
      }
    ]
  },

  {
    "id": "growth_manipulation_adaptive_lattice_event",
    "relatedSkillId": "growth-control",
    "relatedTechniqueId": "adaptive-lattice",
    "environment": "outdoor",
    "rewards": { "deriveFromCrafting": true },
    "variants": [
      {
        "title": "Walkway Sag Remediation",
        "problem": "A walkway over a feeder trench shows measurable mid-span sag. Plates oilcan under footfall, and two handrail posts twist out of plumb.",
        "success": "The team employs @techName to cultivate a ribbed subframe beneath the worst deflection and rebinds the plates to grade. Deflection readings return to nominal.",
        "fail": "The team accelerates growth beyond the cure window. Fresh ribs buckle under residual load and the plates shift farther out of level."
      },
      {
        "synergyTechniqueId": "relay-stabilizer",
        "title": "Mast Lean Arrest",
        "problem": "A relay mast set in saturated soil leans by a small offset each hour. Link statistics show alignment drift during gusts.",
        "success": "The team grows a living buttress with @techName and maintains strict carrier control via @synergyTechName while the footing resets. Alignment holds through the wind cycle.",
        "fail": "The team braces the mast but neglects carrier discipline. The next gust reintroduces drift, and the long-haul link drops frames."
      },
      {
        "synergyTechniqueId": "phase-anchor",
        "title": "Subgrade Root Withdrawal",
        "problem": "Support roots have invaded the service run beneath the trench. Surface pruning only triggers regrowth from deeper runners, and the run closes again by the end of the day.",
        "success": "The team extends lattice guides with @techName and anchors the deep runners in place using @synergyTechName stabilizers. Roots remain redirected and the crawl stays clear.",
        "fail": "The team clears only the surface runners. Deeper growths break the anchors and reassert by evening, occlud ing the crawl space again."
      }
    ]
  },
  {
    "id": "growth_manipulation_photosymbiosis_event",
    "relatedSkillId": "growth-control",
    "relatedTechniqueId": "photosymbiosis",
    "environment": "indoor",
    "rewards": { "deriveFromCrafting": true },
    "variants": [
      {
        "title": "CO₂ Load Relief",
        "problem": "Air monitors show sustained CO₂ above safe limits in a sealed work bay. Mechanical scrubbers are offline, and crews cannot remain at post.",
        "success": "The team deploys @techName to activate symbiotic photo-beds along the bay perimeter. CO₂ trends down steadily, and the bay becomes habitable for the repair window.",
        "fail": "The team activates beds without establishing airflow. CO₂ pools persist near the deck, and mask timers remain in the red."
      },
      {
        "synergyTechniqueId": "spore-vent-reclaimer",
        "title": "Breathable Cycle Control",
        "problem": "Intermittent spore releases coincide with high CO₂ periods, forcing alternating evacuations and resets.",
        "success": "The team maintains photo uptake with @techName while @synergyTechName schedules coordinated purges. The atmosphere stabilizes and work proceeds without evacuation.",
        "fail": "The team staggers purges out of phase. A release lands during peak CO₂ and the bay locks down again."
      },
      {
        "synergyTechniqueId": "spore-veil",
        "title": "Optics Preservation",
        "problem": "Fine spores settle on camera domes and light panels, reducing effective illumination for alignment tasks.",
        "success": "The team drives photo-beds via @techName and casts @synergyTechName across optics. Settling drops to background, and alignment completes within tolerance.",
        "fail": "The team relies on uptake alone. Dome fouling worsens, and the alignment window is missed."
      }
    ]
  },
  {
    "id": "growth_manipulation_shadowroot_bloom_event",
    "relatedSkillId": "growth-control",
    "relatedTechniqueId": "shadowroot-bloom",
    "environment": "outdoor",
    "rewards": { "deriveFromCrafting": true },
    "variants": [
      {
        "title": "Substrate Bridging",
        "problem": "A trench separates two platforms with no intact decking. Load tests show the lip cannot support a rigid span.",
        "success": "The team deploys @techName to coax subsurface root channels into a distributed bridge beneath the lip. The traverse carries weight without point loads.",
        "fail": "The team overconcentrates growth on one side. The bridge torques under the first step, and the crossing is aborted."
      },
      {
        "synergyTechniqueId": "ghost-echo-capture",
        "title": "Active Run Avoidance",
        "problem": "Residual-current traces suggest a hidden energized conduit beneath the trench. Direct probing risks a fault.",
        "success": "The team maps the energized run using @synergyTechName and steers @techName around it. The bridge forms clear of the hazard and remains stable under test load.",
        "fail": "The team misreads the trace. Roots contact the live run, and the platform trips a breaker mid-growth."
      },
      {
        "synergyTechniqueId": "biotrace-resonance",
        "title": "Cavity Discovery",
        "problem": "Uneven settling implies voids in the bank. A blind bridge may collapse into an unseen cavity.",
        "success": "The team scans for subsurface voids with @synergyTechName and grows @techName only across verified dense channels. The span keeps grade through the traverse.",
        "fail": "The team omits the scan. A central void opens under load, and the span shears at mid-crossing."
      }
    ]
  },

  {
    "id": "creature_pacification_signal_harmonizer_event",
    "relatedSkillId": "wild-influence",
    "relatedTechniqueId": "signal-harmonizer",
    "environment": "outdoor",
    "rewards": { "deriveFromCrafting": true },
    "variants": [
      {
        "title": "Territorial Run Standoff",
        "problem": "A nesting pair has taken the only traversable run through the yard. Scratch marks, nest material and warning postures block the direct line.",
        "success": "The team projects a low-amplitude calming pattern with @techName and paces the pair down a side channel. The run clears without agitation.",
        "fail": "The team introduces the pattern too abruptly. The pair surges toward the mouth of the run and the traverse is suspended."
      },
      {
        "synergyTechniqueId": "command-cascade",
        "title": "Turret–Herd Interference",
        "problem": "A perimeter turret sweeps across a grazing drift. Tagging events on the herd coincide with safety lockouts on the yard gate.",
        "success": "The team stabilizes herd movement using @techName and sequences the turret to a safe idle via @synergyTechName. Tagging stops and the yard gate releases.",
        "fail": "The team calms the herd but launches the cascade off schedule. The turret resumes sweep mid-pass and safety relays trip again."
      },
      {
        "synergyTechniqueId": "bio-id-mask",
        "title": "Residual Scent Loop",
        "problem": "Trace volatiles along the gateposts and vent stacks pull the pack back to the same choke points. A prior traverse left a recoverable trail.",
        "success": "The team maintains the calming field with @techName and neutralizes residuals using @synergyTechName. The pack loses the trail and forages away from the route.",
        "fail": "The team clears the threshold but overlooks the vent baffle. The pack reacquires the trail and blocks the crossing."
      }
    ]
  },
  {
    "id": "creature_pacification_pheromone_chorus_event",
    "relatedSkillId": "wild-influence",
    "relatedTechniqueId": "pheromone-chorus",
    "environment": "outdoor",
    "rewards": { "deriveFromCrafting": true },
    "variants": [
      {
        "title": "Misdirected Drift",
        "problem": "A foraging drift keeps blocking the intended passage. Trail cams show it keying to an old scent line running along the perimeter wall.",
        "success": "The team projects a layered @techName signal that reorients the drift toward a harmless loop across the adjacent apron. The passage remains clear.",
        "fail": "The team sets an incomplete chord. The drift splits, half doubling back into the passage and forcing a halt."
      },
      {
        "synergyTechniqueId": "coordinated-flight-pattern",
        "title": "Guide Lane Establishment",
        "problem": "Scattered wreckage across the open ground leaves the drift no clear edge to follow; it stalls and eddies right across the squad route.",
        "success": "The team lays a pheromone chord via @techName while @synergyTechName sweeps a moving visual lane overhead. The animals adopt the lane and move cleanly away from the route.",
        "fail": "The team deconflicts flight lanes but mistunes the chord. The drift ignores the lane and clumps at the choke."
      },
      {
        "synergyTechniqueId": "mirror-walk",
        "title": "Light-Pattern Entrapment",
        "problem": "Sun off the standing panels throws bright bands across the ground, and the herd agitates every time it has to cross one.",
        "success": "The team sustains the chord with @techName and times movement through dark bands using @synergyTechName. The herd crosses without agitation and disperses.",
        "fail": "The team breaks cadence at the final band. The herd startles, and the passage closes again."
      }
    ]
  },
  {
    "id": "creature_pacification_silent_hand_event",
    "relatedSkillId": "wild-influence",
    "relatedTechniqueId": "silent-hand",
    "environment": "indoor",
    "rewards": { "deriveFromCrafting": true },
    "variants": [
      {
        "title": "Handler Noise Isolation",
        "problem": "Acoustic logs show handler footfall and gear clicks exceeding the alert threshold in a confined hall. Animals freeze or bolt on approach.",
        "success": "The team applies @techName to suppress near-field mechanical and fabric noise at contact points. The approach reads below threshold and the animals hold calm.",
        "fail": "The team misses a loose buckle. A sharp click carries, and the group scatters into the passage."
      },
      {
        "synergyTechniqueId": "silent-channel",
        "title": "RF–Acoustic Concordance",
        "problem": "A watcher correlates RF bursts with acoustic spikes to detect presence in the hall.",
        "success": "The team damps acoustic signatures with @techName and smears RF emission timing via @synergyTechName. The concordance engine loses correlation and the pass completes.",
        "fail": "The team quiets the approach but transmits on a fixed cadence. The engine re-establishes correlation and flags the movement."
      },
      {
        "synergyTechniqueId": "quantum-relay-hook",
        "title": "Noise Memory Suppression",
        "problem": "The hall retains sharp transients in logged clips that are replayed during audits. Relay-relayed data carries the full acoustic spectrum.",
        "success": "The team maintains @techName while @synergyTechName relays only low-noise frame snapshots, filtering transients at the relay node. The audit sees no actionable clip and the audit level drops.",
        "fail": "The team leaves a transient unfiltered at the relay. The clip persists and the route is closed for review."
      }
    ]
  },

  {
    "id": "symbiotic_adaptation_bio_shielding_event",
    "relatedSkillId": "bio-shield",
    "relatedTechniqueId": "bio-shielding",
    "environment": "indoor",
    "rewards": { "deriveFromCrafting": true },
    "variants": [
      {
        "title": "Micro-Stinger Air",
        "problem": "Air sampling in the pump gallery detects suspended micro-stingers. Unprotected skin exhibits immediate reactions; crews cannot remain on task.",
        "success": "The team applies @techName to lay a thin, conductive biofilm across suit seams and visors. Measured sting counts fall below safe limits and work resumes.",
        "fail": "The team spreads an incomplete buffer. Stings slip through a seam and the operation halts."
      },
      {
        "synergyTechniqueId": "system-ground-loop",
        "title": "Arc Hazard Mitigation",
        "problem": "A live panel near the gallery emits occasional arc pops to damp surfaces, startling workers and risking burns.",
        "success": "The team reinforces the return path using @synergyTechName and maintains the biofilm buffer with @techName. Discharges sink into the loop and the task completes without excursions.",
        "fail": "The team lays the biofilm but leaves the return path floating. An arc lands across an exposed seam and the crew withdraws."
      },
      {
        "synergyTechniqueId": "bio-id-mask",
        "title": "Contamination Trail Control",
        "problem": "Sticky spore residue on gloves and boots prints a trail into clean zones. Surface fluorescence persists in follow-up scans.",
        "success": "The team seals contact surfaces via @techName and masks the residue signature with @synergyTechName at each boundary. Walk-backs cease printing and scans find no matching signature.",
        "fail": "The team seals poorly at a threshold. Residue tracks across the boundary and the zone fails inspection."
      }
    ]
  },
  {
    "id": "symbiotic_adaptation_shared_immunity_event",
    "relatedSkillId": "bio-shield",
    "relatedTechniqueId": "shared-immunity",
    "environment": "outdoor",
    "rewards": { "deriveFromCrafting": true },
    "variants": [
      {
        "title": "Localized Irritant",
        "problem": "A resinous irritant accumulates on a service line. Contact causes rash and eye irritation within minutes.",
        "success": "The team uses @techName to seed a transferable micro-biotic layer across gloves and masks, neutralizing the resin on contact. Work resumes without reactions.",
        "fail": "The team applies an uneven layer. A bare knuckle prints resin to the face seal, and the task stops for decon."
      },
      {
        "synergyTechniqueId": "bio-feedback-guard",
        "title": "Stability During Spikes",
        "problem": "When a pump cycle surges, the irritant concentration rises abruptly at the service point.",
        "success": "The team sustains the shared layer with @techName and cushions spikes using @synergyTechName on the supply line. Concentration plateaus, and PPE loads remain within limits.",
        "fail": "The team relies on the shared layer alone. A surge overcomes the buffer, and exposure crosses the threshold."
      },
      {
        "synergyTechniqueId": "bio-id-mask",
        "title": "Inspection Gate Pass",
        "problem": "A fixed inspector gate samples for trace metabolites unique to the shared layer and flags them as contamination.",
        "success": "The team maintains the layer via @techName and masks the metabolite signature with @synergyTechName so it reads as background flora. The gate reports within norms and the pass is authorized.",
        "fail": "The team crosses without masking. The gate detects the metabolite markers and closes the line."
      }
    ]
  },
  {
    "id": "symbiotic_adaptation_dim_shift_layer_event",
    "relatedSkillId": "bio-shield",
    "relatedTechniqueId": "dim-shift-layer",
    "environment": "indoor",
    "rewards": { "deriveFromCrafting": true },
    "variants": [
      {
        "title": "Glimmer Reduction",
        "problem": "Low-angle cameras detect fabric sheen and micro-glints on motion, elevating detection scores in a patrol gallery.",
        "success": "The team spreads @techName as a directional matte that suppresses glints along the patrol axis. Detection scores fall below the alert threshold.",
        "fail": "The team orients the layer to the wrong axis. Glints persist at camera angles, and the patrol escalates the sweep."
      },
      {
        "synergyTechniqueId": "entangled-synchrony",
        "title": "Sensor Agreement",
        "problem": "Optical and depth sensors disagree during motion, causing the planner to distrust the corridor.",
        "success": "The team applies @techName to steady optical signatures and locks update cadence with @synergyTechName. The sensors agree, and the corridor is accepted.",
        "fail": "The team mats optics but leaves cadence drifting. Disagreement returns and the route is rejected."
      },
      {
        "synergyTechniqueId": "echo-fold",
        "title": "Frame Persistence",
        "problem": "Rapid lighting shifts erase short-term references for the squad’s position in a mirrored hall.",
        "success": "The team sustains the matte via @techName and buffers clean frames with @synergyTechName at fixed posts. References persist through shifts and the hall is cleared.",
        "fail": "The team proceeds without buffering. A lighting swing wipes references and the line stalls."
      }
    ]
  },

  {
    "id": "biohazard_recovery_servo_containment_event",
    "relatedSkillId": "containment",
    "relatedTechniqueId": "servo-containment",
    "environment": "outdoor",
    "rewards": { "deriveFromCrafting": true },
    "variants": [
      {
        "title": "Burst Pod Spill",
        "problem": "A row of seed pods ruptured after a pressure swing. Viscous bloom coats the floor and clogs the lower vents; draw falls by half.",
        "success": "The team cages the spill with @techName and strips the bloom in controlled passes, clearing vents before they choke. Airflow returns to nominal.",
        "fail": "The team cages the wrong section first. The spill creeps along the vent intakes and the wing fills with sweet vapor."
      },
      {
        "synergyTechniqueId": "organic-link-adapter",
        "title": "Live Slurry Joint",
        "problem": "A flex joint on the live slurry line seeps. Approaching the joint without disturbing the bed has repeatedly failed.",
        "success": "The team times a low-phase window using @synergyTechName and sets the cage via @techName while the bed is quiescent. The joint seals without waking the bed.",
        "fail": "The team misses the quiet phase. The bed flares, the cage jams half-set, and the repair is abandoned."
      },
      {
        "synergyTechniqueId": "servo-alignment-tool",
        "title": "Choked Intake",
        "problem": "A bolus of pulp sits behind the intake screen; the lung wheezes at half draw despite repeated cycles.",
        "success": "The team aligns the shutter stack with @synergyTechName and pulses a controlled pull through @techName. The bolus breaks, the screen clears, and draw returns to spec.",
        "fail": "The team cycles the shutter off time. The bolus compacts against the screen and the draw drops further."
      }
    ]
  },
  {
    "id": "biohazard_recovery_mutualist_growth_event",
    "relatedSkillId": "containment",
    "relatedTechniqueId": "mutualist-growth",
    "environment": "indoor",
    "rewards": { "deriveFromCrafting": true },
    "variants": [
      {
        "title": "Toxic Surface Binding",
        "problem": "Bench swabs show a persistent toxic residue that reappears after standard wipes. The residue spreads under heat.",
        "success": "The team cultivates @techName across the work surface to bind and metabolize the residue. Follow-up swabs return clean and the bench remains stable under load.",
        "fail": "The team seeds an incompatible culture. The residue persists and spreads into tool racks."
      },
      {
        "synergyTechniqueId": "manual-override-line",
        "title": "Fail-Safe Decon",
        "problem": "The residue source intermittently injects fresh contamination from an upstream valve when power cycles.",
        "success": "The team binds the surface with @techName and interlocks the upstream valve using @synergyTechName. Injections cease and the decon holds.",
        "fail": "The team binds the bench but leaves the valve uncontrolled. A power cycle re-seeds the surface, forcing a restart."
      },
      {
        "synergyTechniqueId": "echo-pulse-decoy",
        "title": "Wear Resistance",
        "problem": "Heavy tool traffic abrades the cultured layer faster than it can recover, reopening contamination spots. Wear patterns are visible under close inspection.",
        "success": "The team cultivates @techName with protective pods while @synergyTechName injects decoy wear signatures elsewhere. Actual wear stays concealed and the surface remains clean through the shift.",
        "fail": "The team omits the decoy pattern. Real wear becomes visible and the bench fails inspection."
      }
    ]
  },
  {
    "id": "biohazard_recovery_reflex_bloom_event",
    "relatedSkillId": "containment",
    "relatedTechniqueId": "reflex-bloom",
    "environment": "indoor",
    "rewards": { "deriveFromCrafting": true },
    "variants": [
      {
        "title": "Rapid Spill Neutralization",
        "problem": "A transport cart overturns, releasing a reactive slurry across a sloped floor. The spill advances toward a drain that feeds a clean zone.",
        "success": "The team triggers @techName to raise fast-growing absorbent mats along the downslope. The spill arrests at the mats and is lifted in sections.",
        "fail": "The team seeds too sparsely. The spill threads gaps between mats and breaches the drain grate."
      },
      {
        "synergyTechniqueId": "coordinated-flight-pattern",
        "title": "Timed Drop Coverage",
        "problem": "Multiple spill fronts advance simultaneously in a large hall. Manual placement cannot cover all fronts in time.",
        "success": "The team stages mats with @techName and coordinates drone drops via @synergyTechName on a shared timer. Coverage meets the fronts and the hall is secured.",
        "fail": "The team mis-times the drops. A front outruns the coverage and enters the stairwell."
      },
      {
        "synergyTechniqueId": "spore-veil",
        "title": "Aerosol Suppression",
        "problem": "As the mats engage, aerosol counts spike above safe limits near the ceiling returns.",
        "success": "The team continues mat deployment with @techName and casts @synergyTechName across the return path. Aerosol counts fall below threshold and cleanup proceeds.",
        "fail": "The team neglects the return path. Aerosols trip the clean-zone intake and force an evacuation."
      }
    ]
  }
]

// ========== factionSkills.js ==========
// Faction data
//
// `doctrine` is what the faction believes caused the Collapse — and therefore
// what its people are looking for down there. The three are mutually exclusive
// and none of them is correct; that is the design goal, not a gap to close
// (docs/story/StoryAlignment.md constraint 4).
//
// WHERE IT IS SHOWN, and where it deliberately is NOT (2026-08-05): only in the
// Archive, as the header of each faction's Collapse Key set. It was originally
// rendered on the creator card too, so the belief would be on screen before the
// first key confirmed it — but that hands a new player the world's central
// question in the first ten seconds, and the call was to hold it back. The
// creator card is back to `description`, and the Skills pane keeps its line
// rendered-but-hidden so restoring it is a one-word change rather than a
// rebuild. If that decision reverses, docs/story/NarrativePlacement.md §2 has
// the measurements: the card is 10rem wide and holds ONE paragraph, not two.
//
// Nothing here may reference the entity. That revelation belongs to L4.
export const factionSkills = {
  dominion: {
    name: "Dominion",
    icon: "🔧",
    description: "Engineers and systems tacticians who restore, command, and repurpose lost technology in collapsed zones.",
    doctrine: "Doctrine: the collapse was machine logic outgrowing its makers. Recover the systems, and prove they can be governed.",
    skills: {
      "signal-relay": {
        icon: "📡",
        name: "Signal Relay",
        description: "Reestablish or hijack long-range comms and data channels. Critical for network access and relay synchronization.",
        short: "Rebuilds or hijacks damaged communication networks.",
      },
      "system-override": {
        icon: "💻",
        name: "System Override",
        description: "Force reboots or assume control of compromised automation, defense turrets, or AI cores.",
        short: "Takes control of hostile machines or security systems.",
      },
      "drone-command": {
        icon: "🤖",
        name: "Drone Command",
        description: "Deploy autonomous or semi-autonomous drones for recon, repair, or field logistics.",
        short: "Uses drones for recon, repair, or support tasks.",
      },
      "safeguard": {
        icon: "⚠️",
        name: "Safeguard",
        description: "Reinforce failing circuits and safety systems to neutralize energy surges, power faults, or mechanical hazards.",
        short: "Restores stability to compromised power or safety systems.",
      },
      "quantum-scan": {
        icon: "🔬",
        name: "Quantum Scan",
        description: "Map rift distortions and energy signatures. Essential for predicting anomaly behavior and safe extraction routes.",
        short: "Scans anomalies to predict energy patterns and distortions.",
      },
    },
  },

  wilders: {
    name: "Wilders",
    icon: "🌿",
    description: "Biotech operatives and survival engineers dedicated to restoring life systems and adapting to hostile biomes.",
    doctrine: "Doctrine: the collapse was the living world rejecting a synthetic one. Read what survived; it knows more than we do.",
    skills: {
      "reclamation": {
        icon: "🌱",
        name: "Reclamation",
        description: "Harvest bioactive compounds and structural tissue from degraded ecosystems for reuse or study.",
        short: "Extracts valuable bio-materials from decayed environments.",
      },
      "growth-control": {
        icon: "🌿",
        name: "Growth Control",
        description: "Trigger accelerated plant or fungal growth to reinforce cover, create paths, or seal breaches.",
        short: "Uses rapid growth to shape terrain or create cover.",
      },
      "wild-influence": {
        icon: "🦎",
        name: "Wild Influence",
        description: "Apply biochemical signals or pheromone fields to calm or redirect aggressive wildlife.",
        short: "Calms or redirects aggressive wildlife using biotech signals.",
      },
      "bio-shield": {
        icon: "🧬",
        name: "Bio Shield",
        description: "Enhance resistance to toxins, pathogens, and radiation through symbiotic nanobiotics.",
        short: "Boosts resistance to toxins and harsh environments.",
      },
      "containment": {
        icon: "☣️",
        name: "Containment",
        description: "Stabilize contaminated zones and safely extract volatile organic material for research or trade.",
        short: "Recovers resources from contaminated or hazardous biozones.",
      },
    },
  },

  veil: {
    name: "Veil",
    icon: "👤",
    description: "Infiltrators and intelligence specialists operating beyond conventional detection.",
    doctrine: "Doctrine: the collapse was oversight failing, then covering for itself. Recover the records nobody meant to leave behind.",
    skills: {
      "echo-trace": {
        icon: "🗺️",
        name: "Echo Trace",
        description: "Use reflected signals to reveal hidden paths and phase corridors within unstable structures.",
        short: "Reveals hidden rooms and routes using signal resonance.",
      },
      "lock-breach": {
        icon: "🔓",
        name: "Lock Breach",
        description: "Bypass hardened locks, vaults, and access gates using precision tools and exploit mapping.",
        short: "Bypasses security locks and restricted access systems.",
      },
      "stealth-transit": {
        icon: "👤",
        name: "Stealth Transit",
        description: "Traverse sensor grids and motion fields undetected. Minimizes signature footprint during raids.",
        short: "Moves unseen through sensor and motion grids.",
      },
      "signal-mask": {
        icon: "👻",
        name: "Signal Mask",
        description: "Scramble biometric, radio, and digital traces to evade active tracking systems.",
        short: "Erases traces to evade tracking and surveillance.",
      },
      "transit-anchor": {
        icon: "🌀",
        name: "Transit Anchor",
        description: "Deploy stabilization anchors and mapping scanners to cross collapsed or structurally unstable terrain safely.",
        short: "Stabilizes collapsed terrain for safe traversal.",
      },
    },
  },
};

// ========== flavorText.js ==========
export const factionNames = {
  dominion: {
    names: [
      "Cipher",
      "Circuit",
      "Nova",
      "Volt",
      "Nexus",
      "Binary",
      "Synth",
      "Logic",
      "Vector",
      "Matrix",
      "Atlas",
      "Chrome",
      "Pulse",
      "Core",
      "Apex",
      "Titan",
      "Forge",
      "Grid",
      "Node",
      "Prime",
      "Beacon",
      "Prism",
      "Zenith",
      "Vortex",
      "Helix",
      "Cortex",
      "Axis",
      "Flux",
      "Echo",
      "Surge",
    ],
    surnames: [
      "Steelwright",
      "Circuitborn",
      "Wireframe",
      "Dataforge",
      "Ironcode",
      "Techbane",
      "Gridlock",
      "Megabyte",
      "Quantumspark",
      "Neurolink",
      "Gearsmith",
      "Boltweaver",
      "Chromeheart",
      "Sparkforge",
      "Titanshield",
      "Datastream",
      "Wireborn",
      "Primecore",
      "Vaultkeeper",
      "Ciphersong",
      "Powergrid",
      "Logicblade",
      "Nanotech",
      "Cybervault",
      "Mechanix",
      "Voltstream",
      "Bytebane",
      "Coderune",
      "Synthwave",
      "Nexusforge",
    ],
  },
  wilders: {
    names: [
      "Sage",
      "Thorn",
      "Moss",
      "Fern",
      "Root",
      "Bloom",
      "Grove",
      "Ivy",
      "Cedar",
      "Briar",
      "Hazel",
      "Ash",
      "Elm",
      "Oak",
      "Pine",
      "Birch",
      "Willow",
      "Maple",
      "Aspen",
      "Laurel",
      "Clover",
      "Meadow",
      "Reed",
      "Sorrel",
      "Basil",
      "Juniper",
      "Cypress",
      "Alder",
      "Rowan",
      "Yarrow",
    ],
    surnames: [
      "Greenmane",
      "Thornweaver",
      "Rootwalker",
      "Wildgrow",
      "Mosscloak",
      "Seedwhisper",
      "Fernblade",
      "Vineborn",
      "Petalstorm",
      "Barkskin",
      "Leafdancer",
      "Brambleheart",
      "Dewsong",
      "Earthshade",
      "Wildroot",
      "Stormgrow",
      "Branchbinder",
      "Flowersong",
      "Earthkeeper",
      "Treeborne",
      "Raincaller",
      "Windleaf",
      "Sunpetal",
      "Moonblossom",
      "Wildmane",
      "Spiritseed",
      "Grovewalker",
      "Thornshield",
      "Lifewoven",
      "Forestborn",
    ],
  },
  veil: {
    names: [
      "Shadow",
      "Wraith",
      "Mist",
      "Echo",
      "Shade",
      "Whisper",
      "Ghost",
      "Raven",
      "Void",
      "Phantom",
      "Dusk",
      "Twilight",
      "Nocturne",
      "Umbra",
      "Gloom",
      "Haze",
      "Murk",
      "Shimmer",
      "Shroud",
      "Veil",
      "Smoke",
      "Mirage",
      "Specter",
      "Sable",
      "Onyx",
      "Obsidian",
      "Eclipse",
      "Nebula",
      "Drift",
      "Abyss",
    ],
    surnames: [
      "Shadowmere",
      "Voidwalker",
      "Nightfall",
      "Darkweaver",
      "Mistral",
      "Grimshade",
      "Blackdrift",
      "Ghostbane",
      "Veilborn",
      "Wraithcloak",
      "Nightwhisper",
      "Duskblade",
      "Silentmoon",
      "Darkmist",
      "Voidheart",
      "Shadowstride",
      "Ghostwind",
      "Nightveil",
      "Phantomborn",
      "Dimwalker",
      "Umbramere",
      "Eclipsesoul",
      "Twilightborne",
      "Duskmantle",
      "Murkweaver",
      "Hazewalker",
      "Smokesinger",
      "Gloomward",
      "Shroudkeeper",
      "Mirageborn",
    ],
  },
};

// keep lines short: ~60–80 chars
export const raidFlavor = {
  material: {
    common: [
      "Scavenged parts—still serviceable.",
      "Field scrap, stripped clean of charge.",
      "Useful metal, no corrosion yet.",
      "Reclaimed fittings—just needs polish.",
      "Someone’s repair job didn’t hold, but the parts did.",
    ],
    rare: [
      "Intact components—hard find this deep.",
      "Precision stock, good for another build.",
      "Recovered hardware, still calibrated.",
      "High-grade alloy, likely pre-Collapse.",
      "Pulled from the wreck, still hums faintly.",
    ],
  },
  consumable: {
    common: [
      "Standard stim—quick and dirty relief.",
      "Energy patch—buys a few more steps.",
      "A dose of focus, if it hasn’t expired.",
      "Compact charge—barely enough, but enough.",
      "You’ve used worse in the field.",
    ],
    rare: [
      "Advanced formula, field-sealed.",
      "Med injector, smooth burn and clean recovery.",
      "Military-issue charge, still stable.",
      "Restores more than it should—must be real stock.",
      "Save it for when the lights go red.",
    ],
    epic: [
      "Top-tier stabilizer, rare to see intact.",
      "Restores everything but your luck.",
      "Combat-grade surge, keep it steady.",
      "Proprietary compound—whatever’s inside, it works.",
      "Last-resort aid. You won’t find another soon.",
    ],
  },
  relic: {
    common: [
      "Old-world scrap—still has form, not function.",
      "Traces of utility, stripped long ago.",
      "A small story, buried in rust.",
      "Every mark means it mattered to someone.",
      "Forgotten tool, remembered by the metal.",
    ],
    rare: [
      "Recovered relic with partial circuitry.",
      "Design’s old, but the craftsmanship holds.",
      "It hums when touched—residual charge, maybe.",
      "Half-art, half-instrument—purpose unclear.",
      "Recovered from deeper layers, mostly intact.",
    ],
    epic: [
      "Pristine core—untouched since the Fall.",
      "A perfect survivor of imperfect times.",
      "Feels almost deliberate that it lasted.",
      "The lines are clean, the memory sharper.",
      "Carries weight that’s more than metal.",
    ],
  },
  blueprint: {
    legendary: [
      "Detailed schematic—someone planned to rebuild.",
      "Blueprint sealed under dust and heat.",
      "This map shows what was possible once.",
      "A guide to lost craftsmanship, now in your hands.",
      "If you can read it, you can make it real.",
    ],
  },
  storyRelic: {
    unique: [
      "It vibrates faintly, like it remembers you.",
      "What it shows isn’t gone—just out of reach.",
      "Feels heavier than it looks. Memory weight.",
      "History condensed into one impossible fragment.",
      "It doesn’t tell stories—it keeps them.",
    ],
  },
  credits: {
    currency: [
      "Still accepted by the desperate and the wise.",
      "Old world money, still spinning through new hands.",
      "Trade keeps the silence at bay.",
      "Worth more in motion than in storage.",
      "Proof that some systems never collapse.",
    ],
  },
};

// optional: faction voice tweak (adds 1 short clause)
export const factionVoice = {
  dominion: ["Factory specs check out.", "Data tag matches.", "Keep it cataloged.", "Run diagnostics."],
  wilders: ["Smells alive.", "Roots remember.", "Still breathing.", "Handle like it grows."],
  veil: ["No witnesses.", "Leave no trace.", "Not on any manifest.", "Burn the logs."],
};

// optional: room/condition context shards
export const contextShards = {
  hazard: ["ion-stained", "biofilm slick", "burnt edges", "cracked seals", "chemical haze"],
  location: ["collapsed gantry", "sealed rack", "growth-choked vent", "sunken conduit", "buried access point"],
  action: ["pried free", "cut loose", "lifted clean", "extracted carefully", "dug out by hand"],
};

export const contractorSkillFlavors = {
  // === DOMINION ===
  "signal-relay": {
    roles: ["Comms Tech", "Relay Engineer", "Signal Operator", "Data Link Specialist", "Transceiver Mechanic"],
    // Command-rank counterparts, index-matched to roles[] (assigned at promotion)
    promotedRoles: ["Signals Directorate Chief", "Relay Network Commander", "Uplink Operations Director", "Data Corridor Overseer", "Transmission Fleet Marshal"],
    // Welcome from a command member, index-matched to promotedRoles[]
    // (shown in the promote dialog when the new post is announced)
    promotedQuotes: [
      "“Every frequency the Dominion holds now routes through your desk. Speak, and the whole network leans in to listen.”",
      "“The towers you patched under fire are a war map on your wall now. Keep every line singing, Commander.”",
      "“Ground to orbit, orbit to home — the uplink schedule is yours. Nothing leaves this world without your signature.”",
      "“You walked the data corridors when they were static and ruin. Now every packet that survives the wastes pays toll to you.”",
      "“The relay fleet answers to your call sign now, Marshal. Where you park those dishes decides who gets heard.”",
    ],
    quotes: [
      "“Noise is just data waiting to be translated.”",
      "“Every message finds its way eventually.”",
      "“Dead air is still information.”",
      "“I speak fluent interference.”",
      "“You can’t hide from a tuned array.”",
    ],
  },
  "system-override": {
    roles: ["Control Hacker", "Override Specialist", "Security Engineer", "Core Manipulator", "Protocol Breacher"],
    promotedRoles: ["Intrusion Doctrine Chief", "Override Command Architect", "Security Directorate Head", "Core Systems Overseer", "Protocol Warfare Strategist"],
    promotedQuotes: [
      "“Your field exploits are the textbook now, Chief. Every recruit learns to break a system the way you broke a hundred.”",
      "“You built override rigs from scrap while sirens screamed. Design our command architecture — this time nothing pushes back.”",
      "“The Dominion’s locks, walls, and watchdogs report to you now. Nobody gets past what you couldn’t get past first.”",
      "“Every core we still control hums under your hand, Overseer. Keep them obedient — you know exactly how they fail.”",
      "“You out-argued hostile machines at gunpoint. Now you write the protocols that win before the argument starts.”",
    ],
    quotes: [
      "“Give me root access and ten seconds.”",
      "“Systems break themselves—I just hurry them along.”",
      "“Firewalls are for decoration.”",
      "“I don’t bypass security—I rewrite it.”",
      "“Every system obeys; it just needs persuasion.”",
    ],
  },
  "drone-command": {
    roles: ["Drone Pilot", "Field Operator", "Remote Systems Tech", "Autonomy Engineer", "Aerial Recon Chief"],
    promotedRoles: ["Drone Wing Commander", "Field Operations Director", "Remote Fleet Coordinator", "Autonomy Program Chair", "Recon Command Marshal"],
    promotedQuotes: [
      "“The wings you flew solo now fly in your formation, Commander. A thousand rotors hold pattern waiting on your word.”",
      "“Field operations runs on your playbook now. Every operator down-well moves the way you taught the machines to move.”",
      "“Every drone the Dominion fields checks home to your board. Route them well — you remember what the ground costs.”",
      "“Your autonomy heuristics graduate today, Chair. Teach the fleet to think the way you flew: carefully, and all the way home.”",
      "“The recon wing is yours, Marshal. Nothing crosses the wastes unseen while your birds are up.”",
    ],
    quotes: [
      "“If it’s in range, it’s in control.”",
      "“You can’t ambush a drone with anxiety.”",
      "“Three drones, two hands, one coffee.”",
      "“Never send a person when a circuit will do.”",
      "“My drones have more personality than most people.”",
    ],
  },
  "safeguard": {
    roles: ["Hazard Control Officer", "Containment Engineer", "Field Safety Tech", "Thermal Specialist", "Environmental Regulator"],
    promotedRoles: ["Hazard Doctrine Commander", "Containment Program Director", "Field Safety Commandant", "Thermal Systems Overseer", "Environmental Command Chief"],
    promotedQuotes: [
      "“Your close calls became checklists, and your checklists became doctrine. Crews come home now because once, you almost didn’t.”",
      "“The containment program carries your name in its footnotes, Director. Run it so no one ever learns what it contains.”",
      "“Commandant of everything that keeps a raider breathing. Drill them hard — the wastes drill harder.”",
      "“Every reactor jacket and heat sink in the fleet is your watch now. Cold numbers, warm crews.”",
      "“The habitat domes hold because somebody obsessed over seals and sensors. Keep obsessing, Chief.”",
    ],
    quotes: [
      "“If you’re sweating, it’s already too late.”",
      "“Nothing’s unmanageable—just unprepared for.”",
      "“Fire, gas, acid—same dance, different tempo.”",
      "“I clean up everyone else’s mistakes.”",
      "“You’ll thank me when you’re still breathing.”",
    ],
  },
  "quantum-scan": {
    roles: ["Rift Analyst", "Anomaly Researcher", "Quantum Field Tech", "Data Collapse Theorist", "Reality Modeler"],
    promotedRoles: ["Rift Intelligence Chief", "Anomaly Research Director", "Quantum Survey Commander", "Collapse Theory Chair", "Reality Modeling Overseer"],
    promotedQuotes: [
      "“You read the rifts back when reading them cost people their minds. Every anomaly report crosses your desk first now.”",
      "“The research wing is yours, Director. Chase what the collapse left behind — carefully. Some of it chases back.”",
      "“Every survey grid we chart follows your calibrations now, Commander. Map the impossible; the fleet steers by it.”",
      "“Your models kept this Chair empty until someone could actually fill it. Sit down.”",
      "“Reality bends near the shards, Overseer. Your models are the only straight lines we trust.”",
    ],
    quotes: [
      "“The math doesn’t lie, but it does laugh.”",
      "“Observe first. Panic later.”",
      "“Reality jitters; I just measure the tremor.”",
      "“Prediction is just organized superstition.”",
      "“If you can’t explain it, log it twice.”",
    ],
  },

  // === WILDERS ===
  "reclamation": {
    roles: ["Bio-Salvager", "Tissue Reclaimer", "Eco-Recycler", "Harvest Operative", "Spore Diver"],
    promotedRoles: ["Salvage Circle Elder", "Reclamation Warden", "Renewal Council Chief", "Harvest Doctrine Keeper", "Deep Spore Marshal"],
    promotedQuotes: [
      "“The Salvage Circle keeps a seat of ruin-wood for you, Elder. What you pulled from the wreckage taught us what was worth pulling.”",
      "“Warden of everything the world threw away. Walk the reclaim-fields and decide what gets a second life.”",
      "“The Renewal Council speaks with your voice now. Where you point, the green follows.”",
      "“You gathered when gathering was starving work. Keep the harvest doctrine, and no circle goes hungry again.”",
      "“The deep spore beds answer to you now, Marshal. Tend what grows in the dark — it remembers who fed it.”",
    ],
    quotes: [
      "“Every ruin has roots.”",
      "“Decay’s just another kind of growth.”",
      "“You find beauty in the rot.”",
      "“Nature wastes nothing—why should I?”",
      "“Dead ecosystems make the best teachers.”",
    ],
  },
  "growth-control": {
    roles: ["Spore Architect", "Growth Technician", "Bio-Builder", "Verdant Engineer", "Mycelial Sculptor"],
    promotedRoles: ["Architect of the Canopy", "Growth Doctrine Warden", "Living Works Overseer", "Verdant Command Elder", "Mycelial Council Chief"],
    promotedQuotes: [
      "“The canopy you coaxed over ruins now shades three settlements. Architect, the skyline is yours to grow.”",
      "“Growth without doctrine is just another collapse. You learned where the line is — hold it for all of us.”",
      "“Every living wall and root-bridge we raise is your works order now, Overseer. Build us shelter that heals itself.”",
      "“The Verdant Command has an elder’s place ready. You made the wastes bloom on a schedule; teach us the timing.”",
      "“The mycelial web already knows your hands. Chief, sit with the Council — the network has been asking for you.”",
    ],
    quotes: [
      "“You don’t build walls—you grow them.”",
      "“Patience is the fastest method.”",
      "“Give it time and light—it’ll move mountains.”",
      "“Blueprints are for people who don’t trust roots.”",
      "“Every structure wants to bloom.”",
    ],
  },
  "wild-influence": {
    roles: ["Beast Handler", "Bio-Communicator", "Eco-Mediator", "Creature Whisperer", "Wildlife Operative"],
    promotedRoles: ["Beastmaster General", "Communion Council Elder", "Mediation Doctrine Keeper", "Whisper Circle Warden", "Wildlands Marshal"],
    promotedQuotes: [
      "“The herds that once scattered raiding parties now hold our perimeter. General, the wild fights beside us because of you.”",
      "“You listened when everything out there wanted us dead. The Communion Council keeps an elder’s seat for the best listener we have.”",
      "“Every truce between tooth and settlement follows your mediation doctrine now. Keep the peace you invented.”",
      "“Warden of the Whisper Circle. The creatures already answer to your voice — now our handlers will too.”",
      "“The wildlands between the circles are yours to marshal. Nothing moves out there you don’t already understand.”",
    ],
    quotes: [
      "“Everything alive understands tone.”",
      "“You calm the world by moving slower.”",
      "“Trust starts with eye contact—even with fangs.”",
      "“Predators aren’t cruel, just honest.”",
      "“Fear smells louder than blood.”",
    ],
  },
  "bio-shield": {
    roles: ["Bio-Resonant Operative", "Adaptive Host", "Symbiosis Technician", "Mutation Researcher", "Genetic Integrator"],
    promotedRoles: ["Resonance Doctrine Elder", "Adaptation Council Chief", "Symbiosis Program Warden", "Mutation Studies Chair", "Integration Command Elder"],
    promotedQuotes: [
      "“Your body learned the wastes’ frequencies before our instruments did. Elder, the resonance doctrine is written from your scars.”",
      "“The Adaptation Council needs a chief who changed instead of breaking. Show the circles how to bend.”",
      "“Warden of every paired life in the program. What grows on us, grows with us — you proved that first.”",
      "“The Mutation Studies Chair is not a safe seat. You’ve never once asked for a safe seat.”",
      "“Integration is the long war, Elder — flesh, spore, and machine learning to share a body politic. Command it kindly.”",
    ],
    quotes: [
      "“Adapt or decompose.”",
      "“Evolution doesn’t wait for permission.”",
      "“If it itches, it’s working.”",
      "“You call it infection—I call it upgrade.”",
      "“Change hurts. That’s how you know it’s real.”",
    ],
  },
  "containment": {
    roles: ["Hazmat Reclaimer", "Biohazard Diver", "Containment Scout", "Toxic Zone Operative", "Decon Specialist"],
    promotedRoles: ["Quarantine Council Chief", "Deep Hazard Warden", "Containment Doctrine Keeper", "Exclusion Zone Marshal", "Decontamination Elder"],
    promotedQuotes: [
      "“The Quarantine Council answers to the one person who never cut a corner on a seal. Chief, the lines you draw hold.”",
      "“Warden of the deep hazards. What sleeps below the reclaim-fields stays asleep on your watch.”",
      "“Your containment rites are doctrine now, Keeper. Teach them exactly — the spores don’t forgive improvisation.”",
      "“The exclusion zones are mapped in your handwriting, Marshal. Keep the living side of every fence living.”",
      "“Elder of the cleansing pools. Everything that comes home from the wastes passes through your hands first.”",
    ],
    quotes: [
      "“If it glows, bag it.”",
      "“Safety’s a spectrum.”",
      "“You can’t clean the world, just manage it.”",
      "“I’ve seen things grow where nothing should.”",
      "“If the mask fogs, run.”",
    ],
  },

  // === VEIL ===
  "social-engineering": {
    roles: ["Persuasion Analyst", "Influence Operative", "Behavioral Infiltrator", "Social Architect", "Echo Diplomat"],
    promotedRoles: ["Persuasion Directorate Head", "Influence Network Handler", "Behavioral Doctrine Chief", "Grand Social Architect", "Shadow Envoy Prime"],
    promotedQuotes: [
      "“You talked your way past things that don’t have ears. The Persuasion Directorate is yours — mind the mirrors.”",
      "“Every asset, informant, and friendly stranger in five regions now reports to your ledger, Handler.”",
      "“Your read on a room became our behavioral doctrine, Chief. People are locks; you wrote the picks.”",
      "“Architect, the web of favors you spun in the field holds half our operations aloft. Keep spinning.”",
      "“Envoy Prime. When the Veil must be seen, it will wear your face — and no one will remember it.”",
    ],
    quotes: [
      "“Truth is just confidence with better posture.”",
      "“Everyone wants to believe they’re in control.”",
      "“Lies travel faster when you smile.”",
      "“I sell trust, not information.”",
      "“If you think I’m honest, I’ve already won.”",
    ],
  },
  "echo-trace": {
    roles: ["Echo Listener", "Trace Analyst", "Sonar Operator", "Signal Tracker", "Resonance Scout"],
    promotedRoles: ["Echo Doctrine Keeper", "Trace Directorate Head", "Deep Sonar Marshal", "Tracking Cell Handler", "Resonance Intelligence Chief"],
    promotedQuotes: [
      "“You heard the city’s bones settle and knew which rooms were safe. Keeper, the echo doctrine is your ear, written down.”",
      "“The Trace Directorate follows anything that leaves a ripple. Head it — nothing has ever slipped past you twice.”",
      "“Deep Sonar Marshal. The sunken levels talk in pressure and echo, and you’re the only one fluent.”",
      "“Your cells can find a heartbeat in a hurricane now, Handler. Teach them which heartbeats matter.”",
      "“Chief of Resonance Intelligence: every hum, knock, and silence the Veil records is evidence on your desk.”",
    ],
    quotes: [
      "“Everything leaves an echo. I just wait for it.”",
      "“Silence has a shape, if you listen.”",
      "“The walls talk. I take notes.”",
      "“You can stop moving. You can’t stop resonating.”",
      "“I found you three corridors ago.”",
    ],
  },
  "lock-breach": {
    roles: ["Breach Tech", "Vault Specialist", "Security Bypass Expert", "Lockpulse Engineer", "Intrusion Mechanic"],
    promotedRoles: ["Breach Doctrine Master", "Vault Directorate Head", "Bypass Program Chief", "Lockpulse Guild Master", "Intrusion Cell Handler"],
    promotedQuotes: [
      "“Every door you opened quietly is a chapter in the breach doctrine now. Master, teach the click before the open.”",
      "“The Vault Directorate keeps what the Veil cannot afford to lose. Fitting that its head is the one no vault could keep out.”",
      "“Chief of the bypass program: every lock the collapse left behind, catalogued, studied, and already yours.”",
      "“The Lockpulse Guild took a vote. It was unanimous, Master — even the abstentions were just showing respect.”",
      "“Your cells work doors on three continents now, Handler. None of them will ever know your name. That’s the compliment.”",
    ],
    quotes: [
      "“Everything opens with the right rhythm.”",
      "“Security is a suggestion.”",
      "“Locks are just puzzles with pride.”",
      "“The click before the open is the best sound.”",
      "“If I can’t open it, it’s cursed.”",
    ],
  },
  "stealth-transit": {
    roles: ["Stealth Operative", "Scout", "Field Infiltrator", "Veil Runner", "Low-Signature Agent"],
    promotedRoles: ["Infiltration Doctrine Lead", "Pathfinder Directorate Head", "Deep Cover Handler", "First Runner of the Veil", "Ghost Protocol Chief"],
    promotedQuotes: [
      "“The routes you walked alone are the infiltration doctrine now. Lead, and the Veil moves where you moved.”",
      "“Pathfinder Directorate is yours. Every safe corridor on the map exists because you crossed it first, unsafely.”",
      "“Your covers held longer than some careers. Handler, run the deep assets — you know what the silence costs them.”",
      "“First Runner. When there is no route, no map, and no time, the Veil sends you. It is not a desk. Congratulations anyway.”",
      "“Ghost Protocol has a chief again. Nobody saw the last one leave, and nobody saw you arrive. Perfect continuity.”",
    ],
    quotes: [
      "“I’m not invisible. You’re just distracted.”",
      "“Noise gets people killed.”",
      "“If you hear me, you’re already too close.”",
      "“I move between moments.”",
      "“The dark’s not hiding me—I’m hiding it.”",
    ],
  },
  "signal-mask": {
    roles: ["Signal Ghost", "Trace Eraser", "Silent Specialist", "Obfuscation Operative", "Data Mirage Agent"],
    promotedRoles: ["Ghost Network Director", "Erasure Doctrine Chief", "Silence Directorate Head", "Obfuscation Cell Handler", "Mirage Program Master"],
    promotedQuotes: [
      "“The ghost network carries every whisper the Veil owns, Director, and it hums at frequencies you invented.”",
      "“You erased trails the Dominion still bills as archive corruption. Chief, the erasure doctrine is your confession — unsigned.”",
      "“Head of the Silence Directorate. Your job is that nothing is ever heard. Your qualification is that nothing ever was.”",
      "“Your static-craft keeps our cells invisible mid-transmission, Handler. Teach them to disappear while speaking.”",
      "“Master of the Mirage Program: every false signal, phantom convoy, and decoy city is staged under your hand now.”",
    ],
    quotes: [
      "“If they can’t log it, it didn’t happen.”",
      "“Proof is the weakest link.”",
      "“Records vanish; I remain.”",
      "“You can’t find what never existed.”",
      "“Logs lie. I don’t.”",
    ],
  },
  "transit-anchor": {
    roles: ["Field Phasist", "Dimensional Scout", "Boundary Operative", "Spatial Recon Agent", "Phase Diver"],
    promotedRoles: ["Phase Doctrine Master", "Dimensional Survey Head", "Boundary Directorate Chief", "Spatial Intelligence Handler", "Deep Phase Marshal"],
    promotedQuotes: [
      "“You anchored crossings that should have scattered whole teams into the between. Master, the phase doctrine is your handwriting.”",
      "“The dimensional survey is yours to head. Chart the soft places — and mark clearly where the map should lie.”",
      "“Chief of the Boundary Directorate. The lines between here and elsewhere hold because you know exactly how thin they are.”",
      "“Every anomalous crossing report lands on your desk now, Handler. You’ve stood in most of them.”",
      "“Deep Phase Marshal. Past a certain depth the transit stops being travel and starts being negotiation. You always came back — teach that.”",
    ],
    quotes: [
      "“Space folds if you know where to press.”",
      "“Physics is a matter of confidence.”",
      "“Walls are only polite suggestions.”",
      "“If you feel a tug, don’t fight it.”",
      "“Everything solid is just waiting to blur.”",
    ],
  },
};


// ========== itemEffects.js ==========
// Item Effects - normalized effect definitions referenced by artifacts
// Each effect is referenced by its ID from artifact items' effects field

export const itemEffects = [
  // ===============================
  // Skill Success Bonuses
  // Increases technique success chance for all techniques in a skill (used by skill artifacts)
  // Applied to specific skills via skill field on artifacts
  // ===============================
  {
    id: "skill-success-bonus",
    type: "skill_success_bonus",
    value: 5,
    unit: "percent",
    description: "Increases technique success chance during room events",
    icon: "🎯✨",
  },

  // ===============================
  // Currency & Payout Bonuses
  // Increases credits earned from data caches and contracts
  // ===============================
  {
    id: "currency-bonus",
    type: "currency_bonus",
    value: 5,
    unit: "percent",
    description: "Increases credits from recovered data caches",
    icon: "💾💰",
  },
  {
    id: "contractor-payout-bonus",
    type: "currency_bonus",
    value: 10,
    unit: "percent",
    category: "contractor",
    description: "Increases payout from successful support contracts",
    icon: "📡💰",
  },

  // ===============================
  // Item Quality & Discovery Bonuses
  // Improves rarity rolls and discovery chance for special items
  // ===============================
  {
    id: "relic-quality-bonus",
    type: "item_quality_bonus",
    value: 10,
    unit: "percent",
    description: "Improves chances of finding higher-quality vendor relics",
    icon: "🔮⬆️",
  },
  // (was "story-relic-bonus" - story relics only drop at tier 4+, which
  // doesn't exist yet; repurposed 2026-07 to blueprint discovery until the
  // story-relic content ships)
  {
    id: "blueprint-find-bonus",
    type: "item_quality_bonus",
    value: 5,
    unit: "percent",
    description: "Increases the chance of discovering legendary blueprint caches",
    icon: "📜✨",
  },

  // ===============================
  // Damage Reduction & Protection
  // Reduces damage taken and prevents incapacitation
  // ===============================
  {
    id: "hazard-damage-reduction",
    type: "damage_reduction",
    value: 5,
    unit: "percent",
    description: "Reduces damage from environmental hazards",
    icon: "⚠️🛡️",
  },
  {
    id: "knockout-prevention",
    type: "knockout_prevention",
    value: 1,
    unit: "count",
    description: "Prevents first incapacitation per raid",
    icon: "💉🛡️",
  },

  // ===============================
  // Faction Synergy Bonuses
  // Improves coordination when working with mixed-faction teams
  // ===============================
  {
    id: "faction-synergy-bonus",
    type: "faction_synergy_bonus",
    value: 5,
    unit: "percent",
    description: "Improves success when multiple factions coordinate on events",
    icon: "🔀✨",
  },

  // ===============================
  // Skill & Support Bonuses
  // Improves effectiveness when acting in support role
  // ===============================
  {
    id: "support-skill-bonus",
    type: "skill_bonus",
    value: 2,
    unit: "percent",
    description: "Boosts skill checks when providing support during events",
    icon: "🤝✨",
  },

  // ===============================
  // Resource & Material Bonuses
  // Increases material yield from salvage operations
  // ===============================
  {
    id: "loot-quantity-bonus",
    type: "loot_quantity_bonus",
    value: 10,
    unit: "percent",
    description: "Increases quantity of materials recovered from events",
    icon: "📦⬆️",
  },
  {
    id: "gate-material-bonus",
    type: "material_yield_bonus",
    value: 15,
    unit: "percent",
    description: "Improves material yield from Gate rooms",
    icon: "⛏️📦",
  },

  // ===============================
  // Downtime Reduction
  // Reduces the energy cost of launching raids
  // (was "cooldown-reduction" - no raid cooldown mechanic exists; repurposed
  // 2026-07 to the real pacing resource, energy)
  // ===============================
  {
    id: "raid-energy-reduction",
    type: "raid_energy_reduction",
    value: 10,
    unit: "percent",
    description: "Reduces the energy cost of launching raids",
    icon: "⚡⬇️",
  },

  // ===============================
  // Technique-Specific Bonuses
  // Applied to specific techniques via techniqueId on items
  // ===============================
  {
    id: "technique-success-bonus",
    type: "technique_success_bonus",
    value: 10,
    unit: "percent",
    description: "Increases technique success chance",
    icon: "🔧✨",
  },
  // NOTE: "technique-retry" (one free retry on the technique's checks) was
  // removed from device data 2026-07-15 - a p -> 1-(1-p)^2 swing needs its
  // own balance pass. Reintroduce deliberately if a capstone effect is wanted.
  {
    id: "room-time-reduction",
    type: "room_time_reduction",
    value: 10,
    unit: "percent",
    description: "Reduces room duration when technique's usage is successful",
    icon: "🔧⏱️",
  },
];


// ========== itemTypes.js ==========
// Type metadata for organizing items
export const itemTypes = {
  material: {
    name: "Material",
    icon: "🔧",
  },
  consumable: {
    name: "Consumable",
    icon: "🧪",
  },
  techGear: {
    name: "Skill Gear",
    icon: "⚙️",
  },
  artifact: {
    name: "Artifact",
    icon: "⚡",
  },
  blueprint: {
    name: "Blueprint",
    icon: "📋",
  },
  relic: {
    name: "Relic",
    icon: "💎",
  },
  storyRelic: {
    name: "Story Relic",
    icon: "🌟",
  },
  credits: {
    name: "Credits",
    icon: "💰",
  },
};

// ========== itemsArtifactsSkill.js ==========
// Artifact system - equipment that provides skill bonuses and special abilities
export const itemsArtifactsSkill = [
  // ===============================
  // Dominion Artifacts
  // ===============================
  {
    id: "signal-repeater-pack",
    name: "Signal Repeater Pack",
    type: "artifact",
    rarity: "unique",
    slot: "skillSlot",
    skill: "signal-relay",
    faction: "dominion",
    icon: "📡",
    effects: { "skill-success-bonus": 5 },
    description: "Portable backpack transceiver used to amplify or intercept long-range signals.",
    short: "Boosts or reroutes long-range communications.",
    flavor: "Old tech, new purpose—signal's alive as long as the pack holds charge.",
    visual: "Backpack-sized radio with folding antenna mast, copper wiring, and carbon casing.",
  },

  {
    id: "control-override-kit",
    name: "Control Override Kit",
    type: "artifact",
    rarity: "unique",
    slot: "skillSlot",
    skill: "system-override",
    faction: "dominion",
    icon: "💻",
    effects: { "skill-success-bonus": 5 },
    description: "Rugged field toolkit used to manually override or reboot autonomous systems.",
    short: "Grants manual control over automated or defensive systems.",
    flavor: "Every system has a door. This is the key.",
    visual: "Metal case with analog dials, fiber cables, and brass coupling clamps.",
  },

  {
    id: "operator-link-harness",
    name: "Operator Link Harness",
    type: "artifact",
    rarity: "unique",
    slot: "skillSlot",
    skill: "drone-command",
    faction: "dominion",
    icon: "🤖",
    effects: { "skill-success-bonus": 5 },
    description: "Wearable control rig providing direct uplink to reconnaissance drones.",
    short: "Improves range and stability for drone operations.",
    flavor: "You don't command the drone—you become its signal.",
    visual: "Chest rig with braided data cables, antenna spine, and LED activity lights.",
  },

  {
    id: "stability-coupler",
    name: "Stability Coupler",
    type: "artifact",
    rarity: "unique",
    slot: "skillSlot",
    skill: "safeguard",
    faction: "dominion",
    icon: "⚠️",
    effects: { "skill-success-bonus": 5 },
    description: "Compact coupler device that redirects power surges and stabilizes damaged circuits.",
    short: "Prevents overloads and stabilizes failing systems.",
    flavor: "Keeps the grid humming when everything else falls apart.",
    visual: "Steel cylinder with magnetic clamps and heat-dispersal vents.",
  },

  {
    id: "spectral-survey-scope",
    name: "Spectral Survey Scope",
    type: "artifact",
    rarity: "unique",
    slot: "skillSlot",
    skill: "quantum-scan",
    faction: "dominion",
    icon: "🔬",
    effects: { "skill-success-bonus": 5 },
    description: "Handheld multi-spectrum scanner for detecting heat signatures and radiation leaks.",
    short: "Reveals structural and energy anomalies.",
    flavor: "If it glows, hums, or leaks radiation—you'll see it first.",
    visual: "Monocular scope with glass lens, rubber grip, and digital readout.",
  },

  // ===============================
  // Wilders Artifacts
  // ===============================
  {
    id: "harvesting-gauntlet",
    name: "Harvesting Gauntlet",
    type: "artifact",
    rarity: "unique",
    slot: "skillSlot",
    skill: "reclamation",
    faction: "wilders",
    icon: "🌱",
    effects: { "skill-success-bonus": 5 },
    description: "Reinforced glove with integrated sampling tools for organic material extraction.",
    short: "Extracts valuable organic materials from decayed environments.",
    flavor: "The world may rot, but the soil still breathes.",
    visual: "Leather glove with metal prongs and sap-stained plating.",
  },

  {
    id: "growth-induction-pod",
    name: "Growth Induction Pod",
    type: "artifact",
    rarity: "unique",
    slot: "skillSlot",
    skill: "growth-control",
    faction: "wilders",
    icon: "🌿",
    effects: { "skill-success-bonus": 5 },
    description: "Handheld canister that releases nutrient mist and spores to trigger rapid plant growth.",
    short: "Stimulates fast vegetation growth to form cover or pathways.",
    flavor: "Press the trigger, and life remembers its old shape.",
    visual: "Metal canister with fogged glass tube and green-lit valves.",
  },

  {
    id: "pheromone-relay-belt",
    name: "Pheromone Relay Belt",
    type: "artifact",
    rarity: "unique",
    slot: "skillSlot",
    skill: "wild-influence",
    faction: "wilders",
    icon: "🦎",
    effects: { "skill-success-bonus": 5 },
    description: "Compact belt device emitting pheromone signals to calm or redirect wildlife.",
    short: "Redirects wildlife behavior using pheromone signals.",
    flavor: "Not control—just conversation.",
    visual: "Canvas belt with small glass ampoules and brass vapor vents.",
  },

  {
    id: "respirator-webbing",
    name: "Respirator Webbing",
    type: "artifact",
    rarity: "unique",
    slot: "skillSlot",
    skill: "bio-shield",
    faction: "wilders",
    icon: "🧬",
    effects: { "skill-success-bonus": 5 },
    description: "Protective breathing rig with adaptive microbial filters for harsh biomes.",
    short: "Filters toxins and airborne contaminants in the field.",
    flavor: "Built for lungs that still believe in clean air.",
    visual: "Lightweight mask and harness with flexible tubing and filter canisters.",
  },

  {
    id: "hazard-recovery-satchel",
    name: "Hazard Recovery Satchel",
    type: "artifact",
    rarity: "unique",
    slot: "skillSlot",
    skill: "containment",
    faction: "wilders",
    icon: "☣️",
    effects: { "skill-success-bonus": 5 },
    description: "Sealed pack with insulated containers for storing contaminated samples.",
    short: "Safely collects and stores hazardous materials.",
    flavor: "Carry the poison home, sell the antidote later.",
    visual: "Canvas satchel with metal clasps, labeled vials, and sealed lining.",
  },

  // ===============================
  // Veil Artifacts
  // ===============================
  {
    id: "resonant-survey-array",
    name: "Resonant Survey Array",
    type: "artifact",
    rarity: "unique",
    slot: "skillSlot",
    skill: "echo-trace",
    faction: "veil",
    icon: "🗺️",
    effects: { "skill-success-bonus": 5 },
    description: "Compact scanner that maps hidden spaces using ultrasonic and optical signals.",
    short: "Reveals hidden corridors and passageways.",
    flavor: "Walls hide nothing from an echo that listens back.",
    visual: "Fold-out brass scanner with projection lens and rotating sensor arms.",
  },

  {
    id: "lockpulse-breach-kit",
    name: "Lockpulse Breach Kit",
    type: "artifact",
    rarity: "unique",
    slot: "skillSlot",
    skill: "lock-breach",
    faction: "veil",
    icon: "🔓",
    effects: { "skill-success-bonus": 5 },
    description: "Portable case containing micro-drills and probe leads for lock bypassing.",
    short: "Bypasses advanced security mechanisms.",
    flavor: "Locks are just puzzles pretending to be walls.",
    visual: "Compact case with metallic tools and a glass touchpad interface.",
  },

  {
    id: "silent-step-pads",
    name: "Silent Step Pads",
    type: "artifact",
    rarity: "unique",
    slot: "skillSlot",
    skill: "stealth-transit",
    faction: "veil",
    icon: "👤",
    effects: { "skill-success-bonus": 5 },
    description: "Noise-dampening inserts that absorb motion vibration for silent movement.",
    short: "Suppresses footstep and motion noise.",
    flavor: "The loudest sound is the one you never make.",
    visual: "Thin polymer pads with translucent gel lining, faint vibration when active.",
  },

  {
    id: "trace-disruptor-band",
    name: "Trace Disruptor Band",
    type: "artifact",
    rarity: "unique",
    slot: "skillSlot",
    skill: "signal-mask",
    faction: "veil",
    icon: "👻",
    effects: { "skill-success-bonus": 5 },
    description: "Wrist-mounted emitter that scrambles biometric and radio signatures.",
    short: "Masks thermal and radio traces from detection.",
    flavor: "Vanish from the data before anyone reads it.",
    visual: "Titanium wristband with fiber coils and soft electromagnetic hum.",
  },

  {
    id: "anchor-resonator",
    name: "Anchor Resonator",
    type: "artifact",
    rarity: "unique",
    slot: "skillSlot",
    skill: "transit-anchor",
    faction: "veil",
    icon: "🌀",
    effects: { "skill-success-bonus": 5 },
    description: "Tripod-mounted stabilizer that emits vibration to settle debris for safe crossing.",
    short: "Stabilizes terrain for safe traversal.",
    flavor: "You can't fix the world, but you can stop it from falling on you.",
    visual: "Carbon tripod with steel joints and an amber-glowing stabilizer core.",
  },
];


// ========== itemsArtifactsUtility.js ==========
export const itemsArtifactsUtility = [
  {
    id: "data-cache-drive",
    name: "Data Cache Drive",
    type: "artifact",
    rarity: "unique",
    slot: "utilitySlot",
    faction: "neutral",
    icon: "💾",
    effects: { "currency-bonus": 5 },
    description: "Rugged steel data drive recovered from collapsed networks.",
    short: "Increases payout from recovered data caches.",
    flavor: "Someone's last backup became your payday.",
    visual: "Steel-plated drive with cracked glass indicator and cable port.",
  },

  {
    id: "relic-resonance-tuner",
    name: "Relic Resonance Tuner",
    type: "artifact",
    rarity: "unique",
    slot: "utilitySlot",
    faction: "neutral",
    icon: "🔮",
    effects: { "relic-quality-bonus": 10 },
    description: "Portable analyzer tuned to detect valuable relic signal emissions.",
    short: "Improves chances of finding higher-quality relics.",
    flavor: "It listens for what the ruins remember.",
    visual: "Palm-sized copper coil device with faint magnetic hum.",
  },

  {
    id: "hazard-lens-visor",
    name: "Hazard Lens Visor",
    type: "artifact",
    rarity: "unique",
    slot: "utilitySlot",
    faction: "neutral",
    icon: "🛡️",
    effects: { "hazard-damage-reduction": 5 },
    description: "Retractable visor overlay with polarized lenses for hazard visibility.",
    short: "Reduces hazard damage and improves visibility.",
    flavor: "You'll see the heat before it burns you.",
    visual: "Fold-down visor with tinted glass and worn metal hinge.",
  },

  {
    id: "recovery-injector",
    name: "Recovery Injector",
    type: "artifact",
    rarity: "unique",
    slot: "utilitySlot",
    faction: "neutral",
    icon: "💉",
    effects: { "knockout-prevention": 1 },
    description: "Compact auto-syringe delivering restorative compounds during emergencies.",
    short: "Prevents first incapacitation per raid.",
    flavor: "Pain's optional if you've got credits.",
    visual: "Reinforced syringe with digital readout and polymer casing.",
  },

  {
    id: "contract-beacon",
    name: "Contract Beacon",
    type: "artifact",
    rarity: "unique",
    slot: "utilitySlot",
    faction: "neutral",
    icon: "📡",
    effects: { "contractor-payout-bonus": 10 },
    description: "Handheld transmitter used to log contractor performance data.",
    short: "Improves payout on successful support contracts.",
    flavor: "Work ends when the beacon says so.",
    visual: "Metal signal beacon with etched serial plate and extendable antenna.",
  },

  {
    id: "salvage-compression-rig",
    name: "Salvage Compression Rig",
    type: "artifact",
    rarity: "unique",
    slot: "utilitySlot",
    faction: "neutral",
    icon: "📦",
    effects: { "loot-quantity-bonus": 10 },
    description: "Compact hydraulic press for field compression of salvaged materials.",
    short: "Increases material quantities recovered from events.",
    flavor: "Carry more by making it smaller. Simple physics, better haul.",
    visual: "Belt-mounted press with steel jaws and cargo netting.",
  },

  {
    id: "sync-regulator-watch",
    name: "Sync Regulator Watch",
    type: "artifact",
    rarity: "unique",
    slot: "utilitySlot",
    faction: "neutral",
    icon: "⏰",
    effects: { "raid-energy-reduction": 10 },
    description: "Wristwatch synced to raid timers, reducing the energy cost of launching runs.",
    short: "Reduces raid energy costs by 10%.",
    flavor: "Keeps your time even when the world's stopped keeping its own.",
    visual: "Analog-digital hybrid watch with cracked face and glowing dial.",
  },

  {
    id: "auto-stabilizer-pack",
    name: "Auto-Stabilizer Pack",
    type: "artifact",
    rarity: "unique",
    slot: "utilitySlot",
    faction: "neutral",
    icon: "🎆",
    effects: { "knockout-prevention": 1 },
    description: "Wearable spinal brace with kinetic dampers to prevent catastrophic injury.",
    short: "Prevents one critical injury per raid.",
    flavor: "Catches you once. Don't make it twice.",
    visual: "Lightweight spine brace with metal pistons and foam padding.",
  },

  {
    id: "training-node-deck",
    name: "Training Node Deck",
    type: "artifact",
    rarity: "unique",
    slot: "utilitySlot",
    faction: "neutral",
    icon: "🎯",
    effects: { "support-skill-bonus": 2 },
    description: "Handheld data deck containing simulated skill routines for training.",
    short: "Boosts support skills during checks.",
    flavor: "Practice until it hurts, then practice better.",
    visual: "Compact data cartridge with keypad and scratched screen.",
  },

  {
    id: "crosslink-amplifier",
    name: "Crosslink Amplifier",
    type: "artifact",
    rarity: "unique",
    slot: "utilitySlot",
    faction: "neutral",
    icon: "🔀",
    effects: { "faction-synergy-bonus": 5 },
    description: "Relay puck enhancing coordination between nearby teammates.",
    short: "Improves success chance when multiple factions assist.",
    flavor: "When everyone's in sync, nobody dies alone.",
    visual: "Hand-sized relay disc with glowing copper inlays and dual terminals.",
  },

  {
    id: "material-extractor",
    name: "Material Extractor",
    type: "artifact",
    rarity: "unique",
    slot: "utilitySlot",
    faction: "neutral",
    icon: "⛏️",
    effects: { "gate-material-bonus": 15 },
    description: "Handheld drill for retrieving salvageable materials from debris.",
    short: "Improves material yield from Gate rooms.",
    flavor: "The gate's trash is tomorrow's rebuild.",
    visual: "Compact hand drill with tungsten bit and transparent debris canister.",
  },

  {
    id: "memory-crystal-shard",
    name: "Memory Crystal Shard",
    type: "artifact",
    rarity: "unique",
    slot: "utilitySlot",
    faction: "neutral",
    icon: "🔮",
    effects: { "blueprint-find-bonus": 5 },
    description: "Resin-sealed data shard that resonates near the caches where blueprints survive.",
    short: "Increases legendary blueprint discovery chance.",
    flavor: "What's stored inside isn't data—it's what was lost trying to keep it.",
    visual: "Clear resin capsule with crystalline core set in a steel ring.",
  },
];


// ========== itemsBlueprints.js ==========
// Artifact blueprints - legendary tier items that unlock artifact crafting
// Each blueprint corresponds to an artifact in itemsArtifactsSkill.js or itemsArtifactsUtility.js

export const itemsBlueprints = [
  // ===============================
  // Dominion Skill Artifacts
  // ===============================
  {
    id: "signal-repeater-pack-blueprint",
    name: "Signal Repeater Pack Blueprint",
    type: "blueprint",
    rarity: "legendary",
    faction: "dominion",
    icon: "📡",
    description: "Blueprint for the Signal Repeater Pack",
    short: "Plans: Signal Repeater Pack.",
  },
  {
    id: "control-override-kit-blueprint",
    name: "Control Override Kit Blueprint",
    type: "blueprint",
    rarity: "legendary",
    faction: "dominion",
    icon: "💻",
    description: "Blueprint for the Control Override Kit",
    short: "Plans: Control Override Kit.",
  },
  {
    id: "operator-link-harness-blueprint",
    name: "Operator Link Harness Blueprint",
    type: "blueprint",
    rarity: "legendary",
    faction: "dominion",
    icon: "🤖",
    description: "Blueprint for the Operator Link Harness",
    short: "Plans: Operator Link Harness.",
  },
  {
    id: "stability-coupler-blueprint",
    name: "Stability Coupler Blueprint",
    type: "blueprint",
    rarity: "legendary",
    faction: "dominion",
    icon: "⚠️",
    description: "Blueprint for the Stability Coupler",
    short: "Plans: Stability Coupler.",
  },
  {
    id: "spectral-survey-scope-blueprint",
    name: "Spectral Survey Scope Blueprint",
    type: "blueprint",
    rarity: "legendary",
    faction: "dominion",
    icon: "🔬",
    description: "Blueprint for the Spectral Survey Scope",
    short: "Plans: Spectral Survey Scope.",
  },

  // ===============================
  // Wilders Skill Artifacts
  // ===============================
  {
    id: "harvesting-gauntlet-blueprint",
    name: "Harvesting Gauntlet Blueprint",
    type: "blueprint",
    rarity: "legendary",
    faction: "wilders",
    icon: "🌱",
    description: "Blueprint for the Harvesting Gauntlet",
    short: "Plans: Harvesting Gauntlet.",
  },
  {
    id: "growth-induction-pod-blueprint",
    name: "Growth Induction Pod Blueprint",
    type: "blueprint",
    rarity: "legendary",
    faction: "wilders",
    icon: "🌿",
    description: "Blueprint for the Growth Induction Pod",
    short: "Plans: Growth Induction Pod.",
  },
  {
    id: "pheromone-relay-belt-blueprint",
    name: "Pheromone Relay Belt Blueprint",
    type: "blueprint",
    rarity: "legendary",
    faction: "wilders",
    icon: "🦎",
    description: "Blueprint for the Pheromone Relay Belt",
    short: "Plans: Pheromone Relay Belt.",
  },
  {
    id: "respirator-webbing-blueprint",
    name: "Respirator Webbing Blueprint",
    type: "blueprint",
    rarity: "legendary",
    faction: "wilders",
    icon: "🧬",
    description: "Blueprint for the Respirator Webbing",
    short: "Plans: Respirator Webbing.",
  },
  {
    id: "hazard-recovery-satchel-blueprint",
    name: "Hazard Recovery Satchel Blueprint",
    type: "blueprint",
    rarity: "legendary",
    faction: "wilders",
    icon: "☣️",
    description: "Blueprint for the Hazard Recovery Satchel",
    short: "Plans: Hazard Recovery Satchel.",
  },

  // ===============================
  // Veil Skill Artifacts
  // ===============================
  {
    id: "resonant-survey-array-blueprint",
    name: "Resonant Survey Array Blueprint",
    type: "blueprint",
    rarity: "legendary",
    faction: "veil",
    icon: "🗺️",
    description: "Blueprint for the Resonant Survey Array",
    short: "Plans: Resonant Survey Array.",
  },
  {
    id: "lockpulse-breach-kit-blueprint",
    name: "Lockpulse Breach Kit Blueprint",
    type: "blueprint",
    rarity: "legendary",
    faction: "veil",
    icon: "🔓",
    description: "Blueprint for the Lockpulse Breach Kit",
    short: "Plans: Lockpulse Breach Kit.",
  },
  {
    id: "silent-step-pads-blueprint",
    name: "Silent Step Pads Blueprint",
    type: "blueprint",
    rarity: "legendary",
    faction: "veil",
    icon: "👤",
    description: "Blueprint for the Silent Step Pads",
    short: "Plans: Silent Step Pads.",
  },
  {
    id: "trace-disruptor-band-blueprint",
    name: "Trace Disruptor Band Blueprint",
    type: "blueprint",
    rarity: "legendary",
    faction: "veil",
    icon: "👻",
    description: "Blueprint for the Trace Disruptor Band",
    short: "Plans: Trace Disruptor Band.",
  },
  {
    id: "anchor-resonator-blueprint",
    name: "Anchor Resonator Blueprint",
    type: "blueprint",
    rarity: "legendary",
    faction: "veil",
    icon: "🌀",
    description: "Blueprint for the Anchor Resonator",
    short: "Plans: Anchor Resonator.",
  },

  // ===============================
  // Neutral Utility Artifacts
  // ===============================
  {
    id: "data-cache-drive-blueprint",
    name: "Data Cache Drive Blueprint",
    type: "blueprint",
    rarity: "legendary",
    faction: "neutral",
    icon: "💾",
    description: "Blueprint for the Data Cache Drive",
    short: "Plans: Data Cache Drive.",
  },
  {
    id: "relic-resonance-tuner-blueprint",
    name: "Relic Resonance Tuner Blueprint",
    type: "blueprint",
    rarity: "legendary",
    faction: "neutral",
    icon: "🔮",
    description: "Blueprint for the Relic Resonance Tuner",
    short: "Plans: Relic Resonance Tuner.",
  },
  {
    id: "hazard-lens-visor-blueprint",
    name: "Hazard Lens Visor Blueprint",
    type: "blueprint",
    rarity: "legendary",
    faction: "neutral",
    icon: "🛡️",
    description: "Blueprint for the Hazard Lens Visor",
    short: "Plans: Hazard Lens Visor.",
  },
  {
    id: "recovery-injector-blueprint",
    name: "Recovery Injector Blueprint",
    type: "blueprint",
    rarity: "legendary",
    faction: "neutral",
    icon: "💉",
    description: "Blueprint for the Recovery Injector",
    short: "Plans: Recovery Injector.",
  },
  {
    id: "contract-beacon-blueprint",
    name: "Contract Beacon Blueprint",
    type: "blueprint",
    rarity: "legendary",
    faction: "neutral",
    icon: "📡",
    description: "Blueprint for the Contract Beacon",
    short: "Plans: Contract Beacon.",
  },
  {
    id: "salvage-compression-rig-blueprint",
    name: "Salvage Compression Rig Blueprint",
    type: "blueprint",
    rarity: "legendary",
    faction: "neutral",
    icon: "📦",
    description: "Blueprint for the Salvage Compression Rig",
    short: "Plans: Salvage Compression Rig.",
  },
  {
    id: "sync-regulator-watch-blueprint",
    name: "Sync Regulator Watch Blueprint",
    type: "blueprint",
    rarity: "legendary",
    faction: "neutral",
    icon: "⏰",
    description: "Blueprint for the Sync Regulator Watch",
    short: "Plans: Sync Regulator Watch.",
  },
  {
    id: "auto-stabilizer-pack-blueprint",
    name: "Auto-Stabilizer Pack Blueprint",
    type: "blueprint",
    rarity: "legendary",
    faction: "neutral",
    icon: "🎆",
    description: "Blueprint for the Auto-Stabilizer Pack",
    short: "Plans: Auto-Stabilizer Pack.",
  },
  {
    id: "training-node-deck-blueprint",
    name: "Training Node Deck Blueprint",
    type: "blueprint",
    rarity: "legendary",
    faction: "neutral",
    icon: "🎯",
    description: "Blueprint for the Training Node Deck",
    short: "Plans: Training Node Deck.",
  },
  {
    id: "crosslink-amplifier-blueprint",
    name: "Crosslink Amplifier Blueprint",
    type: "blueprint",
    rarity: "legendary",
    faction: "neutral",
    icon: "🔀",
    description: "Blueprint for the Crosslink Amplifier",
    short: "Plans: Crosslink Amplifier.",
  },
  {
    id: "material-extractor-blueprint",
    name: "Material Extractor Blueprint",
    type: "blueprint",
    rarity: "legendary",
    faction: "neutral",
    icon: "⛏️",
    description: "Blueprint for the Material Extractor",
    short: "Plans: Material Extractor.",
  },
  {
    id: "memory-crystal-shard-blueprint",
    name: "Memory Crystal Shard Blueprint",
    type: "blueprint",
    rarity: "legendary",
    faction: "neutral",
    icon: "🔮",
    description: "Blueprint for the Memory Crystal Shard",
    short: "Plans: Memory Crystal Shard.",
  },
];


// ========== itemsCommon.js ==========
export const itemsCommon = [
  // ===============================
  // Common Materials
  // ===============================
  {
    id: "steel-plates",
    name: "Steel Plates",
    type: "material",
    rarity: "common",
    faction: "neutral",
    icon: "🧱",
    description: "Reinforced steel sheets salvaged from collapsed structures, used for repairs and fabrication.",
    short: "Structural steel for frames and reinforcements.",
  },
  {
    id: "copper-wire",
    name: "Copper Wire",
    type: "material",
    rarity: "common",
    faction: "dominion",
    icon: "🧶",
    description: "Insulated copper wire used for communications, power routing, and control circuits.",
    short: "Electrical wiring for circuits and comms.",
  },
  {
    id: "brass-fittings",
    name: "Brass Fittings",
    type: "material",
    rarity: "common",
    faction: "veil",
    icon: "⚙️",
    description: "Polished brass connectors and mechanical couplings used in precision assemblies.",
    short: "Mechanical connectors for field gear.",
  },
  {
    id: "glass-lens",
    name: "Glass Lens",
    type: "material",
    rarity: "common",
    faction: "neutral",
    icon: "🔍",
    description: "Optical glass lens salvaged from scopes and sensors, used in scanning devices.",
    short: "Optical lens for scopes and sensors.",
  },
  {
    id: "ceramic-insulator",
    name: "Ceramic Insulator",
    type: "material",
    rarity: "common",
    faction: "dominion",
    icon: "🏺",
    description: "High-heat ceramic components that isolate electrical and thermal energy.",
    short: "Heat-resistant electrical insulation.",
  },
  {
    id: "composite-casing",
    name: "Composite Casing",
    type: "material",
    rarity: "common",
    faction: "dominion",
    icon: "📦",
    description: "Lightweight polymer and carbon housings used to encase sensitive systems.",
    short: "Protective housing for tech equipment.",
  },
  {
    id: "fabric-mesh",
    name: "Fabric Mesh",
    type: "material",
    rarity: "common",
    faction: "wilders",
    icon: "🪡",
    description: "Durable woven material used for straps, belts, and wearable rigs.",
    short: "Reinforced cloth for wearable gear.",
  },
  {
    id: "canvas-fiber",
    name: "Canvas Fiber",
    type: "material",
    rarity: "common",
    faction: "wilders",
    icon: "🧵",
    description: "Tough, weatherproof textile favored by Wilder survival teams for packs and satchels.",
    short: "Heavy fabric for field packs.",
  },
  {
    id: "leather-straps",
    name: "Leather Straps",
    type: "material",
    rarity: "common",
    faction: "wilders",
    icon: "🎒",
    description: "Cured animal hide straps used in Wilder clothing, gloves, and carrying gear.",
    short: "Leather bands for field equipment.",
  },
  {
    id: "rubber-seal",
    name: "Rubber Seal",
    type: "material",
    rarity: "common",
    faction: "neutral",
    icon: "🧰",
    description: "Rubberized sealing rings for airtight or watertight containers.",
    short: "Insulating seal for protective containers.",
  },
  {
    id: "polymer-tubing",
    name: "Polymer Tubing",
    type: "material",
    rarity: "common",
    faction: "wilders",
    icon: "🧪",
    description: "Flexible tubing used in breathing rigs, injectors, and liquid transfer systems.",
    short: "Flexible tubing for filtration or injection systems.",
  },
  {
    id: "carbon-housing",
    name: "Carbon Housing",
    type: "material",
    rarity: "common",
    faction: "veil",
    icon: "⬛",
    description: "Durable carbon-composite shell used in portable scanning and stealth gear.",
    short: "Lightweight carbon shell for tech devices.",
  },
  {
    id: "plastic-casing",
    name: "Plastic Casing",
    type: "material",
    rarity: "common",
    faction: "veil",
    icon: "🧴",
    description: "Recycled polymer panels used for consumer-grade or field-modified electronics.",
    short: "Lightweight housing for electronic tools.",
  },
  {
    id: "aluminum-shell",
    name: "Aluminum Shell",
    type: "material",
    rarity: "common",
    faction: "dominion",
    icon: "🥫",
    description: "Lightweight aluminum housing used for beacons, sensor mounts, and handheld devices.",
    short: "Light metal housing for compact field gear.",
  },
  {
    id: "circuit-board",
    name: "Circuit Board",
    type: "material",
    rarity: "common",
    faction: "dominion",
    icon: "💻",
    description: "Salvaged printed circuit board used for control panels and signal devices.",
    short: "Base electronic component for all digital tech.",
  },
  {
    id: "resin-casing",
    name: "Resin Casing",
    type: "material",
    rarity: "common",
    faction: "wilders",
    icon: "🧴",
    description: "Transparent resin shell used for insulation, bonding, or sealing delicate materials.",
    short: "Clear protective housing for fragile items.",
  },
  {
    id: "iron-frame",
    name: "Iron Frame",
    type: "material",
    rarity: "common",
    faction: "neutral",
    icon: "🏗️",
    description: "Reinforced iron supports salvaged from export construction scaffolds and vehicles.",
    short: "Structural frame material for heavy gear.",
  },
  {
    id: "relay-antenna-strut",
    name: "Relay Antenna Strut",
    type: "material",
    rarity: "common",
    faction: "dominion",
    icon: "📡",
    description: "Telescoping antenna segment stripped from dead relay towers, still true enough to hold a signal line.",
    short: "Antenna segment for comm rigs and beacons.",
  },
  {
    id: "power-cell-casing",
    name: "Power Cell Casing",
    type: "material",
    rarity: "common",
    faction: "dominion",
    icon: "🔋",
    description: "Hardened shell of a depleted power cell, reusable as housing for charge regulators and battery packs.",
    short: "Battery shell for power systems.",
  },
  {
    id: "logic-ribbon-cable",
    name: "Logic Ribbon Cable",
    type: "material",
    rarity: "common",
    faction: "dominion",
    icon: "🎗️",
    description: "Flat multi-channel cable pulled from control consoles, carries data lines between salvaged modules.",
    short: "Data cabling for control interfaces.",
  },
  {
    id: "chitin-plate",
    name: "Chitin Plate",
    type: "material",
    rarity: "common",
    faction: "wilders",
    icon: "🪲",
    description: "Hardened shell segment shed by overgrown insects, light as polymer and twice as tough.",
    short: "Natural armor plating from giant insects.",
  },
  {
    id: "spore-felt-padding",
    name: "Spore Felt Padding",
    type: "material",
    rarity: "common",
    faction: "wilders",
    icon: "🍄",
    description: "Dense fungal felt harvested from spore beds, insulates and cushions delicate biological cargo.",
    short: "Fungal padding for insulation and cushioning.",
  },
  {
    id: "tanned-gut-cord",
    name: "Tanned Gut Cord",
    type: "material",
    rarity: "common",
    faction: "wilders",
    icon: "🪢",
    description: "Sinew cordage cured over smoke, the Wilders' answer to wire ties and suture thread alike.",
    short: "Tough natural cordage for bindings.",
  },
  {
    id: "optic-fiber-spool",
    name: "Optic Fiber Spool",
    type: "material",
    rarity: "common",
    faction: "veil",
    icon: "🧿",
    description: "Coil of hair-thin light-guide fiber, favored for taps and scopes that leave no electromagnetic trace.",
    short: "Light-guide fiber for covert optics.",
  },
  {
    id: "matte-coating-compound",
    name: "Matte Coating Compound",
    type: "material",
    rarity: "common",
    faction: "veil",
    icon: "🎨",
    description: "Light-drinking paste applied to gear that should not glint, reflect, or be remembered.",
    short: "Anti-reflective coating for stealth gear.",
  },
  {
    id: "micro-bearing-set",
    name: "Micro Bearing Set",
    type: "material",
    rarity: "common",
    faction: "veil",
    icon: "⚪",
    description: "Case of near-frictionless bearings that let mechanisms turn in perfect silence.",
    short: "Silent precision bearings for quiet mechanisms.",
  },
  {
    id: "acoustic-foam-wafer",
    name: "Acoustic Foam Wafer",
    type: "material",
    rarity: "common",
    faction: "veil",
    icon: "🔲",
    description: "Cut sheet of sound-eating foam, lines the housings of equipment meant to run unheard.",
    short: "Sound-dampening liner for covert equipment.",
  },
  {
    id: "etched-keyplate-blank",
    name: "Etched Keyplate Blank",
    type: "material",
    rarity: "common",
    faction: "veil",
    icon: "🗝️",
    description: "Unprogrammed access plate with etched circuit channels, ready to take any lock's profile.",
    short: "Blank keycard stock for bypass tools.",
  },
];

// ========== itemsConsumables.js ==========
export const itemsCommonConsumables = [
  {
    id: "field-health-stim",
    name: "Field Health Stim (35 HP)",
    type: "consumable",
    rarity: "common",
    icon: "💊",
    description: "A quick-use medical injector restoring 35 HP.",
    short: "Restores 35 HP.",
    credits: 25,
    restoresHp: 35,
    recipe: [
      { id: "polymer-tubing", qty: 2 },
      { id: "plastic-casing", qty: 1 },
      { id: "rubber-seal", qty: 1 },
    ],
  },
  {
    id: "portable-energy-pack",
    name: "Portable Energy Pack",
    type: "consumable",
    rarity: "common",
    icon: "⚡",
    description: "Compact power injection cell restoring 30 energy.",
    short: "Restores 30 energy.",
    credits: 150,
    restoresEnergy: 30,
    recipe: [
      { id: "copper-wire", qty: 2 },
      { id: "ceramic-insulator", qty: 1 },
      { id: "steel-plates", qty: 1 },
    ],
  },
  {
    id: "credits",
    name: "Credits",
    type: "credits",
    rarity: "currency",
    icon: "💰",
    description: "Standard currency for trade, hiring, and fabrication.",
    short: "Universal currency for trade.",
  },
];

export const itemsUncommonConsumables = [
  {
    id: "recovery-injector-mk2",
    name: "Recovery Injector (50 HP)",
    type: "consumable",
    rarity: "uncommon",
    icon: "💉",
    description: "Upgraded stimulant blend restoring 50 HP with faster onset and reduced side effects.",
    short: "Restores 50 HP.",
    credits: 100,
    restoresHp: 50,
    recipe: [
      { id: "medical-stimulant-cartridge", qty: 1 },
      { id: "nutrient-catalyst-ampoule", qty: 1 },
      { id: "rubber-seal", qty: 1 },
    ],
  },
  {
    id: "energy-conversion-pack",
    name: "Energy Conversion Pack",
    type: "consumable",
    rarity: "uncommon",
    icon: "🔋",
    description: "Refined energy module restoring 45 energy.",
    short: "Restores 45 energy.",
    credits: 600,
    restoresEnergy: 45,
    recipe: [
      { id: "power-regulation-unit", qty: 1 },
      { id: "signal-processor-core", qty: 1 },
      { id: "copper-wire", qty: 2 },
    ],
  },
];

export const itemsRareConsumables = [
  {
    id: "advanced-recovery-serum",
    name: "Recovery Serum (70 HP)",
    type: "consumable",
    rarity: "rare",
    icon: "🧪",
    description: "Refined medical stimulant restoring 70 HP.",
    short: "Restores 70 HP.",
    credits: 300,
    restoresHp: 70,
    recipe: [
      { id: "synthetic-pheromone-capsule", qty: 1 },
      { id: "medical-stimulant-cartridge", qty: 1 },
      { id: "containment-gel-lining", qty: 1 },
    ],
  },
  {
    id: "power-cell-mk3",
    name: "Power Cell Mk. III",
    type: "consumable",
    rarity: "rare",
    icon: "⚙️",
    description: "High-density cell restoring 60 energy.",
    short: "Restores 60 energy.",
    credits: 1800,
    restoresEnergy: 60,
    recipe: [
      { id: "power-regulation-unit", qty: 1 },
      { id: "voltage-regulator-unit", qty: 1 },
      { id: "signal-processor-core", qty: 2 },
    ],
  },
];


// ========== itemsEpicConsumable.js ==========
// Epic Consumables — grounded descriptions
export const itemsEpicConsumableDominion = [
  {
    id: "relay-stabilizer-gel-pack",
    techniqueId: "relay-stabilizer",
    name: "Relay Stabilizer Gel Packs",
    rarity: "epic",
    type: "consumable",
    effects: { "room-time-reduction": 10 },
    credits: 100,
    icon: "🧴📡",
    short: "Quick-sets mast alignment for steady comms.",
    description: "High-viscosity damping gel that reduces dish/mast micro-vibration and bearing drift, stabilizing RF alignment during Signal Relay."
  },
  {
    id: "biofiber-splice-kit",
    techniqueId: "organic-link-adapter",
    name: "Biofiber Splice Kits",
    rarity: "epic",
    type: "consumable",
    effects: { "room-time-reduction": 10 },
    credits: 100,
    icon: "🪡🌿",
    short: "Patch cracked lines with living fiber.",
    description: "Sterile polymer-fiber splices and crimp ferrules for fast field repairs of damaged signal runs; restores continuity for organic-safe link adapters."
  },
  {
    id: "noise-floor-buffer-pack",
    techniqueId: "silent-channel",
    name: "Noise Floor Buffer Packs",
    rarity: "epic",
    type: "consumable",
    effects: { "room-time-reduction": 10 },
    credits: 100,
    icon: "🔇📶",
    short: "Drops trace noise below scan.",
    description: "Adaptive notch and low-pass inserts that suppress hiss, intermod, and harmonics to lower the effective noise floor on shared bands."
  },
  {
    id: "multi-node-override-cart",
    techniqueId: "command-cascade",
    name: "Multi-Node Override Cartridges",
    rarity: "epic",
    type: "consumable",
    effects: { "room-time-reduction": 10 },
    credits: 100,
    icon: "🧩🖥️",
    short: "Stage chained takeovers faster.",
    description: "Pre-staged command bundles and credential handoffs that reduce human timing error in sequential takeovers of multiple endpoints."
  },
  {
    id: "surge-damping-couplers",
    techniqueId: "bio-feedback-guard",
    name: "Surge Damping Couplers",
    rarity: "epic",
    type: "consumable",
    effects: { "room-time-reduction": 10 },
    credits: 100,
    icon: "⚡🧬",
    short: "Soaks reboot spikes safely.",
    description: "Inline TVS elements and RC snubbers that absorb transient voltage spikes during forced reboots to protect connected subsystems."
  },
  {
    id: "decoy-traffic-cartridge",
    techniqueId: "signal-mask-injection",
    name: "Decoy Traffic Cartridges",
    rarity: "epic",
    type: "consumable",
    effects: { "room-time-reduction": 10 },
    credits: 100,
    icon: "🎭📡",
    short: "Hide control packets in noise.",
    description: "Traffic-shaping frames and padding packets that disguise control bursts inside believable network chatter without altering payloads."
  },
  {
    id: "swarm-sync-beacon-cart",
    techniqueId: "coordinated-flight-pattern",
    name: "Swarm Sync Beacon Carts",
    rarity: "epic",
    type: "consumable",
    effects: { "room-time-reduction": 10 },
    credits: 100,
    icon: "🛰️🤖",
    short: "Snap-syncs drone routes.",
    description: "Short-range UWB timing beacons that provide a common clock for nearby drones, improving lane separation and route timing."
  },
  {
    id: "overgrowth-lidar-pack",
    techniqueId: "rootpath-protocol",
    name: "Overgrowth LIDAR Packs",
    rarity: "epic",
    type: "consumable",
    effects: { "room-time-reduction": 10 },
    credits: 100,
    icon: "🌿📡",
    short: "Map safe lanes through brush.",
    description: "Compact LIDAR + proximity sweep that identifies low-resistance channels through dense brush for safer autonomous routing."
  },
  {
    id: "quiet-sonar-cartridge",
    techniqueId: "echo-pulse-beacon",
    name: "Quiet Sonar Cartridges",
    rarity: "epic",
    type: "consumable",
    effects: { "room-time-reduction": 10 },
    credits: 100,
    icon: "🔈🛰️",
    short: "Low-profile ping mapping.",
    description: "Low-intensity ultrasonic transceivers that enable short-range mapping with minimal acoustic signature."
  },
  {
    id: "rapid-grounding-spikes",
    techniqueId: "system-ground-loop",
    name: "Rapid Grounding Spikes",
    rarity: "epic",
    type: "consumable",
    effects: { "room-time-reduction": 10 },
    credits: 100,
    icon: "📎⚡",
    short: "Drop fault current to ground.",
    description: "Temporary ground electrodes and clamps that create safe earth paths to quickly dissipate fault current."
  },
  {
    id: "vent-cycler-pack",
    techniqueId: "spore-vent-reclaimer",
    name: "Vent Cycler Packs",
    rarity: "epic",
    type: "consumable",
    effects: { "room-time-reduction": 10 },
    credits: 100,
    icon: "🌬️🧪",
    short: "Purge, filter, reuse air.",
    description: "Disposable microfilters and purge actuators that accelerate intake/exhaust cycling and capture particulates for recovery."
  },
  {
    id: "cutoff-line-kit",
    techniqueId: "manual-override-line",
    name: "Cutoff Line Kits",
    rarity: "epic",
    type: "consumable",
    effects: { "room-time-reduction": 10 },
    credits: 100,
    icon: "✂️🧵",
    short: "Install a physical kill-line.",
    description: "Insulated cable, crimps, and lockout tags for adding a physical isolation line to de-energize a local zone quickly."
  },
  {
    id: "scan-link-bridge",
    techniqueId: "entangled-synchrony",
    name: "Scan Link Bridges",
    rarity: "epic",
    type: "consumable",
    effects: { "room-time-reduction": 10 },
    credits: 100,
    icon: "🧠🔗",
    short: "Lock scanners into sync.",
    description: "Clock fan-out and shared buffer modules that keep multiple instruments sampling in the same time base for coherent reads."
  },
  {
    id: "dual-spectrum-cartridge",
    techniqueId: "bio-spectral-crosscheck",
    name: "Dual-Spectrum Cartridges",
    rarity: "epic",
    type: "consumable",
    effects: { "room-time-reduction": 10 },
    credits: 100,
    icon: "🧬🔬",
    short: "Overlay bio + energy reads.",
    description: "Paired sensors and calibration markers that co-register biological signatures with radiation/field measurements for cross-checks."
  },
  {
    id: "residual-current-probe-kit",
    techniqueId: "ghost-echo-capture",
    name: "Residual Current Probe Kits",
    rarity: "epic",
    type: "consumable",
    effects: { "room-time-reduction": 10 },
    credits: 100,
    icon: "🧲📏",
    short: "Sniff faint live lines.",
    description: "High-impedance clamp probes and tracer dye to detect low-level leakage and find viable paths through damaged circuits."
  },
];

export const itemsEpicConsumableWilders = [
  {
    id: "harvest-sync-brace-pack",
    techniqueId: "reclamation-drone-link",
    name: "Harvest Sync Brace Packs",
    rarity: "epic",
    type: "consumable",
    effects: { "room-time-reduction": 10 },
    credits: 100,
    icon: "🦾🌱",
    short: "Lock harvester + drone timing.",
    description: "Rigid braces and timing tabs that mechanically synchronize harvester motion with companion drones to reduce sample loss."
  },
  {
    id: "fast-filter-plenum-cart",
    techniqueId: "mycelial-rebalance",
    name: "Fast Filter Plenum Carts",
    rarity: "epic",
    type: "consumable",
    effects: { "room-time-reduction": 10 },
    credits: 100,
    icon: "🍄🧪",
    short: "Accelerate live fungal sorting.",
    description: "Pre-wetted filtration pads and flow baffles that speed separation of viable biomass from debris in mixed slurries."
  },
  {
    id: "micro-mist-emitter",
    techniqueId: "phase-cleanse",
    name: "Micro-Mist Emitters",
    rarity: "epic",
    type: "consumable",
    effects: { "room-time-reduction": 10 },
    credits: 100,
    icon: "💧🧼",
    short: "Lift spores/oils quickly.",
    description: "Single-use atomizer that lays down a uniform cleaning mist to lift spores/oils while minimizing residue."
  },
  {
    id: "rapid-brace-binder",
    techniqueId: "adaptive-lattice",
    name: "Rapid Brace Binders",
    rarity: "epic",
    type: "consumable",
    effects: { "room-time-reduction": 10 },
    credits: 100,
    icon: "🪵🕸️",
    short: "Snap-grow support frames.",
    description: "Two-part structural resin and mineral fillers that cure into lightweight struts for quick bracing or gap-spanning."
  },
  {
    id: "light-channel-films",
    techniqueId: "photosymbiosis",
    name: "Light Channel Films",
    rarity: "epic",
    type: "consumable",
    effects: { "room-time-reduction": 10 },
    credits: 100,
    icon: "🔆🌿",
    short: "Pipe light through leaves.",
    description: "Thin-film optics that route and diffuse light to shaded growth surfaces, improving local photosynthetic efficiency."
  },
  {
    id: "root-guidance-stakes",
    techniqueId: "shadowroot-bloom",
    name: "Root Guidance Stakes",
    rarity: "epic",
    type: "consumable",
    effects: { "room-time-reduction": 10 },
    credits: 100,
    icon: "📍🌱",
    short: "Lead roots through cracks.",
    description: "Moisture-biased guide stakes that encourage root growth along existing micro-fractures to expose cavities safely."
  },
  {
    id: "breath-sync-strips",
    techniqueId: "signal-harmonizer",
    name: "Breath Sync Strips",
    rarity: "epic",
    type: "consumable",
    effects: { "room-time-reduction": 10 },
    credits: 100,
    icon: "😮‍💨🎚️",
    short: "Align heart/breath rhythms.",
    description: "Skin-safe haptic strips that cue synchronized breathing patterns across the team, reducing stress responses without drugs."
  },
  {
    id: "chorus-diffuser-ampoule",
    techniqueId: "pheromone-chorus",
    name: "Chorus Diffuser Ampoules",
    rarity: "epic",
    type: "consumable",
    effects: { "room-time-reduction": 10 },
    credits: 100,
    icon: "🧪🎶",
    short: "Blend multi-handler signals.",
    description: "Timed micro-diffusers that release low-dose, non-sedative botanical blends to stabilize handler-group behavior."
  },
  {
    id: "scent-dampening-salve",
    techniqueId: "silent-hand",
    name: "Scent Dampening Salves",
    rarity: "epic",
    type: "consumable",
    effects: { "room-time-reduction": 10 },
    credits: 100,
    icon: "🧴👃",
    short: "Kill trail and gear snaps.",
    description: "Neutralizing balm for fabrics and skin that suppresses volatile compounds and squeak/rattle points at contact surfaces."
  },
  {
    id: "reactive-gel-patch",
    techniqueId: "bio-shielding",
    name: "Reactive Gel Patches",
    rarity: "epic",
    type: "consumable",
    effects: { "room-time-reduction": 10 },
    credits: 100,
    icon: "🩹🛡️",
    short: "Spread shock/heat on impact.",
    description: "Non-Newtonian impact-spread gel patches that distribute force and heat, improving short-term tolerance to arcs and hotspots."
  },
  {
    id: "enzyme-swap-kit",
    techniqueId: "shared-immunity",
    name: "Enzyme Swap Kits",
    rarity: "epic",
    type: "consumable",
    effects: { "room-time-reduction": 10 },
    credits: 100,
    icon: "🔁🧬",
    short: "Pass resistance packs fast.",
    description: "Color-coded topical kits containing broad-spectrum cleansing and barrier components for rapid, shared exposure mitigation."
  },
  {
    id: "surface-tuning-spray",
    techniqueId: "dim-shift-layer",
    name: "Surface Tuning Sprays",
    rarity: "epic",
    type: "consumable",
    effects: { "room-time-reduction": 10 },
    credits: 100,
    icon: "🖌️🪞",
    short: "Match tone and sheen quickly.",
    description: "Microflake coating with adjustable binder that tunes reflectivity and tone to match surrounding surfaces."
  },
  {
    id: "hardcase-quickframe-kit",
    techniqueId: "servo-containment",
    name: "Hardcase Quickframe Kits",
    rarity: "epic",
    type: "consumable",
    effects: { "room-time-reduction": 10 },
    credits: 100,
    icon: "📦🤖",
    short: "Shell hot samples fast.",
    description: "Fold-out frames and latch nets that convert drone housings into sealed transport cases for unstable samples."
  },
  {
    id: "pod-starter-seeds",
    techniqueId: "mutualist-growth",
    name: "Pod Starter Seeds",
    rarity: "epic",
    type: "consumable",
    effects: { "room-time-reduction": 10 },
    credits: 100,
    icon: "🌰🧫",
    short: "Grow living containment.",
    description: "Expandable biopolymer pellets with inert scaffolding that form shock-absorbing pods around irregular objects."
  },
  {
    id: "recovery-pod-capsules",
    techniqueId: "reflex-bloom",
    name: "Recovery Pod Capsules",
    rarity: "epic",
    type: "consumable",
    effects: { "room-time-reduction": 10 },
    credits: 100,
    icon: "💊🌸",
    short: "Auto-release healing compounds.",
    description: "Timed-release capsules that deploy micro-pods carrying standard wound-care and decontamination compounds post-exposure."
  },
];

export const itemsEpicConsumableVeil = [
  {
    id: "relay-bridge-link-cart",
    techniqueId: "quantum-relay-hook",
    name: "Relay Bridge Link Carts",
    rarity: "epic",
    type: "consumable",
    effects: { "room-time-reduction": 10 },
    credits: 100,
    icon: "🪝🗺️",
    short: "Piggyback maps on relays.",
    description: "Interface shims that attach mapping metadata to existing relay management frames for reliable piggybacking."
  },
  {
    id: "bio-pulse-amplifier-pack",
    techniqueId: "biotrace-resonance",
    name: "Bio-Pulse Amplifier Packs",
    rarity: "epic",
    type: "consumable",
    effects: { "room-time-reduction": 10 },
    credits: 100,
    icon: "📡🌱",
    short: "Boost returns off living matter.",
    description: "Low-power pulse amplifiers tuned to common organic absorption/reflectance bands to clarify returns from living matter."
  },
  {
    id: "map-cache-sync-cart",
    techniqueId: "shadow-archive",
    name: "Map Cache Sync Carts",
    rarity: "epic",
    type: "consumable",
    effects: { "room-time-reduction": 10 },
    credits: 100,
    icon: "💾🗺️",
    short: "Auto-sync portable map caches.",
    description: "Proximity sync cartridges using short-range NFC/optical channels to handshake map caches without manual transfer."
  },
  {
    id: "micro-torque-paste",
    techniqueId: "servo-alignment-tool",
    name: "Micro-Torque Pastes",
    rarity: "epic",
    type: "consumable",
    effects: { "room-time-reduction": 10 },
    credits: 100,
    icon: "🧴🔧",
    short: "Seat pins and rails cleanly.",
    description: "Friction-balanced paste that stabilizes micro-movements in pins and rails for precise servo/lock alignment."
  },
  {
    id: "quiet-seal-injector",
    techniqueId: "biofilm-stabilizer",
    name: "Quiet Seal Injectors",
    rarity: "epic",
    type: "consumable",
    effects: { "room-time-reduction": 10 },
    credits: 100,
    icon: "🤫🧪",
    short: "Stop rattles and squeals.",
    description: "Thin polymer sealant applied under panel seams to eliminate rattle and high-frequency squeal during movement."
  },
  {
    id: "decoy-pulse-cartridge",
    techniqueId: "echo-pulse-decoy",
    name: "Decoy Pulse Cartridges",
    rarity: "epic",
    type: "consumable",
    effects: { "room-time-reduction": 10 },
    credits: 100,
    icon: "📡🎯",
    short: "Flash a harmless misread.",
    description: "Programmable emitters that send harmless ranging pings to bias attention away from the working area."
  },
  {
    id: "route-shadow-taggers",
    techniqueId: "drone-path-shadow",
    name: "Route Shadow Taggers",
    rarity: "epic",
    type: "consumable",
    effects: { "room-time-reduction": 10 },
    credits: 100,
    icon: "🏷️🚶",
    short: "Mark drone lanes for feet.",
    description: "Low-gloss floor markers that couple human pathing to known drone lanes, reducing the number of distinct traces."
  },
  {
    id: "low-shine-veil-pack",
    techniqueId: "spore-veil",
    name: "Low-Shine Veil Packs",
    rarity: "epic",
    type: "consumable",
    effects: { "room-time-reduction": 10 },
    credits: 100,
    icon: "🌫️🕶️",
    short: "Dull edges, cut glare.",
    description: "Aerosolized matte coat that dulls hard edges and specular highlights to lower visual detection."
  },
  {
    id: "pace-sync-patches",
    techniqueId: "mirror-walk",
    name: "Pace Sync Patches",
    rarity: "epic",
    type: "consumable",
    effects: { "room-time-reduction": 10 },
    credits: 100,
    icon: "🩹👣",
    short: "Keep squad as one trace.",
    description: "Haptic cadence patches that keep a squad’s step timing uniform to reduce multi-source motion cues."
  },
  {
    id: "auth-spoof-keycard",
    techniqueId: "control-spoof",
    name: "Auth Spoof Keycards",
    rarity: "epic",
    type: "consumable",
    effects: { "room-time-reduction": 10 },
    credits: 100,
    icon: "🪪🎭",
    short: "Borrowed machine identity.",
    description: "Burn-once cards with limited, sandboxed credential profiles for passing routine, low-privilege checks."
  },
  {
    id: "thermal-scent-ampoule",
    techniqueId: "bio-id-mask",
    name: "Thermal Scent Ampoules",
    rarity: "epic",
    type: "consumable",
    effects: { "room-time-reduction": 10 },
    credits: 100,
    icon: "🌡️👃",
    short: "Mimic local heat + scent.",
    description: "Dual-channel projectors that shape IR apparent temperature and trace scent to blend with local background."
  },
  {
    id: "temp-log-writer-cart",
    techniqueId: "echo-loop",
    name: "Temp Log Writer Carts",
    rarity: "epic",
    type: "consumable",
    effects: { "room-time-reduction": 10 },
    credits: 100,
    icon: "📝🔁",
    short: "Plant harmless dummy logs.",
    description: "Write-only, time-limited log entries that add benign detours to audit trails without altering critical records."
  },
  {
    id: "quickset-anchor-rods",
    techniqueId: "phase-anchor",
    name: "Quickset Anchor Rods",
    rarity: "epic",
    type: "consumable",
    effects: { "room-time-reduction": 10 },
    credits: 100,
    icon: "🛠️🌀",
    short: "Pin drifting surfaces.",
    description: "Expandable rods and resin anchors that mechanically stabilize shifting walls and floors."
  },
  {
    id: "timing-pulse-tag",
    techniqueId: "depth-step",
    name: "Timing Pulse Tags",
    rarity: "epic",
    type: "consumable",
    effects: { "room-time-reduction": 10 },
    credits: 100,
    icon: "⏱️📍",
    short: "Step on stable beats.",
    description: "Clip-on transponders that detect vibration cycles and flag brief low-motion windows for safer crossings."
  },
  {
    id: "scan-cache-array-cart",
    techniqueId: "echo-fold",
    name: "Scan Cache Array Carts",
    rarity: "epic",
    type: "consumable",
    effects: { "room-time-reduction": 10 },
    credits: 100,
    icon: "🗂️📡",
    short: "Pocket + restore scans.",
    description: "Portable buffer arrays that capture recent sensor frames for quick re-loads after interference or resets."
  },
];

// ========== itemsEpicDevice.js ==========
// Epic Devices — grounded descriptions
export const itemsEpicDeviceDominion = [
  // signal-relay
  {
    id: "precision-mast-calibrator",
    techniqueId: "relay-stabilizer",
    name: "Precision Mast Calibrator",
    rarity: "epic",
    type: "techGear",
    effects: { "technique-success-bonus": 10 },
    icon: "🎛️📡",
    short: "Laser-align relay masts.",
    description: "Gyro-stabilized laser jig that zeroes dish bearings and mast tilt for a stable RF path during alignment."
  },
  {
    id: "biofiber-manifold",
    techniqueId: "organic-link-adapter",
    name: "Biofiber Manifold",
    rarity: "epic",
    type: "techGear",
    effects: { "technique-success-bonus": 10 },
    icon: "🌿🧰",
    short: "Seat living splices cleanly.",
    description: "Multi-port manifold that tensions and seats polymer fiber splices for durable link repair."
  },
  {
    id: "adaptive-noise-gate",
    techniqueId: "silent-channel",
    name: "Adaptive Noise Gate",
    rarity: "epic",
    type: "techGear",
    effects: { "technique-success-bonus": 10 },
    icon: "🔇🖧",
    short: "Auto-null scanner pickup.",
    description: "Digital filter that tracks spectrum drift and attenuates pickup around sensitive bands."
  },

  // system-override
  {
    id: "multi-handshake-router",
    techniqueId: "command-cascade",
    name: "Multi-Handshake Router",
    rarity: "epic",
    type: "techGear",
    effects: { "technique-success-bonus": 10 },
    icon: "🔁🖥️",
    short: "Chain control handshakes.",
    description: "Router core that sequences credentials and command bundles to reduce operator timing errors."
  },
  {
    id: "surge-buffer-backplane",
    techniqueId: "bio-feedback-guard",
    name: "Surge Buffer Backplane",
    rarity: "epic",
    type: "techGear",
    effects: { "technique-success-bonus": 10 },
    icon: "🧯⚡",
    short: "Bleed off reboot spikes.",
    description: "Dielectric backplane with TVS arrays that bleeds transient load to ground during resets."
  },
  {
    id: "credential-spoof-matrix",
    techniqueId: "signal-mask-injection",
    name: "Credential Spoof Matrix",
    rarity: "epic",
    type: "techGear",
    effects: { "technique-success-bonus": 10 },
    icon: "🎭🔐",
    short: "Forge believable auth.",
    description: "Hardware emulator that produces constrained, audit-safe credential responses for masking maintenance traffic."
  },

  // drone-command
  {
    id: "swarm-route-controller",
    techniqueId: "coordinated-flight-pattern",
    name: "Swarm Route Controller",
    rarity: "epic",
    type: "techGear",
    effects: { "technique-success-bonus": 10 },
    icon: "🕹️🛸",
    short: "Orchestrate flight lanes.",
    description: "Low-latency planner that de-conflicts headings and intervals across multiple airframes."
  },
  {
    id: "overgrowth-nav-suite",
    techniqueId: "rootpath-protocol",
    name: "Overgrowth Nav Suite",
    rarity: "epic",
    type: "techGear",
    effects: { "technique-success-bonus": 10 },
    icon: "🌿🧭",
    short: "Plan safe bio-channels.",
    description: "Biometric + terrain planner that fits paths along low-resistance natural channels."
  },
  {
    id: "quiet-sonar-array",
    techniqueId: "echo-pulse-beacon",
    name: "Quiet Sonar Array",
    rarity: "epic",
    type: "techGear",
    effects: { "technique-success-bonus": 10 },
    icon: "📡🔈",
    short: "Stealth map pings.",
    description: "Low-signature ultrasonic array for short-range mapping with minimal acoustic profile."
  },

  // safeguard
  {
    id: "auto-ground-bus",
    techniqueId: "system-ground-loop",
    name: "Auto Ground Bus",
    rarity: "epic",
    type: "techGear",
    effects: { "technique-success-bonus": 10 },
    icon: "🧰⚡",
    short: "Instant safe ground path.",
    description: "Self-seeking clamp bus that identifies viable earth points and bonds equipment rapidly."
  },
  {
    id: "cycler-governor",
    techniqueId: "spore-vent-reclaimer",
    name: "Cycler Governor",
    rarity: "epic",
    type: "techGear",
    effects: { "technique-success-bonus": 10 },
    icon: "🌬️⚙️",
    short: "Regulate purge cycles.",
    description: "Programmable controller that times purge and recapture phases for ventilation cycles."
  },
  {
    id: "hardline-junction-box",
    techniqueId: "manual-override-line",
    name: "Hardline Junction Box",
    rarity: "epic",
    type: "techGear",
    effects: { "technique-success-bonus": 10 },
    icon: "🧲🧵",
    short: "Secure physical cut-off.",
    description: "Lockable junction that routes a physically isolated kill line for fast de-energizing."
  },

  // quantum-scan
  {
    id: "scan-link-backbone",
    techniqueId: "entangled-synchrony",
    name: "Scan Link Backbone",
    rarity: "epic",
    type: "techGear",
    effects: { "technique-success-bonus": 10 },
    icon: "🔗🔬",
    short: "Synchronize instruments.",
    description: "Common-clock distribution and shared buffering that keep instruments time-aligned."
  },
  {
    id: "dual-spectrum-integrator",
    techniqueId: "bio-spectral-crosscheck",
    name: "Dual-Spectrum Integrator",
    rarity: "epic",
    type: "techGear",
    effects: { "technique-success-bonus": 10 },
    icon: "🧬📈",
    short: "Fuse bio + energy bands.",
    description: "Inline integrator that co-registers biological, radiological, and EM field data."
  },
  {
    id: "low-current-amplifier",
    techniqueId: "ghost-echo-capture",
    name: "Low-Current Amplifier",
    rarity: "epic",
    type: "techGear",
    effects: { "technique-success-bonus": 10 },
    icon: "📈🔌",
    short: "Boost trace line signals.",
    description: "High-impedance amplifier that raises low-level line signals above measurement noise."
  },
];

export const itemsEpicDeviceWilders = [
  // reclamation
  {
    id: "harvest-flight-controller",
    techniqueId: "reclamation-drone-link",
    name: "Harvest Flight Controller",
    rarity: "epic",
    type: "techGear",
    effects: { "technique-success-bonus": 10 },
    icon: "🛸🌱",
    short: "Co-pilot harvest flights.",
    description: "Link hub that synchronizes drone and harvester timing to reduce sample loss."
  },
  {
    id: "fungal-flow-regulator",
    techniqueId: "mycelial-rebalance",
    name: "Fungal Flow Regulator",
    rarity: "epic",
    type: "techGear",
    effects: { "technique-success-bonus": 10 },
    icon: "🍄⚙️",
    short: "Stabilize live filters.",
    description: "Pressure/flow controller that maintains efficient separation across bio-filters."
  },
  {
    id: "surface-decon-manifold",
    techniqueId: "phase-cleanse",
    name: "Surface Decon Manifold",
    rarity: "epic",
    type: "techGear",
    effects: { "technique-success-bonus": 10 },
    icon: "🧼🧩",
    short: "Evenly spread micro-mist.",
    description: "Distributor that ensures even micro-mist coverage for surface decontamination."
  },

  // growth-control
  {
    id: "lattice-lock-ribs",
    techniqueId: "adaptive-lattice",
    name: "Lattice Lock Ribs",
    rarity: "epic",
    type: "techGear",
    effects: { "technique-success-bonus": 10 },
    icon: "🦴🕸️",
    short: "Harden bio-metal frames.",
    description: "Expandable ribs that stiffen quickly cured resin struts for temporary structural support."
  },
  {
    id: "leaf-optic-panel",
    techniqueId: "photosymbiosis",
    name: "Leaf Optic Panel",
    rarity: "epic",
    type: "techGear",
    effects: { "technique-success-bonus": 10 },
    icon: "🔆🪴",
    short: "Channel light efficiently.",
    description: "Adjustable optic panel that concentrates and angles light toward growth surfaces."
  },
  {
    id: "seam-probe-kit",
    techniqueId: "shadowroot-bloom",
    name: "Seam Probe Kit",
    rarity: "epic",
    type: "techGear",
    effects: { "technique-success-bonus": 10 },
    icon: "🪛🌱",
    short: "Find the right crack lines.",
    description: "Micro-sensors that locate moisture-bearing cracks suitable for guided root growth."
  },

  // wild-influence
  {
    id: "rhythm-guide-hub",
    techniqueId: "signal-harmonizer",
    name: "Rhythm Guide Hub",
    rarity: "epic",
    type: "techGear",
    effects: { "technique-success-bonus": 10 },
    icon: "🎚️🫀",
    short: "Broadcast calm cadence.",
    description: "Biofeedback hub broadcasting calm cadence cues via low-latency haptics."
  },
  {
    id: "multi-diffuser-crown",
    techniqueId: "pheromone-chorus",
    name: "Multi-Diffuser Crown",
    rarity: "epic",
    type: "techGear",
    effects: { "technique-success-bonus": 10 },
    icon: "👑🪼",
    short: "Layer handler signals.",
    description: "Head-mounted array that blends multiple low-dose scent channels for wider coverage."
  },
  {
    id: "contact-silence-kit",
    techniqueId: "silent-hand",
    name: "Contact Silence Kit",
    rarity: "epic",
    type: "techGear",
    effects: { "technique-success-bonus": 10 },
    icon: "🤫🧰",
    short: "Mute close-range tells.",
    description: "Shims and absorbents that eliminate contact squeaks/snaps on gear and panels."
  },

  // bio-shield
  {
    id: "layer-activator-pad",
    techniqueId: "bio-shielding",
    name: "Layer Activator Pad",
    rarity: "epic",
    type: "techGear",
    effects: { "technique-success-bonus": 10 },
    icon: "🧫🛡️",
    short: "Prime the spread layer.",
    description: "Induction pad that warms and activates shear-thickening gel layers prior to use."
  },
  {
    id: "enzyme-hub-manifold",
    techniqueId: "shared-immunity",
    name: "Enzyme Hub Manifold",
    rarity: "epic",
    type: "techGear",
    effects: { "technique-success-bonus": 10 },
    icon: "🧬🧪",
    short: "Distribute resistance packs.",
    description: "Metering hub that distributes standardized decon/barrier packets among teammates."
  },
  {
    id: "surface-tuner-core",
    techniqueId: "dim-shift-layer",
    name: "Surface Tuner Core",
    rarity: "epic",
    type: "techGear",
    effects: { "technique-success-bonus": 10 },
    icon: "🎛️🪞",
    short: "Auto-match tone/sheens.",
    description: "Optical feedback controller that adjusts a microflake layer’s reflectivity in real time."
  },

  // containment
  {
    id: "hot-sample-frame",
    techniqueId: "servo-containment",
    name: "Hot Sample Frame",
    rarity: "epic",
    type: "techGear",
    effects: { "technique-success-bonus": 10 },
    icon: "🔥📦",
    short: "Carry volatile finds safely.",
    description: "Reconfigurable frame set that converts drone housings into sealed transport cases."
  },
  {
    id: "pod-growth-hub",
    techniqueId: "mutualist-growth",
    name: "Pod Growth Hub",
    rarity: "epic",
    type: "techGear",
    effects: { "technique-success-bonus": 10 },
    icon: "🌰⚙️",
    short: "Drive living pod formation.",
    description: "Controller that drives expansion and curing of shock-absorbing biopolymer pods."
  },
  {
    id: "recovery-pod-manifold",
    techniqueId: "reflex-bloom",
    name: "Recovery Pod Manifold",
    rarity: "epic",
    type: "techGear",
    effects: { "technique-success-bonus": 10 },
    icon: "🌸⚙️",
    short: "Time restorative releases.",
    description: "Pulse manifold that staggers timed release of standard restorative compounds."
  },
];

export const itemsEpicDeviceVeil = [
  // echo-trace
  {
    id: "relay-pairing-bridge",
    techniqueId: "quantum-relay-hook",
    name: "Relay Pairing Bridge",
    rarity: "epic",
    type: "techGear",
    effects: { "technique-success-bonus": 10 },
    icon: "🪝📡",
    short: "Bind maps to relays.",
    description: "Bridge board that associates scanner output with relay control frames for piggybacking."
  },
  {
    id: "bio-pulse-coupler",
    techniqueId: "biotrace-resonance",
    name: "Bio-Pulse Coupler",
    rarity: "epic",
    type: "techGear",
    effects: { "technique-success-bonus": 10 },
    icon: "🌱🔗",
    short: "Sharpen organic echoes.",
    description: "Coupler optimized for organic reflectance bands to clarify weak returns."
  },
  {
    id: "map-sync-hub",
    techniqueId: "shadow-archive",
    name: "Map Sync Hub",
    rarity: "epic",
    type: "techGear",
    effects: { "technique-success-bonus": 10 },
    icon: "🗂️🔌",
    short: "Hands-free cache sync.",
    description: "Passive NFC/optical hub that auto-syncs portable map caches within close range."
  },

  // lock-breach
  {
    id: "micro-torque-rail",
    techniqueId: "servo-alignment-tool",
    name: "Micro-Torque Rail",
    rarity: "epic",
    type: "techGear",
    effects: { "technique-success-bonus": 10 },
    icon: "🧰🧭",
    short: "Guide picks to seat line.",
    description: "Instrument rail with torque sensors that guides precise pick seating and servo alignment."
  },
  {
    id: "quiet-seal-rail",
    techniqueId: "biofilm-stabilizer",
    name: "Quiet Seal Rail",
    rarity: "epic",
    type: "techGear",
    effects: { "technique-success-bonus": 10 },
    icon: "🤫🧵",
    short: "Lay silent panel seams.",
    description: "Applicator rail for even deposition of low-noise seam sealant."
  },
  {
    id: "decoy-phase-driver",
    techniqueId: "echo-pulse-decoy",
    name: "Decoy Phase Driver",
    rarity: "epic",
    type: "techGear",
    effects: { "technique-success-bonus": 10 },
    icon: "🎯📡",
    short: "Throw believable pings.",
    description: "Programmable driver that emits harmless distractor pings away from the work zone."
  },

  // stealth-transit
  {
    id: "route-shadow-hub",
    techniqueId: "drone-path-shadow",
    name: "Route Shadow Hub",
    rarity: "epic",
    type: "techGear",
    effects: { "technique-success-bonus": 10 },
    icon: "🛣️👤",
    short: "Merge with drone lanes.",
    description: "Planner hub that snaps team pathing to known drone traces to reduce distinct movement signatures."
  },
  {
    id: "low-shine-housing",
    techniqueId: "spore-veil",
    name: "Low-Shine Housing",
    rarity: "epic",
    type: "techGear",
    effects: { "technique-success-bonus": 10 },
    icon: "🕶️🧩",
    short: "Cut edges and glare.",
    description: "Matte micro-texture housing that binds dulling coats and cuts specular highlights."
  },
  {
    id: "pace-sync-hub",
    techniqueId: "mirror-walk",
    name: "Pace Sync Hub",
    rarity: "epic",
    type: "techGear",
    effects: { "technique-success-bonus": 10 },
    icon: "⏱️🚶",
    short: "Keep formation cadence.",
    description: "Haptic metronome router that keeps formation cadence uniform under movement."
  },

  // signal-mask
  {
    id: "auth-emulator-core",
    techniqueId: "control-spoof",
    name: "Auth Emulator Core",
    rarity: "epic",
    type: "techGear",
    effects: { "technique-success-bonus": 10 },
    icon: "🪪🧠",
    short: "Clone basic credentials.",
    description: "Constrained emulator that serves limited, low-privilege machine IDs for routine checkpoints."
  },
  {
    id: "thermal-scent-hub",
    techniqueId: "bio-id-mask",
    name: "Thermal Scent Hub",
    rarity: "epic",
    type: "techGear",
    effects: { "technique-success-bonus": 10 },
    icon: "🌡️🕵️",
    short: "Blend heat + scent masks.",
    description: "Dual-channel projector that blends IR apparent temperature and trace scent output."
  },
  {
    id: "log-sanitizer-kernel",
    techniqueId: "echo-loop",
    name: "Log Sanitizer Kernel",
    rarity: "epic",
    type: "techGear",
    effects: { "technique-success-bonus": 10 },
    icon: "🧼📝",
    short: "Write harmless trails.",
    description: "Kernel that writes time-limited, non-critical log noise to support benign alternate paths."
  },

  // transit-anchor
  {
    id: "anchor-brace-grid",
    techniqueId: "phase-anchor",
    name: "Anchor Brace Grid",
    rarity: "epic",
    type: "techGear",
    effects: { "technique-success-bonus": 10 },
    icon: "🧱🌀",
    short: "Stiffen local geometry.",
    description: "Rigid grid mounts that couple to braces and anchors to stabilize shifting surfaces."
  },
  {
    id: "timing-window-hub",
    techniqueId: "depth-step",
    name: "Timing Window Hub",
    rarity: "epic",
    type: "techGear",
    effects: { "technique-success-bonus": 10 },
    icon: "🪟⏱️",
    short: "Spot safe crossing beats.",
    description: "Predictive timing hub that detects low-motion windows using vibration sensors."
  },
  {
    id: "scan-cache-backplane",
    techniqueId: "echo-fold",
    name: "Scan Cache Backplane",
    rarity: "epic",
    type: "techGear",
    effects: { "technique-success-bonus": 10 },
    icon: "🗄️📡",
    short: "Retain sensor context.",
    description: "Redundant buffer backplane that preserves recent scan frames for rapid re-load."
  },
];

// Slot rules unchanged; included here if this file owns them
export const slotRules = {
  permanentSlotsPerCharacter: 5,
  permanentSlotBinding: "1 per skill (affects one chosen technique for that skill via loadout toggle)",
  consumableCarryLimit: 6,
  consumablePerTechniqueCapPerRoom: 1,
  consumableStackCapPerTechnique: 2,
  consumableTimeBonusCap: -0.4,
};

// ========== itemsRare.js ==========
// ===============================
// Rare Materials
// ===============================
export const itemsRare = [
  {
    id: "encrypted-transceiver-chip",
    name: "Encrypted Transceiver Chip",
    type: "material",
    rarity: "rare",
    faction: "dominion",
    icon: "📶",
    description: "High-security chip enabling encrypted signal transmission and remote control.",
    short: "Encrypted chip for drone and comm systems.",
  },
  {
    id: "radiation-sensor-module",
    name: "Radiation Sensor Module",
    type: "material",
    rarity: "rare",
    faction: "dominion",
    icon: "☢️",
    description: "Advanced sensor module for detecting radiation and electromagnetic anomalies.",
    short: "Detection module for radiation scanners.",
  },
  {
    id: "bio-conductive-mesh",
    name: "Bio-Conductive Mesh",
    type: "material",
    rarity: "rare",
    faction: "wilders",
    icon: "🧬",
    description: "Flexible organic mesh that transmits low electrical signals through tissue or plant matter.",
    short: "Organic mesh used in biotech interfaces.",
  },
  {
    id: "synthetic-pheromone-capsule",
    name: "Synthetic Pheromone Capsule",
    type: "material",
    rarity: "rare",
    faction: "wilders",
    icon: "🦋",
    description: "Pressurized capsule releasing synthetic pheromones for wildlife modulation.",
    short: "Behavioral control capsule for wildlife devices.",
  },
  {
    id: "em-interference-chip",
    name: "EM Interference Chip",
    type: "material",
    rarity: "rare",
    faction: "veil",
    icon: "💾",
    description: "Microchip designed to emit controlled electromagnetic interference for masking signals.",
    short: "Chip used to disrupt tracking and detection.",
  },
  {
    id: "harmonic-stabilizer-core",
    name: "Harmonic Stabilizer Core",
    type: "material",
    rarity: "rare",
    faction: "veil",
    icon: "🌀",
    description: "Gyroscopic core used to counterbalance unstable structures and terrain.",
    short: "Stabilization core for terrain support units.",
  },
  {
    id: "signal-sync-crystal",
    name: "Signal Sync Crystal",
    type: "material",
    rarity: "rare",
    faction: "dominion",
    icon: "🔀",
    description: "Calibrated crystal matrix that synchronizes multi-device signal frequencies.",
    short: "Signal alignment crystal for coordination amplifiers.",
  },
  {
    id: "data-storage-crystal",
    name: "Data Storage Crystal",
    type: "material",
    rarity: "rare",
    faction: "veil",
    icon: "🔮",
    description: "Miniature crystal lattice capable of storing large volumes of encoded data.",
    short: "Data storage crystal for recovered memories.",
  },
  {
    id: "precision-chrono-core",
    name: "Precision Chrono Core",
    type: "material",
    rarity: "rare",
    faction: "veil",
    icon: "⏱️",
    description: "High-grade mechanical core used in advanced timekeeping and synchronization devices.",
    short: "Core for timing, synchronization, and cooldown systems.",
  },
  {
    id: "high-torque-motor-core",
    name: "High-Torque Motor Core",
    type: "material",
    rarity: "rare",
    faction: "wilders",
    icon: "🔩",
    description: "Industrial motor assembly capable of driving heavy extraction tools.",
    short: "Motor core for drills and extraction equipment.",
  },
  {
    id: "containment-gel-lining",
    name: "Containment Gel Lining",
    type: "material",
    rarity: "rare",
    faction: "wilders",
    icon: "🧫",
    description: "Reactive gel layer used to stabilize and store hazardous or bioactive materials safely.",
    short: "Protective lining for biological containment equipment.",
  },
  {
    id: "voltage-regulator-unit",
    name: "Voltage Regulator Unit",
    type: "material",
    rarity: "rare",
    faction: "dominion",
    icon: "⚙️",
    description: "Industrial-grade component for maintaining current stability across damaged grids.",
    short: "Voltage control hardware for electrical systems.",
  },
];

// ========== itemsRarities.js ==========
// Item rarity tiers and their drop chance configurations
//
// Each rarity tier has:
// - dropChance: Drop rate configuration with optional per-item weights
//   - Rarity-level rates (base, perTier, max, minTier, gate/hazard): the chance this
//     rarity drops at all (per event roll / cascade step)
//   - perItem: relative selection WEIGHTS within this rarity's item pool (default 1.0).
//     When a drop of this rarity occurs, items are picked proportionally to weight -
//     weights shape the mix of items, not the total quantity dropped.
//     Example: { 'composite-casing': 0.06, 'resin-casing': 0.75 } -> resin drops ~12x as often
export const itemRarities = {
  common: {
    name: "Common",
    order: 1,
    color: "#9ca3af",
    craftingCost: 5,
    dropChance: {
      // Per-event chance, DECREASING with tier: early crafting needs healthy common
      // inflow, but at T3 raid volume commons otherwise swamp the inventory.
      // Events are the ONLY common source (the bonus event awards intel credits).
      // T1: 70%, T2: 55%, T3: 40% per event.
      base: 0.7,
      perTier: -0.15,
      max: 0.75,
      // Relative selection weights fitted by tools/loot-equilibrium.mjs --fit
      // (mix-only iterative income-vs-demand fit across factions and regions,
      // mean-1.0 normalized). Regenerate when recipes/events change.
      perItem: {
        // Fitted by tools/loot-equilibrium.mjs --fit against the ownership-
        // clustered recipes (materialOwnership.js): weights reconcile each
        // item's drop income with its recipe demand inside the now-focused
        // pools. Regenerate when recipes/events change.
        "composite-casing": 10, // fit suggested 11.55; clamped to the balanceParity sanity cap
        "aluminum-shell": 4.82,
        "logic-ribbon-cable": 2.58,
        "circuit-board": 1.55,
        "ceramic-insulator": 1.45,
        "copper-wire": 1.43,
        "relay-antenna-strut": 1.41,
        "power-cell-casing": 1.22,
        "etched-keyplate-blank": 0.15,
        "micro-bearing-set": 0.13,
        "canvas-fiber": 0.12,
        "tanned-gut-cord": 0.12,
        "resin-casing": 0.12,
        "spore-felt-padding": 0.12,
        "plastic-casing": 0.09,
        "glass-lens": 0.09,
        "brass-fittings": 0.09,
        "iron-frame": 0.09,
        "steel-plates": 0.09,
        "fabric-mesh": 0.09,
        "polymer-tubing": 0.09,
        "carbon-housing": 0.09,
        "rubber-seal": 0.09,
        "leather-straps": 0.09,
        "chitin-plate": 0.09,
        "matte-coating-compound": 0.09,
        "acoustic-foam-wafer": 0.09,
        "optic-fiber-spool": 0.09,
      },
    },
    quantity: {
      // Sized against the sticky-region player model: 1-2 per drop paces gear
      // completion toward the 30-day target and keeps inventory piles lean
      gate: { base: 1, random: 1 }, // 1-2
      hazard: { base: 1, random: 1 }, // 1-2
    },
  },
  uncommon: {
    name: "Uncommon",
    order: 2,
    // 6-digit, like every other rarity. This carried a redundant `ff` alpha —
    // no rendering difference, but it is the one swatch that could not be fed
    // to anything expecting #rrggbb, and the old value-pinned test only ever
    // looked at `rare` so nothing noticed.
    color: "#3bf683",
    craftingCost: 15,
    dropChance: {
      // Per-event chance; gates compound over 2-4 events so keep this low
      // (0.30 -> ~66% per gate room, ~41% per hazard room at tier 1)
      base: 0.3,
      perTier: 0.03,
      max: 0.6,
      // Fitted by tools/loot-equilibrium.mjs --fit: uncommon intermediates now
      // drop from the skill chains (full recipe components), so shape the mix
      // to demand like the other tiers. Regenerate when recipes change.
      perItem: {
        "sealed-hydraulic-pump": 10, // fit suggested 15.78; clamped to the balanceParity sanity cap
        "medical-stimulant-cartridge": 0.22,
        "precision-servo-gear": 0.12,
        "signal-processor-core": 0.12,
        "override-datakey": 0.12,
        "power-regulation-unit": 0.12,
        "drone-gyro-assembly": 0.12,
        "ultrasonic-sensor-grid": 0.12,
        "thermal-polarizer-film": 0.12,
        "nutrient-catalyst-ampoule": 0.12,
        "symbiotic-culture-vial": 0.12,
        "pheromone-distiller-coil": 0.12,
        "nutrient-gel-pack": 0.12,
        "adaptive-filter-membrane": 0.12,
        "phase-lens-array": 0.12,
        "micro-servo-drill": 0.12,
        "vibration-dampener": 0.12,
        "ghost-signal-modulator": 0.12,
      },
    },
    quantity: {
      gate: { base: 2, random: 1 }, // 2-3
      hazard: { base: 1, random: 1 }, // 1-2
    },
  },
  rare: {
    name: "Rare",
    order: 3,
    // blue-400, not blue-500: the -500 stop failed WCAG AA on every background
    // it is used against (4.10 on the raid log, 3.52 on a room-card header,
    // 4.33 on the game body). Same hue, one stop lighter, 5.09-6.26 everywhere.
    color: "#60a5fa",
    craftingCost: 70,
    dropChance: {
      // Per-event chance; also drives the bonus-cascade rare step
      // (~55% per gate room, ~33% per hazard room at tier 1, all paths combined)
      gate: { base: 0.18, perTier: 0.04, max: 0.5 },
      hazard: { base: 0.15, perTier: 0.04, max: 0.4 },
      perItem: {
        // Fitted by tools/loot-equilibrium.mjs --fit against the ownership-
        // clustered recipes. Regenerate when recipes change.
        "radiation-sensor-module": 8.0,
        "signal-sync-crystal": 2.41,
        "data-storage-crystal": 0.17,
        "precision-chrono-core": 0.16,
        "high-torque-motor-core": 0.16,
        "encrypted-transceiver-chip": 0.16,
        "voltage-regulator-unit": 0.16,
        "bio-conductive-mesh": 0.16,
        "synthetic-pheromone-capsule": 0.16,
        "containment-gel-lining": 0.16,
        "em-interference-chip": 0.16,
        "harmonic-stabilizer-core": 0.16,
      },
    },
    quantity: {
      gate: { base: 2, perTier: 1, max: 4 }, // 2-3 for tier 1, up to 4 for tier 4+
      hazard: { base: 1, random: 2 }, // 1-2
    },
  },
  epic: {
    name: "Epic",
    order: 4,
    // purple-400, not purple-500 — same reason as rare above. The -500 stop was
    // the worst rarity on the palette (3.81 / 3.27 / 4.02); this reads 4.89-6.02
    // and still sits clearly between rare's blue and legendary's gold.
    color: "#c084fc",
    craftingCost: 300,
    dropChance: {
      gate: { base: 0.2, perTier: 0.05, max: 0.4 },
      hazard: { base: 0.15, perTier: 0.05, max: 0.3 },
      perItem: {}, // Per-item drop rates override gate/hazard rates
    },
    quantity: 1,
  },
  legendary: {
    name: "Legendary",
    order: 5,
    color: "#eab308",
    craftingCost: 750,
    dropChance: {
      base: 0.000001,
      perTier: 0.01,
      max: 0.05,
      // Utility-artifact blueprints have no skill tie, so they appear in
      // EVERY room's legendary pool and would soak most rolls. Half weight
      // keeps them obtainable anywhere while skill-tied blueprints (weight
      // 1.0, present only in rooms using their skill) win more of the rolls
      // in the rooms players target for them.
      perItem: {
        "data-cache-drive-blueprint": 0.5,
        "relic-resonance-tuner-blueprint": 0.5,
        "hazard-lens-visor-blueprint": 0.5,
        "recovery-injector-blueprint": 0.5,
        "contract-beacon-blueprint": 0.5,
        "salvage-compression-rig-blueprint": 0.5,
        "sync-regulator-watch-blueprint": 0.5,
        "auto-stabilizer-pack-blueprint": 0.5,
        "training-node-deck-blueprint": 0.5,
        "crosslink-amplifier-blueprint": 0.5,
        "material-extractor-blueprint": 0.5,
        "memory-crystal-shard-blueprint": 0.5,
      },
    },
    quantity: 1, // Blueprints always = 1
  },
  unique: {
    name: "Unique",
    order: 6,
    color: "#f97316",
    craftingCost: 1500,
    dropChance: {
      base: 0.015,
      minTier: 4,
      // Selection WEIGHTS within the pool, like every other rarity here — NOT
      // per-item rates, and they do not override `minTier`. The old comment
      // claimed otherwise and was wrong: `getRarityDropChance` reads minTier
      // and never looks at perItem, so there is no per-item escape from the
      // tier gate. Worth knowing before L4's T0 — reaching story relics below
      // Tier 4 means changing minTier or the pool, not adding entries here.
      perItem: {},
    },
    quantity: 1, // Story relics always = 1
  },
};


// ========== itemsStoryRelics.js ==========
export const itemsStoryRelics = [
  // === Story Relics ===
  {
    id: "collapse-key-fragment",
    name: "Collapse Key Fragment",
    type: "storyRelic",
    rarity: "unique",
    icon: "🗝️",
    description: "Encrypted shard containing partial records of the Collapse.",
    short: "Encrypted shard with Collapse records.",
  },
  {
    id: "echo-memory-shard",
    name: "Echo Memory Shard",
    type: "storyRelic",
    rarity: "unique",
    icon: "💎",
    description: "Crystallized imprint of pre-collapse memories and signals.",
    short: "Crystallized pre-collapse memories.",
  },
  {
    id: "reality-breach-record",
    name: "Reality Breach Record",
    type: "storyRelic",
    rarity: "unique",
    icon: "📜",
    description: "Primary-source documentation of the earliest breach events.",
    short: "Records of the first breaches.",
  },
  {
    id: "dimensional-artifact",
    name: "Dimensional Artifact",
    type: "storyRelic",
    rarity: "unique",
    icon: "🌀",
    description: "Object exhibiting multi-state presence across unstable fields.",
    short: "Object with multi-state presence.",
  },
  {
    id: "temporal-echo-crystal",
    name: "Temporal Echo Crystal",
    type: "storyRelic",
    rarity: "unique",
    icon: "⏳",
    description: "Crystal with layered timestamps—echoes from divergent timelines.",
    short: "Crystal echoing divergent timelines.",
  },
];


// ========== itemsUncommon.js ==========
// ===============================
// Uncommon Materials
// ===============================
export const itemsUncommon = [
  {
    id: "signal-processor-core",
    name: "Signal Processor Core",
    type: "material",
    rarity: "uncommon",
    faction: "dominion",
    icon: "💽",
    description: "Miniature processor unit that boosts data throughput in repeater systems.",
    short: "Processing chip for advanced comm systems.",
  },
  {
    id: "precision-servo-gear",
    name: "Precision Servo Gear",
    type: "material",
    rarity: "uncommon",
    faction: "dominion",
    icon: "⚙️",
    description: "Fine-tuned metal servo component used in precision motor assemblies.",
    short: "Precision gear for mechanical control units.",
  },
  {
    id: "nutrient-catalyst-ampoule",
    name: "Nutrient Catalyst Ampoule",
    type: "material",
    rarity: "uncommon",
    faction: "wilders",
    icon: "💧",
    description: "Sealed glass vial of nutrient mist used to accelerate biological growth.",
    short: "Catalyst fluid for rapid plant growth.",
  },
  {
    id: "adaptive-filter-membrane",
    name: "Adaptive Filter Membrane",
    type: "material",
    rarity: "uncommon",
    faction: "wilders",
    icon: "🌫️",
    description: "Reactive fiber mesh that filters toxins and airborne pathogens.",
    short: "Filter sheet for respirators and purifiers.",
  },
  {
    id: "ultrasonic-sensor-grid",
    name: "Ultrasonic Sensor Grid",
    type: "material",
    rarity: "uncommon",
    faction: "veil",
    icon: "📡",
    description: "Array of micro-emitters tuned for acoustic mapping and detection.",
    short: "Acoustic sensor assembly for scanners.",
  },
  {
    id: "micro-servo-drill",
    name: "Micro-Servo Drill",
    type: "material",
    rarity: "uncommon",
    faction: "veil",
    icon: "🪛",
    description: "High-speed micro drill used in lockpicking and mechanical breach kits.",
    short: "Precision drill component for breach tools.",
  },
  {
    id: "vibration-dampener",
    name: "Vibration Dampener",
    type: "material",
    rarity: "uncommon",
    faction: "veil",
    icon: "🔇",
    description: "Rubberized composite that absorbs vibration and reduces sound transmission.",
    short: "Noise-canceling gel used in stealth equipment.",
  },
  {
    id: "thermal-polarizer-film",
    name: "Thermal Polarizer Film",
    type: "material",
    rarity: "uncommon",
    faction: "veil",
    icon: "🪞",
    description: "Transparent sheet that filters heat and glare for protective visors.",
    short: "Heat-filtering film for visors and lenses.",
  },
  {
    id: "medical-stimulant-cartridge",
    name: "Medical Stimulant Cartridge",
    type: "material",
    rarity: "uncommon",
    faction: "wilders",
    icon: "💉",
    description: "Preloaded cartridge containing controlled restorative compounds.",
    short: "Pharma cartridge for emergency injectors.",
  },
  {
    id: "power-regulation-unit",
    name: "Power Regulation Unit",
    type: "material",
    rarity: "uncommon",
    faction: "dominion",
    icon: "⚡",
    description: "Compact control block used to distribute and stabilize power flow in complex systems.",
    short: "Stabilizer unit for energy systems and machinery.",
  },
  {
    id: "sealed-hydraulic-pump",
    name: "Sealed Hydraulic Pump",
    type: "material",
    rarity: "uncommon",
    faction: "dominion",
    icon: "🛠️",
    description: "Compact hydraulic pump designed for portable mechanical devices.",
    short: "Fluid pressure unit for small machines and stabilizers.",
  },
  {
    id: "nutrient-gel-pack",
    name: "Nutrient Gel Pack",
    type: "material",
    rarity: "uncommon",
    faction: "wilders",
    icon: "🥼",
    description: "Sealed packet of regenerative gel used to sustain and accelerate plant regrowth.",
    short: "Growth medium for biological repair devices.",
  },
  {
    id: "override-datakey",
    name: "Override Datakey",
    type: "material",
    rarity: "uncommon",
    faction: "dominion",
    icon: "🔑",
    description: "Pre-authorized command key that grants temporary root access to compliant Dominion systems.",
    short: "Root-access key for system takeovers.",
  },
  {
    id: "drone-gyro-assembly",
    name: "Drone Gyro Assembly",
    type: "material",
    rarity: "uncommon",
    faction: "dominion",
    icon: "🛸",
    description: "Self-leveling gyroscope block recovered from downed drones, the heart of any stable airframe.",
    short: "Stabilizing gyro for drone frames.",
  },
  {
    id: "symbiotic-culture-vial",
    name: "Symbiotic Culture Vial",
    type: "material",
    rarity: "uncommon",
    faction: "wilders",
    icon: "🧪",
    description: "Living culture of paired organisms that accelerate each other's growth when introduced to a host.",
    short: "Living culture for growth acceleration.",
  },
  {
    id: "pheromone-distiller-coil",
    name: "Pheromone Distiller Coil",
    type: "material",
    rarity: "uncommon",
    faction: "wilders",
    icon: "🐝",
    description: "Condensing coil that concentrates raw gland extracts into potent behavioral signals.",
    short: "Distiller coil for pheromone extraction.",
  },
  {
    id: "phase-lens-array",
    name: "Phase Lens Array",
    type: "material",
    rarity: "uncommon",
    faction: "veil",
    icon: "🔭",
    description: "Stacked lens set that splits and rejoins light out of phase, bending sightlines around its bearer.",
    short: "Phase optics for displacement gear.",
  },
  {
    id: "ghost-signal-modulator",
    name: "Ghost Signal Modulator",
    type: "material",
    rarity: "uncommon",
    faction: "veil",
    icon: "👻",
    description: "Modulator that wraps real transmissions inside fabricated noise, leaving trackers chasing echoes.",
    short: "Signal wrapper that fakes transmission sources.",
  },
];


// ========== itemsVendorRelics.js ==========
export const itemsVendorRelicsCommon = [
  {
    id: "photo-charm",
    name: "Photo Charm Pendant",
    rarity: "common",
    type: "relic",
    icon: "📷",
    description: "Transparent polymer locket containing a color photo of a pet. Image still bright.",
    short: "Polymer locket with pet photo.",
    variantTags: ["pendant", "jewelry", "photo", "polymer"],
    credits: 100,
  },
  {
    id: "festival-wristband",
    name: "Festival Wristband",
    rarity: "common",
    type: "relic",
    icon: "🎟️",
    description: "Printed polymer band from a summer event. Microtext lists the performer lineup.",
    short: "Summer event polymer wristband.",
    variantTags: ["wristband", "festival", "plastic", "print"],
    credits: 95,
  },
  {
    id: "souvenir-mug",
    name: "Souvenir Mug",
    rarity: "common",
    type: "relic",
    icon: "☕",
    description: "Ceramic cup labeled 'First Orbit Park.' Glaze intact despite chipping.",
    short: "Ceramic mug from orbit park.",
    variantTags: ["mug", "ceramic", "souvenir", "cup"],
    credits: 100,
  },
  {
    id: "keyring-tag",
    name: "Keyring Tag",
    rarity: "common",
    type: "relic",
    icon: "🔑",
    description: "Stamped aluminum key-tag from a transit depot locker bank.",
    short: "Stamped locker key tag.",
    variantTags: ["key", "tag", "metal", "transit"],
    credits: 90,
  },
  {
    id: "school-badge",
    name: "School Badge",
    rarity: "common",
    type: "relic",
    icon: "📛",
    description: "Enamel pin depicting a school crest with a stylized orbit and laurel.",
    short: "Enamel pin with school crest.",
    variantTags: ["pin", "enamel", "badge", "school"],
    credits: 105,
  },
  {
    id: "magazine-cover",
    name: "Magazine Cover (Laminate)",
    rarity: "common",
    type: "relic",
    icon: "📰",
    description: "Fashion issue showing bright colors and optimistic slogans about 'The Age of Harmony.'",
    short: "Laminated fashion magazine cover.",
    variantTags: ["magazine", "print", "fashion", "poster"],
    credits: 98,
  },
  {
    id: "transit-map",
    name: "Transit Map Foldout",
    rarity: "common",
    type: "relic",
    icon: "🗺️",
    description: "Folding map for a multi-line rail network. Still legible with UV markings.",
    short: "Fold-out map of rail lines.",
    variantTags: ["map", "paper", "transit", "foldout"],
    credits: 105,
  },
  {
    id: "childrens-figurine",
    name: "Children's Figurine",
    rarity: "common",
    type: "relic",
    icon: "🧸",
    description: "Small injection-molded hero figure with a chipped cape and faded paint.",
    short: "Small molded hero figure.",
    variantTags: ["toy", "figurine", "plastic", "child"],
    credits: 100,
  },
  {
    id: "cafe-token",
    name: "Café Token",
    rarity: "common",
    type: "relic",
    icon: "🪙",
    description: "Stamped brass token for a downtown café's loyalty promotion.",
    short: "Stamped brass café token.",
    variantTags: ["token", "brass", "cafe", "coin"],
    credits: 110,
  },
];
export const itemsVendorRelicsUncommon = [
  {
    id: "signed-novel",
    name: "Signed Novel",
    rarity: "uncommon",
    type: "relic",
    icon: "📚",
    description: "Printed softcover novel with a personal autograph on the title page.",
    short: "Autographed softcover novel.",
    variantTags: ["book", "signed", "print", "literature"],
    credits: 150,
  },
  {
    id: "photo-album",
    name: "Family Photo Album",
    rarity: "uncommon",
    type: "relic",
    icon: "📔",
    description: "Cloth-bound album with adhesive pages; family celebrations and travel scenes.",
    short: "Cloth-bound family album.",
    variantTags: ["photos", "album", "family", "paper"],
    credits: 140,
  },
  {
    id: "gallery-poster",
    name: "Gallery Poster",
    rarity: "uncommon",
    type: "relic",
    icon: "🖼️",
    description: "Matte poster for a local art gallery opening. Colors remain stable.",
    short: "Matte poster for gallery opening.",
    variantTags: ["poster", "art", "gallery", "print"],
    credits: 160,
  },
  {
    id: "award-plaque",
    name: "Award Plaque",
    rarity: "uncommon",
    type: "relic",
    icon: "🏅",
    description: "Laser-engraved acrylic plaque recognizing a community service contribution.",
    short: "Laser-engraved award plaque.",
    variantTags: ["plaque", "award", "engraved", "acrylic"],
    credits: 145,
  },
  {
    id: "studio-sketchbook",
    name: "Studio Sketchbook",
    rarity: "uncommon",
    type: "relic",
    icon: "📓",
    description: "Wire-bound sketchbook filled with charcoal studies and ink experiments.",
    short: "Wire-bound artist sketchbook.",
    variantTags: ["sketch", "charcoal", "ink", "paper"],
    credits: 155,
  },
  {
    id: "recipe-tablet",
    name: "Recipe Tablet",
    rarity: "uncommon",
    type: "relic",
    icon: "📱",
    description: "Lightweight e-ink tablet preloaded with family recipes and grocery lists.",
    short: "E-ink tablet with recipes.",
    variantTags: ["tablet", "e-ink", "recipe", "notes"],
    credits: 175,
  },
  {
    id: "wedding-invitation",
    name: "Wedding Invitation",
    rarity: "uncommon",
    type: "relic",
    icon: "💌",
    description: "Foil-stamped cardstock with embossed names and an ornate border.",
    short: "Foil-stamped invitation card.",
    variantTags: ["invitation", "wedding", "card", "foil"],
    credits: 135,
  },
  {
    id: "journal-pen",
    name: "Journal & Pen",
    rarity: "uncommon",
    type: "relic",
    icon: "🖊️",
    description: "Leatherette notebook paired with a smooth fine-point pen.",
    short: "Notebook with fine-point pen.",
    variantTags: ["journal", "pen", "stationery", "paper"],
    credits: 165,
  },
  {
    id: "vacation-souvenir",
    name: "Vacation Souvenir",
    rarity: "uncommon",
    type: "relic",
    icon: "❄️",
    description: "Miniature snow globe of a coastal skyline, filled with preserved glitter flakes.",
    short: "Coastal skyline snow globe.",
    variantTags: ["souvenir", "snowglobe", "travel", "miniature"],
    credits: 170,
  },
];
export const itemsVendorRelicsRare = [
  {
    id: "archive-disk",
    name: "Archive Disk",
    rarity: "rare",
    type: "relic",
    icon: "💽",
    description: "Data crystal containing preserved recordings fro...ly public broadcasts and performances. Still partially readable.",
    short: "Data crystal with old programs.",
    variantTags: ["archive", "data", "recording", "media"],
    credits: 350,
  },
  {
    id: "unity-ribbon",
    name: "Unity Ribbon",
    rarity: "rare",
    type: "relic",
    icon: "🎗️",
    description: "Ribbon worn during a city-wide unification ceremony; dyes remain vibrant.",
    short: "Ceremonial unity ribbon.",
    variantTags: ["ribbon", "ceremony", "textile", "symbol"],
    credits: 320,
  },
  {
    id: "art-glass-vase",
    name: "Art Glass Vase",
    rarity: "rare",
    type: "relic",
    icon: "🏺",
    description: "Hand-blown glass with layered color bands and a maker's stamp near the base.",
    short: "Hand-blown art glass vase.",
    variantTags: ["glass", "vase", "art", "handmade"],
    credits: 375,
  },
  {
    id: "architectural-model",
    name: "Architectural Model",
    rarity: "rare",
    type: "relic",
    icon: "🏗️",
    description: "Scale model of a civic building in foam and resin with etched window detail.",
    short: "Scale model of civic building.",
    variantTags: ["model", "architecture", "scale", "civic"],
    credits: 340,
  },
  {
    id: "performance-mask",
    name: "Performance Mask",
    rarity: "rare",
    type: "relic",
    icon: "🎭",
    description: "Stage mask crafted from lacquered wood; interior padding still intact.",
    short: "Lacquered theatrical mask.",
    variantTags: ["mask", "theatre", "stage", "wood"],
    credits: 330,
  },
  {
    id: "sports-trophy",
    name: "Sports Trophy",
    rarity: "rare",
    type: "relic",
    icon: "🏆",
    description: "Metal cup on a wooden base with a dedication plate for a city championship.",
    short: "Metal cup on wood base.",
    variantTags: ["trophy", "sports", "metal", "award"],
    credits: 360,
  },
  {
    id: "broadcast-award",
    name: "Broadcast Award",
    rarity: "rare",
    type: "relic",
    icon: "📺",
    description: "Regional accolade for excellence in news programming; engraved date partially worn.",
    short: "Regional news award.",
    variantTags: ["award", "broadcast", "news", "engraved"],
    credits: 345,
  },
  {
    id: "city-banner-fragment",
    name: "City Banner Fragment",
    rarity: "rare",
    type: "relic",
    icon: "🚩",
    description: "Cloth fragment from a civic banner; hemmed edge and reinforced grommet hole.",
    short: "Fragment of civic banner.",
    variantTags: ["banner", "cloth", "city", "fragment"],
    credits: 310,
  },
  {
    id: "public-sculpture-miniature",
    name: "Public Sculpture Miniature",
    rarity: "rare",
    type: "relic",
    icon: "🗿",
    description: "Desk-sized resin replica of a well-known city sculpture.",
    short: "Miniature of public artwork.",
    variantTags: ["sculpture", "replica", "resin", "art"],
    credits: 390,
  },
];
export const itemsVendorRelicsEpic = [
  {
    id: "time-capsule",
    name: "Time Capsule",
    rarity: "epic",
    type: "relic",
    icon: "⏳",
    description: "Sealed alloy container with personal notes, coins, and local newspapers.",
    short: "Sealed container of mementos.",
    variantTags: ["capsule", "sealed", "alloy", "time"],
    credits: 850,
  },
  {
    id: "national-flag",
    name: "National Flag",
    rarity: "epic",
    type: "relic",
    icon: "🚩",
    description: "Folded synthetic fabric in perfect colors, sealed in inert gas casing.",
    short: "Folded synthetic national flag.",
    variantTags: ["flag", "national", "fabric", "sealed"],
    credits: 900,
  },
  {
    id: "concert-guitar",
    name: "Concert Guitar",
    rarity: "epic",
    type: "relic",
    icon: "🎸",
    description: "Stage-played instrument with visible pick wear and intact signature on the body.",
    short: "Signed, stage-played guitar.",
    variantTags: ["guitar", "music", "signed", "stage"],
    credits: 950,
  },
  {
    id: "literary-manuscript",
    name: "Literary Manuscript",
    rarity: "epic",
    type: "relic",
    icon: "📜",
    description: "Typed pages with redline edits and margin notes; author's seal on first sheet.",
    short: "Typed pages with redlines.",
    variantTags: ["manuscript", "pages", "typed", "edits"],
    credits: 820,
  },
  {
    id: "museum-maquette",
    name: "Museum Maquette",
    rarity: "epic",
    type: "relic",
    icon: "🏛️",
    description: "Small-scale exhibition model with labeled plinths and lighting plan.",
    short: "Small-scale museum exhibit model.",
    variantTags: ["maquette", "museum", "model", "exhibit"],
    credits: 875,
  },
  {
    id: "charter-document",
    name: "Charter Document",
    rarity: "epic",
    type: "relic",
    icon: "📜",
    description: "Parchment-style print with embossed seal and signatures of founding council.",
    short: "Founding charter with seal.",
    variantTags: ["charter", "document", "seal", "founding"],
    credits: 880,
  },
  {
    id: "film-award",
    name: "Film Award",
    rarity: "epic",
    type: "relic",
    icon: "🎬",
    description: "Sculpted statuette for excellence in filmmaking; base plate lists the production crew.",
    short: "Statuette for filmmaking.",
    variantTags: ["film", "award", "statuette", "cinema"],
    credits: 925,
  },
  {
    id: "fashion-archive",
    name: "Fashion Archive",
    rarity: "epic",
    type: "relic",
    icon: "👗",
    description: "Transparent crate of preserved high-fashion garments, colors unfaded.",
    short: "Preserved high-fashion garment collection.",
    variantTags: ["fashion", "archive", "garment", "case"],
    credits: 1000,
  },
  {
    id: "founders-journal",
    name: "Founders' Journal",
    rarity: "epic",
    type: "relic",
    icon: "📖",
    description: "Leather-bound personal chronicle written by ea...rly colonists, containing reflections on cooperation and hope.",
    short: "Early colonist personal chronicle.",
    variantTags: ["journal", "book", "historic", "colonial"],
    credits: 860,
  },
];


// ========== materialOwnership.js ==========
// ============================================================================
// MATERIAL OWNERSHIP MATRIX
// ============================================================================
// Source of truth for which materials belong to which skill's loot signature.
// Recipe authoring, the lootFocus guardrail test, and the coverage report
// (tools/loot-coverage-report.mjs) all read this — recipes must stay closed
// over it so every skill's drop pool remains a focused, distinct subset.
//
// Design (per faction, skills s1..s5 in factionSkills order):
//   - Commons: each skill owns 4 of the faction's 8, via the 4-subset design
//     s1{c1-c4} s2{c5-c8} s3{c1,c2,c5,c6} s4{c3,c4,c7,c8} s5{c1,c3,c5,c7}
//     (all pairwise overlaps <= 2), plus 2 of the 4 neutral core commons.
//   - Uncommons: 6 per faction, each owned by exactly 2 skills (cyclic pairs
//     u1{s1,s2} u2{s2,s3} u3{s3,s4} u4{s4,s5} u5{s5,s1} u6{s1,s3}).
//   - Rares: 4 per faction, owner pairs aligned with u1..u4 so each rare's
//     uncommon recipe input is the same-pair uncommon (chains stay closed).
//
// Closure rules (enforced by tests/data/lootFocus.test.js):
//   1. An uncommon's recipe inputs ⊆ (intersection of its owners' commons)
//      + core. (u1/u3 pair disjoint-common skills → core-only recipes.)
//   2. A rare's uncommon input is its same-pair uncommon; commons same rule.
//   3. Epic/artifact recipes for a skill use only that skill's owned
//      uncommons/rares and commons from its signature + core.

export const materialOwnership = {
  coreCommons: ["steel-plates", "glass-lens", "rubber-seal", "iron-frame"],

  skills: {
    // ===== DOMINION =====
    // c1 copper-wire, c2 circuit-board, c3 relay-antenna-strut,
    // c4 logic-ribbon-cable, c5 ceramic-insulator, c6 power-cell-casing,
    // c7 composite-casing, c8 aluminum-shell
    "signal-relay": { // s1
      commons: ["copper-wire", "circuit-board", "relay-antenna-strut", "logic-ribbon-cable"],
      core: ["steel-plates", "glass-lens"],
      uncommons: ["signal-processor-core", "precision-servo-gear", "power-regulation-unit"], // u1, u5, u6
      rares: ["encrypted-transceiver-chip"], // r1
    },
    "system-override": { // s2
      commons: ["ceramic-insulator", "power-cell-casing", "composite-casing", "aluminum-shell"],
      core: ["rubber-seal", "iron-frame"],
      uncommons: ["signal-processor-core", "override-datakey"], // u1, u2
      rares: ["encrypted-transceiver-chip", "voltage-regulator-unit"], // r1, r2
    },
    "drone-command": { // s3
      commons: ["copper-wire", "circuit-board", "ceramic-insulator", "power-cell-casing"],
      core: ["steel-plates", "rubber-seal"],
      uncommons: ["override-datakey", "drone-gyro-assembly", "power-regulation-unit"], // u2, u3, u6
      rares: ["voltage-regulator-unit", "signal-sync-crystal"], // r2, r3
    },
    "safeguard": { // s4
      commons: ["relay-antenna-strut", "logic-ribbon-cable", "composite-casing", "aluminum-shell"],
      core: ["glass-lens", "iron-frame"],
      uncommons: ["drone-gyro-assembly", "sealed-hydraulic-pump"], // u3, u4
      rares: ["signal-sync-crystal", "radiation-sensor-module"], // r3, r4
    },
    "quantum-scan": { // s5
      commons: ["copper-wire", "relay-antenna-strut", "ceramic-insulator", "composite-casing"],
      core: ["steel-plates", "iron-frame"],
      uncommons: ["sealed-hydraulic-pump", "precision-servo-gear"], // u4, u5
      rares: ["radiation-sensor-module"], // r4
    },

    // ===== WILDERS =====
    // c1 fabric-mesh, c2 canvas-fiber, c3 leather-straps, c4 tanned-gut-cord,
    // c5 polymer-tubing, c6 resin-casing, c7 chitin-plate, c8 spore-felt-padding
    "reclamation": { // s1
      commons: ["fabric-mesh", "canvas-fiber", "leather-straps", "tanned-gut-cord"],
      core: ["steel-plates", "glass-lens"],
      uncommons: ["nutrient-catalyst-ampoule", "nutrient-gel-pack", "medical-stimulant-cartridge"], // u1, u5, u6
      rares: ["high-torque-motor-core"], // r1
    },
    "growth-control": { // s2
      commons: ["polymer-tubing", "resin-casing", "chitin-plate", "spore-felt-padding"],
      core: ["rubber-seal", "iron-frame"],
      uncommons: ["nutrient-catalyst-ampoule", "symbiotic-culture-vial"], // u1, u2
      rares: ["high-torque-motor-core", "bio-conductive-mesh"], // r1, r2
    },
    "wild-influence": { // s3
      commons: ["fabric-mesh", "canvas-fiber", "polymer-tubing", "resin-casing"],
      core: ["steel-plates", "rubber-seal"],
      uncommons: ["symbiotic-culture-vial", "pheromone-distiller-coil", "medical-stimulant-cartridge"], // u2, u3, u6
      rares: ["bio-conductive-mesh", "synthetic-pheromone-capsule"], // r2, r3
    },
    "bio-shield": { // s4
      commons: ["leather-straps", "tanned-gut-cord", "chitin-plate", "spore-felt-padding"],
      core: ["glass-lens", "iron-frame"],
      uncommons: ["pheromone-distiller-coil", "adaptive-filter-membrane"], // u3, u4
      rares: ["synthetic-pheromone-capsule", "containment-gel-lining"], // r3, r4
    },
    "containment": { // s5
      commons: ["fabric-mesh", "leather-straps", "polymer-tubing", "chitin-plate"],
      core: ["steel-plates", "iron-frame"],
      uncommons: ["adaptive-filter-membrane", "nutrient-gel-pack"], // u4, u5
      rares: ["containment-gel-lining"], // r4
    },

    // ===== VEIL =====
    // c1 brass-fittings, c2 carbon-housing, c3 plastic-casing,
    // c4 micro-bearing-set, c5 optic-fiber-spool, c6 matte-coating-compound,
    // c7 acoustic-foam-wafer, c8 etched-keyplate-blank
    "echo-trace": { // s1
      commons: ["brass-fittings", "carbon-housing", "plastic-casing", "micro-bearing-set"],
      core: ["steel-plates", "glass-lens"],
      uncommons: ["ultrasonic-sensor-grid", "phase-lens-array", "thermal-polarizer-film"], // u1, u5, u6
      rares: ["data-storage-crystal"], // r1
    },
    "lock-breach": { // s2
      commons: ["optic-fiber-spool", "matte-coating-compound", "acoustic-foam-wafer", "etched-keyplate-blank"],
      core: ["rubber-seal", "iron-frame"],
      uncommons: ["ultrasonic-sensor-grid", "micro-servo-drill"], // u1, u2
      rares: ["data-storage-crystal", "precision-chrono-core"], // r1, r2
    },
    "stealth-transit": { // s3
      commons: ["brass-fittings", "carbon-housing", "optic-fiber-spool", "matte-coating-compound"],
      core: ["steel-plates", "rubber-seal"],
      uncommons: ["micro-servo-drill", "vibration-dampener", "thermal-polarizer-film"], // u2, u3, u6
      rares: ["precision-chrono-core", "em-interference-chip"], // r2, r3
    },
    "signal-mask": { // s4
      commons: ["plastic-casing", "micro-bearing-set", "acoustic-foam-wafer", "etched-keyplate-blank"],
      core: ["glass-lens", "iron-frame"],
      uncommons: ["vibration-dampener", "ghost-signal-modulator"], // u3, u4
      rares: ["em-interference-chip", "harmonic-stabilizer-core"], // r3, r4
    },
    "transit-anchor": { // s5
      commons: ["brass-fittings", "plastic-casing", "optic-fiber-spool", "acoustic-foam-wafer"],
      core: ["steel-plates", "iron-frame"],
      uncommons: ["ghost-signal-modulator", "phase-lens-array"], // u4, u5
      rares: ["harmonic-stabilizer-core"], // r4
    },
  },
};


// ========== progressionScaling.js ==========
/**
 * Progression Scaling Configuration
 *
 * SINGLE SOURCE OF TRUTH for balance values shared by the game and the
 * headless simulator (tools/simulation/game-simulator.js). The simulator
 * defaults to these values; tools/simulation/simulation-config.json is
 * pure override/experiment space. To ship a validated experiment value,
 * edit it HERE and run `npm run build` (regenerates js/constants.js).
 *
 * Values are tuned so that:
 * - All 15 faction epic tech gear items are craftable in a 30-day cycle
 * - Inventory quantities stay in balanced range (50-150 per item)
 * - Higher-rarity intermediates are properly gated by their availability
 */

export const progressionScaling = {
  /**
   * Durable-gear craft cap. Skill (tech) gear and artifacts are equip-once
   * items - one slot per skill / per artifact slot, never consumed, sold, or
   * traded - so a second copy earns nothing (marks are already distinct-only)
   * and just burns materials. When false (default) each such design can be
   * crafted once per character; the crafting card shows "✓ CRAFTED" instead.
   * Flip to true to re-enable duplicate crafting (e.g. if a gear marketplace
   * ever makes spare copies worth something). Consumables are unaffected.
   */
  allowDuplicateCrafts: false,

  recipeInputs: {
    // Re-tuned for weighted per-item drops: selection weights spread drops across
    // all items (nothing starves to zero anymore), so demand carries the pacing
    common: 5, // Common ingredients cost 5x base (sized to rarity-filtered event loot income)
    uncommon: 5, // Uncommon ingredients cost 5x base
    rare: 4, // Rare ingredients cost 4x base
    epic: 4, // Epic ingredients cost 4x base (scarce)
    legendary: 1, // Legendary ingredients cost 1x base (very scarce)
    unique: 1, // Unique ingredients cost 1x base (very scarce)
  },

  /**
   * Skill points awarded per level up (character-core.js levelUpCore).
   * Formula: base + Math.floor(level / levelDivisor) * increment
   * where `level` is the level just REACHED.
   *
   * With the values below (base: 2, levelDivisor: 2, increment: 3):
   * - Level 1:   3 skill points (the starting allotment, not a level-up grant)
   * - Level 2-3: 5 per level-up
   * - Level 4-5: 8 per level-up
   * - Level 6-7: 11 per level-up
   */
  skillPointsPerLevel: {
    base: 2, // Base skill points awarded on level up
    levelDivisor: 2, // Award +increment skill points every N levels
    increment: 3, // Amount to increase per tier
  },

  /**
   * Per-skill synergy unlocks (progressive disclosure of the synergy layer):
   * hidden entirely below minLevel, then each skill's synergies are bought
   * with skill points. A synergy variant can fire only when it touches an
   * unlocked skill on either side (the event's skill, or the skill of the
   * player's partner device).
   */
  synergyUnlocks: {
    minLevel: 4, // Character level where the synergy layer is revealed
    cost: 2, // Skill points per skill unlock
  },

  /**
   * Raid XP tier weighting (Command era). The level curve's old assumption
   * ("level 8 in 1-2 months") predates promotion cycling; with 3-5 day
   * tours, character level was the binding progression constraint - tours
   * stalled at level 4-5 on tier-1 XP. Pushing tiers is now the leveling
   * ladder: T1 pacing (and first-promotion timing) unchanged, T2/T3 raids
   * pay multiples so deep tours can level past the plateau.
   */
  raidXpTierMultipliers: { 1: 1, 2: 3, 3: 5 },

  /**
   * Skill event damage scaling per raid tier
   * Damage formula: minDamage + random(0, maxDamage - minDamage)
   * where minDamage = baseDamage * tier, maxDamage = minDamage + (baseDamage * tier)
   *
   * Example with defaults (baseDamage: 5):
   * - Tier 1: 5-15 damage (5 + 0-10)
   * - Tier 2: 10-30 damage (10 + 0-20)
   * - Tier 3: 15-45 damage (15 + 0-30)
   */
  skillEventDamage: {
    baseDamage: 5, // Base damage multiplied by tier number (used if perTier not set)
    perTier: {
      1: { min: 20, max: 30 },
      2: { min: 25, max: 35 },
      3: { min: 30, max: 40 },
    },
  },

  /**
   * Bonus event cascade chance multipliers, per raid tier AND per cascade step.
   * Applied as: rarityDropChance * bonusEventCascadeMultipliers[tier][nextRarity]
   * when the end-of-room bonus event rolls to cascade common→rare→epic→legendary→unique.
   *
   * Per-rarity so the legendary step can be boosted (its base chance is near zero,
   * this is the main legendary blueprint source) WITHOUT saturating the rare/epic
   * steps - a flat multiplier clamped those to 100% per clean room.
   */
  bonusEventCascadeMultipliers: {
    // rare < 1.0 keeps leaf-rare cascade volume near recipe demand.
    // Legendary (= artifact blueprints, the whole legendary pool) is a TIER 3
    // EXCLUSIVE: reaching T3 is the plannable path to artifacts (and the
    // Syndicate boundary), not a lottery ticket at every tier. 50 saturates
    // the legendary step (0.02 base x 50 = chance 1.0 once the cascade
    // reaches epic), so a clean T3 room drops a blueprint at its epic-chain
    // rate (~3% hazard, ~7% gate).
    1: { rare: 0.5, epic: 1.0, legendary: 0, unique: 1.0 },
    2: { rare: 0.5, epic: 1.0, legendary: 0, unique: 1.0 },
    3: { rare: 0.5, epic: 1.0, legendary: 50, unique: 1.0 },
  },

  /**
   * Share of a promoted character's MATERIALS that passes to their successor.
   * The remainder is consigned to the Syndicate dock as Operations cargo
   * (command-core.js disburseInventory).
   *
   * Fixed, and was a three-rank Command upgrade ("Guild Vault", 80/240/720
   * marks) until the S7c pass removed it. The rank bought nothing: gear crafted
   * per tour is FLAT across a 2.5x range of this value - 5.57 at 10/20/30,
   * 6.07 at 15/30/50, 5.61 at 25/50/75, n=4 each - so the upgrade taxed 1,040
   * marks for an effect that does not exist. An upgrade that cannot be measured
   * is worse than no upgrade, because buying it is a trap.
   *
   * Why the share does not matter: it is not a one-way transfer. Whatever the
   * successor inherits and does not CONSUME is disbursed again at their own
   * promotion, and again after that, so the dial changes timing rather than
   * totals. Measured: at 75% carryover the L3 dock was unchanged (17,977 vs
   * 18,504 units) and zero convoys were ever refused for cargo.
   *
   * 0.3 because it equals the old rank-3 value - nobody who had fully invested
   * is worse off - and because a flat "a third of your kit passes on" reads
   * cleanly in the fiction. Any value in 0.1-0.75 measures the same.
   */
  vaultCarryover: 0.3,

  /**
   * Share of the NON-carried remainder that reaches the Syndicate dock as
   * Operations cargo. The rest is consumed — spent living, in the fiction.
   *
   * The accounting used to be closed: 30% to the successor, 70% to L3, nothing
   * lost. That was tidy and produced an unusable economy. A dock held ~18,000
   * units while a whole era's convoys consumed ~1,500 (8%), so the "common
   * material piles plateau (<300/signature)" target measured ~2,500 and rising,
   * and cargo could never be a real constraint on Operations — even 10x the
   * cargo per convoy would not have drained it.
   *
   * No dial inside Operations could fix that, because the problem is supply,
   * not demand. This is the supply valve.
   */
  consignmentRate: 0.15,

  /**
   * Command layer (M2) - Command Marks formula coefficients + unlock threshold.
   * Consumed by command-core.js calculateCommandMarks:
   *   marks = perTechGear       * distinct tech gear owned (inventory + equipped)
   *         + perArtifactUnlock * lifetime.artifactsUnlocked
   *         + perArtifactCraft  * lifetime.artifactsCrafted
   *         + floor(lifetime.t3Wins / t3WinsDivisor)
   *         + perLevel          * level
   * The sim promotion policy tunes these so an un-upgraded character #1's
   * marks/hour peaks ~day 3-5 (see docs/Roadmap-070426.md playtime budget).
   */
  commandMarks: {
    // Marks reward CRAFTING, which is the player's active progress: skill gear
    // early (the only thing craftable at first), artifacts later (once T3 is
    // reachable). Character level is an ENABLER, not a payout - it unlocks
    // higher tiers, which yield T3 wins + blueprints -> artifacts; its direct
    // marks contribution is deliberately small so it can't become the main
    // driver (it previously grew to ~50% of marks as accounts matured).
    // Held at 4. Raising it was MEASURED in the S7c pass and rejected: at 8,
    // three runs put the first founding on day 31/34/31 - identical to the
    // 31-36 baseline - while Writs overshot to 34-38 and the L1 era collapsed
    // from ~40h to 18-21h. Two reasons, both structural:
    //
    //  - It cannot shorten a tour. Tour length is when cumulative marks/hour
    //    PEAKS, and a bigger gear channel moves that peak later by about as
    //    much as it pays, so the schedule does not move.
    //  - Marks are also the CURRENCY for Command upgrades, so doubling this
    //    halves the real price of the whole catalogue. The first promotion
    //    fires at the 60-mark floor, which needs ~15 gear at 4 and only 8 at
    //    8 - halving the onboarding era as a side effect.
    //
    // The L2 era overruns because tours 2-15 run ~26h against the ~18h the
    // 300h budget implies. That is a CYCLE COMPRESSION problem (officer-academy
    // / guild-vault head starts not paying off enough), not a throughput one,
    // and this coefficient is the wrong lever for it.
    perTechGear: 4,
    // Artifacts split into two channels (see command-core unlockArtifactCore):
    //   perArtifactUnlock - ONE-TIME per design per ACCOUNT, banked when the
    //     design's blueprint is consumed to unlock it. The milestone: 27
    //     designs => a finite ~675-mark collection goal.
    //   perArtifactCraft - each distinct craft per character (parallel to gear,
    //     ~2x a gear piece). Keeps a modest sustained artifact-marks trickle
    //     without the runaway 25-per-craft farm.
    perArtifactUnlock: 25,
    perArtifactCraft: 8,
    // T3 wins are the "operating at high tier" marks channel that level
    // enables (2 wins per mark); artifacts are the high-tier crafting payoff
    t3WinsDivisor: 2,
    perLevel: 1,
    // Command tab reveals when estimated marks first reach this. Tied to
    // perTechGear: it fires on the 7th piece of gear. If perTechGear ever
    // moves, scale this by the same ratio - it is a progression milestone
    // measured in what the player has BUILT, and leaving it fixed would slide
    // a designed reveal earlier as a side effect of an unrelated change.
    unlockThreshold: 25,
  },

  /**
   * Promotion credit levy (applied in command-core.js promoteCharacterCore).
   * Credits are account-shared, so promotion takes a percentage cut instead
   * of a flat reset: kept = max(floor, credits * keepFraction). The levy is
   * the economy's terminal credit sink — it must scale with wealth to keep
   * absorbing late-game surplus; the floor doubles as a safety net so a
   * broke account still restarts with working capital.
   */
  promotionCreditLevy: {
    keepFraction: 0.25,
    floor: 1000,
  },

  /**
   * Collapse Key recovery — the ONLY tunable in the story track.
   *
   * Chance that a successful raid recovers the next Collapse Key its faction is
   * still missing. Not an economic number: keys have no value, no sink and no
   * sale, so this tunes STORY PACING alone and no balance target reads it.
   *
   * The tier table (keys 1-2 at T1, 3-4 at T2, 5-7 at T3) is what actually
   * paces the arc — a T1 character can only ever hold two keys, however long
   * they raid. This number decides how quickly they reach that ceiling: at 0.25
   * the first key lands in the opening session and the T1 pair inside ~8 wins,
   * which is the point of shipping keys 1-7 at all. The game previously told
   * the player nothing until ~900 hours in.
   */
  collapseKeyDropChance: 0.25,

  /**
   * Raid credit reward scaling (applied in raidUtil.js applyCreditsScaling)
   * effective = earned * globalMultiplier * tierMultipliers[tier]
   */
  creditsScaling: {
    globalMultiplier: 0.3,
    tierMultipliers: {
      1: 1.0,
      2: 1.0,
      3: 1.0,
    },
  },

  /**
   * Energy repurchase escalation (applied in raidUtil.js
   * getConsumablePurchasePrice): energy-restoring consumables cost
   * base * escalationFactor^(purchases today), resetting daily.
   * The primary Command-era credit sink - converts surplus credits into
   * raid throughput at a net credit loss (late game: 5-6 packs/day
   * ~ the 4-10k/day income it must soak). 1.0 disables escalation.
   */
  energyPurchaseScaling: {
    escalationFactor: 2.0,
  },

  /**
   * Per-tour crafting discount (Command layer). Each completed tour makes
   * crafting cheaper for every later tour — the institutional-knowledge payoff.
   *
   * DERIVED, never compounded:
   *   globalMultiplier = craftingCostScaling.globalMultiplier * factor^(tour - 1)
   *
   * It used to be applied by multiplying raidUtil's module state at each
   * promotion, which main.js then re-seeded from config on every boot — so the
   * discount silently vanished on reload. Deriving it from `command.tour` makes
   * it reload-safe, and makes L3's founding reset free: tour returns to 1, so
   * the multiplier returns to base with nothing extra to persist.
   *
   * 1.0 disables the discount.
   */
  craftingDiscountPerTour: 0.9,

  /**
   * Crafting credit cost scaling (applied in raidUtil.js applyCraftingCostScaling)
   * effective = cost * globalMultiplier * rarityMultipliers[rarity]
   */
  craftingCostScaling: {
    globalMultiplier: 5,
    rarityMultipliers: {
      common: 1.0,
      uncommon: 1.0,
      rare: 1.0,
      epic: 1.0,
      legendary: 1.0,
      unique: 1.0,
      artifact: 1.0,
    },
  },
};

/**
 * Get the input multiplier for a given rarity (per-input scaling)
 * @param {string} rarity - The rarity of the input item ('common', 'uncommon', 'rare', 'epic', etc.)
 * @returns {number} Multiplier for this input's quantity
 */
export function getInputRarityMultiplier(rarity) {
  const multiplier = progressionScaling.recipeInputs[rarity];
  if (multiplier === undefined) {
    console.warn(`Unknown input rarity: ${rarity}, returning 1.0`);
    return 1.0;
  }
  return multiplier;
}

/**
 * Round a quantity to a clean multiple (1, 10, or 100) based on magnitude
 *
 * Logic:
 * - 1-9: round to nearest 1
 * - 10-99: round to nearest 10
 * - 100-999: round to nearest 100
 * - 1000+: round to nearest 100
 *
 * Always rounds UP to ensure we don't accidentally make recipes too easy.
 *
 * @param {number} quantity - The quantity to round
 * @returns {number} Rounded quantity
 */
export function roundQuantityToCleanMultiple(quantity) {
  if (quantity < 10) {
    // Keep 1-9 as-is
    return Math.max(1, Math.ceil(quantity));
  } else if (quantity < 100) {
    // Round 10-99 to nearest 5
    return Math.ceil(quantity / 5) * 5;
  } else {
    // Round 100+ to nearest 10
    return Math.ceil(quantity / 10) * 10;
  }
}

/**
 * Scale a recipe's inputs by the per-rarity input multipliers
 *
 * Each ingredient is multiplied by its rarity's configured multiplier.
 * These multipliers come from simulator balancing and ensure that:
 * - All 15 tech gear items are craftable
 * - Resources are properly gated by their availability
 *
 * @param {Array} inputs - Array of { id, qty, rarity } input objects
 * @param {Function} getRarityFn - Function to get rarity of an item (id => rarity)
 * @returns {Array} Scaled inputs with clean rounded quantities
 */
export function scaleRecipeInputs(inputs, getRarityFn = null) {
  if (!inputs || !Array.isArray(inputs)) {
    return inputs;
  }

  return inputs.map((input) => {
    let inputQty = input.qty;

    // Get input rarity (from input object or from provided function)
    let inputRarity = input.rarity;
    if (!inputRarity && getRarityFn) {
      inputRarity = getRarityFn(input.id);
    }

    // Apply per-input scaling based on rarity
    if (inputRarity) {
      const inputMultiplier = getInputRarityMultiplier(inputRarity);
      inputQty *= inputMultiplier;
    }

    return {
      ...input,
      qty: roundQuantityToCleanMultiple(inputQty),
    };
  });
}


// ========== raid.js ==========
// Tier data (independent of regions)
export const raidTierData = {
  1: {
    name: "Tier 1 - Exploration",
    description: "Entry-level raids with manageable challenges",
    gridSize: 7,
    gridRows: 7,
    gridCols: 7,
    contractorMinLevel: 1,
    contractorMaxLevel: 8,
    hazardSkillLevel: 3,
    gateSkillLevel: 5,
    creditRewards: {
      baseRoomCredit: 10,
      successEventCredit: 15,
      completionBonus: 30
    }
  },
  2: {
    name: "Tier 2 - Veteran",
    description: "Moderate difficulty with increased rewards",
    gridSize: 9,
    gridRows: 7,
    gridCols: 13,
    contractorMinLevel: 6,
    contractorMaxLevel: 14,
    hazardSkillLevel: 6,
    gateSkillLevel: 8,
    creditRewards: {
      baseRoomCredit: 15,
      successEventCredit: 23,
      completionBonus: 45
    }
  },
  3: {
    name: "Tier 3 - Elite",
    description: "High-risk, high-reward advanced operations",
    gridSize: 11,
    gridRows: 7,
    gridCols: 17,
    contractorMinLevel: 10,
    contractorMaxLevel: 999,
    hazardSkillLevel: 8,
    gateSkillLevel: 10,
    creditRewards: {
      baseRoomCredit: 25,
      successEventCredit: 39,
      completionBonus: 75
    }
  },
};

// ========== raidRegions.js ==========
// ===============================
// DISTRICTS
// ===============================
// The districts a map is laid out in, and the only place their names are
// written down. A cell's district is a property of the CELL — `room.zone` — and
// of nothing else; a room template says which district it would rather stand in
// with `districtAffinity`, which is a different question and now has a
// different name for it. The two were both called `zone`, which is how the map
// came to have an invisible district structure that agreed with nothing.
//
// No `transit` district. Roads are already a layer of their own — `room.road`,
// `roadRank`, `roadDist` — and a transit district is the same claim made twice
// by two systems that will disagree about where it is. Its entry in the old
// preferences table was, tellingly, empty.
// `recipes` is what a district is BUILT of: the mix of tile art its ordinary
// cells are drawn from, weighted, summing to 100 so the numbers read as
// percentages. It is what makes a district look like one.
//
// It is deliberately separate from which ROOM stands on a cell. A region owns
// twelve room templates for a map of up to 119 cells, so each is reused eight
// to eleven times — far too few to build a district out of. Picking rooms by
// district instead would collapse a residential district onto the one
// residential template, testing the one skill it carries. So the district
// decides the building type, the room keeps its own name, category and skill,
// and the skill anchor already drawn on every encounter tile says what is
// actually being attempted there. The recipe says where you are; the anchor
// says what you are doing; the name says what it is called.
//
// Every district leads on one recipe at around half its cells, which is what
// makes it recognisable, and carries three or four others so it does not read
// as a monoculture.
// `built` is how densely a district is built up, 0..1, and the renderer reads
// it directly: it thins a tile's structures, and inversely lets the growth back
// in and the tarmac go to seed. Authored rather than sampled from noise so a
// district's look and its name agree — an outskirt is thin because it IS the
// outskirts, not because a lattice happened to dip there.
//
// The numbers are centred on MEAN_BUILT (roomImageGen) so the average tile
// lands where it already was. This redistributes built stock across the map; it
// does not delete it, and there is a test holding the total to within a tenth.
export const zoneArchetypes = {
  entry: {
    description: "Starting area with easier encounters",
    recipes: { infrastructure: 35, transit: 25, security: 20, storage: 20 },
    built: 0.72,
  },
  peripheral: {
    description: "Outer areas with mixed encounters",
    recipes: { environmental: 40, transit: 20, storage: 20, habitation: 20 },
    built: 0.58,
  },
  security: {
    description: "Defensive checkpoints",
    recipes: { security: 45, infrastructure: 25, storage: 20, power: 10 },
    built: 0.90,
  },
  industrial: {
    description: "Resource/infrastructure areas",
    recipes: { power: 30, storage: 30, infrastructure: 25, transit: 15 },
    built: 0.88,
  },
  core: {
    description: "High-value protected areas",
    recipes: { records: 35, commerce: 25, security: 20, infrastructure: 20 },
    built: 1.00,
  },
  residential: {
    description: "Habitats, apartments, communal living spaces",
    recipes: { habitation: 50, environmental: 20, records: 15, commerce: 15 },
    built: 0.85,
  },
  commercial: {
    description: "Shops, offices, market halls, logistics fronts",
    recipes: { commerce: 50, storage: 20, records: 15, habitation: 15 },
    built: 0.95,
  },
};

// ===============================
// COMMON ROOMS
// ===============================
export const commonAreas = {
  campsite: {
    type: "campsite",
    name: "Campsite",
    primary: "",
    support: [],
    baseDuration: 120,
    icon: "🏕️",
    description: "A safe place to rest and recover",
    healingAmount: 30,
  },
  water: {
    type: "water",
    name: "Water",
    primary: "",
    support: [],
    baseDuration: 120,
    icon: "🌊",
    description: "A water source for fishing",
  },
  road: {
    type: "road",
    name: "Road",
    primary: "",
    support: [],
    baseDuration: 10,
    icon: "🛣️",
    description: "A roadway for fast travel",
  },
};

// raidRegions.js
// Regions data with synchronized time-of-day and clarified lighting.
// Square, pixel-art oriented image prompts; natural/team lighting only.
// Adds: “Explorers’ handheld lights only; no street or facility lighting.”
// Includes shortDescription and longDescription on regions, gates, and hazards.

export const raidRegions = {
  // ======================================================
  "shard-cities": {
    name: "Shard Cities",
    theme: "Collapsed megacities and fractured districts",
    icon: "🏙️",
    shortDescription:
      "Towering urban ruins split by broken transit lines and silent civic grids.",
    longDescription:
      "These were once continuous skylines: stacked transit, dense housing, and civic utilities woven into mile-high towers. Centuries of neglect cracked the pavement and snapped the skyways. You can still read the street plan, even if whole blocks have slumped into their own debris.\n\nPower occasionally surges through old feeders, enough to light a dead intersection for a breath. Most systems do nothing at all—just rust, dust, and old signage pointing nowhere.",
    artDirection:
      "Two scales of building, and the contrast between them IS the region. The ordinary stock is warm — brick and brown roofing, low, dense, packed kerb to kerb. Over it stand cool concrete-and-glass masses: tower cores with their service shafts open to the sky, skybridge decks that start nowhere and stop in mid-air, sign gantries still spanning the carriageway. Ground is dark slate tarmac, cracked on a slab pitch, so both tiers read light against it. Street trees have come up through the pavement everywhere there was a cutout.",
    gates: [
      {
        icon: "🚧",
        name: "Checkpoint Sector",
        primary: "lock-breach",
        support: ["signal-relay", "stealth-transit"],
        baseDuration: 120,
        category: "security",
        difficulty: "chokepoint",
        placement_priority: "critical",
        shortDescription: "Barricaded intersection controlling movement between ruined blocks.",
        longDescription:
          "Concrete barriers, rusted railings, and makeshift roadblocks choke an old arterial. The city’s last attempts at order still shape the flow of rubble and weeds. Nothing powers here—only wind and dust."
      },
      {
        icon: "⚙️",
        name: "Civic Relay Block",
        primary: "signal-relay",
        support: ["quantum-scan", "lock-breach"],
        baseDuration: 120,
        category: "infrastructure",
        difficulty: "chokepoint",
        placement_priority: "high",
        shortDescription: "Collapsed service block with dead comms towers and trunk conduits.",
        longDescription:
          "Stubbed antenna masts lean over a plaza of broken panels and cable vaults. Dust-muted signage points to services that no longer exist. A faint click from a relay box sometimes answers fresh power before falling silent again."
      },
      {
        icon: "🏢",
        name: "Apartment Access Lot",
        primary: "stealth-transit",
        support: ["quantum-scan", "lock-breach"],
        baseDuration: 120,
        category: "security",
        difficulty: "chokepoint",
        placement_priority: "high",
        districtAffinity: "residential",
        shortDescription: "Collapsed housing block entrances clustered around a cracked forecourt.",
        longDescription:
          "Entry canopies hang at angles over door stacks long since jammed. Mailboxes gape, names scoured to metal. The forecourt is a map of fractures and weeds, with scaffolds rusted into stillness."
      },
      {
        icon: "🛍️",
        name: "Arcade Shutter Sector",
        primary: "system-override",
        support: ["drone-command", "containment"],
        baseDuration: 120,
        category: "security",
        difficulty: "chokepoint",
        placement_priority: "high",
        districtAffinity: "commercial",
        shortDescription: "Market frontage sealed by fallen shutters and buckled grates.",
        longDescription:
          "A run of storefronts slouches under dead signage and twisted awnings. Steel shutters are half-dropped or jammed, creating a maze of narrow access. Any movement here is the wind through canvas and chain."
      }
    ],
    hazards: [
      {
        icon: "🛢️",
        name: "Residential Power Yard",
        primary: "containment",
        support: ["signal-relay", "transit-anchor"],
        baseDuration: 60,
        category: "power",
        difficulty: "standard",
        placement_priority: "high",
        shortDescription: "Small substation between towers with fallen lines and rusted switchgear.",
        longDescription:
          "Transformer housings slouch on cracked pads; the fence is more gap than mesh. A wrong step crunches glass and ceramic. If anything lights, it’s for a breath, then the quiet deepens."
      },
      {
        icon: "🌉",
        name: "Transit Collapse Zone",
        primary: "transit-anchor",
        support: ["reclamation", "system-override"],
        baseDuration: 60,
        category: "infrastructure",
        difficulty: "standard",
        placement_priority: "normal",
        shortDescription: "Broken overpass and skybridge stubs sprawled over a debris-choked road.",
        longDescription:
          "Guardrails spear the pavement; cables drape from shattered spans. The echo under concrete ribs tells how big it was. Weeds stitch rebar and dust into something like ground again."
      },
      {
        icon: "📦",
        name: "Marketfront Depot",
        primary: "drone-command",
        support: ["transit-anchor", "echo-trace"],
        baseDuration: 60,
        category: "storage",
        difficulty: "high_value",
        placement_priority: "high",
        shortDescription: "Cluster of loading bays and shuttered storerooms behind ruined shops.",
        longDescription:
          "Dock plates sit crooked; pallets rot into dark lines on the ground. The loading lights are dead—only daylight finds its way in through gaps and broken louvers."
      },
      {
        icon: "🏢",
        name: "Administrative Records Block",
        primary: "quantum-scan",
        support: ["system-override", "lock-breach"],
        baseDuration: 60,
        category: "storage",
        difficulty: "high_value",
        placement_priority: "critical",
        shortDescription: "Abandoned office district with shattered facades and paper-littered streets.",
        longDescription: "Stacks of files turned to mulch line the curbs where floodwater once ran. A rusted maintenance crane leans against a curtain wall. Nothing watches here—only glass dust and wind."
      },
      {
        icon: "🚉",
        name: "Transit Exchange Block",
        primary: "echo-trace",
        support: ["stealth-transit", "containment"],
        baseDuration: 60,
        category: "infrastructure",
        difficulty: "standard",
        placement_priority: "normal",
        shortDescription: "A surface-level transit interchange and service plaza linking several collapsed lines.",
        longDescription: "Once a hub for elevated trams and vertical shuttles, the block still shows the scar of its platforms. Canopies hang over cracked concrete lanes, and access ramps descend into dust-filled tunnels below."
      },
      {
        icon: "🚛",
        name: "Logistics Yard Ruins",
        // overturned cargo trucks and container stacks, not a service yard
        recipe: "storage",
        primary: "reclamation",
        support: ["drone-command", "echo-trace"],
        baseDuration: 60,
        category: "infrastructure",
        difficulty: "standard",
        placement_priority: "normal",
        shortDescription: "Collapsed distribution yard filled with overturned cargo trucks and container stacks.",
        longDescription: "What was once a multi-lane loading grid now lies in layers — trucks fossilized in place, containers torn open by time. Tower cranes stand idle at the corners like monuments to motionless trade."
      },
      {
        icon: "⚡",
        name: "Substation Complex",
        primary: "system-override",
        support: ["reclamation", "quantum-scan"],
        baseDuration: 60,
        category: "power",
        difficulty: "high_value",
        placement_priority: "high",
        shortDescription: "A cluster of transformers and conduit fields forming a decommissioned power block.",
        longDescription: "Heavy transformer shells sit on cracked pads, their housings marked with long-gone warning paint. Busbars bridge between fenced sections, while cable vaults yawn open into the dark below."
      },
      {
        icon: "🛂",
        name: "Civic Control District",
        // a ring of checkpoints; "civic" pulls it to infrastructure otherwise
        recipe: "security",
        primary: "quantum-scan",
        support: ["transit-anchor", "echo-trace"],
        baseDuration: 60,
        category: "security",
        difficulty: "standard",
        placement_priority: "high",
        shortDescription: "A cluster of interconnected checkpoints surrounding a former civic administration block.",
        longDescription: "Security kiosks ring the perimeter of a low central building, their scanner frames bent but still standing. The entire block was once a controlled corridor — a mix of barriers, scanners, and causeways now frozen in collapse."
      }      
    ]
  },

  // ======================================================
  "verdant-reclamation": {
    name: "Green Reclamation",
    theme: "Overgrown research campuses and greenhouse grids",
    icon: "🌿",
    shortDescription:
      "Bio-reclaimed districts where labs and irrigation frames disappear beneath growth.",
    longDescription:
      "This landscape was engineered to coax life back into sterile soil. Greenhouses formed shining corridors; irrigation lattices pulsed under glass. After the departure, containment failed and nature took the reins.\n\nNow, fibrous canopies bind whole blocks together. Steel trusses wear bark; road paint lies under moss. Machinery answers only sometimes, a pump stuttering once before the vines hold it still again.",
    artDirection:
      "Growth is the loudest thing on the tile and the buildings are what it grew over. Heavy canopy in deep greens, moss and wildflowers through the ground, tarmac worn back to soil. The architecture that survives is glasshouse: long shallow glazed spans ridged down their spine with panes missing and the planting showing through, irrigation lattices over what used to be beds, seed silos. Ordinary buildings are muted steel grey and mostly hidden — that is the point, they lost.",
    gates: [
      {
        icon: "🧪",
        name: "Bio-Lab Complex Gate",
        primary: "bio-shield",
        support: ["containment", "growth-control"],
        baseDuration: 120,
        category: "security",
        difficulty: "chokepoint",
        placement_priority: "critical",
        shortDescription: "Collapsed research compound entrance tangled in vines and glass.",
        longDescription:
          "A trellis of bent greenhouse ribs leads into a court of moss and broken tiles. Safety placards lie sun-bleached among seed trays and cracked tanks. The air is warm and wet, heavy with plant breath."
      },
      {
        icon: "🚿",
        name: "Irrigation Control Sector",
        primary: "reclamation",
        support: ["growth-control", "wild-influence"],
        baseDuration: 120,
        category: "security",
        difficulty: "chokepoint",
        placement_priority: "high",
        shortDescription: "Valve fields, cisterns, and control kiosks overtaken by roots.",
        longDescription:
          "Pipes run like exposed roots along the ground, bolted kiosks eaten by rust and kudzu. A cistern lid thumps underfoot—water still trapped below. If anything moves, it’s the wind dragging leaves across metal."
      },
      {
        icon: "🏚️",
        name: "Habitation Annex Gate",
        primary: "wild-influence",
        support: ["bio-shield", "reclamation"],
        baseDuration: 120,
        category: "security",
        difficulty: "chokepoint",
        placement_priority: "high",
        districtAffinity: "residential",
        shortDescription: "Apartment annex entrances swallowed by creepers and fallen trellis.",
        longDescription:
          "Balconies have become planters; stairwells bloom with ferns. Doorways stand open or warped shut, their seals crumbled to powder. A single gutter still drips into a barrel of green water."
      },
      {
        icon: "🧪",
        name: "Market Decontamination Sector",
        primary: "containment",
        support: ["safeguard", "reclamation"],
        baseDuration: 120,
        category: "security",
        difficulty: "chokepoint",
        placement_priority: "high",
        districtAffinity: "commercial",
        shortDescription: "Outdoor wash stations and shuttered stalls clustered around a drained basin.",
        longDescription:
          "Decon arches frame a plaza of cracked tile and dried residue. The stalls around it are skeletal, their tarps long rotted away. Nothing cycles here unless you bring the power yourself."
      }
    ],
    hazards: [
      {
        icon: "🌱",
        name: "Seed Distribution Yard",
        primary: "growth-control",
        support: ["bio-shield", "reclamation"],
        baseDuration: 60,
        category: "storage",
        difficulty: "high_value",
        placement_priority: "high",
        shortDescription: "Silos, bins, and pallet stacks buried in moss and husks.",
        longDescription:
          "A drift of cracked pods crunches underfoot. Stenciled measurements fade from the silo sides. Somewhere a vent exhales warm, earthy air that smells like the start of rain."
      },
      {
        icon: "🍄",
        name: "Fungal Spread Block",
        primary: "growth-control",
        support: ["bio-shield", "reclamation"],
        baseDuration: 60,
        category: "environmental",
        difficulty: "standard",
        placement_priority: "normal",
        shortDescription: "A shaded block carpeted with fungus and spore fans.",
        longDescription:
          "Stems as thick as cable push through fissures in the concrete. Spores hang in the air like dust motes in a cathedral. Your breath fogs just enough to make the light feel heavy."
      },
      {
        icon: "💧",
        name: "Irrigation Breach Sector",
        primary: "bio-shield",
        support: ["growth-control", "safeguard"],
        baseDuration: 60,
        category: "infrastructure",
        difficulty: "standard",
        placement_priority: "normal",
        shortDescription: "Ruptured mains flooding a low lot, roots drinking the overflow.",
        longDescription:
          "A trench of clear water has cut through soil and tile, pooling around toppled control posts. Algae paints the edges; insects tap the surface. The flow wanes and returns without reason."
      },
      {
        icon: "📚",
        name: "Research Supply Yard",
        primary: "reclamation",
        support: ["wild-influence", "safeguard"],
        baseDuration: 60,
        category: "storage",
        difficulty: "high_value",
        placement_priority: "critical",
        shortDescription: "Crated lab gear and broken cages stacked beside a ruined annex.",
        longDescription:
          "Numbers stenciled on crates tell of a mission left unfinished. Metal shelving slumps like wet paper. A single rain gutter ticks as it cools in the shade."
      },
      {
        icon: "🏗️",
        name: "Habitation Block",
        primary: "wild-influence",
        support: ["containment", "growth-control"],
        baseDuration: 60,
        category: "environmental",
        difficulty: "standard",
        placement_priority: "normal",
        districtAffinity: "residential",
        shortDescription: "A cluster of low-rise apartments overtaken by gardens and nests.",
        longDescription:
          "Bedsprings and trellis wire bind stairwells into new shapes. Window boxes have become small forests. The only movement is leaves, and the small quick things that live in them."
      },
      {
        icon: "🫗",
        name: "Nutrient Line Rupture",
        primary: "bio-shield",
        support: ["wild-influence", "safeguard"],
        baseDuration: 60,
        category: "infrastructure",
        difficulty: "standard",
        placement_priority: "high",
        districtAffinity: "residential",
        shortDescription: "Leaking feed lines turning a block into a slick, humming wetland.",
        longDescription:
          "Plastic tubing loops across the ground like shed skin, seeping a sweet, metallic film. New shoots force through asphalt in a straight line where the pipe runs below."
      },
      {
        icon: "🏪",
        name: "Agri-Market Depot",
        primary: "growth-control",
        support: ["containment", "bio-shield"],
        baseDuration: 60,
        category: "storage",
        difficulty: "high_value",
        placement_priority: "high",
        districtAffinity: "commercial",
        shortDescription: "Open-air market storage with collapsed canopies and crate rows.",
        longDescription:
          "Sun-faded tarps hang like old flags. The smell of dust and seed oil lingers. You can still see where chalk mapped out stall fees on the concrete."
      },
      {
        icon: "🧯",
        name: "Greenhouse Containment Sector",
        primary: "safeguard",
        support: ["containment", "wild-influence"],
        baseDuration: 60,
        category: "environmental",
        difficulty: "standard",
        placement_priority: "normal",
        districtAffinity: "commercial",
        shortDescription: "Firebreak lanes and isolation frames choking in vines.",
        longDescription:
          "White-painted fire lanes vanish under creepers. The shutters meant to fall in emergencies are rusted half-down. A distant pane taps in the breeze like a metronome."
      }
    ]
  },

  // ======================================================
  "sunken-bastions": {
    name: "Flooded Bastions",
    theme: "Semi-submerged fortifications and silted logistics",
    icon: "🌊",
    shortDescription:
      "Defense districts with the sea in them: the coast took the low blocks and the silt took the rest.",
    longDescription:
      "Coastal defenses once ringed this region with docks, bunkers, and pressure locks. Storm surge and slow subsidence rolled in over centuries, flooding the low blocks and salting everything else.\n\nSome pressure doors still cycle if you give them power, drawing a final gulp of air through corroded ducts. Mostly, there is water, silt, and the sound of old metal moving in the tide.",
    artDirection:
      "One edge of the map is open ocean and everything reads off how far it is from it. At the shore, water over the outer half of the tile, a buttressed sea wall along the waterline with its bollards behind, and what is left of the buildings is mostly wreckage in the shallows. A row in, tidal flats and standing water pooling in the streets first. Beyond that, dry. Ground is cold silt, not sand — blue-grey, damp-looking, tide-marked. Concrete is wet and weeping rust.",
    gates: [
      {
        icon: "🛑",
        name: "Pressure Gate Sector",
        primary: "lock-breach",
        support: ["signal-mask", "wild-influence"],
        baseDuration: 120,
        category: "security",
        difficulty: "chokepoint",
        placement_priority: "high",
        shortDescription: "Cluster of rusted airlock doors and corroded valves around a flooded court.",
        longDescription:
          "Door rings sit proud with barnacle rust; inspection windows are opaque with salt. The control pedestals are still in place, their labels sanded flat by wind and spray."
      },
      {
        icon: "⚓",
        name: "Drydock Access Block",
        primary: "lock-breach",
        support: ["quantum-scan", "system-override"],
        baseDuration: 120,
        category: "security",
        difficulty: "chokepoint",
        placement_priority: "critical",
        shortDescription: "Collapsed dock gates and winch frames guarding entry to an old basin.",
        longDescription:
          "The dock walls lean inward, heavy with waterlogged stone. Winch cables coil like sleeping eels beside shattered bollards. Every surface is stained a dozen kinds of orange."
      },
      {
        icon: "🚪",
        name: "Residential Seal Lot",
        primary: "containment",
        support: ["safeguard", "system-override"],
        baseDuration: 120,
        category: "security",
        difficulty: "chokepoint",
        placement_priority: "high",
        districtAffinity: "residential",
        shortDescription: "Pressure-rated housing entries scattered along a raised walkway.",
        longDescription:
          "Doors that once sealed against storms now gape, warped on their hinges. A painted muster diagram flakes off the wall in curls as thin as fish scales."
      },
      {
        icon: "🧳",
        name: "Commercial Airlock Sector",
        primary: "signal-mask",
        support: ["bio-shield", "containment"],
        baseDuration: 120,
        category: "security",
        difficulty: "chokepoint",
        placement_priority: "high",
        districtAffinity: "commercial",
        shortDescription: "Cargo airlocks and submerged loading bays along a corroded quay.",
        longDescription:
          "Crate hoists stand frozen above black water. The quay edge is a jag of broken concrete and rebar, polished smooth where the tide reaches."
      }
    ],
    hazards: [
      {
        icon: "🌊",
        name: "Flooded Records Sector",
        primary: "reclamation",
        support: ["wild-influence", "transit-anchor"],
        baseDuration: 60,
        category: "storage",
        difficulty: "high_value",
        placement_priority: "critical",
        shortDescription: "Archives drowned to the window line, shelves like ribs under the surface.",
        longDescription:
          "Folders have turned to silt drifts. A rolling ladder floats, bumping the stacks with a hollow rhythm. Light puddles on the water and slides across the ceiling."
      },
      {
        icon: "💨",
        name: "Ventilation Intake Lot",
        primary: "echo-trace",
        support: ["bio-shield", "reclamation"],
        baseDuration: 60,
        category: "infrastructure",
        difficulty: "standard",
        placement_priority: "normal",
        shortDescription: "Banks of rusted intake fans towering over a silted forecourt.",
        longDescription:
          "Fan blades are choked with ropeweed and netting. A metal tag rattles faintly when the wind shifts. The air smells like creosote and tide."
      },
      {
        icon: "📦",
        name: "Freight Access Zone",
        primary: "transit-anchor",
        support: ["safeguard", "signal-mask"],
        baseDuration: 60,
        category: "security",
        difficulty: "standard",
        placement_priority: "high",
        shortDescription: "Collapsed ramps and container rows leading into a drowned terminal.",
        longDescription:
          "Container paint has bled into pastel ghosts. The ramp rails end abruptly in water that hides a second city of debris below."
      },
      {
        icon: "🪸",
        name: "Marine Observation Block",
        primary: "wild-influence",
        support: ["echo-trace", "lock-breach"],
        baseDuration: 60,
        category: "environmental",
        difficulty: "standard",
        placement_priority: "normal",
        shortDescription: "Sea-worn platforms and pylons with algae film and broken rails.",
        longDescription:
          "Scope housings point at horizons no one watches. The handrails flake in layers that stain your gloves. Gulls nest in the elbows of old floodlights that will never shine again."
      },
      {
        icon: "🏚️",
        name: "Flooded Living Sector",
        primary: "bio-shield",
        support: ["echo-trace", "lock-breach"],
        baseDuration: 60,
        category: "environmental",
        difficulty: "standard",
        placement_priority: "normal",
        districtAffinity: "residential",
        shortDescription: "Lower housing blocks drowned to the window line, interiors slumped inward.",
        longDescription:
          "Mildew blooms on every surface; curtains drift like slow jelly. The line where the tide reaches is written as a salt band around the rooms."
      },
      {
        icon: "🪜",
        name: "Access Tower Sector",
        primary: "safeguard",
        support: ["echo-trace", "lock-breach"],
        baseDuration: 60,
        category: "infrastructure",
        difficulty: "standard",
        placement_priority: "high",
        districtAffinity: "residential",
        shortDescription: "Stair towers and emergency ladders rising above the flood.",
        longDescription:
          "Cages of stairs keep their geometry against the soft line of the water. Every landing clicks and settles when the wind turns."
      },
      {
        icon: "📦",
        name: "Cargo Intake Block",
        primary: "containment",
        support: ["quantum-scan", "reclamation"],
        baseDuration: 60,
        category: "infrastructure",
        difficulty: "standard",
        placement_priority: "normal",
        districtAffinity: "commercial",
        shortDescription: "Shallow-water cargo ramps and corroded conveyors into a warehouse face.",
        longDescription:
          "Conveyor housings gape like fish skulls. A strip of weed waves where a belt used to run. The warehouse doors are heavy with swollen wood."
      },
      {
        icon: "💰",
        name: "Finance Quarter",
        primary: "quantum-scan",
        support: ["containment", "transit-anchor"],
        baseDuration: 60,
        category: "storage",
        difficulty: "high_value",
        placement_priority: "high",
        districtAffinity: "commercial",
        shortDescription: "Collapsed teller halls and armored delivery bays, empty as vaults.",
        longDescription:
          "Coin-counting machines sit in rows like old teeth. Delivery lanes are silted to their thresholds. Everything smells faintly of metal and mildew."
      }
    ]
  },

  // ======================================================
  "cradle-expanse": {
    name: "Industrial Expanse",
    theme: "Manufacturing grids and rust plains",
    icon: "🌅",
    shortDescription:
      "Endless factory lots, idle conveyors, and yard-wide machinery asleep in rust.",
    longDescription:
      "Production once moved through these blocks in steady lines—ore in one side, finished goods out the other. Now the conveyors are seized and the cranes lean on their cables, frozen mid-motion for generations.\n\nWhen power returns, a servo might twitch or a stack light might glow and die. The wind does most of the work here: sandblasting paint, driving grit into bearings, and covering tracks almost as fast as you make them.",
    artDirection:
      "The one region whose plant stands LIGHTER than its ground: pale umber steel on dark iron-rich dirt, which is the opposite of shard-cities and is what keeps two brown regions apart. Silhouettes are industrial and long — gantry cranes straddling bays, conveyor runs seized where they stopped and carrying on across tiles, bunded tank farms. Rust everywhere, in streaks and pools and oil stains. Almost nothing green: a few dead snags, and the wind has taken the rest.",
    gates: [
      {
        icon: "🏭",
        name: "Factory Command Sector",
        // control rooms and relay racks under gantries, not a checkpoint
        recipe: "infrastructure",
        primary: "system-override",
        support: ["growth-control", "signal-mask"],
        baseDuration: 120,
        category: "security",
        difficulty: "chokepoint",
        placement_priority: "critical",
        shortDescription: "Central yard of control rooms and relay racks flanked by gantries.",
        longDescription:
          "Stack lights are long dead above cracked control housings. Catwalks draw hard lines across a sky of dust. A paper logbook lies fused into a solid wedge on a podium."
      },
      {
        icon: "🚛",
        name: "Logistics Yard Gate",
        primary: "drone-command",
        support: ["stealth-transit", "signal-relay"],
        baseDuration: 120,
        category: "security",
        difficulty: "chokepoint",
        placement_priority: "high",
        shortDescription: "Gatehouse and weighbridge spanning lanes into container lots.",
        longDescription:
          "The weighbridge plate is welded to its frame by corrosion. The gate arms point at the ground like broken fingers. Dust piles where wheels once wore it away."
      },
      {
        icon: "🪖",
        name: "Barracks Gate",
        primary: "signal-mask",
        support: ["signal-relay", "system-override"],
        baseDuration: 120,
        category: "security",
        difficulty: "chokepoint",
        placement_priority: "high",
        districtAffinity: "residential",
        shortDescription: "Perimeter fence and turnstiles into a worker barracks compound.",
        longDescription:
          "Turnstiles are frozen mid-rotation. A loudspeaker hangs by its cable, a mouth that will never speak. The ground inside is a quilt of footprints long since filled with dust."
      },
      {
        icon: "🍽️",
        name: "Commissary Shutter Sector",
        primary: "system-override",
        support: ["safeguard", "drone-command"],
        baseDuration: 120,
        category: "security",
        difficulty: "chokepoint",
        placement_priority: "high",
        districtAffinity: "commercial",
        shortDescription: "Canteen frontage sealed by roll-down shutters and collapsed awnings.",
        longDescription:
          "Menu boards peel in the heat. Trays clatter when the wind gets under them. At noon the whole block smells like hot metal and old oil."
      }
    ],
    hazards: [
      {
        icon: "⛏️",
        name: "Extraction Yard",
        primary: "drone-command",
        support: ["signal-mask", "stealth-transit"],
        baseDuration: 60,
        category: "storage",
        difficulty: "high_value",
        placement_priority: "high",
        shortDescription: "Ore hoppers, crushed drums, and loader tracks etched into dust.",
        longDescription:
          "Chutes hang open over empty ground. Piles of fines drift into dunes against dead conveyors. The only sparkle is mica in the grit."
      },
      {
        icon: "⚙️",
        name: "Assembly Corridor Block",
        primary: "drone-command",
        support: ["wild-influence", "stealth-transit"],
        baseDuration: 60,
        category: "infrastructure",
        difficulty: "standard",
        placement_priority: "normal",
        shortDescription: "Long run of conveyor frames and work platforms seized in place.",
        longDescription:
          "Tool shadows are burned into paint where the sun never reached. Belt rollers are fused in rings of red. The wind writes its own schedule on hanging tags."
      },
      {
        icon: "🗑️",
        name: "Salvage Lot",
        primary: "wild-influence",
        support: ["drone-command", "signal-relay"],
        baseDuration: 60,
        category: "environmental",
        difficulty: "standard",
        placement_priority: "normal",
        shortDescription: "Heap of stripped machinery and scaffold bones stacked by a fence.",
        longDescription:
          "Cranes stripped to spines cast laddered shadows. Wire harnesses dangle like dead vines. Footing is uncertain—everything gives a little when you test it."
      },
      {
        icon: "🔧",
        name: "Operations Hub",
        primary: "signal-relay",
        support: ["drone-command", "system-override"],
        baseDuration: 60,
        category: "infrastructure",
        difficulty: "high_value",
        placement_priority: "critical",
        shortDescription: "Control kiosks and cable troughs forming a nerve-center yard.",
        longDescription:
          "Cable trays cross like irrigation over bare concrete. A signal flag still flaps from a line that never fell. If anything comes alive, it does so in stutters and stops."
      },
      {
        icon: "🔥",
        name: "Boiler Leak Row",
        primary: "safeguard",
        support: ["signal-relay", "system-override"],
        baseDuration: 60,
        category: "environmental",
        difficulty: "standard",
        placement_priority: "normal",
        districtAffinity: "residential",
        shortDescription: "Steam-scarred lane between boiler sheds and maintenance stacks.",
        longDescription:
          "Mineral bloom and peeling paint texture every pipe. Valves whistle ghosts when the wind fits their mouths. The ground is white with the memory of heat."
      },
      {
        icon: "🥫",
        name: "Barracks Pantry Depot",
        primary: "stealth-transit",
        support: ["wild-influence", "growth-control"],
        baseDuration: 60,
        category: "storage",
        difficulty: "standard",
        placement_priority: "normal",
        districtAffinity: "residential",
        shortDescription: "Food stores and cold-room bays opened to the air long ago.",
        longDescription:
          "Shelving prints squares on the walls where cold once lived. A pyramid of cans has slumped into a gentle hill of dull tin."
      },
      {
        icon: "🏚️",
        name: "Warehouse Racking Collapse",
        primary: "safeguard",
        support: ["drone-command", "system-override"],
        baseDuration: 60,
        category: "infrastructure",
        difficulty: "standard",
        placement_priority: "high",
        districtAffinity: "commercial",
        shortDescription: "Racking aisles tumbled into a lattice of unstable paths.",
        longDescription:
          "Number placards hang at wrong angles, their arrows pointing into heaps of beams. The floor is a patchwork of shadows and sunlight where the roof gave up."
      },
      {
        icon: "📒",
        name: "Ledger Block",
        primary: "signal-relay",
        support: ["safeguard", "signal-mask"],
        baseDuration: 60,
        category: "storage",
        difficulty: "high_value",
        placement_priority: "high",
        districtAffinity: "commercial",
        shortDescription: "Accounting offices and file stores woven into a single crumbling block.",
        longDescription:
          "Adding machines sit like fossils on desks. Ledger paper lies fused into brick-hard stacks. The sun makes dust glitter where a roof panel fell away."
      }
    ]
  },

  // ======================================================
  "hollow-core": {
    name: "Hollow Core",
    theme: "Sublevel transit and collapsed substructures",
    icon: "⚫",
    shortDescription:
      "Buried civic arteries: pillared halls, rail spurs and maintenance blocks, half of them sealed by the collapse.",
    longDescription:
      "Everything aboveground depended on what ran below—transit rings, service corridors, and power trunks stitched together across sublevels. Collapse sealed off some blocks entirely; others breathe stale air through long cracks in the concrete.\n\nOccasionally, indicator strips flare for a moment, enough to show distance receding into black. Then the dark returns, settling even heavier than before.",
    artDirection:
      "A hall, not a street. Pillars on a regular pitch march across every tile and carry on into the next one — that rhythm is the only thing a top-down view has to say there is a ceiling overhead. Rock mass laps in from the corners and outer edges where the roof has come down, sealing parts of blocks, carved back around the rooms and never across a route. Rail beds and cable trays run through. Light is the region's subject: almost none of it, teal bioluminescence for ambient, warm pools under the daylight wells, and the darkness graded so it is heaviest against the rock and lightest over the floor. The concrete itself is NOT black — the palette has to survive the dark and still show which parts are wall.",
    gates: [
      {
        icon: "🚇",
        name: "Sublevel Transit Hub",
        primary: "transit-anchor",
        support: ["signal-relay", "drone-command"],
        baseDuration: 120,
        category: "storage",
        difficulty: "chokepoint",
        placement_priority: "critical",
        shortDescription: "Collapsed interchange where multiple rail spurs once crossed.",
        longDescription:
          "Pillars hold up more air than concrete now. Signal plates stand blank along a bed of gravel and dust. The far wall is a shadow that pretends to be distance."
      },
      {
        icon: "🔌",
        name: "Power Relay Block",
        primary: "signal-relay",
        support: ["quantum-scan", "stealth-transit"],
        baseDuration: 120,
        category: "infrastructure",
        difficulty: "chokepoint",
        placement_priority: "high",
        shortDescription: "Cable trays and junction housings clustered around a maintenance court.",
        longDescription:
          "Insulators line a wall like teeth. A skein of cables has slumped from the ceiling into a black loop. Dust swirls when you step, then remembers how to be still."
      },
      {
        icon: "🚪",
        name: "Habitat Access Sector",
        primary: "lock-breach",
        support: ["quantum-scan", "signal-mask"],
        baseDuration: 120,
        category: "security",
        difficulty: "chokepoint",
        placement_priority: "high",
        districtAffinity: "residential",
        shortDescription: "Door clusters and service nooks leading into sealed living blocks.",
        longDescription:
          "Numbered arrows march along the wall toward units you’ll never enter. A cart sits on square wheels of dust. The air is dry and old, like a book left open too long."
      },
      {
        icon: "🛒",
        name: "Commerce Access Sector",
        primary: "signal-mask",
        support: ["lock-breach", "echo-trace"],
        baseDuration: 120,
        category: "security",
        difficulty: "chokepoint",
        placement_priority: "high",
        districtAffinity: "commercial",
        shortDescription: "Service corridors feeding into a forgotten plaza and loading cutouts.",
        longDescription:
          "A map board with no map. A mural worn to colors without shapes. The corridor breathes the same cold air in and out, as if thinking of a crowd."
      }
    ],
    hazards: [
      {
        icon: "📦",
        name: "Storage Compound",
        primary: "quantum-scan",
        support: ["lock-breach", "growth-control"],
        baseDuration: 60,
        category: "storage",
        difficulty: "high_value",
        placement_priority: "critical",
        shortDescription: "Box canyons of crates and shelving under a low slab ceiling.",
        longDescription:
          "Label paint lingers like ghosts on metal faces. A broken roller door shows a sliver of stale light. The air tastes like paper and iron."
      },
      {
        icon: "🧱",
        name: "Maintenance Network Sector",
        primary: "growth-control",
        support: ["signal-relay", "drone-command"],
        baseDuration: 60,
        category: "environmental",
        difficulty: "standard",
        placement_priority: "normal",
        shortDescription: "Grid of service corridors, tool bays, and grates over dark shafts.",
        longDescription:
          "Tool outlines haunt the walls. Floor grates ring when crossed, answering back from below. The network feels larger than the ground it serves."
      },
      {
        icon: "☢️",
        name: "Reactor Service Lot",
        primary: "stealth-transit",
        support: ["transit-anchor", "quantum-scan"],
        baseDuration: 60,
        category: "power",
        difficulty: "high_value",
        placement_priority: "high",
        shortDescription: "Shield doors and access gantries around a dead core housing.",
        longDescription:
          "Warning chevrons fade across concrete that still remembers heat. Cable stumps jut like bones from panel edges. Everything hums with a silence that feels heavy."
      },
      {
        icon: "🛠️",
        name: "Data Spine Junction",
        primary: "echo-trace",
        support: ["stealth-transit", "signal-mask"],
        baseDuration: 60,
        category: "infrastructure",
        difficulty: "standard",
        placement_priority: "normal",
        shortDescription: "Trunk conduits converge into a node of cracked panels and conduit arcs.",
        longDescription:
          "Cables repeat the curve of the arching ceiling. Labels peel off in satisfying curls. Your headlamp shows dust floating like a slow snowfall."
      },
      {
        icon: "🛗",
        name: "Access Network Block",
        primary: "transit-anchor",
        support: ["echo-trace", "growth-control"],
        baseDuration: 60,
        category: "infrastructure",
        difficulty: "standard",
        placement_priority: "normal",
        districtAffinity: "residential",
        shortDescription: "Lift shafts, catwalks, and ladders crisscrossing a deep chamber.",
        longDescription:
          "Mesh floors draft cool air over your boots. The shaft walls are a history of paint colors layered like sediment. The dark holds its shape until you look into it."
      },
      {
        icon: "🧸",
        name: "Nursery Annex Sector",
        primary: "echo-trace",
        support: ["stealth-transit", "signal-mask"],
        baseDuration: 60,
        category: "environmental",
        difficulty: "standard",
        placement_priority: "normal",
        districtAffinity: "residential",
        shortDescription: "Quiet block of sealed playrooms, murals eroded to color fields.",
        longDescription:
          "Small chairs lie on their backs like beetles. A mobile has tangled into a single shape. Everyone left a long time ago, but the rooms still feel paused."
      },
      {
        icon: "📚",
        name: "Records Quarter",
        primary: "stealth-transit",
        support: ["bio-shield", "transit-anchor"],
        baseDuration: 60,
        category: "storage",
        difficulty: "high_value",
        placement_priority: "high",
        districtAffinity: "commercial",
        shortDescription: "Storage stacks and data cages in a commercial sub-block.",
        longDescription:
          "Cage doors have slumped out of square. A spill of binders lies like fallen bricks. Your light finds the path numbers and makes them legible for a heartbeat."
      },
      {
        icon: "🏬",
        name: "Commerce Plaza",
        primary: "signal-mask",
        support: ["bio-shield", "transit-anchor"],
        baseDuration: 60,
        category: "environmental",
        difficulty: "standard",
        placement_priority: "normal",
        districtAffinity: "commercial",
        shortDescription: "Lower concourse ringed by shuttered storefronts and dead kiosks.",
        longDescription:
          "A fountain bowl holds dust instead of water. Kiosk screens are gray mirrors. Every footstep you make has an echo measured in seconds."
      }
    ]
  }
};

// ===============================
// DYNAMIC NAMING SYSTEM (5 options, grounded + region flavor)
// ===============================

// Global brand pools
export const ROOM_NAME_BRANDS = {
  civic: ["CivicSys", "CityWorks", "MetroGrid", "Municipalix", "UrbanNet"],
  industrial: ["StrataDyne", "HelixCore", "OmniForge", "FerrumWorks", "Rivetron"],
  utilities: ["Nextera", "Voltline", "Auralis", "BlueArc", "GridNova"],
  retail: ["MetroTrade", "ApexMart", "Cornerline", "Shoreline Logistics", "Mercantis"],
  bio: ["VerdanCo", "BioNet", "PhytoLabs", "GreenRidge", "GenExis"],
  defense: ["WardTek", "Rampart Systems", "Bastionex", "IronCrest", "Shieldline"],
  data: ["Archivum", "Memora", "DataPlain", "ByteHarbor", "Recorda"],
};

// Region-specific brands
export const ROOM_NAME_REGIONAL_BRANDS = {
  "shard-cities": [
    "MetroGuard",
    "CivicSys",
    "CityWorks",
    "UrbanNet",
    "MetroGrid",
    "ApexLift",
    "Blockline Security",
    "SentraCore",
    "Wayfinder Transit",
    "Arcology Services",
  ],
  "verdant-reclamation": [
    "VerdanCo",
    "PhytoLabs",
    "BioNet",
    "Greencap Utilities",
    "GardenWorks",
    "Nutrivane",
    "IrrigaTech",
    "SeedSafe",
    "Rootline Systems",
    "Auralis Bio",
  ],
  "sunken-bastions": [
    "HarborFleet",
    "DryDock Systems",
    "Bastionex",
    "BlueArc Marine",
    "Deepline",
    "Breakwater Logistics",
    "WardTek Maritime",
    "Pierstone",
    "Tidewell",
    "Seaforth Controls",
  ],
  "cradle-expanse": [
    "FerrumWorks",
    "OmniForge",
    "StrataDyne",
    "Rivetron",
    "Gantry & Co.",
    "Loadstar Fabrication",
    "ConveyorDynamics",
    "IronCrest",
    "Forgeway",
    "MachinaGrid",
  ],
  "hollow-core": [
    "CivicSys Sublevel",
    "MetroGrid Infrastructure",
    "Nextera Utility",
    "ServiceWay",
    "Subterra Transit",
    "CorridorWorks",
    "Spanline",
    "NodeWest",
    "ControlPoint",
    "Concourse Ops",
  ],
};

// Descriptors per category (plus a couple composites)
export const ROOM_NAME_DESCRIPTORS = {
  power: ["Power Node", "Junction Panel", "Substation", "Transformer Bay", "Distribution Rack"],
  storage: ["Storage Bay", "Records Vault", "Supply Depot", "Inventory Room", "Archive Annex"],
  security: ["Security Gate", "Checkpoint", "Access Control", "Barrier Post", "Perimeter Gate"],
  infrastructure: ["Relay Node", "Transit Hub", "Service Corridor", "Maintenance Bay", "Control Room"],
  environmental: ["Containment Zone", "Hazard Control", "Firebreak", "Isolation Area", "Cleanup Sector"],
  wildlife: ["Hive Chamber", "Containment Atrium", "Observation Pen", "Keeper Station", "Handling Room"],
  power_infra: ["Switchgear Room", "Feeder Junction", "Utility Interface", "Load Center", "Breaker Hall"],
  records_data: ["Records Cache", "Data Vault", "Archive Store", "Tape Annex", "Records Room"],
};

// Generic qualifiers + region-specific qualifiers
export const ROOM_NAME_QUALIFIERS = [
  "Block A",
  "Block C",
  "Sector 7",
  "Sector 9",
  "East Annex",
  "West Annex",
  "North Wing",
  "South Wing",
  "Sublevel 2",
  "Sublevel 3",
  "Sublevel 4",
  "Tower 2",
  "Depot Lane",
  "Service Row",
  "Line 3",
  "Bay 12",
  "Bay 17",
  "Midtown Annex",
];

export const ROOM_NAME_REGION_QUALIFIERS = {
  "shard-cities": ["Midtown Annex", "Tower 3", "Sector 9", "Skyway Deck", "Service Row"],
  "verdant-reclamation": ["Greenhouse 2A", "Irrigation Loop B", "Seed Wing", "Containment Hall", "Botany Annex"],
  "sunken-bastions": ["Pier 6", "Drydock 3", "Bulkhead E", "Sublevel Pump 2", "Bilge Corridor"],
  "cradle-expanse": ["Line 3", "Bay 12", "Casting Floor", "Yard 5", "Maintenance Track"],
  "hollow-core": ["Sublevel 4", "Service Spine", "Transit Ring", "Access Spur", "Node J"],
};

// Brand affinity by region/zone/category
export const ROOM_NAME_BRAND_AFFINITY = {
  "shard-cities": ["civic", "utilities", "data", "industrial", "retail", "defense"],
  "verdant-reclamation": ["bio", "utilities", "civic", "industrial", "data"],
  "sunken-bastions": ["defense", "utilities", "industrial", "civic", "data"],
  "cradle-expanse": ["industrial", "utilities", "civic", "retail", "data"],
  "hollow-core": ["civic", "utilities", "industrial", "data", "retail"],
  zone: {
    residential: ["civic", "utilities", "bio", "retail"],
    commercial: ["retail", "civic", "data", "utilities"],
    industrial: ["industrial", "utilities", "defense"],
    security: ["defense", "civic", "utilities"],
    core: ["data", "defense", "industrial"],
    transit: ["civic", "utilities", "industrial"],
    peripheral: ["civic", "industrial", "retail"],
    entry: ["civic", "utilities", "retail"],
  },
  category: {
    power: ["utilities", "industrial", "civic"],
    storage: ["industrial", "retail", "data"],
    security: ["defense", "civic", "utilities"],
    infrastructure: ["civic", "utilities", "industrial"],
    environmental: ["civic", "industrial", "utilities"],
    wildlife: ["bio", "civic", "utilities"],
  },
};

// RNG (seeded)
function xmur3(str) {
  let h = 1779033703 ^ str.length;
  for (let i = 0; i < str.length; i++) {
    h = Math.imul(h ^ str.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  return () => {
    h = Math.imul(h ^ (h >>> 16), 2246822507);
    h = Math.imul(h ^ (h >>> 13), 3266489909);
    return (h ^ (h >>> 16)) >>> 0;
  };
}
function mulberry32(a) {
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
export function makePRNG(seedStr = "") {
  const seed = xmur3(seedStr)();
  return mulberry32(seed);
}
function pick(array, rng) {
  return array[Math.floor(rng() * array.length)];
}

// Brand pool builder with regional weighting
function getBrandPool({ regionKey, zone, category }) {
  const base = ROOM_NAME_BRAND_AFFINITY[regionKey] || ["civic", "industrial", "utilities"];
  const zoneBias = ROOM_NAME_BRAND_AFFINITY.zone[zone] || [];
  const catBias = ROOM_NAME_BRAND_AFFINITY.category[category] || [];
  const order = [...base, ...zoneBias, ...catBias];

  const weighted = [];
  const regional = ROOM_NAME_REGIONAL_BRANDS[regionKey] || [];
  for (let i = 0; i < 3; i++) weighted.push(...regional); // heavy regional weight

  order.forEach((bucket, idx) => {
    const list = ROOM_NAME_BRANDS[bucket] || [];
    const weight = idx < 2 ? 2 : 1;
    for (let i = 0; i < weight; i++) weighted.push(...list);
  });

  return weighted.length ? weighted : [].concat(...Object.values(ROOM_NAME_BRANDS));
}

function getDescriptorPool(category) {
  if (category === "power" && ROOM_NAME_DESCRIPTORS.power_infra) {
    return [...ROOM_NAME_DESCRIPTORS.power, ...ROOM_NAME_DESCRIPTORS.power_infra];
  }
  if (category === "storage" && ROOM_NAME_DESCRIPTORS.records_data) {
    return [...ROOM_NAME_DESCRIPTORS.storage, ...ROOM_NAME_DESCRIPTORS.records_data];
  }
  return ROOM_NAME_DESCRIPTORS[category] || ROOM_NAME_DESCRIPTORS.infrastructure;
}

function pickQualifier(regionKey, rng) {
  const base = ROOM_NAME_QUALIFIERS;
  const regional = ROOM_NAME_REGION_QUALIFIERS[regionKey] || [];
  const pool = [...regional, ...regional, ...base]; // 2:1 favor regional
  return pool.length ? pool[Math.floor(rng() * pool.length)] : "";
}

// Core API: returns 5 grounded, region-flavored name options
export function generateRoomNames(
  {
    baseName, // string e.g. "Housing Power Node"
    regionKey, // one of raidRegions keys
    zone, // "residential" | "commercial" | ...
    category, // "power" | "storage" | "security" | ...
    seed = "", // stable key (coords/roomId)
  },
  count = 5
) {
  const rng = makePRNG(`${seed}|${baseName}|${regionKey}|${zone}|${category}`);
  const brands = getBrandPool({ regionKey, zone, category });
  const descs = getDescriptorPool(category);
  const names = new Set();

  const canon = [baseName, `${baseName} – ${pickQualifier(regionKey, rng)}`];

  let safety = 200;
  while (names.size < count && safety-- > 0) {
    const brand = pick(brands, rng);
    const desc = pick(descs, rng);
    const useBaseTerm = rng() < 0.35; // 35% anchor to base term
    const qualifier = rng() < 0.65 ? ` – ${pickQualifier(regionKey, rng)}` : "";

    const candidate = useBaseTerm ? `${brand} ${baseName}${qualifier}` : `${brand} ${desc}${qualifier}`;

    names.add(candidate);
  }

  for (const c of canon) {
    if (names.size >= count) break;
    names.add(c);
  }
  return Array.from(names).slice(0, count);
}

// ===============================
// RENDER CONFIG (consumed by js/modules/roomImageGen.js)
// ===============================
// Pure JSON-compatible data describing how tiles are drawn — no functions.
// Designing a new room look means editing config here, not render code.
//
// regions.<key>: per-region theme
//   backdrop — floor treatment (city|greenhouse|harbor|foundry|underground)
//   overlay  — weathering pass (dust|vines|flood|rust|dark)
//   tune     — knobs, merged over defaultTune:
//     buildingScale — multiplier on roof footprints (bigger blocks)
//     treeDensity   — multiplier on the vegetation scatter counts
//     roadWear      — 0..1 degradation of road connectors
//     shadowAngle/shadowAlpha/shadowLength — static shadow direction,
//       darkness, and max dawn/dusk offset (animated)
//     lightUptime / lightFlickerSpeed — intermittent powered lights
//     sinkholeChance — environmental rooms: sinkhole vs overgrowth
//     lightRays     — daylight-well density (underground regions only)
//     cloudType     — cloud preset name (see CLOUD_TYPES in the renderer)
//   pal      — palette; keys are referenced by name from recipes
//
// recipes.<key>: what a room type draws, interpreted by the renderer.
//   Either { elements: [...] } or { variants: [{ elements }, ...] } —
//   variants are an even seeded pick, or gated by a tune knob when
//   variantKey is set (variants[1] is chosen when rng < tune[variantKey]).
//   Recipe-level settings (all optional):
//     (roads are not a recipe concern — they come from the generated road
//      network stamped on each room; see roadNetwork-core.js)
//     treeDensity: n   — vegetation multiplier for this room type, stacked
//                        on the region's tune.treeDensity (default 1)
//   Each element spec: { el: '<registry name>', ...params } plus optional
//     chance: 0..1     — probability the element appears
//     repeat: n|[a,b]  — draw n times, params re-rolled each time
//     when: { tune, gte } — only when a tune knob reaches a threshold
//   Param values: scalar, [min,max] range, or { pick: [v, v, ...] }.
//   Rooms in this file may also set `recipe: '<name>'` to override the
//   name-keyword selection entirely.
export const RENDER_CONFIG = {
  defaultTune: {
    buildingScale: 1, treeDensity: 1, roadWear: 0.15,
    shadowAngle: 45, shadowAlpha: 0.85, shadowLength: 6,
    lightUptime: 0.12, lightFlickerSpeed: 1,
    sinkholeChance: 0.5, lightRays: 0,
    // How often a region's own signature props appear — the one pass that is
    // about a PARTICULAR place rather than about a town. 0 turns them off.
    signature: 1,
    // How far inland the sea reaches on a region with a shoreline. 0 = no sea,
    // which is every region but the drowned one.
    floodLevel: 0,
    // How much rock mass has come down around the built ground. Sublevels only.
    enclosure: 0,
    cloudType: 'scattered',
  },

  regions: {
    // Two tiers of architecture per region. `struct` is the ordinary building
    // stock — the sheds, blocks and terraces the whole map is made of. `mass`
    // is the monumental tier: the towers, glasshouses, sea walls, mill frames
    // and pillar halls a region builds at a scale nothing else on the tile
    // reaches. Keeping them apart is what lets shard-cities have warm brown
    // roofs on its small buildings AND cool concrete over them, instead of
    // picking one and reading as somewhere else's town in a filter.
    //
    // The rule both tiers answer to: a structure must separate from the ground
    // it stands on in VALUE, not just in hue. Two regions with the same value
    // relationship look like the same place no matter what colour they are.
    'shard-cities': {
      backdrop: 'city', overlay: 'dust',
      tune: { buildingScale: 1.3, treeDensity: 3, roadWear: 0.35, shadowAngle: 45, shadowAlpha: 0.85, shadowLength: 6, lightUptime: 0.18, lightFlickerSpeed: 1, sinkholeChance: 0.5, lightRays: 0, signature: 1, cloudType: 'broken' },
      pal: {
        far: '#1c2333',
        // Warm brick and brown roofing, but a stop LIGHTER than it used to be
        // and over darker tarmac. The hue contrast against grey ground always
        // worked; the value contrast did not — seven luma points apart, the
        // roofs sank into the street at map scale and the region read as one
        // undifferentiated grey-brown field.
        struct: '#8a705c', structDark: '#15171e', structLight: '#b99983',
        mass: '#79828f', massDark: '#2a3038', massLight: '#a9b6c4',
        ground1: '#3f434e', ground2: '#4b5160', road: '#20242f',
        metal: '#6b7893', rust: '#8a6a4a', accent: '#a9bee0',
        veg1: '#3b5841', veg2: '#5d8058', water1: '#33455c',
        water2: '#55708c', glow: '#ffb45e',
      },
    },
    'verdant-reclamation': {
      backdrop: 'greenhouse', overlay: 'vines',
      recipes: { storage: 'storage-seed' },
      tune: { buildingScale: 1.1, treeDensity: 4, roadWear: 0.75, cloudType: 'scattered', lightUptime: 0.05, signature: 1.1 },
      pal: {
        far: '#4c6350',
        struct: '#7d8a8c', structDark: '#455256', structLight: '#b0bcba',
        mass: '#9fb0a8', massDark: '#4a5a52', massLight: '#cfdcd2',
        // The ground is most of the tile, so it decides whether the region reads
        // as flat — and a light mid-green under light grey frames was the whole
        // problem. Taken down to shaded loam, but only halfway: the first attempt
        // went the full distance and the region lost its light instead of gaining
        // contrast. The canopy is darker still, the structures sit well above
        // both, and the tile has a range without going gloomy.
        ground1: '#3e5a34', ground2: '#4f5b30', road: '#42402f',
        metal: '#8b9598', rust: '#8a7050', accent: '#d8d2a8',
        // Deep canopy. These are the darkest greens on the tile and the shapes
        // they make are what the eye reads first, so they carry the contrast.
        veg1: '#254d2a', veg2: '#4a7a3a', water1: '#3f7a70', water2: '#6fb0a4',
        glow: '#f4e9c8',
      },
    },
    'sunken-bastions': {
      backdrop: 'harbor', overlay: 'flood',
      recipes: { storage: 'storage-quay' },
      tune: { buildingScale: 1.15, treeDensity: 1, roadWear: 0.4, cloudType: 'overcast', sinkholeChance: 0.35, floodLevel: 1, signature: 1 },
      pal: {
        far: '#3a4854',
        struct: '#6d8290', structDark: '#2c3841', structLight: '#9db6c2',
        mass: '#7d8f99', massDark: '#2c3841', massLight: '#a8bcc4',
        ground1: '#4a5450', ground2: '#5b6763', road: '#39434c',
        metal: '#7b8a93', rust: '#b06a3d', accent: '#8fc4cc',
        veg1: '#3c5f4c', veg2: '#58825e', water1: '#2f5c6b', water2: '#5a8f9b',
        glow: '#ffd27f',
      },
    },
    'cradle-expanse': {
      backdrop: 'foundry', overlay: 'rust',
      recipes: { storage: 'storage-silo' },
      tune: { buildingScale: 1.25, treeDensity: 0.8, roadWear: 0.3, shadowAngle: 15, cloudType: 'wispy', signature: 1 },
      pal: {
        far: '#4a3226',
        // The one region whose plant is LIGHTER than its ground. Dark dirt with
        // pale steel on it is the opposite value relationship to shard-cities'
        // dark roofs on grey tarmac, which is what stops two brown regions
        // reading as one.
        struct: '#a5825c', structDark: '#3a2417', structLight: '#d2ad7f',
        mass: '#a89279', massDark: '#4a3a28', massLight: '#d3c0a4',
        ground1: '#6b5137', ground2: '#7d6244', road: '#3d3226',
        metal: '#9c8a6e', rust: '#b55a2b', accent: '#e8b060',
        veg1: '#635831', veg2: '#807236', water1: '#5d564a', water2: '#7a705c',
        glow: '#ffcf7a',
      },
    },
    'hollow-core': {
      backdrop: 'underground', overlay: 'dark',
      // Everything here is lifted a stop from where it was: a sublevel is dark
      // because of its LIGHT, not because its concrete is black. The palette
      // has to survive a x0.5 night tint and a darkness wash on top and still
      // show which parts of the tile are wall.
      tune: { buildingScale: 1.1, treeDensity: 1, roadWear: 0.25, lightRays: 1.5, enclosure: 1, signature: 1.2 },
      pal: {
        far: '#07080c',          // the void below a chasm — must stay near-black
        struct: '#5c6782', structDark: '#1b1f2a', structLight: '#7f8cad',
        mass: '#6b7690', massDark: '#1b1f2a', massLight: '#909dbb',
        ground1: '#333a46', ground2: '#454d5d', road: '#1c212b',
        metal: '#5b6478', rust: '#5a4630', accent: '#4fa0a0',
        veg1: '#2d473f', veg2: '#41684e', water1: '#234049', water2: '#356470',
        glow: '#ffc46a',
      },
    },
  },

  recipes: {
    power: { variants: [
      { elements: [
        { el: 'transformerPad', x: [9, 21], y: [9, 21] },
        { el: 'transformerPad', x: [84, 93], y: [84, 93] },
        { el: 'rect', x: 30, y: [24, 30], w: 3, h: 60, color: 'structDark' },   // trunk cable
        { el: 'rect', x: 30, y: 87, w: [51, 60], h: 3, color: 'structDark' },
        { el: 'fence', x: 6, y: 6, w: 36, h: 36 },
      ] },
      { elements: [
        { el: 'tank', x: [24, 33], y: [24, 33], r: 18 },                          // fuel tank + feed pipes
        { el: 'pipeline', x: 6, y: [87, 96], w: 108 },
        { el: 'transformerPad', x: [84, 93], y: [15, 24] },
      ] },
    ] },

    storage: { variants: [
      { elements: [
        { el: 'crates', x: [9, 15], y: [9, 18], cols: 3, rows: 2 },
        { el: 'crates', x: [78, 87], y: [78, 87], cols: 2, rows: 2 },
        { el: 'tank', x: [90, 99], y: [21, 30], r: 15 },                         // silo top
        { el: 'building', x: [9, 15], y: [78, 84], w: 27, h: 24 },                // warehouse shed
      ] },
      { elements: [
        { el: 'container', x: [9, 18], y: [9, 15], vert: 0 },                    // container yard
        { el: 'container', x: [9, 18], y: [27, 33], vert: 0 },
        { el: 'container', x: [81, 93], y: [75, 84], vert: 1 },
        { el: 'crates', x: [15, 24], y: [81, 90], cols: 2, rows: 2 },
      ] },
    ] },

    // ---- region-specific stock ----
    // A region may point one of the shared recipes at its own layout via
    // `recipes` on its RENDER_CONFIG.regions entry. The signature layer adds a
    // region's landmarks ON TOP of whatever a tile drew; this changes what the
    // tile is made of in the first place, which is the difference between a
    // harbour with containers on it and a harbour built out of them.
    //
    // Kept deliberately few. Every one of these is a second layout to maintain
    // for a recipe that already had one, so it has to earn its place by being
    // somewhere the shared version reads plainly wrong.
    'storage-quay': { variants: [
      { elements: [                                                              // stacked container rows
        { el: 'container', x: [9, 15], y: [12, 18], vert: 0 },
        { el: 'container', x: [9, 15], y: [27, 33], vert: 0 },
        { el: 'container', x: [9, 15], y: [42, 48], vert: 0 },
        { el: 'container', x: [78, 90], y: [69, 81], vert: 1 },
        { el: 'crates', x: [66, 75], y: [15, 24], cols: 2, rows: 2 },
      ] },
      { elements: [                                                              // transit shed on the quay
        { el: 'building', x: [15, 24], y: [15, 24], w: 60, h: 27 },
        { el: 'container', x: [15, 27], y: [78, 87], vert: 0 },
        { el: 'container', x: [60, 72], y: [75, 84], vert: 1 },
      ] },
    ] },
    'storage-silo': { variants: [
      { elements: [                                                              // the silo farm
        { el: 'silo', x: [21, 27], y: [24, 30], r: 13 },
        { el: 'silo', x: [21, 27], y: [69, 75], r: 11 },
        { el: 'silo', x: [72, 81], y: [45, 54], r: 14 },
        { el: 'pipeline', x: 33, y: [51, 57], w: [30, 39] },
      ] },
      { elements: [                                                              // hoppers and a loading shed
        { el: 'silo', x: [78, 87], y: [24, 33], r: 12 },
        { el: 'tank', x: [24, 33], y: [21, 30], r: 15 },
        { el: 'building', x: [15, 24], y: [72, 81], w: 42, h: 24 },
        { el: 'crates', x: [72, 84], y: [78, 90], cols: 2, rows: 2 },
      ] },
    ] },
    'storage-seed': { variants: [
      { elements: [                                                              // seed store under glass
        { el: 'glassSpan', x: [12, 18], y: [15, 24], w: 66, h: 27 },
        { el: 'silo', x: [24, 30], y: [78, 87], r: 11 },
        { el: 'silo', x: [54, 63], y: [81, 90], r: 9 },
        { el: 'crates', x: [84, 93], y: [69, 78], cols: 2, rows: 2 },   // pallets in the yard
      ] },
      { elements: [
        { el: 'silo', x: [18, 24], y: [21, 30], r: 12 },
        { el: 'glassSpan', x: [63, 72], y: [21, 30], w: 33, h: 66 },
        { el: 'crates', x: [15, 24], y: [78, 87], cols: 3, rows: 2 },
      ] },
    ] },

    security: { elements: [
      { el: 'fence', x: 15, y: 15, w: 90, h: 90, gaps: 1 },
      { el: 'barrier', x: 24, y: 24 },
      { el: 'barrier', x: 81, y: 81 },
      { el: 'vehicle', chance: 0.4, x: [72, 84], y: [78, 87], vert: { pick: [0, 1] } }, // patrol wreck
      { el: 'building', x: [72, 81], y: [21, 30], w: 18, h: 15 },                 // guard post
    ] },

    infrastructure: { variants: [
      { elements: [
        { el: 'building', x: [9, 15], y: [9, 15], w: [36, 45], h: [27, 33] },
        { el: 'pipeline', x: 6, y: [87, 96], w: 108 },                          // surface pipe run
        { el: 'rect', x: [81, 93], y: [15, 24], w: 15, h: 15, color: 'metal' },    // vent pad
      ] },
      { elements: [
        { el: 'tower', x: [15, 24], y: [15, 24] },                                 // mast + cable spans
        { el: 'rect', x: 24, y: 30, w: 3, h: [54, 66], color: 'structDark' },
        { el: 'rect', x: 24, y: 90, w: [54, 66], h: 3, color: 'structDark' },
        { el: 'tank', x: [84, 93], y: [81, 90], r: 12 },
        { el: 'rect', x: [81, 93], y: [15, 24], w: 15, h: 15, color: 'metal' },
      ] },
    ] },

    transit: { elements: [
      { el: 'rect', x: [18, 30], y: [72, 84], w: 6, h: 27, color: 'struct' },    // fallen girders
      { el: 'rect', x: [78, 90], y: [15, 27], w: 27, h: 6, color: 'struct' },
      { el: 'vehicle', x: [12, 24], y: [12, 24], vert: { pick: [0, 1] } },         // abandoned wrecks
      { el: 'vehicle', chance: 0.6, x: [78, 90], y: [78, 90], vert: { pick: [0, 1] } },
      { el: 'rubble', x: 12, y: 12, w: 93, h: 93, n: 12 },
    ] },

    environmental: { variantKey: 'sinkholeChance', variants: [
      { elements: [                                                            // overgrowth
        { el: 'crack', repeat: 4, x: [9, 105], y: [9, 96], len: [18, 36] },
        { el: 'rubble', x: 6, y: 6, w: 105, h: 105, n: 18 },
        { el: 'foliage', x: [15, 36], y: [78, 102], size: [4, 6] },
        { el: 'foliage', x: [81, 102], y: [15, 36], size: [4, 6] },
        { el: 'tree', x: [81, 99], y: [81, 99], r: [9, 12] },
      ] },
      { elements: [                                                            // sinkhole
        // The hole goes down LAST. Scatter drawn over it is debris hanging in
        // a shaft; drawn under it, the hole simply swallows whatever fell in
        // and what is left reads as ground that gave way. The pit brings its
        // own spoil ring with it, so the rim still has a heap on it.
        { el: 'rubble', x: 12, y: 12, w: 93, h: 93, n: 14 },
        { el: 'sinkhole', x: [36, 78], y: [36, 78] },
      ] },
    ] },

    records: { variants: [
      { elements: [
        { el: 'building', x: [12, 18], y: [12, 18], w: [36, 42], h: [27, 33] },
        { el: 'building', when: { tune: 'buildingScale', gte: 1.15 }, x: [78, 87], y: [12, 18], w: 24, h: 21 },
        { el: 'speckle', x: 6, y: 6, w: 105, h: 105, n: 8, color: 'structLight', alpha: 0.8 }, // scattered paper
      ] },
      { elements: [
        { el: 'building', x: [12, 18], y: [12, 18], w: [36, 42], h: [27, 33] },
        { el: 'building', x: [78, 84], y: [78, 84], w: [24, 30], h: [21, 27] },
        { el: 'building', when: { tune: 'buildingScale', gte: 1.15 }, x: [78, 87], y: [12, 18], w: 24, h: 21 },
        { el: 'speckle', x: 6, y: 6, w: 105, h: 105, n: 8, color: 'structLight', alpha: 0.8 },
      ] },
      // The two above put a hall in each corner, which leaves a road nowhere to
      // go: mirroring the layout only moves which corner is which. These two
      // range the halls along ONE edge instead, so half the tile stays open —
      // and the mirror decides which half, to suit wherever the road landed.
      // Ranged east-west, for a road crossing that way:
      { elements: [
        { el: 'building', x: [9, 15], y: 9, w: [45, 54], h: [27, 33] },
        { el: 'building', x: [66, 72], y: 9, w: [33, 39], h: [24, 30] },
        { el: 'speckle', x: 6, y: 6, w: 105, h: 105, n: 8, color: 'structLight', alpha: 0.8 },
      ] },
      // and ranged north-south, for one crossing the other way:
      { elements: [
        { el: 'building', x: 9, y: [9, 15], w: [27, 33], h: [42, 48] },
        { el: 'building', x: 9, y: [66, 72], w: [24, 30], h: [33, 39] },
        { el: 'speckle', x: 6, y: 6, w: 105, h: 105, n: 8, color: 'structLight', alpha: 0.8 },
      ] },
    ] },

    habitation: { variants: [
      { elements: [
        { el: 'building', x: [9, 15], y: [9, 15], w: [30, 36], h: [24, 30] },
        { el: 'building', x: [78, 84], y: [12, 18], w: [27, 33], h: [21, 27] },
        { el: 'building', x: [12, 18], y: [81, 87], w: [27, 33], h: [21, 27] },
        { el: 'building', when: { tune: 'buildingScale', gte: 1.15 }, x: [81, 87], y: [81, 87], w: 24, h: 21 },
        { el: 'rect', repeat: 4, x: [78, 105], y: [81, 105], w: 6, h: 6, color: 'veg1' }, // planters
        { el: 'bush', x: [81, 102], y: [18, 36] },
      ] },
      { elements: [
        { el: 'building', x: [9, 15], y: [9, 15], w: [42, 48], h: [36, 42] },    // one large block
        { el: 'tree', x: [84, 96], y: [15, 27], r: 9 },
        { el: 'bush', x: [81, 102], y: [18, 36] },
      ] },
      // Housing is the one thing that is genuinely built in rows, so two of
      // them: one long range and a shorter one behind it, facing the other way
      // as a back street does. The gap between is the lane.
      { elements: [
        { el: 'terrace', x: [9, 15], y: [9, 15], w: [54, 69], h: [21, 27] },
        { el: 'terrace', chance: 0.8, x: [15, 27], y: [69, 75], w: [42, 57], h: [21, 24] },
        { el: 'bush', x: [84, 102], y: [45, 63] },
      ] },
      // and the same row stood on end, so a mirror is not the only way this
      // recipe ever runs north-south
      { elements: [
        { el: 'terrace', x: [9, 15], y: [9, 15], w: [21, 27], h: [54, 69], vert: 1 },
        { el: 'terrace', chance: 0.8, x: [69, 75], y: [15, 27], w: [21, 24], h: [42, 57], vert: 1 },
        { el: 'bush', x: [45, 63], y: [84, 102] },
      ] },
    ] },

    commerce: { variants: [
      { elements: [
        { el: 'storefront', x: [12, 18], y: [9, 15], w: [54, 66], h: [27, 33] },
        { el: 'stalls' },                                                      // market stalls
      ] },
      { elements: [
        { el: 'storefront', x: [12, 18], y: [9, 15], w: [54, 66], h: [27, 33] },
        { el: 'crates', x: [78, 90], y: [75, 87], cols: 2, rows: 2 },           // stock left on the lot
      ] },
      // A parade: shops in a row under one roof, with the lot they served in
      // front of them. One storefront alone reads as a retail park; the row is
      // what a high street is.
      //
      // Both ways round, and both shallower than the storefronts above, because
      // this is the only thing in the recipe that fits on a tile the road runs
      // through. A carriageway swings up to EDGE_SWING off centre, so the
      // clear ground beside one is about 43 units — and a storefront is 46 to
      // 49 deep once the region's scale and its awning are on it. It does not
      // fit in either orientation, in any mirror, which left a market tile on a
      // road as bare ground and some crates. A range 27 to 35 deep clears the
      // carriageway with room to spare, and the north-south copy does the same
      // for a road running the other way, which no mirror of an east-west range
      // can do. The layout pass prefers whichever of the five keeps a structure
      // standing, so a tile with no road through it still gets its big shop.
      { elements: [
        { el: 'terrace', x: [9, 15], y: [9, 12], w: [57, 72], h: [21, 27] },
        { el: 'crates', chance: 0.5, x: [15, 27], y: [78, 87], cols: 2, rows: 2 },
      ] },
      { elements: [
        { el: 'terrace', x: [9, 12], y: [9, 15], w: [21, 27], h: [57, 72], vert: 1 },
        { el: 'crates', chance: 0.5, x: [78, 87], y: [15, 27], cols: 2, rows: 2 },
      ] },
      // A corner unit, for where not even a parade reaches. A bend sweeps an L
      // through the tile and leaves a quarter of it clear, which nothing that
      // has to span an axis can use — so the smallest thing this recipe can be
      // is three shops in a corner rather than no shop at all.
      { elements: [
        { el: 'terrace', x: [9, 15], y: [9, 15], w: [24, 30], h: [18, 24] },
        { el: 'stalls', chance: 0.5 },
      ] },
    ] },

    reactor: { elements: [{ el: 'reactorCore' }] },

    // Entry and extraction. A portal is a DEVICE somebody installed and powers,
    // so it stands in a facility rather than out in the open: ranges of
    // blockwork around three sides of the aperture, a transformer feeding it,
    // and the pad itself left clear in the middle (portalPad and portalGlow
    // both draw at the tile centre, and the glow goes down last of all).
    //
    // The buildings sit in one corner-half so the layout pass can mirror them
    // clear of the track that runs up to the tile — every mirror leaves two
    // adjacent sides open, which between them covers whichever edge the road
    // arrives from.
    portal: { elements: [
      { el: 'building', x: 9, y: 9, w: [48, 57], h: [21, 27] },      // north range
      { el: 'building', x: 9, y: [36, 42], w: [21, 27], h: [39, 45] }, // west range
      { el: 'transformerPad', x: [84, 93], y: [12, 18] },            // what powers it
      { el: 'crates', x: [78, 87], y: [72, 81], cols: 2, rows: 2 },
      // a deck under the aperture, so it reads as installed rather than as
      // something that happened to appear in the yard
      { el: 'rect', x: 36, y: 36, w: 48, h: 48, color: 'structDark' },
      { el: 'rect', x: 39, y: 39, w: 42, h: 42, color: 'ground2' },
      { el: 'portalPad' },
    ] },

    road: { elements: [
      // Debris on the carriageway goes down FIRST, so the frontage below covers
      // what lands on its ground instead of speckling its roofs.
      { el: 'rubble', x: 6, y: 6, w: 105, h: 105, n: 4 },
      // Frontage. What makes a trunk read as a STREET rather than as a strip of
      // surfacing is the building line either side of it — and the recipe is
      // never told which way the road goes, so it puts a block against all four
      // edges and lets the roadway pick. The layout pass suppresses whatever
      // the road runs through: on a straight run that is the two edges it
      // enters and leaves by, leaving the other two facing each other across
      // the carriageway; on a bend it is the two sides it turns between,
      // leaving the outside of the corner built up. Same rule, no cases.
      //
      // Depth is set so a block stands where a road CAN reach and is dropped
      // when it does. A crossing swings up to EDGE_SWING (24) off centre, so
      // shrinking these until they always survive would leave a strip of open
      // ground between every building and the road it faces — a setback that
      // varies tile to tile and reads as waste. Deep blocks sit tight against
      // the carriageway instead, and where the road hugs one side that side's
      // block goes and the far one stands alone, which is what a street pushed
      // against a boundary actually looks like.
      //
      // The far pair is anchored at 80 rather than hard against the edge, and
      // the near pair at 8: a building is clamped 6 units short of the tile
      // edge, and the layout pass MIRRORS it, so one authored against the edge
      // is measured clamped and then drawn somewhere it is not — bigger than
      // the footprint it was cleared on, with the difference landing on the
      // road. Authored sizes are multiplied by buildingScale (1.3 at most), so
      // 80 + 24 x 1.3 = 111 keeps every one of these inside the clamp at both
      // ends of its mirror, and the two passes agree.
      //
      // Terraced, because a STREET is what this recipe is for. A free-standing
      // block set back behind a carriageway is a depot; a run of units flush
      // along it, sharing party walls, is a street, and the difference is the
      // whole reason a trunk tile exists. The run is authored to the same 8/80
      // anchors and the same depth as the blocks it replaces, so the clamp
      // arithmetic above is unchanged; only what stands on the frontage is.
      { el: 'terrace', chance: 0.85, x: [12, 21], y: 8, w: [45, 57], h: 24 },
      { el: 'terrace', chance: 0.85, x: [12, 21], y: 80, w: [45, 57], h: 24 },
      { el: 'terrace', chance: 0.85, x: 8, y: [12, 21], w: 24, h: [45, 57], vert: 1 },
      { el: 'terrace', chance: 0.85, x: 80, y: [12, 21], w: 24, h: [45, 57], vert: 1 },
      { el: 'manhole', chance: 0.5, x: [48, 72], y: [48, 72] },
      // Traffic is not in here: drawRoadside lays it against the carriageway off
      // the road's own samples, so it lies parallel to the road and on the
      // shoulder, on every tile the network runs through rather than only on
      // the trunk tiles this recipe draws.
      { el: 'streetLamp', x: { pick: [39, 78] }, y: [39, 81] },
    ] },

    // rest stops are untouched clearings, and they
    // sit in the greenest spots on the map (5x the region's tree density)
    water: { treeDensity: 5, elements: [{ el: 'pond' }] },

    campsite: { treeDensity: 5, elements: [
      { el: 'tent', x: [15, 24], y: [15, 24] },
      { el: 'fireRing', x: [78, 90], y: [78, 90] },
      { el: 'rect', x: [18, 30], y: [84, 96], w: 18, h: 6, color: 'rust' },      // bedroll
      { el: 'rect', x: [81, 96], y: [18, 27], w: 6, h: 15, color: 'structDark' }, // supply post
    ] },

    // Impassable because the ground itself has gone, not because something fell
    // over on it: a shaft or a rift opened here and there is no way across.
    // Collapsed slabs read as debris you could pick your way through, which is
    // exactly what these tiles are not.
    // The hole goes down LAST in every variant. Ground litter drawn over it is
    // debris hanging in mid-air above a drop; drawn under it, the hole swallows
    // whatever fell in and what survives reads as the edge of the collapse.
    // Each hole lays its own spoil around its rim afterwards.
    blocked: { variants: [
      { elements: [                                                            // a shaft
        { el: 'crack', repeat: 4, x: [9, 105], y: [9, 96], len: [21, 36] },
        { el: 'rubble', x: 6, y: 6, w: 105, h: 105, n: 18 },
        { el: 'sinkhole', x: [51, 69], y: [51, 69], r: [33, 39] },
        { el: 'darkWash', alpha: 0.15 },
      ] },
      { elements: [                                                            // a rift, east-west
        { el: 'crack', repeat: 3, x: [9, 105], y: [9, 96], len: [21, 36] },
        { el: 'rubble', x: 6, y: 6, w: 105, h: 105, n: 18 },
        { el: 'chasm', x: [54, 66], y: [48, 72], len: [96, 108], r: [18, 24] },
        { el: 'darkWash', alpha: 0.15 },
      ] },
      { elements: [                                                            // and north-south
        { el: 'crack', repeat: 3, x: [9, 105], y: [9, 96], len: [21, 36] },
        { el: 'rubble', x: 6, y: 6, w: 105, h: 105, n: 18 },
        { el: 'chasm', x: [48, 72], y: [54, 66], len: [96, 108], r: [18, 24], vert: 1 },
        { el: 'darkWash', alpha: 0.15 },
      ] },
    ] },
  },

  // ===============================
  // SKILL ANCHORS
  // ===============================
  // A recipe says what kind of place a tile IS; the anchor says what the team
  // will be doing there. One per skill, chosen from the room's primary skill
  // and drawn ON TOP of the recipe — a power yard still reads as a power yard
  // and also shows its skill. Every skill is the primary of exactly four rooms
  // (see tests/data/skillBalance.test.js), so all fifteen of these are reachable.
  //
  // Same element vocabulary and same interpreter as recipes above, with two
  // differences: coordinates are PLATE-RELATIVE (0,0 is the anchor's own top
  // left, not the tile's), and w/h declare the reserved footprint, which must
  // stay within ANCHOR_MAX (24) so the anchor can never land on the connector
  // cross that joins tiles together.
  //
  // Every entry opens with an anchorPlate: it makes an anchor that overlaps a
  // roof read as apparatus installed there, which is what buys us out of doing
  // collision detection against the recipe. shape marks the faction — square
  // for Dominion, cut for Veil, round for Wilders.
  //
  // The anchor tracks the room's PRIMARY skill, not the event it will run —
  // and the two can disagree, because a room's event pool is primary plus
  // support. That is deliberate: a room runs one to four events, so keying the
  // anchor to any single one of them would mismatch the others regardless. The
  // primary is the only skill true for the whole room. (What does follow the
  // pinned event is the roof cutaway — see the environment field on events.)
  skillAnchors: {
    // ---- Dominion: systems and instrumentation ----
    'signal-relay': { w: 24, h: 24, elements: [
      { el: 'anchorPlate', x: 0, y: 0, w: 24, h: 24 },
      { el: 'tower', x: 4, y: 4 },                                  // guyed lattice mast
    ] },
    'system-override': { w: 24, h: 24, elements: [
      { el: 'anchorPlate', x: 0, y: 0, w: 24, h: 24 },
      { el: 'rect', x: 6, y: 5, w: 12, h: 11, color: 'struct' },    // console pedestal
      { el: 'rect', x: 7, y: 6, w: 10, h: 1, color: 'structLight' },
      { el: 'rect', x: 8, y: 8, w: 8, h: 5, color: 'accent' },      // lit screen face
      { el: 'rect', x: 11, y: 16, w: 2, h: 5, color: 'structDark' },// cable to the deck edge
    ] },
    'drone-command': { w: 24, h: 24, elements: [
      { el: 'anchorPlate', x: 0, y: 0, w: 24, h: 24 },
      { el: 'dronePad', x: 4, y: 4, s: 16 },
    ] },
    'safeguard': { w: 24, h: 24, elements: [
      { el: 'anchorPlate', x: 0, y: 0, w: 24, h: 24 },
      { el: 'transformerPad', x: 2, y: 2 },                         // fenced transformer, 21x21
    ] },
    'quantum-scan': { w: 24, h: 24, elements: [
      { el: 'anchorPlate', x: 0, y: 0, w: 24, h: 24 },
      { el: 'dish', x: 12, y: 12, r: 7, rings: 1, arm: 1, color: 'accent' },
    ] },

    // ---- Veil: access and concealment ----
    'lock-breach': { w: 24, h: 24, elements: [
      { el: 'anchorPlate', x: 0, y: 0, w: 24, h: 24, shape: 'cut' },
      { el: 'rect', x: 4, y: 4, w: 16, h: 16, color: 'metal' },     // armoured hatch slab
      { el: 'rect', x: 4, y: 4, w: 16, h: 1, color: 'structLight' },
      { el: 'rect', x: 4, y: 19, w: 16, h: 1, color: 'structDark' },
      // hazard stripe: a bare metal square reads as nothing from above, and
      // this is the one thing that separates a sealed door from a floor plate
      { el: 'rect', x: 4, y: 5, w: 16, h: 2, color: 'accent', alpha: 0.85 },
      { el: 'manhole', x: 12, y: 13 },                              // wheel lock
      { el: 'rect', x: 3, y: 8, w: 2, h: 3, color: 'structDark' },  // hinges
      { el: 'rect', x: 3, y: 15, w: 2, h: 3, color: 'structDark' },
    ] },
    'stealth-transit': { w: 24, h: 24, elements: [
      { el: 'anchorPlate', x: 0, y: 0, w: 24, h: 24, shape: 'cut' },
      { el: 'rect', x: 5, y: 5, w: 3, h: 14, color: 'structDark' }, // sensor gate posts
      { el: 'rect', x: 16, y: 5, w: 3, h: 14, color: 'structDark' },
      { el: 'rect', x: 5, y: 6, w: 3, h: 1, color: 'metal' },
      { el: 'rect', x: 16, y: 6, w: 3, h: 1, color: 'metal' },
      { el: 'rect', x: 8, y: 11, w: 8, h: 2, color: 'accent', alpha: 0.7 }, // the beam
    ] },
    'echo-trace': { w: 24, h: 24, elements: [
      { el: 'anchorPlate', x: 0, y: 0, w: 24, h: 24, shape: 'cut' },
      { el: 'dish', x: 12, y: 12, r: 3, rings: 2, color: 'accent' },// flush emitter + ripples
    ] },
    'signal-mask': { w: 24, h: 24, elements: [
      { el: 'anchorPlate', x: 0, y: 0, w: 24, h: 24, shape: 'cut' },
      { el: 'rect', x: 6, y: 9, w: 12, h: 9, color: 'struct' },     // jammer box
      { el: 'rect', x: 6, y: 9, w: 12, h: 1, color: 'structLight' },
      { el: 'rect', x: 11, y: 2, w: 1, h: 8, color: 'metal' },      // whip antenna
      { el: 'speckle', x: 3, y: 3, w: 18, h: 18, n: 10, color: 'accent', alpha: 0.35 }, // hash
    ] },
    'transit-anchor': { w: 24, h: 24, elements: [
      { el: 'anchorPlate', x: 0, y: 0, w: 24, h: 24, shape: 'cut' },
      { el: 'crack', x: 4, y: 6, len: 14 },                         // the fissure
      { el: 'rect', x: 4, y: 10, w: 16, h: 5, color: 'rust' },      // pinned gangway
      { el: 'rect', x: 4, y: 10, w: 16, h: 1, color: 'structLight' },
      { el: 'rect', x: 4, y: 9, w: 2, h: 7, color: 'metal' },       // pins
      { el: 'rect', x: 18, y: 9, w: 2, h: 7, color: 'metal' },
    ] },

    // ---- Wilders: growth, salvage and containment ----
    'reclamation': { w: 24, h: 24, elements: [
      { el: 'anchorPlate', x: 0, y: 0, w: 24, h: 24, shape: 'round' },
      { el: 'tank', x: 12, y: 13, r: 8 },                           // harvest vat
      { el: 'foliage', x: 16, y: 8, size: 3 },                      // biomass over the rim
    ] },
    'growth-control': { w: 24, h: 24, elements: [
      { el: 'anchorPlate', x: 0, y: 0, w: 24, h: 24, shape: 'round' },
      { el: 'rect', x: 4, y: 5, w: 16, h: 14, color: 'structDark' },// framed grow bed
      { el: 'rect', x: 5, y: 6, w: 14, h: 12, color: 'ground1' },
      { el: 'rect', x: 6, y: 8, w: 12, h: 2, color: 'veg1' },       // seeded rows
      { el: 'rect', x: 6, y: 12, w: 12, h: 2, color: 'veg2' },
      { el: 'rect', x: 6, y: 16, w: 12, h: 1, color: 'veg1' },
    ] },
    'wild-influence': { w: 24, h: 24, elements: [
      { el: 'anchorPlate', x: 0, y: 0, w: 24, h: 24, shape: 'round' },
      { el: 'dish', x: 12, y: 13, r: 7, rings: 1, color: 'veg2' },  // pheromone bloom
      { el: 'rect', x: 11, y: 2, w: 2, h: 5, color: 'structDark' }, // beacon post
    ] },
    'bio-shield': { w: 24, h: 24, elements: [
      { el: 'anchorPlate', x: 0, y: 0, w: 24, h: 24, shape: 'round' },
      { el: 'pipeline', x: 2, y: 6, w: 20 },                        // scrubber feed
      { el: 'tank', x: 15, y: 15, r: 6 },
    ] },
    'containment': { w: 24, h: 24, elements: [
      { el: 'anchorPlate', x: 0, y: 0, w: 24, h: 24, shape: 'round' },
      { el: 'fence', x: 2, y: 2, w: 20, h: 20 },                    // quarantine plot
      { el: 'barrier', x: 3, y: 17 },                               // striped barrier, 18x6
    ] },
  },
};


// ========== recipesArtifact.js ==========
// Artifact recipes - inputs conform to js/data/materialOwnership.js
// (each skill's chain stays inside its material signature; see lootFocus test)
export const recipesArtifact = [
  {
    target: "signal-repeater-pack",
    station: "workbench",
    inputs: [
      { id: "circuit-board", qty: 1 },
      { id: "logic-ribbon-cable", qty: 1 },
      { id: "glass-lens", qty: 1 },
      { id: "precision-servo-gear", qty: 1 },
      { id: "signal-processor-core", qty: 1 },
      { id: "encrypted-transceiver-chip", qty: 1 },
    ],
  },
  {
    target: "control-override-kit",
    station: "workbench",
    inputs: [
      { id: "aluminum-shell", qty: 1 },
      { id: "power-cell-casing", qty: 1 },
      { id: "rubber-seal", qty: 1 },
      { id: "iron-frame", qty: 1 },
      { id: "override-datakey", qty: 1 },
      { id: "voltage-regulator-unit", qty: 1 },
    ],
  },
  {
    target: "operator-link-harness",
    station: "workbench",
    inputs: [
      { id: "copper-wire", qty: 1 },
      { id: "circuit-board", qty: 1 },
      { id: "ceramic-insulator", qty: 1 },
      { id: "power-regulation-unit", qty: 1 },
      { id: "signal-sync-crystal", qty: 1 },
    ],
  },
  {
    target: "stability-coupler",
    station: "workbench",
    inputs: [
      { id: "aluminum-shell", qty: 1 },
      { id: "relay-antenna-strut", qty: 1 },
      { id: "logic-ribbon-cable", qty: 1 },
      { id: "sealed-hydraulic-pump", qty: 1 },
      { id: "drone-gyro-assembly", qty: 1 },
      { id: "signal-sync-crystal", qty: 1 },
    ],
  },
  {
    target: "spectral-survey-scope",
    station: "workbench",
    inputs: [
      { id: "composite-casing", qty: 1 },
      { id: "steel-plates", qty: 1 },
      { id: "iron-frame", qty: 1 },
      { id: "copper-wire", qty: 1 },
      { id: "precision-servo-gear", qty: 1 },
      { id: "radiation-sensor-module", qty: 1 },
    ],
  },
  {
    target: "harvesting-gauntlet",
    station: "workbench",
    inputs: [
      { id: "tanned-gut-cord", qty: 1 },
      { id: "fabric-mesh", qty: 1 },
      { id: "canvas-fiber", qty: 1 },
      { id: "medical-stimulant-cartridge", qty: 1 },
      { id: "high-torque-motor-core", qty: 1 },
    ],
  },
  {
    target: "growth-induction-pod",
    station: "workbench",
    inputs: [
      { id: "resin-casing", qty: 1 },
      { id: "spore-felt-padding", qty: 1 },
      { id: "polymer-tubing", qty: 1 },
      { id: "nutrient-catalyst-ampoule", qty: 1 },
      { id: "symbiotic-culture-vial", qty: 1 },
      { id: "high-torque-motor-core", qty: 1 },
    ],
  },
  {
    target: "pheromone-relay-belt",
    station: "workbench",
    inputs: [
      { id: "resin-casing", qty: 1 },
      { id: "fabric-mesh", qty: 1 },
      { id: "canvas-fiber", qty: 1 },
      { id: "pheromone-distiller-coil", qty: 1 },
      { id: "bio-conductive-mesh", qty: 1 },
    ],
  },
  {
    target: "respirator-webbing",
    station: "workbench",
    inputs: [
      { id: "leather-straps", qty: 1 },
      { id: "tanned-gut-cord", qty: 1 },
      { id: "chitin-plate", qty: 1 },
      { id: "pheromone-distiller-coil", qty: 1 },
      { id: "synthetic-pheromone-capsule", qty: 1 },
    ],
  },
  {
    target: "hazard-recovery-satchel",
    station: "workbench",
    inputs: [
      { id: "leather-straps", qty: 1 },
      { id: "polymer-tubing", qty: 1 },
      { id: "chitin-plate", qty: 1 },
      { id: "fabric-mesh", qty: 1 },
      { id: "nutrient-gel-pack", qty: 1 },
      { id: "containment-gel-lining", qty: 1 },
    ],
  },
  {
    target: "resonant-survey-array",
    station: "workbench",
    inputs: [
      { id: "micro-bearing-set", qty: 1 },
      { id: "brass-fittings", qty: 1 },
      { id: "carbon-housing", qty: 1 },
      { id: "plastic-casing", qty: 1 },
      { id: "phase-lens-array", qty: 1 },
      { id: "data-storage-crystal", qty: 1 },
    ],
  },
  {
    target: "lockpulse-breach-kit",
    station: "workbench",
    inputs: [
      { id: "etched-keyplate-blank", qty: 1 },
      { id: "matte-coating-compound", qty: 1 },
      { id: "acoustic-foam-wafer", qty: 1 },
      { id: "ultrasonic-sensor-grid", qty: 1 },
      { id: "micro-servo-drill", qty: 1 },
      { id: "precision-chrono-core", qty: 1 },
    ],
  },
  {
    target: "silent-step-pads",
    station: "workbench",
    inputs: [
      { id: "optic-fiber-spool", qty: 1 },
      { id: "matte-coating-compound", qty: 1 },
      { id: "brass-fittings", qty: 1 },
      { id: "thermal-polarizer-film", qty: 1 },
      { id: "precision-chrono-core", qty: 1 },
    ],
  },
  {
    target: "trace-disruptor-band",
    station: "workbench",
    inputs: [
      { id: "micro-bearing-set", qty: 1 },
      { id: "etched-keyplate-blank", qty: 1 },
      { id: "acoustic-foam-wafer", qty: 1 },
      { id: "vibration-dampener", qty: 1 },
      { id: "em-interference-chip", qty: 1 },
    ],
  },
  {
    target: "anchor-resonator",
    station: "workbench",
    inputs: [
      { id: "plastic-casing", qty: 1 },
      { id: "optic-fiber-spool", qty: 1 },
      { id: "ghost-signal-modulator", qty: 1 },
      { id: "phase-lens-array", qty: 1 },
      { id: "harmonic-stabilizer-core", qty: 1 },
    ],
  },
  {
    target: "data-cache-drive",
    station: "workbench",
    inputs: [
      { id: "steel-plates", qty: 1 },
      { id: "rubber-seal", qty: 1 },
      { id: "plastic-casing", qty: 1 },
      { id: "signal-processor-core", qty: 1 },
      { id: "circuit-board", qty: 1 },
      { id: "encrypted-transceiver-chip", qty: 1 },
    ],
  },
  {
    target: "relic-resonance-tuner",
    station: "workbench",
    inputs: [
      { id: "copper-wire", qty: 1 },
      { id: "brass-fittings", qty: 1 },
      { id: "iron-frame", qty: 1 },
      { id: "ultrasonic-sensor-grid", qty: 1 },
      { id: "signal-sync-crystal", qty: 1 },
    ],
  },
  {
    target: "hazard-lens-visor",
    station: "workbench",
    inputs: [
      { id: "glass-lens", qty: 1 },
      { id: "steel-plates", qty: 1 },
      { id: "plastic-casing", qty: 1 },
      { id: "thermal-polarizer-film", qty: 1 },
      { id: "radiation-sensor-module", qty: 1 },
    ],
  },
  {
    target: "recovery-injector",
    station: "workbench",
    inputs: [
      { id: "plastic-casing", qty: 1 },
      { id: "polymer-tubing", qty: 1 },
      { id: "rubber-seal", qty: 1 },
      { id: "medical-stimulant-cartridge", qty: 1 },
      { id: "bio-conductive-mesh", qty: 1 },
    ],
  },
  {
    target: "contract-beacon",
    station: "workbench",
    inputs: [
      { id: "aluminum-shell", qty: 1 },
      { id: "circuit-board", qty: 1 },
      { id: "copper-wire", qty: 1 },
      { id: "signal-processor-core", qty: 1 },
      { id: "signal-sync-crystal", qty: 1 },
    ],
  },
  {
    target: "sync-regulator-watch",
    station: "workbench",
    inputs: [
      { id: "brass-fittings", qty: 1 },
      { id: "glass-lens", qty: 1 },
      { id: "precision-servo-gear", qty: 1 },
      { id: "precision-chrono-core", qty: 1 },
    ],
  },
  {
    target: "auto-stabilizer-pack",
    station: "workbench",
    inputs: [
      { id: "steel-plates", qty: 1 },
      { id: "polymer-tubing", qty: 1 },
      { id: "fabric-mesh", qty: 1 },
      { id: "precision-servo-gear", qty: 1 },
      { id: "sealed-hydraulic-pump", qty: 1 },
      { id: "harmonic-stabilizer-core", qty: 1 },
    ],
  },
  {
    target: "training-node-deck",
    station: "workbench",
    inputs: [
      { id: "plastic-casing", qty: 1 },
      { id: "copper-wire", qty: 1 },
      { id: "circuit-board", qty: 1 },
      { id: "signal-processor-core", qty: 1 },
      { id: "data-storage-crystal", qty: 1 },
    ],
  },
  {
    target: "crosslink-amplifier",
    station: "workbench",
    inputs: [
      { id: "copper-wire", qty: 1 },
      { id: "aluminum-shell", qty: 1 },
      { id: "carbon-housing", qty: 1 },
      { id: "signal-processor-core", qty: 1 },
      { id: "signal-sync-crystal", qty: 1 },
    ],
  },
  {
    target: "material-extractor",
    station: "workbench",
    inputs: [
      { id: "steel-plates", qty: 1 },
      { id: "plastic-casing", qty: 1 },
      { id: "iron-frame", qty: 1 },
      { id: "precision-servo-gear", qty: 1 },
      { id: "high-torque-motor-core", qty: 1 },
    ],
  },
  {
    target: "salvage-compression-rig",
    station: "workbench",
    inputs: [
      { id: "steel-plates", qty: 1 },
      { id: "iron-frame", qty: 1 },
      { id: "rubber-seal", qty: 1 },
      { id: "sealed-hydraulic-pump", qty: 1 },
      { id: "high-torque-motor-core", qty: 1 },
    ],
  },
  {
    target: "memory-crystal-shard",
    station: "workbench",
    inputs: [
      { id: "glass-lens", qty: 1 },
      { id: "resin-casing", qty: 1 },
      { id: "circuit-board", qty: 1 },
      { id: "data-storage-crystal", qty: 1 },
    ],
  },
];


// ========== recipesEpic.js ==========
// Epic gear/consumable recipes - inputs conform to js/data/materialOwnership.js
// (each skill's chain stays inside its material signature; see lootFocus test)
export const recipesEpic = [
  {
    target: "relay-stabilizer-gel-pack",
    station: "lab",
    inputs: [
      { id: "logic-ribbon-cable", qty: 1 },
      { id: "circuit-board", qty: 1 },
      { id: "precision-servo-gear", qty: 1 },
      { id: "power-regulation-unit", qty: 1 },
    ],
  },
  {
    target: "biofiber-splice-kit",
    station: "lab",
    inputs: [
      { id: "logic-ribbon-cable", qty: 1 },
      { id: "copper-wire", qty: 1 },
      { id: "encrypted-transceiver-chip", qty: 1 },
    ],
  },
  {
    target: "noise-floor-buffer-pack",
    station: "lab",
    inputs: [
      { id: "circuit-board", qty: 1 },
      { id: "logic-ribbon-cable", qty: 1 },
      { id: "encrypted-transceiver-chip", qty: 1 },
    ],
  },
  {
    target: "multi-node-override-cart",
    station: "lab",
    inputs: [
      { id: "aluminum-shell", qty: 1 },
      { id: "signal-processor-core", qty: 1 },
      { id: "override-datakey", qty: 1 },
    ],
  },
  {
    target: "surge-damping-couplers",
    station: "lab",
    inputs: [
      { id: "aluminum-shell", qty: 2 },
      { id: "ceramic-insulator", qty: 1 },
      { id: "signal-processor-core", qty: 1 },
    ],
  },
  {
    target: "decoy-traffic-cartridge",
    station: "lab",
    inputs: [
      { id: "power-cell-casing", qty: 1 },
      { id: "composite-casing", qty: 1 },
      { id: "voltage-regulator-unit", qty: 1 },
    ],
  },
  {
    target: "swarm-sync-beacon-cart",
    station: "lab",
    inputs: [
      { id: "copper-wire", qty: 2 },
      { id: "circuit-board", qty: 1 },
      { id: "drone-gyro-assembly", qty: 1 },
    ],
  },
  {
    target: "overgrowth-lidar-pack",
    station: "lab",
    inputs: [
      { id: "ceramic-insulator", qty: 1 },
      { id: "power-cell-casing", qty: 1 },
      { id: "power-regulation-unit", qty: 1 },
      { id: "override-datakey", qty: 1 },
    ],
  },
  {
    target: "quiet-sonar-cartridge",
    station: "lab",
    inputs: [
      { id: "copper-wire", qty: 1 },
      { id: "circuit-board", qty: 1 },
      { id: "drone-gyro-assembly", qty: 1 },
      { id: "power-regulation-unit", qty: 1 },
    ],
  },
  {
    target: "rapid-grounding-spikes",
    station: "lab",
    inputs: [
      { id: "aluminum-shell", qty: 1 },
      { id: "relay-antenna-strut", qty: 1 },
      { id: "radiation-sensor-module", qty: 1 },
    ],
  },
  {
    target: "vent-cycler-pack",
    station: "lab",
    inputs: [
      { id: "logic-ribbon-cable", qty: 1 },
      { id: "sealed-hydraulic-pump", qty: 1 },
      { id: "drone-gyro-assembly", qty: 1 },
    ],
  },
  {
    target: "cutoff-line-kit",
    station: "lab",
    inputs: [
      { id: "composite-casing", qty: 1 },
      { id: "aluminum-shell", qty: 1 },
      { id: "relay-antenna-strut", qty: 1 },
      { id: "sealed-hydraulic-pump", qty: 1 },
    ],
  },
  {
    target: "scan-link-bridge",
    station: "lab",
    inputs: [
      { id: "ceramic-insulator", qty: 1 },
      { id: "precision-servo-gear", qty: 1 },
      { id: "radiation-sensor-module", qty: 1 },
    ],
  },
  {
    target: "dual-spectrum-cartridge",
    station: "lab",
    inputs: [
      { id: "composite-casing", qty: 1 },
      { id: "precision-servo-gear", qty: 1 },
      { id: "radiation-sensor-module", qty: 1 },
    ],
  },
  {
    target: "residual-current-probe-kit",
    station: "lab",
    inputs: [
      { id: "copper-wire", qty: 1 },
      { id: "relay-antenna-strut", qty: 1 },
      { id: "radiation-sensor-module", qty: 1 },
    ],
  },
  {
    target: "harvest-sync-brace-pack",
    station: "lab",
    inputs: [
      { id: "tanned-gut-cord", qty: 2 },
      { id: "canvas-fiber", qty: 1 },
      { id: "nutrient-gel-pack", qty: 1 },
    ],
  },
  {
    target: "fast-filter-plenum-cart",
    station: "lab",
    inputs: [
      { id: "tanned-gut-cord", qty: 1 },
      { id: "medical-stimulant-cartridge", qty: 1 },
      { id: "nutrient-catalyst-ampoule", qty: 1 },
    ],
  },
  {
    target: "micro-mist-emitter",
    station: "lab",
    inputs: [
      { id: "fabric-mesh", qty: 1 },
      { id: "canvas-fiber", qty: 1 },
      { id: "nutrient-gel-pack", qty: 1 },
      { id: "medical-stimulant-cartridge", qty: 1 },
    ],
  },
  {
    target: "rapid-brace-binder",
    station: "lab",
    inputs: [
      { id: "spore-felt-padding", qty: 2 },
      { id: "polymer-tubing", qty: 1 },
      { id: "symbiotic-culture-vial", qty: 1 },
    ],
  },
  {
    target: "light-channel-films",
    station: "lab",
    inputs: [
      { id: "spore-felt-padding", qty: 1 },
      { id: "nutrient-catalyst-ampoule", qty: 1 },
      { id: "symbiotic-culture-vial", qty: 1 },
    ],
  },
  {
    target: "root-guidance-stakes",
    station: "lab",
    inputs: [
      { id: "resin-casing", qty: 2 },
      { id: "chitin-plate", qty: 1 },
      { id: "nutrient-catalyst-ampoule", qty: 1 },
    ],
  },
  {
    target: "breath-sync-strips",
    station: "lab",
    inputs: [
      { id: "fabric-mesh", qty: 1 },
      { id: "synthetic-pheromone-capsule", qty: 1 },
    ],
  },
  {
    target: "chorus-diffuser-ampoule",
    station: "lab",
    inputs: [
      { id: "canvas-fiber", qty: 1 },
      { id: "polymer-tubing", qty: 1 },
      { id: "synthetic-pheromone-capsule", qty: 1 },
    ],
  },
  {
    target: "scent-dampening-salve",
    station: "lab",
    inputs: [
      { id: "resin-casing", qty: 1 },
      { id: "pheromone-distiller-coil", qty: 1 },
      { id: "bio-conductive-mesh", qty: 1 },
    ],
  },
  {
    target: "reactive-gel-patch",
    station: "lab",
    inputs: [
      { id: "tanned-gut-cord", qty: 1 },
      { id: "adaptive-filter-membrane", qty: 1 },
      { id: "containment-gel-lining", qty: 1 },
    ],
  },
  {
    target: "enzyme-swap-kit",
    station: "lab",
    inputs: [
      { id: "spore-felt-padding", qty: 1 },
      { id: "pheromone-distiller-coil", qty: 1 },
      { id: "containment-gel-lining", qty: 1 },
    ],
  },
  {
    target: "surface-tuning-spray",
    station: "lab",
    inputs: [
      { id: "leather-straps", qty: 1 },
      { id: "adaptive-filter-membrane", qty: 1 },
      { id: "containment-gel-lining", qty: 1 },
    ],
  },
  {
    target: "hardcase-quickframe-kit",
    station: "lab",
    inputs: [
      { id: "chitin-plate", qty: 4 },
      { id: "fabric-mesh", qty: 1 },
      { id: "leather-straps", qty: 1 },
    ],
  },
  {
    target: "pod-starter-seeds",
    station: "lab",
    inputs: [
      { id: "nutrient-gel-pack", qty: 1 },
      { id: "adaptive-filter-membrane", qty: 1 },
    ],
  },
  {
    target: "recovery-pod-capsules",
    station: "lab",
    inputs: [
      { id: "nutrient-gel-pack", qty: 1 },
      { id: "adaptive-filter-membrane", qty: 1 },
    ],
  },
  {
    target: "relay-bridge-link-cart",
    station: "lab",
    inputs: [
      { id: "micro-bearing-set", qty: 1 },
      { id: "carbon-housing", qty: 1 },
      { id: "data-storage-crystal", qty: 1 },
    ],
  },
  {
    target: "bio-pulse-amplifier-pack",
    station: "lab",
    inputs: [
      { id: "micro-bearing-set", qty: 1 },
      { id: "phase-lens-array", qty: 1 },
      { id: "data-storage-crystal", qty: 1 },
    ],
  },
  {
    target: "map-cache-sync-cart",
    station: "lab",
    inputs: [
      { id: "brass-fittings", qty: 1 },
      { id: "carbon-housing", qty: 1 },
      { id: "data-storage-crystal", qty: 1 },
    ],
  },
  {
    target: "micro-torque-paste",
    station: "lab",
    inputs: [
      { id: "etched-keyplate-blank", qty: 1 },
      { id: "ultrasonic-sensor-grid", qty: 1 },
      { id: "micro-servo-drill", qty: 1 },
    ],
  },
  {
    target: "quiet-seal-injector",
    station: "lab",
    inputs: [
      { id: "etched-keyplate-blank", qty: 1 },
      { id: "ultrasonic-sensor-grid", qty: 1 },
      { id: "precision-chrono-core", qty: 1 },
    ],
  },
  {
    target: "decoy-pulse-cartridge",
    station: "lab",
    inputs: [
      { id: "optic-fiber-spool", qty: 1 },
      { id: "micro-servo-drill", qty: 1 },
      { id: "precision-chrono-core", qty: 1 },
    ],
  },
  {
    target: "route-shadow-taggers",
    station: "lab",
    inputs: [
      { id: "matte-coating-compound", qty: 2 },
      { id: "brass-fittings", qty: 1 },
      { id: "thermal-polarizer-film", qty: 1 },
    ],
  },
  {
    target: "low-shine-veil-pack",
    station: "lab",
    inputs: [
      { id: "carbon-housing", qty: 1 },
      { id: "vibration-dampener", qty: 1 },
      { id: "thermal-polarizer-film", qty: 1 },
    ],
  },
  {
    target: "pace-sync-patches",
    station: "lab",
    inputs: [
      { id: "optic-fiber-spool", qty: 1 },
      { id: "matte-coating-compound", qty: 1 },
      { id: "em-interference-chip", qty: 1 },
    ],
  },
  {
    target: "auth-spoof-keycard",
    station: "lab",
    inputs: [
      { id: "micro-bearing-set", qty: 1 },
      { id: "harmonic-stabilizer-core", qty: 1 },
    ],
  },
  {
    target: "thermal-scent-ampoule",
    station: "lab",
    inputs: [
      { id: "ghost-signal-modulator", qty: 1 },
      { id: "vibration-dampener", qty: 1 },
    ],
  },
  {
    target: "temp-log-writer-cart",
    station: "lab",
    inputs: [
      { id: "acoustic-foam-wafer", qty: 1 },
      { id: "em-interference-chip", qty: 1 },
    ],
  },
  {
    target: "quickset-anchor-rods",
    station: "lab",
    inputs: [
      { id: "plastic-casing", qty: 1 },
      { id: "acoustic-foam-wafer", qty: 1 },
      { id: "harmonic-stabilizer-core", qty: 1 },
    ],
  },
  {
    target: "timing-pulse-tag",
    station: "lab",
    inputs: [
      { id: "brass-fittings", qty: 1 },
      { id: "phase-lens-array", qty: 1 },
      { id: "harmonic-stabilizer-core", qty: 1 },
    ],
  },
  {
    target: "scan-cache-array-cart",
    station: "lab",
    inputs: [
      { id: "plastic-casing", qty: 1 },
      { id: "ghost-signal-modulator", qty: 1 },
      { id: "harmonic-stabilizer-core", qty: 1 },
    ],
  },
  {
    target: "precision-mast-calibrator",
    station: "lab",
    inputs: [
      { id: "logic-ribbon-cable", qty: 8 },
      { id: "signal-processor-core", qty: 1 },
      { id: "encrypted-transceiver-chip", qty: 2 },
    ],
  },
  {
    target: "biofiber-manifold",
    station: "lab",
    inputs: [
      { id: "circuit-board", qty: 4 },
      { id: "logic-ribbon-cable", qty: 1 },
      { id: "encrypted-transceiver-chip", qty: 3 },
    ],
  },
  {
    target: "adaptive-noise-gate",
    station: "lab",
    inputs: [
      { id: "copper-wire", qty: 2 },
      { id: "circuit-board", qty: 1 },
      { id: "encrypted-transceiver-chip", qty: 3 },
    ],
  },
  {
    target: "multi-handshake-router",
    station: "lab",
    inputs: [
      { id: "power-cell-casing", qty: 3 },
      { id: "override-datakey", qty: 2 },
      { id: "voltage-regulator-unit", qty: 1 },
      { id: "encrypted-transceiver-chip", qty: 1 },
    ],
  },
  {
    target: "surge-buffer-backplane",
    station: "lab",
    inputs: [
      { id: "aluminum-shell", qty: 4 },
      { id: "ceramic-insulator", qty: 4 },
      { id: "signal-processor-core", qty: 1 },
      { id: "voltage-regulator-unit", qty: 1 },
    ],
  },
  {
    target: "credential-spoof-matrix",
    station: "lab",
    inputs: [
      { id: "power-cell-casing", qty: 2 },
      { id: "composite-casing", qty: 1 },
      { id: "voltage-regulator-unit", qty: 2 },
      { id: "encrypted-transceiver-chip", qty: 1 },
    ],
  },
  {
    target: "swarm-route-controller",
    station: "lab",
    inputs: [
      { id: "ceramic-insulator", qty: 1 },
      { id: "power-cell-casing", qty: 1 },
      { id: "power-regulation-unit", qty: 2 },
      { id: "signal-sync-crystal", qty: 1 },
    ],
  },
  {
    target: "overgrowth-nav-suite",
    station: "lab",
    inputs: [
      { id: "copper-wire", qty: 2 },
      { id: "circuit-board", qty: 1 },
      { id: "override-datakey", qty: 1 },
      { id: "drone-gyro-assembly", qty: 1 },
    ],
  },
  {
    target: "quiet-sonar-array",
    station: "lab",
    inputs: [
      { id: "ceramic-insulator", qty: 2 },
      { id: "power-cell-casing", qty: 1 },
      { id: "copper-wire", qty: 1 },
      { id: "power-regulation-unit", qty: 1 },
    ],
  },
  {
    target: "auto-ground-bus",
    station: "lab",
    inputs: [
      { id: "aluminum-shell", qty: 6 },
      { id: "relay-antenna-strut", qty: 3 },
      { id: "logic-ribbon-cable", qty: 2 },
      { id: "signal-sync-crystal", qty: 1 },
    ],
  },
  {
    target: "cycler-governor",
    station: "lab",
    inputs: [
      { id: "composite-casing", qty: 3 },
      { id: "aluminum-shell", qty: 1 },
      { id: "sealed-hydraulic-pump", qty: 2 },
      { id: "drone-gyro-assembly", qty: 1 },
    ],
  },
  {
    target: "hardline-junction-box",
    station: "lab",
    inputs: [
      { id: "relay-antenna-strut", qty: 4 },
      { id: "logic-ribbon-cable", qty: 4 },
      { id: "composite-casing", qty: 2 },
      { id: "sealed-hydraulic-pump", qty: 1 },
    ],
  },
  {
    target: "scan-link-backbone",
    station: "lab",
    inputs: [
      { id: "iron-frame", qty: 2 },
      { id: "precision-servo-gear", qty: 2 },
      { id: "radiation-sensor-module", qty: 2 },
    ],
  },
  {
    target: "dual-spectrum-integrator",
    station: "lab",
    inputs: [
      { id: "relay-antenna-strut", qty: 2 },
      { id: "ceramic-insulator", qty: 2 },
      { id: "radiation-sensor-module", qty: 2 },
    ],
  },
  {
    target: "low-current-amplifier",
    station: "lab",
    inputs: [
      { id: "composite-casing", qty: 8 },
      { id: "steel-plates", qty: 1 },
      { id: "radiation-sensor-module", qty: 2 },
    ],
  },
  {
    target: "harvest-flight-controller",
    station: "lab",
    inputs: [
      { id: "tanned-gut-cord", qty: 2 },
      { id: "canvas-fiber", qty: 2 },
      { id: "medical-stimulant-cartridge", qty: 1 },
      { id: "high-torque-motor-core", qty: 1 },
    ],
  },
  {
    target: "fungal-flow-regulator",
    station: "lab",
    inputs: [
      { id: "tanned-gut-cord", qty: 3 },
      { id: "fabric-mesh", qty: 1 },
      { id: "medical-stimulant-cartridge", qty: 2 },
      { id: "nutrient-catalyst-ampoule", qty: 1 },
    ],
  },
  {
    target: "surface-decon-manifold",
    station: "lab",
    inputs: [
      { id: "canvas-fiber", qty: 3 },
      { id: "leather-straps", qty: 2 },
      { id: "nutrient-gel-pack", qty: 1 },
      { id: "medical-stimulant-cartridge", qty: 1 },
    ],
  },
  {
    target: "lattice-lock-ribs",
    station: "lab",
    inputs: [
      { id: "spore-felt-padding", qty: 2 },
      { id: "polymer-tubing", qty: 1 },
      { id: "symbiotic-culture-vial", qty: 1 },
      { id: "high-torque-motor-core", qty: 1 },
    ],
  },
  {
    target: "leaf-optic-panel",
    station: "lab",
    inputs: [
      { id: "resin-casing", qty: 2 },
      { id: "symbiotic-culture-vial", qty: 2 },
      { id: "nutrient-catalyst-ampoule", qty: 1 },
    ],
  },
  {
    target: "seam-probe-kit",
    station: "lab",
    inputs: [
      { id: "chitin-plate", qty: 2 },
      { id: "spore-felt-padding", qty: 2 },
      { id: "symbiotic-culture-vial", qty: 1 },
      { id: "bio-conductive-mesh", qty: 1 },
    ],
  },
  {
    target: "rhythm-guide-hub",
    station: "lab",
    inputs: [
      { id: "polymer-tubing", qty: 1 },
      { id: "bio-conductive-mesh", qty: 1 },
      { id: "synthetic-pheromone-capsule", qty: 1 },
    ],
  },
  {
    target: "multi-diffuser-crown",
    station: "lab",
    inputs: [
      { id: "resin-casing", qty: 3 },
      { id: "fabric-mesh", qty: 2 },
      { id: "bio-conductive-mesh", qty: 2 },
    ],
  },
  {
    target: "contact-silence-kit",
    station: "lab",
    inputs: [
      { id: "canvas-fiber", qty: 2 },
      { id: "pheromone-distiller-coil", qty: 1 },
      { id: "synthetic-pheromone-capsule", qty: 1 },
    ],
  },
  {
    target: "layer-activator-pad",
    station: "lab",
    inputs: [
      { id: "tanned-gut-cord", qty: 1 },
      { id: "pheromone-distiller-coil", qty: 1 },
      { id: "containment-gel-lining", qty: 1 },
      { id: "synthetic-pheromone-capsule", qty: 1 },
    ],
  },
  {
    target: "enzyme-hub-manifold",
    station: "lab",
    inputs: [
      { id: "chitin-plate", qty: 3 },
      { id: "pheromone-distiller-coil", qty: 1 },
      { id: "containment-gel-lining", qty: 1 },
    ],
  },
  {
    target: "surface-tuner-core",
    station: "lab",
    inputs: [
      { id: "spore-felt-padding", qty: 1 },
      { id: "adaptive-filter-membrane", qty: 2 },
      { id: "synthetic-pheromone-capsule", qty: 1 },
    ],
  },
  {
    target: "hot-sample-frame",
    station: "lab",
    inputs: [
      { id: "leather-straps", qty: 6 },
      { id: "polymer-tubing", qty: 2 },
      { id: "chitin-plate", qty: 1 },
    ],
  },
  {
    target: "pod-growth-hub",
    station: "lab",
    inputs: [
      { id: "nutrient-gel-pack", qty: 2 },
      { id: "adaptive-filter-membrane", qty: 1 },
      { id: "containment-gel-lining", qty: 1 },
    ],
  },
  {
    target: "recovery-pod-manifold",
    station: "lab",
    inputs: [
      { id: "nutrient-gel-pack", qty: 2 },
      { id: "adaptive-filter-membrane", qty: 1 },
      { id: "containment-gel-lining", qty: 1 },
    ],
  },
  {
    target: "relay-pairing-bridge",
    station: "lab",
    inputs: [
      { id: "micro-bearing-set", qty: 2 },
      { id: "carbon-housing", qty: 1 },
      { id: "data-storage-crystal", qty: 2 },
    ],
  },
  {
    target: "bio-pulse-coupler",
    station: "lab",
    inputs: [
      { id: "micro-bearing-set", qty: 2 },
      { id: "phase-lens-array", qty: 1 },
      { id: "data-storage-crystal", qty: 1 },
    ],
  },
  {
    target: "map-sync-hub",
    station: "lab",
    inputs: [
      { id: "brass-fittings", qty: 2 },
      { id: "carbon-housing", qty: 1 },
      { id: "data-storage-crystal", qty: 1 },
    ],
  },
  {
    target: "micro-torque-rail",
    station: "lab",
    inputs: [
      { id: "etched-keyplate-blank", qty: 3 },
      { id: "ultrasonic-sensor-grid", qty: 2 },
      { id: "micro-servo-drill", qty: 1 },
    ],
  },
  {
    target: "quiet-seal-rail",
    station: "lab",
    inputs: [
      { id: "etched-keyplate-blank", qty: 1 },
      { id: "ultrasonic-sensor-grid", qty: 1 },
      { id: "precision-chrono-core", qty: 1 },
    ],
  },
  {
    target: "decoy-phase-driver",
    station: "lab",
    inputs: [
      { id: "optic-fiber-spool", qty: 1 },
      { id: "micro-servo-drill", qty: 1 },
      { id: "precision-chrono-core", qty: 1 },
    ],
  },
  {
    target: "route-shadow-hub",
    station: "lab",
    inputs: [
      { id: "matte-coating-compound", qty: 1 },
      { id: "optic-fiber-spool", qty: 1 },
      { id: "thermal-polarizer-film", qty: 1 },
    ],
  },
  {
    target: "low-shine-housing",
    station: "lab",
    inputs: [
      { id: "matte-coating-compound", qty: 1 },
      { id: "vibration-dampener", qty: 2 },
      { id: "thermal-polarizer-film", qty: 1 },
    ],
  },
  {
    target: "pace-sync-hub",
    station: "lab",
    inputs: [
      { id: "brass-fittings", qty: 1 },
      { id: "carbon-housing", qty: 1 },
      { id: "em-interference-chip", qty: 1 },
    ],
  },
  {
    target: "auth-emulator-core",
    station: "lab",
    inputs: [
      { id: "acoustic-foam-wafer", qty: 2 },
      { id: "em-interference-chip", qty: 1 },
      { id: "harmonic-stabilizer-core", qty: 1 },
    ],
  },
  {
    target: "thermal-scent-hub",
    station: "lab",
    inputs: [
      { id: "ghost-signal-modulator", qty: 2 },
      { id: "vibration-dampener", qty: 1 },
      { id: "em-interference-chip", qty: 1 },
    ],
  },
  {
    target: "log-sanitizer-kernel",
    station: "lab",
    inputs: [
      { id: "etched-keyplate-blank", qty: 2 },
      { id: "em-interference-chip", qty: 1 },
      { id: "harmonic-stabilizer-core", qty: 1 },
    ],
  },
  {
    target: "anchor-brace-grid",
    station: "lab",
    inputs: [
      { id: "plastic-casing", qty: 2 },
      { id: "acoustic-foam-wafer", qty: 2 },
      { id: "harmonic-stabilizer-core", qty: 1 },
    ],
  },
  {
    target: "timing-window-hub",
    station: "lab",
    inputs: [
      { id: "plastic-casing", qty: 1 },
      { id: "phase-lens-array", qty: 1 },
      { id: "harmonic-stabilizer-core", qty: 1 },
    ],
  },
  {
    target: "scan-cache-backplane",
    station: "lab",
    inputs: [
      { id: "optic-fiber-spool", qty: 2 },
      { id: "ghost-signal-modulator", qty: 1 },
      { id: "harmonic-stabilizer-core", qty: 2 },
    ],
  },
];


// ========== recipesRare.js ==========
// Rare material recipes - inputs conform to js/data/materialOwnership.js
// (each skill's chain stays inside its material signature; see lootFocus test)
export const recipesRare = [
  {
    target: "encrypted-transceiver-chip",
    station: "lab",
    inputs: [
      { id: "iron-frame", qty: 2 },
      { id: "steel-plates", qty: 1 },
      { id: "signal-processor-core", qty: 1 },
    ],
  },
  {
    target: "radiation-sensor-module",
    station: "lab",
    inputs: [
      { id: "composite-casing", qty: 2 },
      { id: "relay-antenna-strut", qty: 1 },
      { id: "sealed-hydraulic-pump", qty: 1 },
    ],
  },
  {
    target: "bio-conductive-mesh",
    station: "lab",
    inputs: [
      { id: "polymer-tubing", qty: 2 },
      { id: "resin-casing", qty: 1 },
      { id: "symbiotic-culture-vial", qty: 1 },
    ],
  },
  {
    target: "synthetic-pheromone-capsule",
    station: "lab",
    inputs: [
      { id: "glass-lens", qty: 2 },
      { id: "rubber-seal", qty: 1 },
      { id: "pheromone-distiller-coil", qty: 1 },
    ],
  },
  {
    target: "em-interference-chip",
    station: "lab",
    inputs: [
      { id: "iron-frame", qty: 2 },
      { id: "steel-plates", qty: 1 },
      { id: "vibration-dampener", qty: 1 },
    ],
  },
  {
    target: "harmonic-stabilizer-core",
    station: "lab",
    inputs: [
      { id: "acoustic-foam-wafer", qty: 2 },
      { id: "plastic-casing", qty: 1 },
      { id: "ghost-signal-modulator", qty: 1 },
    ],
  },
  {
    target: "signal-sync-crystal",
    station: "lab",
    inputs: [
      { id: "glass-lens", qty: 2 },
      { id: "rubber-seal", qty: 1 },
      { id: "drone-gyro-assembly", qty: 1 },
    ],
  },
  {
    target: "data-storage-crystal",
    station: "lab",
    inputs: [
      { id: "iron-frame", qty: 2 },
      { id: "steel-plates", qty: 1 },
      { id: "ultrasonic-sensor-grid", qty: 1 },
    ],
  },
  {
    target: "precision-chrono-core",
    station: "lab",
    inputs: [
      { id: "optic-fiber-spool", qty: 2 },
      { id: "matte-coating-compound", qty: 1 },
      { id: "micro-servo-drill", qty: 1 },
    ],
  },
  {
    target: "high-torque-motor-core",
    station: "lab",
    inputs: [
      { id: "glass-lens", qty: 2 },
      { id: "rubber-seal", qty: 1 },
      { id: "nutrient-catalyst-ampoule", qty: 1 },
    ],
  },
  {
    target: "containment-gel-lining",
    station: "lab",
    inputs: [
      { id: "chitin-plate", qty: 2 },
      { id: "leather-straps", qty: 1 },
      { id: "adaptive-filter-membrane", qty: 1 },
    ],
  },
  {
    target: "voltage-regulator-unit",
    station: "lab",
    inputs: [
      { id: "ceramic-insulator", qty: 2 },
      { id: "power-cell-casing", qty: 1 },
      { id: "override-datakey", qty: 1 },
    ],
  },
];


// ========== recipesUncommon.js ==========
// Uncommon material recipes - inputs conform to js/data/materialOwnership.js
// (each skill's chain stays inside its material signature; see lootFocus test)
export const recipesUncommon = [
  {
    target: "signal-processor-core",
    station: "bench",
    inputs: [
      { id: "steel-plates", qty: 2 },
      { id: "glass-lens", qty: 1 },
    ],
  },
  {
    target: "precision-servo-gear",
    station: "bench",
    inputs: [
      { id: "copper-wire", qty: 2 },
      { id: "relay-antenna-strut", qty: 1 },
    ],
  },
  {
    target: "nutrient-catalyst-ampoule",
    station: "bench",
    inputs: [
      { id: "rubber-seal", qty: 1 },
      { id: "iron-frame", qty: 1 },
      { id: "steel-plates", qty: 1 },
    ],
  },
  {
    target: "adaptive-filter-membrane",
    station: "bench",
    inputs: [
      { id: "leather-straps", qty: 2 },
      { id: "chitin-plate", qty: 1 },
    ],
  },
  {
    target: "ultrasonic-sensor-grid",
    station: "bench",
    inputs: [
      { id: "glass-lens", qty: 1 },
      { id: "rubber-seal", qty: 1 },
      { id: "iron-frame", qty: 1 },
    ],
  },
  {
    target: "micro-servo-drill",
    station: "bench",
    inputs: [
      { id: "optic-fiber-spool", qty: 1 },
      { id: "matte-coating-compound", qty: 1 },
      { id: "steel-plates", qty: 1 },
    ],
  },
  {
    target: "vibration-dampener",
    station: "bench",
    inputs: [
      { id: "glass-lens", qty: 2 },
      { id: "rubber-seal", qty: 1 },
    ],
  },
  {
    target: "thermal-polarizer-film",
    station: "bench",
    inputs: [
      { id: "brass-fittings", qty: 2 },
      { id: "carbon-housing", qty: 1 },
    ],
  },
  {
    target: "medical-stimulant-cartridge",
    station: "bench",
    inputs: [
      { id: "fabric-mesh", qty: 1 },
      { id: "canvas-fiber", qty: 1 },
      { id: "iron-frame", qty: 1 },
    ],
  },
  {
    target: "power-regulation-unit",
    station: "bench",
    inputs: [
      { id: "circuit-board", qty: 1 },
      { id: "copper-wire", qty: 1 },
      { id: "steel-plates", qty: 1 },
    ],
  },
  {
    target: "sealed-hydraulic-pump",
    station: "bench",
    inputs: [
      { id: "composite-casing", qty: 1 },
      { id: "relay-antenna-strut", qty: 1 },
      { id: "glass-lens", qty: 1 },
    ],
  },
  {
    target: "nutrient-gel-pack",
    station: "bench",
    inputs: [
      { id: "fabric-mesh", qty: 1 },
      { id: "leather-straps", qty: 1 },
      { id: "rubber-seal", qty: 1 },
    ],
  },
  {
    target: "override-datakey",
    station: "bench",
    inputs: [
      { id: "ceramic-insulator", qty: 2 },
      { id: "power-cell-casing", qty: 1 },
    ],
  },
  {
    target: "drone-gyro-assembly",
    station: "bench",
    inputs: [
      { id: "iron-frame", qty: 2 },
      { id: "steel-plates", qty: 1 },
    ],
  },
  {
    target: "symbiotic-culture-vial",
    station: "bench",
    inputs: [
      { id: "polymer-tubing", qty: 2 },
      { id: "resin-casing", qty: 1 },
    ],
  },
  {
    target: "pheromone-distiller-coil",
    station: "bench",
    inputs: [
      { id: "glass-lens", qty: 2 },
      { id: "rubber-seal", qty: 1 },
    ],
  },
  {
    target: "phase-lens-array",
    station: "bench",
    inputs: [
      { id: "plastic-casing", qty: 2 },
      { id: "brass-fittings", qty: 1 },
    ],
  },
  {
    target: "ghost-signal-modulator",
    station: "bench",
    inputs: [
      { id: "acoustic-foam-wafer", qty: 2 },
      { id: "plastic-casing", qty: 1 },
    ],
  },
];


// ========== skillTechniques.js ==========
export const skillTechniques = {
  "dominion": [
    //
    // "signal-relay"
    //
    {
      "id": "relay-stabilizer",
      "name": "Relay Stabilizer",
      "icon": "🛰️📡",
      "skill": "signal-relay",
      "faction": "dominion",
      "description": "Restores damaged comm towers and aligns dishes for steady transmission.",
      "synergies": [
        {
          "targetFaction": "wilders",
          "targetSkill": "growth-control",
          "targetTechnique": "adaptive-lattice",
          "effect": "Rooted support frames keep relay masts upright on soft or shifting ground."
        },
        {
          "targetFaction": "veil",
          "targetSkill": "echo-trace",
          "targetTechnique": "quantum-relay-hook",
          "effect": "Maps ride the same channel as comm traffic, boosting range while lowering scan noise."
        }
      ]
    },
    {
      "id": "organic-link-adapter",
      "name": "Organic Link Adapter",
      "icon": "🔗🧬",
      "skill": "signal-relay",
      "faction": "dominion",
      "description": "Threads bio-fiber into cracked relay lines to hold connection when metal cabling fails.",
      "synergies": [
        {
          "targetFaction": "wilders",
          "targetSkill": "containment",
          "targetTechnique": "servo-containment",
          "effect": "Enzyme runoff is routed through bio-sheaths so relay panels don’t corrode."
        },
        {
          "targetFaction": "veil",
          "targetSkill": "transit-anchor",
          "targetTechnique": "depth-step",
          "effect": "Relays sync with surface settling moments to reduce transmission interference."
        }
      ]
    },
    {
      "id": "silent-channel",
      "name": "Silent Channel",
      "icon": "🔕📶",
      "skill": "signal-relay",
      "faction": "dominion",
      "description": "Masks signal noise so scanners can’t trace team transmissions.",
      "synergies": [
        {
          "targetFaction": "wilders",
          "targetSkill": "wild-influence",
          "targetTechnique": "silent-hand",
          "effect": "Quiet comms match scent-neutral zones so movement near wildlife stays unseen."
        },
        {
          "targetFaction": "veil",
          "targetSkill": "signal-mask",
          "targetTechnique": "echo-loop",
          "effect": "Comms timing matches data-scrub cycles so logs don’t record the team’s traffic."
        }
      ]
    },

    //
    // "system-override"
    //
    {
      "id": "command-cascade",
      "name": "Command Cascade",
      "icon": "🧩📟",
      "skill": "system-override",
      "faction": "dominion",
      "description": "Links multiple machines to take control in a single sequence.",
      "synergies": [
        {
          "targetFaction": "wilders",
          "targetSkill": "wild-influence",
          "targetTechnique": "signal-harmonizer",
          "effect": "Pacified groups keep pace with redirected turrets and drones without panic."
        },
        {
          "targetFaction": "veil",
          "targetSkill": "signal-mask",
          "targetTechnique": "control-spoof",
          "effect": "Spoofed credentials make the takeover look like a routine maintenance command."
        }
      ]
    },
    {
      "id": "bio-feedback-guard",
      "name": "Bio-Feedback Guard",
      "icon": "🛡️⚡",
      "skill": "system-override",
      "faction": "dominion",
      "description": "Uses flexible bio-couplers to absorb surge current during forced reboots.",
      "synergies": [
        {
          "targetFaction": "wilders",
          "targetSkill": "bio-shield",
          "targetTechnique": "bio-shielding",
          "effect": "Excess current sinks into bio-layers so panels don’t flash over."
        },
        {
          "targetFaction": "veil",
          "targetSkill": "transit-anchor",
          "targetTechnique": "phase-anchor",
          "effect": "Stabilizers bleed off spikes so control doesn't drop mid-sequence."
        }
      ]
    },
    {
      "id": "signal-mask-injection",
      "name": "Signal Mask Injection",
      "icon": "📦📡",
      "skill": "system-override",
      "faction": "dominion",
      "description": "Routes control packets through decoy traffic so monitors don’t flag the breach.",
      "synergies": [
        {
          "targetFaction": "wilders",
          "targetSkill": "bio-shield",
          "targetTechnique": "bio-shielding",
          "effect": "Bio-layers and signal masks combine to confuse hybrid detection systems."
        },
        {
          "targetFaction": "veil",
          "targetSkill": "signal-mask",
          "targetTechnique": "control-spoof",
          "effect": "Borrowed access patterns make the override look authorized."
        }
      ]
    },

    //
    // "drone-command"
    //
    {
      "id": "coordinated-flight-pattern",
      "name": "Coordinated Flight Pattern",
      "icon": "🛸🧭",
      "skill": "drone-command",
      "faction": "dominion",
      "description": "Syncs drone routes to avoid overlap and missed coverage.",
      "synergies": [
        {
          "targetFaction": "wilders",
          "targetSkill": "containment",
          "targetTechnique": "reflex-bloom",
          "effect": "Drones spread recovery spores evenly after hazards to reduce downtime."
        },
        {
          "targetFaction": "veil",
          "targetSkill": "stealth-transit",
          "targetTechnique": "drone-path-shadow",
          "effect": "Veil routes piggy-back drone paths so scouts move under the same visual cover."
        }
      ]
    },
    {
      "id": "rootpath-protocol",
      "name": "Rootpath Protocol",
      "icon": "🧭🌿",
      "skill": "drone-command",
      "faction": "dominion",
      "description": "Programs drones to move along overgrowth channels for safe passage.",
      "synergies": [
        {
          "targetFaction": "wilders",
          "targetSkill": "growth-control",
          "targetTechnique": "adaptive-lattice",
          "effect": "Vine scaffolds give drones lift points for field repairs."
        },
        {
          "targetFaction": "veil",
          "targetSkill": "echo-trace",
          "targetTechnique": "biotrace-resonance",
          "effect": "Organic scans mark low-snag routes through dense brush and debris."
        }
      ]
    },
    {
      "id": "echo-pulse-beacon",
      "name": "Echo Pulse Beacon",
      "icon": "📟🌊",
      "skill": "drone-command",
      "faction": "dominion",
      "description": "Adds quiet sonar to drones for mapping and quick relays.",
      "synergies": [
        {
          "targetFaction": "wilders",
          "targetSkill": "reclamation",
          "targetTechnique": "reclamation-drone-link",
          "effect": "Drones coordinate with bio-harvesters to map and salvage in synchronized sweeps."
        },
        {
          "targetFaction": "veil",
          "targetSkill": "echo-trace",
          "targetTechnique": "shadow-archive",
          "effect": "Drones upload map snapshots so scouts don’t need to rescan rooms."
        }
      ]
    },
    
    //
    // "safeguard"
    //
    {
      "id": "system-ground-loop",
      "name": "System Ground Loop",
      "icon": "⚓⚡",
      "skill": "safeguard",
      "faction": "dominion",
      "description": "Drops unstable current into safe grounds to prevent arcs.",
      "synergies": [
        {
          "targetFaction": "wilders",
          "targetSkill": "bio-shield",
          "targetTechnique": "bio-shielding",
          "effect": "Grounded charge feeds into bio-layers so suits take less shock."
        },
        {
          "targetFaction": "veil",
          "targetSkill": "transit-anchor",
          "targetTechnique": "phase-anchor",
          "effect": "Ground paths tie to stabilizers so readouts don't drift under load."
        }
      ]
    },
    {
      "id": "spore-vent-reclaimer",
      "name": "Spore Vent Reclaimer",
      "icon": "🌬️🧼",
      "skill": "safeguard",
      "faction": "dominion",
      "description": "Purges bad air and filters it for reuse after a spill or fire.",
      "synergies": [
        {
          "targetFaction": "wilders",
          "targetSkill": "growth-control",
          "targetTechnique": "photosymbiosis",
          "effect": "Plant beds turn vented gas into cleaner air, easing strain on masks."
        },
        {
          "targetFaction": "veil",
          "targetSkill": "transit-anchor",
          "targetTechnique": "depth-step",
          "effect": "Purge cycles align with surface dips to stop pressure swings."
        }
      ]
    },
    {
      "id": "manual-override-line",
      "name": "Manual Override Line",
      "icon": "🛠️⛓️",
      "skill": "safeguard",
      "faction": "dominion",
      "description": "Adds a physical cut-off so systems can be made safe by hand.",
      "synergies": [
        {
          "targetFaction": "wilders",
          "targetSkill": "growth-control",
          "targetTechnique": "adaptive-lattice",
          "effect": "Cut-offs trigger lattice to seal, stopping leaks instantly."
        },
        {
          "targetFaction": "veil",
          "targetSkill": "transit-anchor",
          "targetTechnique": "echo-fold",
          "effect": "Saved system states let shutoffs restore safe configurations instantly."
        }
      ]
    },

    //
    // "quantum-scan"
    //
    {
      "id": "entangled-synchrony",
      "name": "Entangled Synchrony",
      "icon": "🔗📊",
      "skill": "quantum-scan",
      "faction": "dominion",
      "description": "Links scanners so readings update together without delay.",
      "synergies": [
        {
          "targetFaction": "wilders",
          "targetSkill": "reclamation",
          "targetTechnique": "phase-cleanse",
          "effect": "Synchronized scans help target clean samples during salvage operations."
        },
        {
          "targetFaction": "veil",
          "targetSkill": "transit-anchor",
          "targetTechnique": "echo-fold",
          "effect": "Results back up to Veil storage so scans can be restored if instruments fail." // revised
        }
      ]
    },
    {
      "id": "bio-spectral-crosscheck",
      "name": "Bio-Spectral Crosscheck",
      "icon": "🧪☢️",
      "skill": "quantum-scan",
      "faction": "dominion",
      "description": "Matches radiation and field strength against known biological sources.",
      "synergies": [
        {
          "targetFaction": "veil",
          "targetSkill": "echo-trace",
          "targetTechnique": "biotrace-resonance",
          "effect": "Pulse returns show which walls hide root channels or nests."
        },
        {
          "targetFaction": "wilders",
          "targetSkill": "reclamation",
          "targetTechnique": "phase-cleanse",
          "effect": "Feedback helps break down thin contamination films faster."
        }
      ]
    },
    {
      "id": "ghost-echo-capture",
      "name": "Ghost Echo Capture",
      "icon": "🔍🔌",
      "skill": "quantum-scan",
      "faction": "dominion",
      "description": "Measures residual current in damaged circuits to locate still-active connections.", // revised
      "synergies": [
        {
          "targetFaction": "wilders",
          "targetSkill": "growth-control",
          "targetTechnique": "shadowroot-bloom",
          "effect": "Root tips wick trace particles out of hairline cracks for cleaner samples."
        },
        {
          "targetFaction": "veil",
          "targetSkill": "transit-anchor",
          "targetTechnique": "echo-fold",
          "effect": "Saved readings fill gaps in later scans for cleaner data continuity." // revised
        }
      ]
    }
  ],

  "wilders": [
    //
    // "reclamation"
    //
    {
      "id": "reclamation-drone-link",
      "name": "Reclamation Drone Link",
      "icon": "🤖🌾",
      "skill": "reclamation",
      "faction": "wilders",
      "description": "Pairs bio-harvesters with repair drones to pull clean samples fast.",
      "synergies": [
        {
          "targetFaction": "dominion",
          "targetSkill": "drone-command",
          "targetTechnique": "coordinated-flight-pattern",
          "effect": "Shared routes keep harvest and repair flights from crossing paths."
        },
        {
          "targetFaction": "veil",
          "targetSkill": "echo-trace",
          "targetTechnique": "shadow-archive",
          "effect": "Findings upload to scout archives so crews don't double dip rooms."
        }
      ]
    },
    {
      "id": "mycelial-rebalance",
      "name": "Mycelial Rebalance",
      "icon": "🍄🔁",
      "skill": "reclamation",
      "faction": "wilders",
      "description": "Uses live fungal filters to sort usable tissue from waste.",
      "synergies": [
        {
          "targetFaction": "dominion",
          "targetSkill": "safeguard",
          "targetTechnique": "spore-vent-reclaimer",
          "effect": "Filtered air keeps samples dry and prevents mold blooms."
        },
        {
          "targetFaction": "veil",
          "targetSkill": "lock-breach",
          "targetTechnique": "biofilm-stabilizer",
          "effect": "Seals around work sites so salvage doesn’t set off contact sensors."
        }
      ]
    },
    {
      "id": "phase-cleanse",
      "name": "Phase Cleanse",
      "icon": "🧼🌫️",
      "skill": "reclamation",
      "faction": "wilders",
      "description": "Applies mild disinfectant waves that lift spores and oils from surfaces.",
      "synergies": [
        {
          "targetFaction": "dominion",
          "targetSkill": "drone-command",
          "targetTechnique": "echo-pulse-beacon",
          "effect": "Drone sweeps spread the cleanse evenly through large rooms."
        },
        {
          "targetFaction": "veil",
          "targetSkill": "echo-trace",
          "targetTechnique": "quantum-relay-hook",
          "effect": "Relay points guide cleanse waves to hidden surfaces behind panels."
        }
      ]
    },
    //
    // "growth-control"
    //
    {
      "id": "adaptive-lattice",
      "name": "Adaptive Lattice",
      "icon": "🧱🌿",
      "skill": "growth-control",
      "faction": "wilders",
      "description": "Grows vine-metal frames that brace walls or span short gaps.",
      "synergies": [
        {
          "targetFaction": "dominion",
          "targetSkill": "signal-relay",
          "targetTechnique": "relay-stabilizer",
          "effect": "Braced masts don’t drift, keeping long links online."
        },
        {
          "targetFaction": "veil",
          "targetSkill": "transit-anchor",
          "targetTechnique": "phase-anchor",
          "effect": "Vine-braced anchors stay stable even when floors shift and settle."
        }
      ]
    },
    {
      "id": "photosymbiosis",
      "name": "Photosymbiosis",
      "icon": "☀️🌿",
      "skill": "growth-control",
      "faction": "wilders",
      "description": "Channels light through leaves to speed growth and thicken cover.",
      "synergies": [
        {
          "targetFaction": "dominion",
          "targetSkill": "safeguard",
          "targetTechnique": "spore-vent-reclaimer",
          "effect": "Filtered vents feed beds so air clears faster after a spill."
        },
        {
          "targetFaction": "veil",
          "targetSkill": "stealth-transit",
          "targetTechnique": "spore-veil",
          "effect": "Light-absorbing spores lower visibility for moving squads."
        }
      ]
    },
    {
      "id": "shadowroot-bloom",
      "name": "Shadowroot Bloom",
      "icon": "🌱🕳️",
      "skill": "growth-control",
      "faction": "wilders",
      "description": "Guides roots through shaded cracks to open paths and expose hidden space.",
      "synergies": [
        {
          "targetFaction": "dominion",
          "targetSkill": "quantum-scan",
          "targetTechnique": "ghost-echo-capture",
          "effect": "Roots pull faint power traces out of sealed joints for scanning."
        },
        {
          "targetFaction": "veil",
          "targetSkill": "echo-trace",
          "targetTechnique": "biotrace-resonance",
          "effect": "Combined pulses show hollows behind walls and floors."
        }
      ]
    },
    //
    // "wild-influence"
    //
    {
      "id": "signal-harmonizer",
      "name": "Signal Harmonizer",
      "icon": "🎼🐾",
      "skill": "wild-influence",
      "faction": "wilders",
      "description": "Lines up heart and breath rhythms to calm groups without drugs.",
      "synergies": [
        {
          "targetFaction": "dominion",
          "targetSkill": "system-override",
          "targetTechnique": "command-cascade",
          "effect": "Calm groups move in step with redirected machines."
        },
        {
          "targetFaction": "veil",
          "targetSkill": "signal-mask",
          "targetTechnique": "bio-id-mask",
          "effect": "Calm scent profiles look like background fauna on sensors."
        }
      ]
    },
    {
      "id": "pheromone-chorus",
      "name": "Pheromone Chorus",
      "icon": "🦋🌬️",
      "skill": "wild-influence",
      "faction": "wilders",
      "description": "Blends signals from multiple handlers to widen the calm zone.",
      "synergies": [
        {
          "targetFaction": "dominion",
          "targetSkill": "drone-command",
          "targetTechnique": "coordinated-flight-pattern",
          "effect": "Drones carry the blend across the room without startling nests."
        },
        {
          "targetFaction": "veil",
          "targetSkill": "stealth-transit",
          "targetTechnique": "mirror-walk",
          "effect": "Team steps track as one trace, reducing sudden noise."
        }
      ]
    },
    {
      "id": "silent-hand",
      "name": "Silent Hand",
      "icon": "🤫👣",
      "skill": "wild-influence",
      "faction": "wilders",
      "description": "Suppresses scent trails and quiets gear snaps near wildlife.",
      "synergies": [
        {
          "targetFaction": "dominion",
          "targetSkill": "signal-relay",
          "targetTechnique": "silent-channel",
          "effect": "Quiet comms match low-scent lanes so patrols pass by."
        },
        {
          "targetFaction": "veil",
          "targetSkill": "echo-trace",
          "targetTechnique": "quantum-relay-hook",
          "effect": "Relay hooks damp vibrations so footfalls don't echo in tight halls."
        }
      ]
    },
    //
    // "bio-shield"
    //
    {
      "id": "bio-shielding",
      "name": "Bio-Shielding",
      "icon": "🛡️⚡",
      "skill": "bio-shield",
      "faction": "wilders",
      "description": "Grows a thin, flexible layer that spreads shock and heat.",
      "synergies": [
        {
          "targetFaction": "dominion",
          "targetSkill": "safeguard",
          "targetTechnique": "system-ground-loop",
          "effect": "Grounded arcs bleed into the layer so skin doesn’t blister."
        },
        {
          "targetFaction": "veil",
          "targetSkill": "signal-mask",
          "targetTechnique": "bio-id-mask",
          "effect": "Heat and scent even out, leaving little for trackers to follow."
        }
      ]
    },
    {
      "id": "shared-immunity",
      "name": "Shared Immunity",
      "icon": "🧬🤝",
      "skill": "bio-shield",
      "faction": "wilders",
      "description": "Passes enzyme packs between teammates for short-term resistance.",
      "synergies": [
        {
          "targetFaction": "dominion",
          "targetSkill": "system-override",
          "targetTechnique": "bio-feedback-guard",
          "effect": "Enzymes protect skin from stray surges during forced reboots."
        },
        {
          "targetFaction": "veil",
          "targetSkill": "signal-mask",
          "targetTechnique": "bio-id-mask",
          "effect": "Body markers shift enough to pass basic biometric checks."
        }
      ]
    },
    {
      "id": "dim-shift-layer",
      "name": "Dim Shift Layer",
      "icon": "🪞🧩",
      "skill": "bio-shield",
      "faction": "wilders",
      "description": "Adjusts surface tone and reflectivity to match nearby materials.", // revised
      "synergies": [
        {
          "targetFaction": "dominion",
          "targetSkill": "quantum-scan",
          "targetTechnique": "entangled-synchrony",
          "effect": "Scan timings help the layer adjust before sensors sweep past."
        },
        {
          "targetFaction": "veil",
          "targetSkill": "transit-anchor",
          "targetTechnique": "echo-fold",
          "effect": "Saved lighting profiles help the layer auto-tune along familiar paths." // revised
        }
      ]
    },
    //
    // "containment"
    //
    {
      "id": "servo-containment",
      "name": "Servo Containment",
      "icon": "📦☣️",
      "skill": "containment",
      "faction": "wilders",
      "description": "Uses drone shells as hard cases for hot samples.",
      "synergies": [
        {
          "targetFaction": "dominion",
          "targetSkill": "signal-relay",
          "targetTechnique": "organic-link-adapter",
          "effect": "Bio-sheaths in relay bays don’t react to sealed cases."
        },
        {
          "targetFaction": "veil",
          "targetSkill": "lock-breach",
          "targetTechnique": "servo-alignment-tool",
          "effect": "Cases align with lock rails, letting crews move them quietly."
        }
      ]
    },
    {
      "id": "mutualist-growth",
      "name": "Mutualist Growth",
      "icon": "🤝🌱",
      "skill": "containment",
      "faction": "wilders",
      "description": "Cultivates living pods around unstable material to keep it from spreading.",
      "synergies": [
        {
          "targetFaction": "dominion",
          "targetSkill": "safeguard",
          "targetTechnique": "manual-override-line",
          "effect": "Cut-offs trigger pod close-up so leaks don’t escape."
        },
        {
          "targetFaction": "veil",
          "targetSkill": "lock-breach",
          "targetTechnique": "echo-pulse-decoy",
          "effect": "Decoy pulses draw guard attention away while pods close and seal."
        }
      ]
    },
    {
      "id": "reflex-bloom",
      "name": "Reflex Bloom",
      "icon": "💊🌿",
      "skill": "containment",
      "faction": "wilders",
      "description": "Deploys small regenerative pods that release healing compounds after exposure.", // revised
      "synergies": [
        {
          "targetFaction": "dominion",
          "targetSkill": "drone-command",
          "targetTechnique": "coordinated-flight-pattern",
          "effect": "Drones spread bloom spores across the team between rooms."
        },
        {
          "targetFaction": "veil",
          "targetSkill": "stealth-transit",
          "targetTechnique": "spore-veil",
          "effect": "Bloom and veil spores mix to hide heat shimmer and breath."
        }
      ]
    }
  ],

  "veil": [
    //
    // "echo-trace"
    //    
    {
      "id": "quantum-relay-hook",
      "name": "Relay Hook",
      "icon": "🪝📡",
      "skill": "echo-trace",
      "faction": "veil",
      "description": "Links mapping scanners to nearby network relays so data and comms share the same path.", // revised (removed 'quantum' jargon)
      "synergies": [
        {
          "targetFaction": "dominion",
          "targetSkill": "signal-relay",
          "targetTechnique": "relay-stabilizer",
          "effect": "Stable masts carry both traffic and survey data without dropouts."
        },
        {
          "targetFaction": "wilders",
          "targetSkill": "growth-control",
          "targetTechnique": "photosymbiosis",
          "effect": "Growth covers relay lines so data travels protected through the room."
        }
      ]
    },
    {
      "id": "biotrace-resonance",
      "name": "Biotrace Resonance",
      "icon": "🧫📶",
      "skill": "echo-trace",
      "faction": "veil",
      "description": "Sends short pulses that reflect off living material to flag organic routes.",
      "synergies": [
        {
          "targetFaction": "dominion",
          "targetSkill": "quantum-scan",
          "targetTechnique": "bio-spectral-crosscheck",
          "effect": "Combined returns show where growth hides power lines and vents."
        },
        {
          "targetFaction": "wilders",
          "targetSkill": "reclamation",
          "targetTechnique": "phase-cleanse",
          "effect": "Pulse images reveal thin films left after salvage so teams can clear them."
        }
      ]
    },
    {
      "id": "shadow-archive",
      "name": "Shadow Archive",
      "icon": "🗄️🗺️",
      "skill": "echo-trace",
      "faction": "veil",
      "description": "Stores map data in portable memory that syncs on contact.",
      "synergies": [
        {
          "targetFaction": "dominion",
          "targetSkill": "drone-command",
          "targetTechnique": "echo-pulse-beacon",
          "effect": "Drones post snapshots so scouts don’t re-scan cleared rooms."
        },
        {
          "targetFaction": "wilders",
          "targetSkill": "wild-influence",
          "targetTechnique": "silent-hand",
          "effect": "Echo dampers cut footfall bounce so nearby wildlife stays calm."
        }
      ]
    },
    //
    // "lock-breach"
    // 
    {
      "id": "servo-alignment-tool",
      "name": "Servo Alignment Tool",
      "icon": "🔧🔓",
      "skill": "lock-breach",
      "faction": "veil",
      "description": "Calibrates lock servos so picks and spinners seat cleanly.",
      "synergies": [
        {
          "targetFaction": "dominion",
          "targetSkill": "safeguard",
          "targetTechnique": "manual-override-line",
          "effect": "Kill-switches and lock rails line up to prevent jolts mid-breach."
        },
        {
          "targetFaction": "wilders",
          "targetSkill": "containment",
          "targetTechnique": "servo-containment",
          "effect": "Sealed cases ride rails quietly through narrow passages."
        }
      ]
    },
    {
      "id": "biofilm-stabilizer",
      "name": "Biofilm Stabilizer",
      "icon": "🧴🔇",
      "skill": "lock-breach",
      "faction": "veil",
      "description": "Applies a thin organic seal so panels don’t rattle or squeal.",
      "synergies": [
        {
          "targetFaction": "dominion",
          "targetSkill": "system-override",
          "targetTechnique": "signal-mask-injection",
          "effect": "Quiet seals and decoy traffic hide the sound and the signal of a breach."
        },
        {
          "targetFaction": "wilders",
          "targetSkill": "reclamation",
          "targetTechnique": "mycelial-rebalance",
          "effect": "Seals keep mold from creeping under plates while crews work."
        }
      ]
    },
    {
      "id": "echo-pulse-decoy",
      "name": "Echo Pulse Decoy",
      "icon": "🎇👀",
      "skill": "lock-breach",
      "faction": "veil",
      "description": "Sends a harmless pulse so guards look the wrong way during a breach.",
      "synergies": [
        {
          "targetFaction": "dominion",
          "targetSkill": "system-override",
          "targetTechnique": "command-cascade",
          "effect": "Distractions line up with machine takeovers so alarms don’t stack."
        },
        {
          "targetFaction": "wilders",
          "targetSkill": "containment",
          "targetTechnique": "mutualist-growth",
          "effect": "Pods close while decoys fire, hiding movement sounds."
        }
      ]
    },
    //
    // "stealth-transit"
    // 
    {
      "id": "drone-path-shadow",
      "name": "Drone Path Shadow",
      "icon": "🕶️🤖",
      "skill": "stealth-transit",
      "faction": "veil",
      "description": "Walks the same lanes as drones so both use one visual trace.",
      "synergies": [
        {
          "targetFaction": "dominion",
          "targetSkill": "drone-command",
          "targetTechnique": "rootpath-protocol",
          "effect": "Scouts follow programmed drone routes for safe, low-visibility passage."
        },
        {
          "targetFaction": "wilders",
          "targetSkill": "wild-influence",
          "targetTechnique": "pheromone-chorus",
          "effect": "Air carries calm signals along the same lanes, reducing spooks."
        }
      ]
    },
    {
      "id": "spore-veil",
      "name": "Spore Veil",
      "icon": "🌫️🕵️",
      "skill": "stealth-transit",
      "faction": "veil",
      "description": "Releases faint spores that dull edges and reduce shine.",
      "synergies": [
        {
          "targetFaction": "dominion",
          "targetSkill": "safeguard",
          "targetTechnique": "system-ground-loop",
          "effect": "Grounded systems prevent static buildup that could reveal the spore cloud."
        },
        {
          "targetFaction": "wilders",
          "targetSkill": "containment",
          "targetTechnique": "reflex-bloom",
          "effect": "Veil spores mix with bloom to hide heat shimmer and breath."
        }
      ]
    },
    {
      "id": "mirror-walk",
      "name": "Mirror Walk",
      "icon": "🪞👣",
      "skill": "stealth-transit",
      "faction": "veil",
      "description": "Matches the lead scout’s pace so the group reads as one unit.",
      "synergies": [
        {
          "targetFaction": "dominion",
          "targetSkill": "drone-command",
          "targetTechnique": "rootpath-protocol",
          "effect": "Shared pacing and overgrowth lanes reduce brush noise."
        },
        {
          "targetFaction": "wilders",
          "targetSkill": "wild-influence",
          "targetTechnique": "pheromone-chorus",
          "effect": "Synchronized steps pair with a single calm signal, lowering alert spikes."
        }
      ]
    },
    //
    // "signal-mask"
    // 
    {
      "id": "control-spoof",
      "name": "Control Spoof",
      "icon": "🎭🔐",
      "skill": "signal-mask",
      "faction": "veil",
      "description": "Builds fake machine credentials that pass quick checks.",
      "synergies": [
        {
          "targetFaction": "dominion",
          "targetSkill": "system-override",
          "targetTechnique": "command-cascade",
          "effect": "Spoofs clear the way for chained takeovers without alarms."
        },
        {
          "targetFaction": "wilders",
          "targetSkill": "bio-shield",
          "targetTechnique": "shared-immunity",
          "effect": "Shifted biometrics keep gates from flagging the team during checks."
        }
      ]
    },
    {
      "id": "bio-id-mask",
      "name": "Bio-ID Mask",
      "icon": "🪪👃",
      "skill": "signal-mask",
      "faction": "veil",
      "description": "Mimics heat and scent patterns so sensors read the team as local life.",
      "synergies": [
        {
          "targetFaction": "dominion",
          "targetSkill": "system-override",
          "targetTechnique": "signal-mask-injection",
          "effect": "Bio and machine masks match, confusing hybrid doors and cameras."
        },
        {
          "targetFaction": "wilders",
          "targetSkill": "wild-influence",
          "targetTechnique": "signal-harmonizer",
          "effect": "Calm scent profiles slot into the mask so patrols ignore the team."
        }
      ]
    },
    {
      "id": "echo-loop",
      "name": "Echo Loop",
      "icon": "🔁📜",
      "skill": "signal-mask",
      "faction": "veil",
      "description": "Writes temporary log entries to hide the real exit trail.", // revised
      "synergies": [
        {
          "targetFaction": "dominion",
          "targetSkill": "signal-relay",
          "targetTechnique": "silent-channel",
          "effect": "Dummy logs and quiet comms leave nothing firm to trace."
        },
        {
          "targetFaction": "wilders",
          "targetSkill": "reclamation",
          "targetTechnique": "mycelial-rebalance",
          "effect": "Fungal filters break down organic traces that could reveal the exit route."
        }
      ]
    },
    //
    // "transit-anchor"
    // 
    {
      "id": "phase-anchor",
      "name": "Phase Anchor",
      "icon": "🪨📍",
      "skill": "transit-anchor",
      "faction": "veil",
      "description": "Drops a small stabilizer so floors and walls stop drifting.",
      "synergies": [
        {
          "targetFaction": "dominion",
          "targetSkill": "signal-relay",
          "targetTechnique": "organic-link-adapter",
          "effect": "Anchors act as grounds so patched relay lines don’t wander."
        },
        {
          "targetFaction": "wilders",
          "targetSkill": "containment",
          "targetTechnique": "mutualist-growth",
          "effect": "Pods close around anchors, preventing leaks from creeping."
        }
      ]
    },
    {
      "id": "depth-step",
      "name": "Depth Step",
      "icon": "⏱️👣",
      "skill": "transit-anchor",
      "faction": "veil",
      "description": "Times crossings for moments when surfaces settle.",
      "synergies": [
        {
          "targetFaction": "dominion",
          "targetSkill": "safeguard",
          "targetTechnique": "spore-vent-reclaimer",
          "effect": "Purge cycles match crossings so pressure and air stay even."
        },
        {
          "targetFaction": "wilders",
          "targetSkill": "growth-control",
          "targetTechnique": "shadowroot-bloom",
          "effect": "Roots shift to create stable footholds at the right moment."
        }
      ]
    },
    {
      "id": "echo-fold",
      "name": "Echo Fold",
      "icon": "🗂️🛰️",
      "skill": "transit-anchor",
      "faction": "veil",
      "description": "Caches visual and sensor data from recent scans for quick reload if equipment glitches.", // revised
      "synergies": [
        {
          "targetFaction": "dominion",
          "targetSkill": "quantum-scan",
          "targetTechnique": "entangled-synchrony",
          "effect": "Readings merge into cached files so analysts can review full logs later." // revised
        },
        {
          "targetFaction": "wilders",
          "targetSkill": "bio-shield",
          "targetTechnique": "dim-shift-layer",
          "effect": "Saved lighting profiles help the layer auto-tune along familiar paths." // revised
        }
      ]
    }
  ]
};

// ========== syndicateUpgrades.js ==========
/**
 * Syndicate Charters - the Syndicate layer (M3) upgrade catalog.
 *
 * Bought with Writs 📜, which are issued at founding. Same schema as
 * commandUpgrades.js, and js/modules/syndicate-core.js consumes it the same way
 * via generated js/constants.js:
 *   - costs[]: Writ price per rank; costs.length = max ranks
 *   - effectValues[]: per-rank magnitude, consumed at the effectHint site
 *
 * The distinction that matters when adding to this file: Command upgrades buy
 * things WITHIN an era and are wiped at founding; charters buy things ABOUT
 * every future era and survive it. An effect that does not outlive a founding
 * belongs in commandUpgrades.js instead.
 *
 * Pricing is anchored to a measured first era banking ~700-900 marks, i.e.
 * 26-30 Writs at first founding (docs/layers/3-syndicate/design.md §2). That makes the
 * first founding a real choice rather than a shopping list: the automation trio
 * (5 + 16 + 8 = 29) takes nearly everything, so the alternative is two of the
 * three, or the economy path (10 + 12 = 22) with change to spare.
 */

export const syndicateUpgrades = {
  // ⚠ WORDING CORRECTED S14 — the behaviour was right, the description was not.
  //
  // It used to read "retained through every founding, instead of being re-bought
  // with marks each era", which describes RETENTION and implies you must have
  // owned Auto-Raid Protocol already. `foundSyndicateCore` has always granted it
  // outright, bought or not, and that is the intent: reaching L3 is what
  // automates L1 for good. The old wording made a correct grant look like a
  // 5-Writ exploit on a 300-mark upgrade.
  //
  // The Command upgrade still earns its price as EARLY ACCESS. Measured: a
  // player who banks everything and buys nothing else reaches 300 marks around
  // day 16, against a first founding at day 25 — so it buys about nine days of
  // hands-off raiding, paid for by going without Officer Academy, Forward Depot
  // and Field Conditioning in the meantime. A real trade, not a formality, and
  // nowhere near "automated a few days in".
  "standing-orders": {
    name: "Standing Orders",
    icon: "📋",
    flavor: "The orders outlive the officers who wrote them.",
    effect: "Every founding starts the new era with Auto-Raid Protocol and Auto-Fabricator running — whether or not Command ever bought them.",
    effectHint: "foundSyndicateCore: grants auto-raid-protocol + auto-fabricator into the fresh command.upgrades",
    costs: [5],
    effectValues: [true],
  },
  "succession-doctrine": {
    name: "Succession Doctrine",
    icon: "🕊️",
    flavor: "No chair stays empty long enough to matter.",
    effect: "Promotes at peak marks rate and raises the successor from your template, hands-off.",
    effectHint: "syndicate-core: peak-age policy, evaluated at raid end (requires auto-raid-protocol + auto-fabricator)",
    costs: [16],
    effectValues: [true],
    // COMMAND upgrades, not charters: the dependency crosses layers on purpose.
    //
    // BOTH, because this charter promotes on the MARKS RATE and 71% of a
    // character's marks are crafting-derived (see `auto-fabricator` in
    // commandUpgrades.js for the measurement). Automating the raid but not the
    // workshop would have this policy promoting off a third of the income it
    // was calibrated against — raid -> craft -> marks -> promote is the loop,
    // and three of four steps automates nothing.
    //
    // `requiresName` is now only a fallback: both upgrades ship in the catalog,
    // so the live name wins. It is kept because it costs nothing and the lock
    // line leaks a raw slug without it if an id is ever renamed.
    requires: ["auto-raid-protocol", "auto-fabricator"],
    requiresName: "Auto-Raid Protocol",
  },
  "operations-charter": {
    name: "Operations Charter",
    icon: "🗺️",
    flavor: "Signed routes, sealed manifests, and somebody else's problem at the far end.",
    effect: "Unlocks Operations, and licenses 1 / 2 / 3 concurrent operations.",
    effectHint: "operations: concurrent slot cap = min(this, leader + idle era-Legends)",
    costs: [8, 20, 45],
    effectValues: [1, 2, 3],
  },
  "founders-endowment": {
    name: "Founder's Endowment",
    icon: "🏛️",
    flavor: "Every charter opens with a line of credit against its own reputation.",
    effect: "Each new Command era starts with 100 / 250 / 500 marks.",
    effectHint: "foundSyndicateCore: seeds command.marks after the wipe",
    costs: [10, 25, 60],
    effectValues: [100, 250, 500],
  },
  // ⚠ REPURPOSED IN S14, from "Command upgrades cost 20/35/50% less".
  //
  // The discount was a DEAD CHARTER, and measurably so: a batch of 8 runs with
  // the discount forced to 90% moved the era-pacing targets not at all
  // (foundings by day 71: 2.63 before, 2.63 after). Command upgrade cost is
  // simply not what sets era length.
  //
  // What does: era length = Legends required x tour length, and TOUR LENGTH
  // CANNOT BE MOVED BY POWER. The promotion policy ends a tour when the
  // incumbent's marks rate falls below what a fresh recruit averages, and that
  // benchmark is measured from the player's own last three tours — so any
  // uniform buff lifts both sides and the crossover never moves. Three
  // independent buffs confirmed it (see docs/layers/3-syndicate/implementation.md § 2026-08-03 "Era pacing").
  //
  // That leaves the Legend requirement as the only lever on era pacing, which
  // is why this charter now spends its ranks there. It is also the layer's own
  // fantasy stated literally: each Syndicate you found makes the next one
  // easier to found.
  "institutional-memory": {
    name: "Institutional Memory",
    icon: "📚",
    flavor: "The second time a thing is built, nobody pays to learn it again.",
    effect: "Found the next Syndicate with 3 / 5 / 6 fewer Legends.",
    effectHint: "foundingLegendRequirement: subtracts from the Legend gate",
    costs: [12, 16, 26],
    effectValues: [3, 5, 6],
  },
  "provision-contracts": {
    name: "Provision Contracts",
    icon: "📦",
    flavor: "Bulk rates, negotiated once, honoured across the whole planet.",
    effect: "Operation cargo costs 25 / 40% less.",
    effectHint: "operations: cargo deduction at launch",
    costs: [15, 40],
    effectValues: [0.25, 0.4],
  },
  "blackout-sanction": {
    name: "Blackout Sanction",
    icon: "🌑",
    flavor: "Authorisation to run the jobs that were never filed.",
    effect: "Unlocks Blackout Contracts - optional raids under hard modifiers, each paying a permanent perk.",
    effectHint: "raid planning: blackout toggle; perks via getBlackoutPerk",
    costs: [30],
    effectValues: [true],
  },
};


// ========== worldMap.js ==========
// ============================================================================
// WORLD MAP — the L3 Operations grid
// ============================================================================
// A hand-authored, FIXED tile set (unlike raid grids, which are generated per
// raid). ONE world, shared by every operation — convoys in transit are drawn on
// this same map, not on a per-operation copy.
//
// Size matches a Tier 3 raid map (7 x 17 = 119 tiles) so the renderer needs no
// new layout maths.
//
// FIXED, not generated, for three reasons: routes must be learnable (a player
// should come to know the map), demand pricing makes specific destinations
// meaningful, and S9's world clock derives each region's LONGITUDE from its
// capital's column — so this file is the single source of truth for both
// geography and time-of-day.
//
// TWO INDEPENDENT LAYERS, deliberately separate:
//
//   regionRows  — which region OWNS each tile. Zones are contiguous, so the map
//                 reads as five territories rather than five pins scattered
//                 through anonymous ground.
//   terrainRows — what the ground IS. Terrain cuts across ownership: a region
//                 can hold highlands and water, and that is what makes one
//                 crossing slower than another.
//
// Keeping them apart means recolouring territory never disturbs routing, and
// re-terraforming never redraws borders.
//
// Terrain legend:
//   .  open ground   cost 1
//   ^  highland      cost 2
//   ~  water         cost 3   (barges/fords — slow, not blocked)
//   #  impassable    no route crosses it
export const worldMap = {
  width: 17,
  height: 9,

  // Region ownership. Digits index `regionOrder` below.
  // Row strings are the authoring format: a 153-entry array is unreadable and
  // undiffable, and this is meant to be edited by hand.
  //
  // Flooded Bastions holds a foothold on the EASTERN shore (row 6, column 13),
  // across the strait. Not decoration: Hollow Core's capital is the far side of
  // that water, and with staging points restricted to land there was no
  // other-region land within a Short Haul's reach of it — the destination
  // silently offered no route at all. A region named for water spanning some is
  // also the least strange way to fix it.
  //
  // Boundaries deliberately WOBBLE row to row rather than running straight
  // down. Straight columns read as a bar chart; an irregular border reads as
  // territory. Each region's run must still overlap its own run in the
  // neighbouring rows or the territory splits into islands — worldMap.test.js
  // fails on that, and it is the easy mistake to make while editing these.
  regionRows: [
    "11112223334444555",
    "11122233344455555",
    "11122233334445555",
    "11112223334445555",
    "11112223334444555",
    "11122233344445555",
    "11112223334444555",
    "11122233334445555",
    "11112223334444555",
  ],

  // Terrain, drawn as CONTINENTS rather than textured ground.
  //
  // 9 rows, not 7: 17x9 is close to the 2:1 of an equirectangular world map,
  // and the extra rows are what give the landmasses room to have a shape at all.
  // The old 7-row grid matched a Tier-3 raid map, but that parity was already
  // vestigial — the world map has its own CSS-grid renderer and never used the
  // raid grid's layout.
  //
  // Ocean is `~`, which is PASSABLE at cost 3. That is what keeps every region
  // routable no matter how the coastline is drawn: a strait is a toll, never a
  // wall. Land is deliberately connected east-west through the middle rows so
  // routes stay overland and the cost model barely moves; the one real crossing
  // is the strait at columns 11-13, which puts Hollow Core genuinely across a
  // sea and costs about a quarter of a Grand Circuit to reach.
  //
  // `#` is true impassable and is used only for a couple of inland peaks. Ocean
  // being passable means a peak can never strand a region.
  //
  // THE BORDER IS ALL OCEAN — row 0, row 8, column 0 and column 16. That frame
  // is what makes the grid read as a world rather than as a cropped tile sheet,
  // and it also guarantees no route or staging point can be pinned against an
  // edge with nowhere to go. Keep it when editing.
  terrainRows: [
    "~~~~~~~~~~~~~~~~~",
    "~..^..~~.^..~~..~",
    "~...^.~..^..~.^.~",
    "~~.^..^...^~~..^~",
    "~.....#~..^..~..~",
    "~.^...~~....~~.^~",
    "~~..^.~~~..~~...~",
    "~~~.^~~~~.^~~~..~",
    "~~~~~~~~~~~~~~~~~",
  ],

  // Digit -> region id. Order is west-to-east, which is also longitude order.
  regionOrder: {
    "1": "cradle-expanse",
    "2": "shard-cities",
    "3": "verdant-reclamation",
    "4": "sunken-bastions",
    "5": "hollow-core",
  },

  // Where convoys actually depart from and arrive at. A region is an area, but
  // a route needs a point — this is that point. Every capital must sit inside
  // its own territory and on passable ground; worldMap.test.js enforces both,
  // because either mistake produces a region that silently cannot be reached.
  capitals: {
    "cradle-expanse": { row: 2, col: 2 },
    "shard-cities": { row: 4, col: 5 },
    "verdant-reclamation": { row: 3, col: 8 },
    "sunken-bastions": { row: 5, col: 11 },
    "hollow-core": { row: 6, col: 15 },
  },

  terrainCost: {
    ".": 1,
    "^": 2,
    "~": 3,
    "#": null, // impassable
  },
};


